/**
 * Builder i18n — react-i18next.
 *
 * Migrated from the previous hand-rolled `useT()` hook to react-i18next
 * so the shared `@pervasivemind/dashboard-render` package can call
 * `useTranslation()` and get the same locale as the rest of the app.
 *
 * Interpolation: the existing 2000+ keys use single-curly placeholders
 * (`{name}`, `{count}`) — we override i18next's default prefix/suffix
 * to `{`/`}` so the dictionaries stay untouched. New keys can follow
 * either convention; both work.
 *
 * Storage: we use the standard `i18nextLng` localStorage key (same as
 * hp-portal) — admins that had `builder.locale` set will revert to
 * browser-detected default on the next reload; acceptable trade-off
 * for aligning both apps on one key.
 *
 * For non-React callers (`getLocale()` / `t(...)`) we expose
 * `i18n.t` and `i18n.language`-backed helpers at the bottom.
 */

import i18n from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { useCallback } from "react";
import { PT } from "./pt";

export type Locale = "en" | "es" | "pt";

type StringTable = Record<string, string>;

const EN: StringTable = {
  // Component gallery (Fase D, look v3, 2026-10-24).
  "gallery.count": "{entries} examples · {components} components",
  "gallery.search": "Search component, table, filter…",
  "gallery.clearSearch": "Clear search",
  "gallery.animations": "Animations",
  "gallery.replay": "Replay examples",
  "gallery.examples": "{count} examples",
  "gallery.sampleData": "Sample data · optional interaction",
  "gallery.searchResults": "Search results",
  "gallery.backToFamily": "← Whole family",
  "gallery.empty.title": "We could not find that component",
  "gallery.empty.hint": "Try «KPI», «Table», «Date» or «Button».",
  "gallery.component": "Component",
  "gallery.composition": "Composition",
  "gallery.composedOf": "Made of {parts}",
  "gallery.preview": "Preview",
  "gallery.interactive.on": "Interaction on",
  "gallery.interactive.off": "Interaction off",
  "gallery.interactive.aria": "Interaction of {title}",
  "gallery.expand": "Expand {label}",
  "gallery.categoriesNav": "Component categories",
  "gallery.copyImport": "Copy import",
  "gallery.copied": "Copied",
  "gallery.source.ui": "UI kit",
  "gallery.source.bi": "BI components",
  "gallery.source.render": "Dashboard render",
  "gallery.source.admin": "Admin UI",
  "gallery.demoError": "This component could not be shown: {message}",
  "gallery.missing": "Kit exports without an example: {names}",
  "gallery.unknownCategory": "There is no category «{category}».",
  "gallery.exploreData": "Explore data",
  "gallery.noData": "No data",
  "gallery.group.system": "System",
  "gallery.group.data": "Data and reports",
  "gallery.group.composition": "Composition",
  "gallery.category.foundation": "Foundations",
  "gallery.category.navigation": "Navigation",
  "gallery.category.controls": "Controls",
  "gallery.category.surfaces": "Cards and states",
  "gallery.category.feedback": "Messages and panels",
  "gallery.category.layout": "App structure",
  "gallery.category.drag": "Drag and upload",
  "gallery.category.kpis": "KPI indicators",
  "gallery.category.charts": "Charts",
  "gallery.category.tables": "Tables",
  "gallery.category.filters": "Filters and selection",
  "gallery.category.dates": "Dates and periods",
  "gallery.category.measures": "Meters and context",
  "gallery.category.maps": "Maps",
  "gallery.category.compositions": "Report compositions",
  "gallery.category.motion-page": "Motion · page",
  "gallery.category.transitions": "Motion · elements",
  "gallery.category.brand": "Engine tokens",
  "gallery.foundation.palette.title": "The visual language",
  "gallery.foundation.palette.text": "Navy to orient, pink to act, the module colour to read the data.",
  "gallery.foundation.usage.ink": "Navigation and titles",
  "gallery.foundation.usage.pink": "Action and brand",
  "gallery.foundation.usage.violet": "Controls and series",
  "gallery.foundation.usage.module": "Module colour",
  "gallery.foundation.usage.canvas": "App background",
  "gallery.foundation.usage.border": "Dividers and cards",
  "gallery.foundation.type.title": "Typography",
  "gallery.foundation.type.sample": "The data, clearer.",
  "gallery.foundation.type.text": "Inter for controls and reading. Nunito Sans for titles and indicators. Secondary text with enough contrast to read it.",
  "gallery.foundation.surfaces.title": "Surfaces and spacing",
  "gallery.foundation.radius.field": "Field",
  "gallery.foundation.radius.control": "Control",
  "gallery.foundation.radius.card": "Card",
  "gallery.foundation.radius.panel": "Panel",
  "gallery.foundation.inContext": "Components in context",
  // Common
  "common.cancel": "Cancel",
  "common.save": "Save",
  "common.delete": "Delete",
  "common.create": "Create",
  "common.edit": "Edit",
  "common.loading": "Loading…",
  // Shared dashboard-render drill / row-action components (DrillSection +
  // RowDrillPanel). Mirror of the portal's keys so the builder Preview
  // renders the exact same strings.
  "drill.title": "Analysis",
  "drill.noData": "No data for this analysis.",
  "drill.closeAnalysis": "Close analysis",
  "drill.close": "Close",
  "drill.fromKpi": "↑ from KPI",
  "table.rowDrill.label": "↳ Row detail",
  "table.close": "Close",
  "common.error": "Something went wrong",
  "common.retry": "Retry",
  "common.missingLabel":
    "This value has no label yet — showing what the backend sent.",
  "common.confirm": "Confirm",

  // Login
  "login.title": "Digital Shelf Analytics",
  "login.subtitle": "Sign in to manage report definitions",
  "login.email": "Email",
  "login.password": "Password",
  "login.submit": "Sign in",
  "login.invalid": "Invalid email or password",

  // Builder list (iter 27: unified Projects + Reports view)
  "list.title": "Reports & Projects",
  "list.subtitle":
    "Every report belongs to a project. Create projects, then add reports to them.",
  "list.new": "New report",
  "list.newProject": "New project",
  "list.addReport": "Report",
  "list.noReportsInProject":
    "No reports yet in this project. Use \"+ report\" to add one.",
  "list.reorderHint":
    "Drag rows to set the portal sidebar order — changes go live in about a minute.",
  "list.reorderSaved": "Order saved.",
  "list.reorderError": "Couldn't save the order — previous order restored.",
  "list.dragHandle": "Drag to reorder",
  "list.empty":
    "No projects yet. Click \"+ New Project\" to create your first one.",

  // PR-0 builder reorg — projects home grid + project hub
  "home.title": "Projects",
  "home.subtitle":
    "One card per project — reports live inside each project.",
  "home.search": "Search projects…",
  "home.reports": "reports",
  "home.published": "published",
  "home.unassigned": "Unclassified",
  "home.unassigned.desc": "Drafts that don't belong to any project yet.",
  "hub.back": "Projects",
  "hub.tab.reports": "Reports",
  "hub.tab.config": "Settings",
  "common.saved": "Saved",

  // PR-1 builder reorg — searchable icon picker + report icon + scope card
  "iconPicker.search": "Search icons…",
  "iconPicker.count": "{total} icons",
  "iconPicker.countFiltered": "{shown} of {total} icons",
  "iconPicker.noResults": "No icons match.",
  "list.create.icon": "Icon (optional)",
  "editor.report.iconLabel": "Icon",
  "hub.scope.title": "What is configured where",
  "hub.scope.projectLabel": "Project",
  "hub.scope.projectItems":
    "Data (OBT), auto-refresh, maestros, files, access, icon/color.",
  "hub.scope.reportLabel": "Report",
  "hub.scope.reportItems":
    "Dashboard, SQL views, AI knowledge, chat, insights, snapshots.",

  // PR-2 builder reorg — la IA del reporte repensada (pills + salud)
  "editor.ai.panes.overview": "Overview",
  "editor.ai.panes.knowledge": "Knowledge",
  "editor.ai.panes.chat": "Chat",
  "editor.ai.panes.insights": "Insights",
  "editor.ai.overview.enabledTitle": "AI active on this report",
  "editor.ai.overview.enabledHint":
    "The portal shows Chat + AI Analysis to subscribed users (per their permissions).",
  "editor.ai.overview.knowledgeDesc":
    "What the AI knows about this report — feeds BOTH the chat and the insights. Configure it once.",
  "editor.ai.overview.chatDesc":
    "The conversational surface in the portal sidebar.",
  "editor.ai.overview.insightsDesc":
    "The AI Analysis narrative, regenerated on every new period.",
  "editor.ai.overview.knowledgeCounts": "{docs} docs · {examples} examples",
  "editor.ai.overview.focusCount": "{count} focus areas",
  "editor.ai.overview.promptOverrideOn": "Prompt override: active",
  "editor.ai.overview.promptOverrideOff": "Prompt override: uses the global",
  "editor.ai.overview.modelDefault": "Model: global default",
  "editor.ai.overview.modelOverride": "Model: {model}",
  "editor.ai.overview.chatCounts":
    "{questions} starter questions · {ctas} CTAs",
  "editor.ai.overview.specCount": "{count} insight specs",
  "editor.ai.overview.temporalMode": "Per-report temporal window",
  "editor.ai.overview.weeklyMode": "Legacy weekly mode",
  "editor.ai.overview.maxInsights": "max {count} insights per snapshot",
  "editor.ai.overview.contextNotesHint":
    "Context notes (business events) live at the PROJECT level and apply to all its reports.",
  "editor.ai.overview.open": "Open",
  "editor.ai.aiHomeTime.title": "AI Home: time",
  "editor.ai.aiHomeRef.title": "AI Home: reference KPIs and charts",
  "editor.ai.aiHomeRef.description": "This report's official measures. When someone asks for one of them, the AI Home uses this SQL as written instead of writing its own, and its AI review never rewrites it.",
  "editor.ai.aiHomeRef.placeholders": "Time: {asOf} (the latest period) and {since} (the start of the range), never a written date. Dashboard filters: mark each column with {filter} in a WHERE; a mark no filter takes reads TRUE.",
  "editor.ai.aiHomeRef.add": "Add",
  "editor.ai.aiHomeRef.fromExample": "From a Q&A example",
  "editor.ai.aiHomeRef.fromExampleHint": "Replace any written date with the time placeholders.",
  "editor.ai.aiHomeRef.noExamples": "This report has no SQL example yet.",
  "editor.ai.aiHomeRef.empty": "No reference yet: the AI Home designs every element.",
  "editor.ai.aiHomeRef.untitled": "Untitled",
  "editor.ai.aiHomeRef.titleLabel": "Title",
  "editor.ai.aiHomeRef.kindLabel": "Type",
  "editor.ai.aiHomeRef.formatLabel": "Format",
  "editor.ai.aiHomeRef.format.number": "Number",
  "editor.ai.aiHomeRef.format.percent": "Percent (0..1)",
  "editor.ai.aiHomeRef.format.currency": "Currency",
  "editor.ai.aiHomeRef.scopeSafe": "Valid under restricted data scopes",
  "editor.ai.aiHomeRef.test": "Try it on the latest period",
  "editor.ai.aiHomeRef.testing": "Running…",
  "editor.ai.aiHomeRef.runs": "Runs · {value}",
  "editor.ai.aiHomeRef.rows_one": "{count} row",
  "editor.ai.aiHomeRef.rows_other": "{count} rows",
  "editor.ai.aiHomeRef.fails": "Does not run",
  "editor.ai.aiHomeRef.filters": "filters it takes: {columns}",
  "editor.ai.aiHomeRef.noFilters": "it takes no dashboard filter",
  "editor.ai.aiHomeRef.remove": "Remove",
  "editor.ai.aiHomeRef.publishNote": "Publishing runs every reference: one that does not run blocks it.",
  "editor.ai.aiHomeTime.baseTitle": "AI Home: time of the base table",
  "editor.ai.aiHomeTime.baseDescription": "With a rule, the AI Home can use the project's whole data (its base table too). Without one, it uses only the reports.",
  "editor.ai.aiHomeTime.description": "How the AI Home reads this report's dates. Always relative to the latest data: a saved dashboard opened tomorrow shows tomorrow's data.",
  "editor.ai.aiHomeTime.missing": "Required: without it the report cannot be published and is not offered in the AI Home.",
  "editor.ai.aiHomeTime.modeLabel": "How it reads time",
  "editor.ai.aiHomeTime.modeHint": "Latest period for snapshot reports (stock, availability); a range for reports read over time.",
  "editor.ai.aiHomeTime.modeUnset": "Choose…",
  "editor.ai.aiHomeTime.mode.day": "Latest period",
  "editor.ai.aiHomeTime.mode.range": "Range of recent periods",
  "editor.ai.aiHomeTime.mode.none": "No time dimension",
  "editor.ai.aiHomeTime.dateColumnLabel": "Date column",
  "editor.ai.aiHomeTime.dateColumnHint": "A date, or an ISO week or month as text (week_iso, month_iso).",
  "editor.ai.aiHomeTime.anchorLabel": "Anchor table",
  "editor.ai.aiHomeTime.anchorHint": "Its latest value of the date column is the latest period.",
  "editor.ai.aiHomeTime.periodsLabel": "Periods",
  "editor.ai.aiHomeTime.periodsHint": "How many periods a trend (latest period) or every element (range) reads.",
  "editor.ai.aiHomeTime.completenessToggle": "Ask before building when the latest period is incomplete",
  "editor.ai.aiHomeTime.completenessHint": "Incomplete = fewer rows than the share below of the median of the previous periods.",
  "editor.ai.aiHomeTime.completenessOff": "Off: the latest period is always shown as it is.",
  "editor.ai.aiHomeTime.minRatioLabel": "Minimum share of a normal period (%)",
  "editor.ai.aiHomeTime.baselineLabel": "Previous periods compared",
  "editor.ai.advanced": "Advanced",
  "editor.ai.blocks.what": "What it analyzes",
  "editor.ai.blocks.when": "How and when",
  "editor.ai.blocks.results": "Results",

  // PR-3 builder reorg — usuarios y permisos
  "nav.admin.users": "Users & permissions",
  // Plan J J3 — the sidebar entry that REPLACED "Users & permissions".
  // `nav.admin.users` stays because `UsersAdminPage` is still on disk
  // (its route redirects; the file is deleted when the legacy tables are
  // parked), and a key deleted ahead of its component is a blank label
  // in whatever still renders it.
  "nav.admin.access": "Access",
  "adminUsers.title": "Users & permissions",
  "adminUsers.subtitle":
    "Portal accounts, report subscriptions and the permission matrix.",
  "adminUsers.tab.users": "Users",
  "adminUsers.tab.defaults": "Role defaults",
  "adminUsers.create": "New user",
  "adminUsers.allRoles": "All roles",
  "adminUsers.usersCount": "{count} users",
  "adminUsers.table.name": "Name",
  "adminUsers.table.email": "Email",
  "adminUsers.table.role": "Role",
  "adminUsers.table.subscriptions": "Subscriptions",
  "adminUsers.table.overrides": "Overrides",
  "adminUsers.table.builder": "Builder",
  "adminUsers.table.status": "Status",
  "adminUsers.table.actions": "Actions",
  "adminUsers.builderFull": "full",
  "adminUsers.builderPartial": "partial",
  "adminUsers.builderFlag.can_enter": "Enter the builder",
  "adminUsers.builderFlag.edit_projects": "Edit projects & reports",
  "adminUsers.builderFlag.publish": "Publish",
  "adminUsers.builderFlag.manage_ai": "AI (Panorama, prompts, issues)",
  "adminUsers.builderFlag.operations": "Operations (loader)",
  "adminUsers.builderFlag.manage_users": "Users & permissions",
  "adminUsers.noUsers": "No users match.",
  "adminUsers.allProjects": "all reports",
  "adminUsers.subsCount": "{count} reports",
  "adminUsers.overridesCount": "{count} custom",
  "adminUsers.active": "Active",
  "adminUsers.inactive": "Inactive",
  "adminUsers.permissions": "Permissions",
  "adminUsers.resetPassword": "Reset password",
  "adminUsers.confirmDelete":
    "Delete user \"{name}\"? The account is deactivated (soft delete).",
  "adminUsers.deleted": "User deleted",
  "adminUsers.created": "User created",
  "adminUsers.updated": "User updated",
  "adminUsers.passwordResetDone": "Password reset",
  "adminUsers.prev": "Previous",
  "adminUsers.next": "Next",
  "adminUsers.createTitle": "New user",
  "adminUsers.stepOf": "step {current} of {total}",
  "adminUsers.fillRequired":
    "Complete name, email and a password of at least 8 characters.",
  "adminUsers.password": "Password",
  "adminUsers.passwordMin": "Minimum 8 characters.",
  "adminUsers.generate": "Generate",
  "adminUsers.back": "Back",
  "adminUsers.skip": "Skip",
  "adminUsers.initialAccessHelp":
    "Pick the published reports this user starts subscribed to.",
  "adminUsers.initialAccessNote":
    "You can adjust subscriptions later in Permissions.",
  "adminUsers.noReports": "No published reports.",
  "adminUsers.editTitle": "Edit user",
  "adminUsers.cloneFromAnother": "Clone permissions from another user…",
  "adminUsers.cloneSource": "Copy subscriptions + overrides from",
  "adminUsers.cloneSelectUser": "Select user…",
  "adminUsers.applyClone": "Apply",
  "adminUsers.cloneConfirm":
    "Replace {name}'s subscriptions and overrides with the source user's?",
  "adminUsers.resetPrompt":
    "Generate a new password for {name}? The current one stops working immediately.",
  "adminUsers.newPassword": "New password",
  "adminUsers.passwordWarning":
    "Shown only once — there is no email delivery (no SMTP). Copy it now and share it through a secure channel.",
  "adminUsers.done": "Done",
  "adminUsers.saveCount": "Save ({count})",
  "adminUsers.defaults.subtitle":
    "Baseline matrix each role falls back to when a user has no explicit override.",
  "adminUsers.defaults.adminNote":
    "The admin role bypasses every permission check — this card is informative only.",
  "adminUsers.defaults.readonly": "read-only",
  "adminUsers.perms.title": "Permissions — {name}",
  "adminUsers.perms.action.view": "See it",
  "adminUsers.perms.action.view_data": "See the data",
  "adminUsers.perms.action.run_chat": "Use the chat",
  "adminUsers.perms.action.run_ai_analysis": "Run the AI analysis",
  "adminUsers.perms.action.send_report": "Send by email",
  "adminUsers.perms.action.schedule_send": "Set up automatic sends",
  "adminUsers.perms.action.upload": "Upload",
  "adminUsers.perms.action.download": "Download",
  "adminUsers.perms.action.delete": "Delete",
  "adminUsers.perms.subscribedOn": "Subscribed — click to remove",
  "adminUsers.perms.subscribedOff": "Not subscribed — click to add",
  "adminUsers.perms.subscriptions": "Subscriptions",
  "adminUsers.perms.subscriptionsHint":
    "Active pills are the reports this user sees. Click to toggle — saves immediately.",
  "adminUsers.perms.overridesTitle": "Per-report overrides",
  "adminUsers.perms.noSubscriptions":
    "No subscriptions — turn on a report pill above first.",
  "adminUsers.perms.quickActions": "Quick actions",
  "adminUsers.perms.applyDefaults": "Apply role defaults",
  "adminUsers.perms.clearAll": "Clear all overrides",
  "adminUsers.perms.copyFrom": "Copy from another user…",
  "adminUsers.perms.confirmClearAll":
    "Remove every explicit override? The user reverts to the pure role defaults.",
  "adminUsers.perms.noOverridesToClear": "No overrides to clear.",
  "adminUsers.perms.adminNote":
    "Admins bypass all checks — the matrix only applies to the user role.",
  "adminUsers.perms.legend":
    "Click cycles each cell: default (·) → granted (✓) → denied (✕).",
  "adminUsers.perms.builderAccessTitle": "Builder access",
  "adminUsers.perms.builderAccessHint":
    "What this user can do inside THIS console. Saved on each toggle.",
  "adminUsers.perms.builderAdminNote":
    "Admins always have full builder access.",
  "adminUsers.perms.projectLevel": "Maestros & Files",
  "adminUsers.perms.projectLevelBadge": "project level",
  "adminUsers.perms.projectSectionsHint":
    "These cells apply to every report in the project.",
  "adminUsers.perms.noProject": "No project",
  "adminUsers.perms.subscribed": "(subscribed)",
  "adminUsers.perms.defaultTitle": "default = {value}",
  "adminUsers.perms.granted": "granted",
  "adminUsers.perms.denied": "denied",
  "hub.tab.access": "Access",
  "hub.access.title": "Who can see this project",
  "hub.access.user": "User",
  "hub.access.role": "Role",
  "hub.access.role.admin": "Admin",
  "hub.access.role.user": "User",
  "hub.access.reports": "Reports",
  "hub.access.reportsOf": "{count} of {total} reports",
  "hub.access.overrides": "Overrides",
  "hub.access.edit": "Edit →",
  "hub.access.empty":
    "No user is subscribed to this project's reports yet.",
  "hub.access.adminNote":
    "Admins bypass all checks and are not listed here.",

  // PR-4 builder reorg — panorama IA
  "common.close": "Close",
  "nav.admin.panorama": "AI Panorama",
  "hub.tab.ia": "Project AI",
  "editor.ai.panes.issues": "Issues",

  "panorama.title": "AI Panorama",
  "panorama.subtitle":
    "Cross-project view of chat issues, AI snapshots and global prompts.",
  "panorama.tab.overview": "Overview",
  "panorama.tab.issues": "Issue queue",
  "panorama.tab.prompts": "Global prompts",
  "panorama.kpi.open": "Open issues",
  "panorama.kpi.week": "This week",
  "panorama.kpi.topReport": "Top report",
  "panorama.kpi.topHeuristic": "Top heuristic",
  "panorama.chart.title": "Issues per week",
  "panorama.chart.subtitle": "Last 60 days",
  "panorama.map.title": "Where each thing is managed",
  "panorama.map.report": "Report",
  "panorama.map.reportItems":
    "Issues, snapshots and AI knowledge live in each report's editor (AI tab).",
  "panorama.map.project": "Project",
  "panorama.map.projectItems":
    "Context notes and project knowledge live in the project hub (Project AI tab).",
  "panorama.map.global": "Global",
  "panorama.map.globalItems":
    "Global prompts and the cross-project issue queue live here.",

  "issues.filter.report": "Report",
  "issues.filter.allReports": "All reports",
  "issues.filter.status": "Status",
  "issues.filter.allStatuses": "All statuses",
  "issues.filter.heuristic": "Heuristic",
  "issues.filter.allHeuristics": "All heuristics",
  "issues.total": "{count} issues",
  "issues.countAll": "{count} in total · {open} open",
  "issues.countFiltered": "{count} match the filter",
  "issues.table.id": "ID",
  "issues.table.created": "Created",
  "issues.table.report": "Report",
  "issues.table.heuristic": "Heuristic",
  "issues.table.question": "Question",
  "issues.table.status": "Status",
  "issues.empty": "No issues match the current filters.",
  "issues.pageOf": "Page {page} of {total}",
  "issues.prev": "Previous",
  "issues.next": "Next",
  "issues.status.new": "New",
  "issues.status.in_review": "In review",
  "issues.status.resolved": "Resolved",
  "issues.status.dismissed": "Dismissed",
  "issues.heuristic.tool_execution_error": "Tool error",
  "issues.heuristic.empty_result": "Empty result",
  "issues.heuristic.max_iterations": "Max iterations",
  "issues.heuristic.empty_response": "Empty response",
  "issues.heuristic.llm_cannot_answer": "LLM cannot answer",
  "issues.heuristic.user_reformulation": "User reformulation",
  "issues.heuristic.user_thumbs_down": "User thumbs down",
  "issues.drawer.title": "Issue #{id}",
  "issues.drawer.question": "Question",
  "issues.drawer.response": "Assistant response",
  "issues.drawer.heuristicDetail": "Heuristic detail",
  "issues.drawer.toolCalls": "Tool calls ({count})",
  "issues.drawer.resolutionNotes": "Resolution notes",
  "issues.action.promote": "Promote to training example",
  "issues.action.resolve": "Mark resolved",
  "issues.action.dismiss": "Dismiss",
  "issues.action.reopen": "Reopen",
  "issues.promote.question": "Question (as it will be stored)",
  "issues.promote.sql": "SQL (validated server-side)",
  "issues.promote.save": "Save training example",
  "issues.promote.required": "Question and SQL are required.",
  "issues.notes": "Notes (optional)",
  "issues.resolve.known_limitation": "Known limitation",
  "issues.resolve.added_doc": "Added documentation",
  "issues.resolve.wont_fix": "Won't fix",
  "issues.dismiss.wont_fix": "Won't fix",
  "issues.dismiss.duplicate": "Duplicate",
  "issues.dismiss.known_limitation": "Known limitation",

  "snapshots.title": "Snapshots",
  "snapshots.description":
    "Persisted AI Analysis snapshots for this report. Regenerate, backfill and the watcher run on the portal runtime.",
  "snapshots.upToDate": "up to date — {week}",
  "snapshots.trigger": "Run watcher",
  "snapshots.triggerDone": "Watcher run — {count} snapshots created",
  "snapshots.backfill": "Backfill…",
  "snapshots.empty": "No snapshots yet for this report.",
  "snapshots.col.week": "Week",
  "snapshots.col.summary": "Summary",
  "snapshots.col.insights": "Insights",
  "snapshots.col.feedback": "Feedback",
  "snapshots.col.regen": "Regen",
  "snapshots.col.actions": "Actions",
  "snapshots.view": "View",
  "snapshots.regenerate": "Regenerate",
  "snapshots.regenConfirm":
    "Regenerate this snapshot? It re-runs the full LLM call on the portal and overwrites the content.",
  "snapshots.detail.regenerated": "regenerated × {count}",
  "snapshots.detail.filters": "Filters applied",
  "snapshots.detail.feedback": "Feedback overview",
  "snapshots.detail.comments": "comments",
  "snapshots.detail.execSummary": "Executive summary",
  "snapshots.detail.insights": "Insights ({count})",
  "snapshots.backfill.title": "Backfill snapshots",
  "snapshots.backfill.help":
    "Generate the missing snapshots for {key}. Leave both dates blank to use the portal defaults.",
  "snapshots.backfill.start": "Start",
  "snapshots.backfill.end": "End",
  "snapshots.backfill.run": "Start",
  "snapshots.backfill.running": "Running…",
  "snapshots.backfill.done": "Done — {created} new snapshots",
  "snapshots.backfill.failed": "Failed: {error}",
  "snapshots.backfill.progress": "{processed} / {total}",

  "projectIA.title": "Project AI",
  "projectIA.subtitle":
    "Context notes and project-level knowledge, shared by every report in this project.",
  "projectIA.back": "Project AI",
  "projectIA.row.notes": "Context notes",
  "projectIA.row.notesDesc":
    "Business events the AI reads when generating each snapshot.",
  "projectIA.row.column_docs": "Column docs",
  "projectIA.row.column_docsDesc":
    "Column-level documentation every report's chat sees as context.",
  "projectIA.row.synonyms": "Synonyms",
  "projectIA.row.synonymsDesc":
    "Business terms mapped to column values so the chat understands the jargon.",
  "projectIA.row.example": "Project examples",
  "projectIA.row.exampleDesc":
    "Project-wide Q&A examples layered on top of each report's own training.",
  "projectIA.activeCount": "{count} active",
  "projectIA.activeCountAtLeast": "{count}+ active",
  "projectIA.truncatedHint":
    "The notes endpoint returns at most 500 rows, and this screen filters them by report afterwards. The list is cut and the count is a floor, not a total.",
  "projectIA.rowCount": "{count} rows",
  "projectIA.manage": "Manage",

  "notes.new": "New note",
  "notes.filter.active": "Active",
  "notes.filter.archived": "Archived",
  "notes.filter.all": "All",
  "notes.filter.report": "Report",
  "notes.filter.allReports": "All reports",
  "notes.search": "Search title or body…",
  "notes.col.title": "Title",
  "notes.col.reports": "Reports",
  "notes.col.slice": "Slice",
  "notes.col.window": "Window",
  "notes.col.visibility": "Visibility",
  "notes.col.actions": "Actions",
  "notes.visibility.internal": "internal",
  "notes.visibility.mentionable": "mentionable",
  "notes.archived": "archived",
  "notes.empty": "No context notes for this project yet.",
  "notes.archive": "Archive",
  "notes.archiveConfirm":
    "Archive this note? Historical snapshots keep their audit trail.",
  "notes.regen": "Regenerate affected",
  "notes.modal.createTitle": "New note",
  "notes.modal.editTitle": "Edit note",
  "notes.field.title": "Title",
  "notes.field.body": "Event detail",
  "notes.field.reports": "Affected reports",
  "notes.field.dateStart": "From (optional)",
  "notes.field.dateEnd": "To (optional)",
  "notes.field.filters": "Descriptive filters (optional)",
  "notes.addFilter": "Add filter",
  "notes.filtersHint":
    "They describe WHERE the event happened. The LLM reads them as context, not as a match criterion.",
  "notes.field.referenceAllowed":
    "Allow the LLM to mention this note to the client",
  "notes.referenceAllowedHint":
    "Unchecked, the LLM uses the note as internal context but does NOT mention it in its narrative. Useful for sensitive topics (legal, financial).",
  "notes.errors.titleRequired": "The title is required.",
  "notes.errors.bodyRequired": "The body is required.",
  "notes.errors.reportRequired": "Pick at least one report.",
  "notes.errors.dateOrder": "The start date must be before the end date.",
  "notes.regen.title": "Regenerate affected snapshots",
  "notes.regen.prompt":
    "{count} existing snapshots overlap this note's date window. Regenerate them now so the LLM redoes them with the new context?",
  "notes.regen.costHint":
    "Each snapshot fires one LLM call. Re-running is safe — snapshots are only overwritten.",
  "notes.regen.start": "Regenerate",
  "notes.regen.none": "No snapshots to regenerate",
  "notes.regen.later": "Later",
  "notes.regen.done": "Done — {count} regenerated",
  "notes.regen.failed": "Failed: {error}",

  "knowledge.add": "Add row",
  "knowledge.empty": "No rows yet.",
  "knowledge.modal.addTitle": "New row",
  "knowledge.modal.editTitle": "Edit row",
  "knowledge.field.name": "Name",
  "knowledge.field.content": "Content",
  "knowledge.contentRequired": "The content is required.",
  "knowledge.deleteConfirm": "Delete this row?",

  // ── Plan C 2.1+2.3 — unified integrations manager ─────────────
  // One section, two tabs, over one idea: a credential is registered
  // once and reused. The per-channel numbered steps are NOT here - they
  // are served from builder/integrations_catalog.py so they exist once.
  "nav.admin.integrations": "Integrations",
  "nav.admin.sendPolicy": "Report sending",
  "sendPolicy.title": "Report sending by portal users",
  "sendPolicy.subtitle":
    "The portal's \"Send by email\" beta: who may receive, how much, and through which credential. Off by default; an empty domain list keeps it off regardless. Every send attempt is audited below.",
  "sendPolicy.enabled": "Enable report sending (beta)",
  "sendPolicy.neverConfigured": "Never configured",
  "sendPolicy.needDomain": "Enabling requires at least one allowed domain — add one below before saving.",
  "sendPolicy.needCredential": "Enabling requires a sending credential — pick one below (register it under Integrations first).",
  "sendPolicy.loadFailed": "The send policy could not be loaded, so the form stays hidden: saving it now would overwrite the stored policy with empty values.",
  "sendPolicy.domains": "Allowed recipient domains",
  "sendPolicy.domainsHint": "Recipients outside these domains are refused, naming the address. Empty list = feature off.",
  "sendPolicy.removeDomain": "Remove {domain}",
  "sendPolicy.maxRecipients": "Max recipients per send",
  "sendPolicy.quota": "Daily sends per user",
  "sendPolicy.quotaNone": "no quota",
  "sendPolicy.quotaHint": "Empty = no quota (an explicit choice, not a default).",
  "sendPolicy.credential": "Sending credential (email)",
  "sendPolicy.credentialNone": "None picked",
  "sendPolicy.audit.title": "Send audit",
  "sendPolicy.audit.total": "{n} attempts on record",
  "sendPolicy.audit.empty": "No send attempts yet.",
  "sendPolicy.audit.pageOf": "Page {page} of {pages}",
  "sendPolicy.audit.prev": "Previous",
  "sendPolicy.audit.next": "Next",
  "sendPolicy.outcome.queued": "Queued",
  "sendPolicy.outcome.collapsed": "Merged into another send",
  "sendPolicy.outcome.schedule_queued": "Queued by a schedule",
  "sendPolicy.outcome.capture_token": "Screenshot token issued",
  "sendPolicy.outcome.prompt_blocked": "Blocked by the guard",
  "sendPolicy.outcome.sent": "Sent",
  "sendPolicy.outcome.failed": "Failed",
  "sendPolicy.outcome.dead": "Failed (retries exhausted)",
  "sendPolicy.outcome.reaped": "Unknown (interrupted)",
  "sendPolicy.outcome.schedule_skipped": "Not sent (skipped)",
  "sendPolicy.outcome.schedule_failed": "Not sent (failed before sending)",
  "sendPolicy.outcome.unknown":
    "This outcome has no label yet — showing what the backend recorded.",
  "sendPolicy.audit.when": "When",
  "sendPolicy.audit.who": "Who",
  "sendPolicy.audit.report": "Report",
  "sendPolicy.audit.recipients": "Recipients",
  "sendPolicy.audit.extras": "Carried",
  "sendPolicy.audit.image": "image",
  "sendPolicy.audit.outcome": "Outcome",
  "delivery.field.credential": "Channel credential",
  "delivery.credential.pick": "Pick a credential…",
  "delivery.credential.legacy": "Operator-configured credential (legacy)",
  "delivery.credential.notNeeded": "Not needed",
  "delivery.credential.noneAvailable":
    "No {channel} credentials registered yet. Register one under Integrations — the value is stored once and reused by every schedule that picks it.",
  "delivery.credential.goToIntegrations": "Open Integrations",
  "delivery.error.credential": "Pick a credential for this channel.",
  "integrations.title": "Integrations",
  "integrations.introShort":
    "Communication channels and data sources in one place. A credential is registered once here and reused by every subscription or project that points at it.",
  "operations.jobStatus.pending": "Pending",
  "operations.jobStatus.queued": "Queued",
  "operations.jobStatus.running": "Running",
  "operations.jobStatus.completed": "Completed",
  "operations.jobStatus.failed": "Failed",
  "operations.jobStatus.skipped": "Skipped",
  "integrations.intro":
    "Communication channels and data sources in one place. A credential is registered once here and reused by every subscription or project that points at it - so rotating it is one action, not one per consumer. Values are stored encrypted and are never shown again.",
  "integrations.tabs.channels": "Channels",
  "integrations.tabs.sources": "Data sources",
  "integrations.comingSoon": "Coming soon",
  "integrations.cancelBtn": "Cancel",
  "integrations.deleteBtn": "Delete",
  "integrations.testBtn": "Test",
  "integrations.testing": "Testing...",
  "integrations.testOk": "OK",
  "integrations.testFailed": "The upstream refused",
  "integrations.usedBy": "Used by {count}:",
  "integrations.guide.title": "What you do in your own system",
  "integrations.guide.warnings": "Worth knowing before you start",
  "integrations.guide.docsLink": "Official documentation",
  "integrations.secret.configured": "Configured",
  "integrations.secret.notConfigured": "No value stored",
  "integrations.secret.notNeeded": "No credential needed",
  "integrations.secret.fromEnv": "Value in an environment variable",
  "integrations.secret.setBtn": "Set value",
  "integrations.secret.replaceBtn": "Replace",
  "integrations.secret.saveBtn": "Save value",
  "integrations.secret.saving": "Storing...",
  "integrations.secret.stored":
    "Value stored for {name}. It is not shown again.",
  "integrations.secret.firstNotice":
    "The value is stored encrypted and is never returned to this screen - not to this form, not to a log, not to a job detail.",
  "integrations.secret.replaceNotice":
    "A value is already stored. Saving REPLACES it; there is nothing to edit because nothing can be read back. The reference does not change, so every subscription or project using it picks the new value up on its next run.",
  "integrations.backend.title": "The secret backend is not switched on yet",
  "integrations.backend.intro":
    "Until it is, credential values cannot be stored from this screen. This is the one part of integrations that cannot be done from the web: the backend a web form writes to has to be enabled by deploy-time variables. The exact steps:",
  "integrations.channels.addBtn": "Add credential",
  "integrations.channels.nameLabel": "Name",
  "integrations.channels.nameHint":
    "Becomes part of the secret path, so it accepts lowercase letters, digits, '.', '_' and '-'. It cannot be changed later - the path is built from it.",
  "integrations.channels.descriptionLabel": "Description (optional)",
  "integrations.channels.createBtn": "Register",
  "integrations.channels.unused": "Not used by any subscription yet.",
  "integrations.channels.deleteConfirm":
    "Delete the credential '{name}' and its stored value? This is refused while a subscription still uses it.",
  "integrations.sources.title": "Data source connections",
  "integrations.sources.intro":
    "A connection can be shared with every project in this client, or owned by one. Sharing is what makes 'one warehouse, one credential' possible instead of one copy per project.",
  "integrations.sources.empty": "No connections yet.",
  "integrations.sources.shared": "Shared",
  "integrations.sources.ownedBy": "Only {slug}",
  "integrations.sources.shareBtn": "Share with all projects",
  "integrations.sources.unused": "No project points at it yet.",
  "integrations.sources.deleteConfirm":
    "Delete the connection '{name}'? This is refused while a project data source still points at it.",
  // Migration 035 — an owned connection granted to a chosen set of
  // projects, the scope between "only the owner" and "every project".
  "integrations.sources.alsoShared": "+ {count} more",
  "integrations.sources.grantBtn": "Share with…",
  "integrations.sources.grantTitle":
    "Also let these projects use '{name}'",
  "integrations.sources.grantHint":
    "The owner ({slug}) always can. Sharing with every project instead is the button next to it. Unticking a project whose data source still relies on this connection is refused, naming the project.",
  "integrations.sources.grantNoOthers": "There is no other project yet.",
  "integrations.sources.grantSave": "Save sharing",
  "integrations.sources.grantSaved": "Sharing for '{name}' saved.",
  // ── PR-6: Operaciones cockpit + Guía del builder ──────────────
  "nav.admin.operations": "Operations",
  // «Caché» page (Administración → Caché, 2026-10-26).
  "nav.admin.cache": "Cache",
  "cache.title": "Cache",
  "cache.subtitle": "What every reload computes before it publishes, so reports open from the cache.",
  "cache.day.title": "Daily warm (estimate)",
  "cache.day.breakdown": "Default views {defaults} · all combinations +{extra} · {languages} language(s)",
  "cache.day.note": "Estimated from each project's last reload, {workers} at a time, scope «all».",
  "cache.day.unmeasured": "{count} report(s) without data yet: not in the total.",
  "cache.locales.title": "Languages to warm",
  "cache.locale.en": "English",
  "cache.locale.es": "Spanish",
  "cache.locale.pt": "Portuguese (Brazil)",
  "cache.locales.help": "They only count for reports that use the language in their SQL. A language left out is computed the first time someone opens it and stays cached from then on.",
  "cache.alert.title": "Email alert",
  "cache.alert.label": "If a project's warm takes more than (minutes)",
  "cache.alert.help": "It does not stop the publish. If the warm fails, the reload is not published and the email always goes out.",
  "cache.settings.save": "Save",
  "cache.settings.saved": "Saved. Applies from each project's next reload.",
  "cache.settings.needOneLocale": "Pick at least one language.",
  "cache.settings.badMinutes": "The threshold must be a whole number of minutes between 1 and {max}.",
  "cache.option.saved": "Saved. Applies from the next reload of {project}.",
  "cache.option.yes": "Yes",
  "cache.option.no": "No",
  "cache.option.aria": "Warm every combination of {report}",
  "cache.option.followsOn": "Follows the tabs: yes",
  "cache.option.followsOff": "Follows the tabs: no",
  "cache.groupOptions": "Options of group {group}",
  "cache.readsLocale": "uses the language",
  "cache.col.report": "Report",
  "cache.col.buttons": "Button filters and combinations",
  "cache.col.option": "All combinations",
  "cache.col.cost": "Added per reload",
  "cache.noData": "no data yet",
  "cache.ifEnabled": "if turned on",
  "cache.last.none": "No warm yet",
  "cache.last.failed": "Failed {when}: previous data stays live",
  "cache.last.overThreshold": "Over the threshold {when} · {dur}",
  "cache.last.complete": "Complete {when} · {dur}",
  "cache.project.desc": "{count} report(s) with button filters · {dur} per reload",
  "cache.without.title": "{count} project(s) without button filters",
  "cache.without.desc": "Only the default views are warmed · {dur} per day",
  "cache.broken.title": "Published reports that do not build: every reload of their project fails until they are fixed",
  "cache.empty.title": "No report has button filters",
  "cache.empty.desc": "Every reload warms the default view of every report.",
  "nav.guide": "Builder guide",
  "common.refresh": "Refresh",
  // Delivery (track B2)
  "hub.tab.delivery": "Delivery",
  // ── Track B3 — embeddable chat for third-party sites ──────────
  "hub.tab.embed": "Embed",
  "common.remove": "Remove",
  "embed.form.title": "Issue an embed key",
  "embed.form.help":
    "An embed key lets an ANONYMOUS visitor of a third-party website chat with one report of this project. It is scoped to one client + one chat context and callable only from the exact origins you list.",
  "embed.form.submit": "Issue key",
  "embed.form.incomplete":
    "Label, client, chat context and at least one origin are required.",
  "embed.field.label": "Label",
  "embed.field.labelPlaceholder": "customer.example marketing page",
  "embed.field.clientKey": "Client key",
  "embed.field.chatContextKey": "Chat context",
  "embed.field.chatContextPick": "Pick a chat context…",
  "embed.field.origins": "Allowed origins",
  "embed.field.originAdd": "Add origin",
  "embed.field.originsHelp":
    "Exact scheme://host[:port] values, one per chip. No wildcards: '*' and 'null' are rejected, and http://localhost:8899 is a DIFFERENT origin from http://127.0.0.1:8899.",
  "embed.field.maxToolIterations": "Tool budget per answer",
  "embed.field.maxToolIterationsHelp":
    "1-8. Lower than the authenticated default (8) on purpose — an anonymous turn should cost less.",
  "embed.field.dailyQuota": "Daily message quota",
  "embed.field.dailyQuotaHelp":
    "Leave empty for NO quota. An unmetered key on a busy page will burn LLM credit until it stops.",
  "embed.field.allowFreeSql": "Allow free-form SQL (off by default)",
  "embed.field.allowFreeSqlWarning":
    "Danger: this lets the model run arbitrary SELECTs over this project's DuckDB, driven by a question ANY visitor of the customer's site can write. Leave off unless you have a specific reason.",
  "embed.error.badOrigin":
    "Not a valid origin. Use scheme://host[:port] with no path, query or wildcard.",
  "embed.issued.title": "Key issued",
  "embed.issued.warning":
    "Copy it now — only a hash is stored, so this key can never be shown again. A lost key must be re-issued.",
  "embed.issued.key": "Embed key (plaintext)",
  "embed.issued.snippet": "Paste-ready snippet",
  "embed.list.empty": "No embed keys for this project yet.",
  "embed.col.label": "Key",
  "embed.col.scope": "Scope",
  "embed.col.origins": "Origins",
  "embed.col.policy": "Policy",
  "embed.col.usage": "Usage",
  // Single-brace interpolation per the builder's i18next config.
  "embed.col.iterations": "{n} tool rounds",
  "embed.col.total": "{n} total",
  "embed.status.active": "Active",
  "embed.status.revoked": "Revoked",
  "embed.badge.freeSql": "Free SQL ON",
  "embed.quota.none": "no quota",
  "embed.action.revoke": "Revoke",
  "embed.action.enableFreeSql": "Enable free SQL",
  "embed.action.disableFreeSql": "Disable free SQL",
  "delivery.title": "Scheduled delivery",
  "delivery.subtitle":
    "Send this project's AI insights to a channel on a schedule. Credentials are picked from Integrations, never typed here.",
  "delivery.empty": "No scheduled deliveries yet.",
  "delivery.noJobs": "No delivery attempts yet.",
  "delivery.channel.webhook": "Webhook",
  "delivery.channel.email": "Email",
  "delivery.channel.slack": "Slack",
  "delivery.channel.teams": "Teams",
  "delivery.field.name": "Name",
  "delivery.field.report": "Report",
  "delivery.field.channel": "Channel",
  "delivery.field.cron": "Cron",
  "delivery.field.timezone": "Timezone",
  "delivery.field.locale": "Language",
  "delivery.locale.en": "English",
  "delivery.locale.es": "Spanish",
  "delivery.field.recipients": "Recipients",
  "delivery.field.subjectPrefix": "Subject prefix",
  "delivery.field.url": "Webhook URL",
  "delivery.field.mode": "Mode",
  "delivery.field.slackChannel": "Slack channel",
  "delivery.field.maxContentAge": "Max content age (days)",
  "delivery.field.onMissingContent": "When content is missing",
  "delivery.field.enabled": "Enabled",
  "delivery.col.nextRun": "Next run",
  "delivery.col.lastStatus": "Last status",
  "delivery.col.schedule": "Schedule",
  // ── Plan F, F0 — the wizard that replaced the flat cron+ref form ──
  "delivery.wizard.titleNew": "New scheduled delivery",
  "delivery.wizard.titleEdit": "Edit scheduled delivery",
  "delivery.wizard.step.what": "What to send",
  "delivery.wizard.step.who": "To whom",
  "delivery.wizard.step.when": "When",
  "delivery.wizard.step.review": "Review & test",
  "delivery.wizard.back": "Back",
  "delivery.wizard.next": "Next",
  "delivery.wizard.saveAndTest": "Save & test",
  "delivery.wizard.savedTestFailed": "Saved, but the test failed:",
  "delivery.wizard.namePlaceholder": "Weekly summary for the ops team",
  "delivery.schedule.kind.daily": "Every day",
  "delivery.schedule.kind.weekly": "Every week",
  "delivery.schedule.kind.monthly": "Every month",
  "delivery.schedule.kind.custom": "Custom (cron)",
  "delivery.schedule.time": "Time",
  "delivery.schedule.monthDay": "Day of the month",
  "delivery.schedule.monthDayHint":
    "Months without that day are skipped — day 31 does not fire in April.",
  "delivery.schedule.summary.daily": "Every day at {time}",
  "delivery.schedule.summary.weekly": "Every week on {days} at {time}",
  "delivery.schedule.summary.monthly":
    "On day {day} of every month at {time}",
  "delivery.schedule.summary.custom": "Custom schedule: {cron}",
  "delivery.weekday.0": "Sunday",
  "delivery.weekday.1": "Monday",
  "delivery.weekday.2": "Tuesday",
  "delivery.weekday.3": "Wednesday",
  "delivery.weekday.4": "Thursday",
  "delivery.weekday.5": "Friday",
  "delivery.weekday.6": "Saturday",
  "delivery.error.weekdays": "Pick at least one day.",
  "delivery.advanced.title": "Advanced",
  "delivery.advanced.cronHint":
    "Editing the cron switches the schedule to custom mode; the string is stored exactly as typed.",
  "delivery.slackMode.incomingWebhook": "Incoming webhook (recommended)",
  "delivery.slackMode.botToken": "Bot + channel",
  "delivery.review.enabledHint":
    "Leave it off until a test looks right — you can enable it any time from this screen.",
  "delivery.review.testOk": "Nothing was sent",
  "delivery.teams.guide.title": "How to get the Teams Workflow URL",
  "delivery.teams.guide.doneInTeams": "Done in Microsoft Teams, not here",
  "delivery.teams.guide.retiredNotice":
    "If you already hold a Teams webhook address containing webhook.office.com, it is dead. Microsoft switched Office 365 connectors off between 18 and 22 May 2026 and those addresses will never deliver again. Our sender refuses them and says why. Create a Workflow instead.",
  "delivery.teams.guide.intro":
    "Only a member of the destination Teams channel can create this, inside your own Microsoft tenant. Send these steps to the channel owner - it takes about a minute - and ask for the address produced at the end.",
  "delivery.teams.guide.step1":
    "In Microsoft Teams, open the channel that should receive the report. It has to be a channel inside a team, not a one-to-one chat.",
  "delivery.teams.guide.step2":
    "Point at the channel name in the list on the left and click the ... button (More options) that appears beside it.",
  "delivery.teams.guide.step3":
    "Choose Workflows. Teams opens a gallery of workflow templates.",
  "delivery.teams.guide.step4":
    'Pick the template "Send webhook alerts to a channel". Its trigger is called "When a Teams webhook request is received".',
  "delivery.teams.guide.step5":
    "Teams shows which Microsoft account the workflow will run as - that account becomes its owner, which matters (see below). Confirm the team and the channel, then save.",
  "delivery.teams.guide.step6":
    "Copy the https address the workflow shows once created. Copy it before closing the dialog: it is all anyone needs to post into that channel, so treat it as a credential.",
  "delivery.teams.guide.step7":
    "Give that address to your platform operator, who sets it as the value of {envVar} on the builder container. This form holds a reference, never a credential value.",
  // Plan C 2.2: with a writable secret backend the last step stopped
  // being "ask an operator to set an env var". Kept alongside the old
  // one, not replacing it, because the Delivery panel still points at
  // env vars for pre-033 references.
  "delivery.teams.guide.step7Web":
    "Paste that address in the field above and save. It is stored encrypted and is never shown again - if you need to change it, you replace it.",
  "delivery.teams.guide.refNote":
    "The Workflow URL is registered as a Teams credential under Integrations; delivery schedules then pick it from a list. Nobody types a reference by hand any more.",
  "delivery.teams.guide.warnTitle": "Two things that bite people later",
  "delivery.teams.guide.warnOwner":
    "The workflow belongs to the PERSON who created it, not to the channel. If that person leaves the organisation or loses their licence, the reports stop arriving with no warning. Right after creating it, open the workflow in Power Automate and add co-owners.",
  "delivery.teams.guide.warnLimits":
    "Microsoft rejects messages larger than 28 KB and throttles above 4 requests per second. One scheduled report is one message, so this only matters when many deliveries fire in the same minute - stagger their cron minutes.",
  "delivery.teams.guide.unavailableTitle": "Not available on this route",
  "delivery.teams.guide.noBotIdentity":
    "No custom sender name or icon: messages arrive under the Workflows app.",
  "delivery.teams.guide.noInteractive":
    "No interactive buttons inside the message. The card carries a link that opens the full report in the portal.",
  "delivery.teams.guide.docsLink":
    "Microsoft's announcement of the connector retirement",
  "delivery.onMissing.fail": "Fail the delivery (recommended)",
  "delivery.onMissing.triggerWatcher": "Ask the portal for one watcher pass",
  "delivery.status.pending": "Pending",
  "delivery.status.running": "Running",
  "delivery.status.sent": "Sent",
  "delivery.sentMeaning":
    "Sent means the mail server accepted the message. If an address does not exist, the bounce can arrive minutes later and is not shown here.",
  "delivery.status.failed": "Failed",
  "delivery.status.dead": "Dead letter",
  "delivery.status.disabled": "Disabled",
  "delivery.action.new": "New delivery",
  "delivery.action.test": "Test",
  "delivery.action.sendNow": "Send now",
  "delivery.action.retry": "Retry",
  "delivery.action.delete": "Delete",
  "delivery.action.history": "History",
  "delivery.confirmSendNow":
    "Send this delivery right now, to the real destination?",
  "delivery.confirmDelete":
    // Single-brace interpolation: this i18next instance sets
    // prefix "{" / suffix "}" (see the init block), so a double-brace
    // placeholder here renders LITERALLY, unreplaced, in front of the
    // user. Verified 2026-07-25 by running i18next with this exact
    // config.
    "Delete the delivery {name}? Its history is kept.",
  "delivery.sendNowResult": "Delivery finished with status: {status}",
  "delivery.test.title":
    "Preview - data as of {date} ({locale}). Nothing was sent.",
  "delivery.error.cron":
    "Cron must have 5 fields (minute hour day month weekday).",
  "delivery.error.url": "Must start with https://",
  "delivery.error.recipients": "At least one recipient is required.",
  "delivery.error.slackChannel": "Channel is required in bot_token mode.",
  "delivery.recent.title": "Recent deliveries",
  "delivery.recent.empty": "No deliveries yet.",
  "operations.title": "Operations",
  "operations.subtitle":
    "Global scheduler, per-project schedules and live runs — the detail (history, reconciliation drawer) lives in each project's Auto-refresh tab.",
  "operations.scheduler.title": "Scheduler",
  "operations.scheduler.enabled": "enabled",
  "operations.scheduler.disabled": "disabled",
  // Three states, three WORDS. The dot beside them used to be the only
  // thing that told "enabled and running" from "enabled, no leader" —
  // and it did it with green vs amber, which a red-green colour-blind
  // reader cannot see at all.
  "operations.scheduler.running": "running",
  "operations.scheduler.noLeader": "no leader",
  "operations.scheduler.paused": "paused",
  "operations.scheduler.lease": "Lease",
  "operations.scheduler.lastTick": "Last tick",
  "operations.scheduler.pause": "Pause scheduler",
  "operations.scheduler.resume": "Resume scheduler",
  "operations.scheduler.confirmDisable":
    "Pause the global scheduler? No cron will fire until it is resumed (manual Run now still works). A pod restart re-enables it.",
  "operations.scheduler.note":
    "Process-level kill-switch. The persisted intent is each project's cron — pausing here stops every schedule at once.",
  "operations.projects.title": "Per-project schedules",
  "operations.projects.project": "Project",
  "operations.projects.schedule": "Schedule",
  "operations.projects.lastRun": "Last run",
  "operations.projects.nextRun": "Next run",
  "operations.projects.manualOnly": "manual only",
  "operations.projects.empty": "No projects yet.",
  "operations.projects.hubLink": "hub",
  "operations.runNow": "Run now",
  "operations.runStarted": "Reload started for {slug}.",
  "operations.forceReap": "Force-reap",
  "operations.forceReapTitle":
    "Abort the in-flight job first, then start a fresh reload",
  "operations.confirmForceReap":
    "Force-abort the in-flight job for {slug} and start a fresh reload? Only use this when a job is genuinely stuck.",
  "operations.jobs.title": "Recent runs",
  "operations.jobs.hint": "across all projects, newest first",
  "operations.jobs.status": "Status",
  "operations.jobs.phase": "Phase",
  "operations.jobs.started": "Started",
  "operations.jobs.duration": "Duration",
  "operations.jobs.recon": "Reconciliation",
  "operations.jobs.empty": "No runs yet.",
  // ── Operations › Data scopes (Plan J, J6) ──────────────────────────
  // Single-brace interpolation per the builder's i18next config.
  "operations.scopes.title": "Data scopes",
  "operations.scopes.summary": "{scopes} scope(s) across {projects} project(s) · {size} on disk · {free} free",
  "operations.scopes.stuckCount": "{count} stuck",
  "operations.scopes.failedCount": "{count} failed",
  "operations.scopes.sizeIncomplete": "Disk total incomplete — not measured: {projects}",
  "operations.scopes.col.project": "Project",
  "operations.scopes.col.scope": "Scope",
  "operations.scopes.col.status": "Last build",
  "operations.scopes.col.freshness": "Freshness",
  "operations.scopes.col.rows": "Rows",
  "operations.scopes.col.size": "Size",
  "operations.scopes.col.lock": "Lock held",
  "operations.scopes.col.when": "When",
  "operations.scopes.status.built": "Built",
  "operations.scopes.status.building": "Building",
  "operations.scopes.status.failed": "Failed",
  "operations.scopes.status.noMapping": "Empty slice",
  "operations.scopes.status.notApplicable": "Does not apply",
  "operations.scopes.status.neverBuilt": "Never built",
  "operations.scopes.status.stuck": "Stuck",
  "operations.scopes.stuckHint":
    "It has been building past the staleness threshold and is blocking every new build of this scope on this project. Nothing clears it automatically — the exact command is in the build history under Access › Scopes.",
  "operations.scopes.unknownStatus":
    "This status has no label yet — the raw value from the database is shown.",
  "operations.scopes.freshnessHint":
    "Whether the scope was cut from the project file that is live right now.",
  "operations.scopes.fresh": "Up to date",
  "operations.scopes.stale": "Outdated",
  "operations.scopes.lockHint":
    "Measured time inside the client's write lock, and how much of it the per-step timings do not account for.",
  "operations.scopes.notMeasured": "not measured",
  "operations.scopes.gap": "{pct}% unaccounted",
  "operations.scopes.empty.title": "No scope has been built yet",
  "operations.scopes.empty.body":
    "A scope gets its file on the project's next reload, or from Access › Scopes › Rebuild.",
  "operations.scopes.orphans.title": "Derived scopes nobody resolves",
  "operations.scopes.orphans.body":
    "Their files are still on disk and nothing removes them automatically. Check them against the file listing before deleting anything by hand.",
  "operations.scopes.gaps.title": "What these numbers do not cover",
  "operations.scopes.gaps.body":
    "Read them with these limits in mind — every one of them is a property of the current code, not of this screen.",
  "operations.scopes.gaps.noTimings":
    "A failed build records no timings at all, so every lock figure here describes successful builds only — and the longest holds are the ones that fail.",
  "operations.scopes.gaps.noEnd":
    "Only a successful build gets a finish time, so a duration derived from it would silently drop every failed or empty one.",
  "operations.scopes.gaps.noElapsed":
    "The wall clock of a single scope is not stored: it survives only in the loader job of the reload that built it.",
  "operations.scopes.gaps.untranslated":
    "This limit has no translated wording yet — the backend's own text is shown.",
  "guide.title": "Builder guide",
  // No chapter COUNT in here, on purpose. "in five chapters" was true
  // until J3 made it eight and nothing anywhere could see it go false:
  // this is a `prose` global, so the CI gate checks presence, length and
  // EN != ES, all of which a stale sentence satisfies perfectly.
  "guide.subtitle":
    "How the platform works, chapter by chapter — from the raw data to who is allowed to read which rows.",
  "guide.detailsTitle": "Details that matter",
  "guide.data.title": "Data & views",
  "guide.data.intro":
    "From the S3 parquet to the elements each dashboard renders.",
  "guide.data.node1": "S3",
  "guide.data.node1sub": "parquet (OBT)",
  "guide.data.node2": "Loader",
  "guide.data.node2sub": "download + swap",
  "guide.data.node3": "DuckDB",
  "guide.data.node3sub": "one file per project",
  "guide.data.node4": "Views",
  "guide.data.node4sub": "per report",
  "guide.data.node5": "Elements",
  "guide.data.node5sub": "KPIs · charts · tables",
  "guide.data.step1":
    "The project declares ONE data source (the OBT parquet in S3) in its Data tab.",
  "guide.data.step2":
    "The loader downloads it and writes the raw table into the project's own DuckDB file with an atomic swap.",
  "guide.data.step3":
    "Each report declares ITS OWN views over the OBT (sources[] in the report config) — the reload rematerialises all of them.",
  "guide.data.step4":
    "Every element (KPI, chart, table, filter) runs SQL against the report's views — never against the raw table directly.",
  "guide.data.step5":
    "The portal reads the same DuckDB file, picking up new versions automatically via the per-project version key.",
  "guide.data.detail1":
    "Each report owns its views, even when they duplicate another report's — autonomy over deduplication (durable team rule).",
  "guide.data.detail2":
    "A hardcoded date floor in a view SQL silently cuts history — views must hold ALL the data; filters do the windowing.",
  "guide.data.detail3":
    "Everything derived from a project's data must open the per-project connection (slug) — the global one is the classic blindspot.",
  // ── Plan J J2 — capítulo 2 de la Guía: Dimensiones ───────────
  // 22 claves por idioma. El set exacto lo exige
  // .github/scripts/check_guide_keys.py a partir de CHAPTERS y
  // FLOW_NODES de GuidePage.tsx: 5 nodos (label + sublabel), 6 pasos,
  // 4 detalles, más title e intro.
  "guide.dimensions.title": "Dimensions",
  "guide.dimensions.intro":
    "One definition of a column — the report's filters offer it and the access scopes cut by it.",
  "guide.dimensions.node1": "Raw table",
  "guide.dimensions.node1sub": "the project's own",
  "guide.dimensions.node2": "Dimension",
  "guide.dimensions.node2sub": "column · mapping · SQL",
  "guide.dimensions.node3": "Enrichment",
  "guide.dimensions.node3sub": "inside the reload",
  "guide.dimensions.node4": "Views",
  "guide.dimensions.node4sub": "see the new column",
  "guide.dimensions.node5": "Used by",
  "guide.dimensions.node5sub": "filters and scopes",
  "guide.dimensions.step1":
    "Dimensions live in Proyecto › Datos › Dimensiones. One dimension is ONE definition of a column: the report's filters offer it and the access scopes cut by it, so there is no second list to keep in sync. Declaring one changes nothing until the next reload.",
  "guide.dimensions.step2":
    "Origin «column»: pick a column the raw table already carries. Name that column explicitly — it is never inferred from the dimension's key — and tick «Scope» under «Usable for», because the default is usable for nothing at all.",
  "guide.dimensions.step3":
    "Origin «mapping»: pick the Master, the sheet, the key column and the value column. That sheet is maintained by the CLIENT (country → commercial region, seller → KAM portfolio), and every value present in the data must have a row in it.",
  "guide.dimensions.step4":
    "Origin «computed»: one SELECT-only expression over the row's own columns — the 2P marking, instalment pricing, the 1P/3P definition that differs per country. No subquery, no window function, no aggregate: the server binds the expression against the real file before it saves.",
  "guide.dimensions.step5":
    "Reload. The enrich_dimensions phase appends one real column per mapping and computed dimension to the raw table — inside the write transaction, before the first view is built — so no view SQL changes and the scopes can see it. The job's phases and its result_json are where you read what it did.",
  "guide.dimensions.step6":
    "When it reports unmapped values: the phase stopped, nothing was published, the previous data is still live, and the message names the values, how many rows they cover and which sheet is missing them. Add those rows to the Master and run the reload again.",
  "guide.dimensions.detail1":
    "Defined once, used twice: the same dimension feeds the report's filters and the access scopes. There is no second list to keep in sync, and therefore no way for the two to disagree.",
  "guide.dimensions.detail2":
    "The enrichment rewrites the raw table in place and runs BEFORE the first view. That ordering is the whole design: a column appended after the views is invisible to them and to the scope predicate, which resolves out of a DESCRIBE of the project file's raw table.",
  "guide.dimensions.detail3":
    "There is no «Other» bucket and the value is never left NULL. A raw value with no row in the Master fails the phase, naming the value and the row count — a row that quietly fell out of every scope would be invisible to everybody.",
  "guide.dimensions.detail4":
    "A dimension nobody marked usable for scopes makes every scope that names it resolve «not applicable» on this project, and nothing on screen looks broken. The default is fail-closed, not «unset»: it has to be ticked.",
  // ── Guía, chapters 3-5: access (Plan J, J3) ──────────────────────
  "guide.scopes.title": "Scopes",
  "guide.scopes.intro":
    "Who reads which rows, decided by a real file per project — never by a WHERE clause somebody can forget.",
  "guide.scopes.node1": "Rules",
  "guide.scopes.node1sub": "dimension = values",
  "guide.scopes.node2": "Predicate",
  "guide.scopes.node2sub": "AND · IN",
  "guide.scopes.node3": "Scope file",
  "guide.scopes.node3sub": "one per project",
  "guide.scopes.node4": "Reader",
  "guide.scopes.node4sub": "opens only this",
  "guide.scopes.node5": "State",
  "guide.scopes.node5sub": "built or closed",
  "guide.scopes.step1":
    "Scopes live in Accesos › Ámbitos and belong to the whole tenant, not to one project. The id IS the file name — 1 to 40 characters of [a-z0-9_-] — and it is never case-folded, so us-amazon and us_amazon are two different scopes. The name is what people read. Creating one moves nothing on disk yet.",
  "guide.scopes.step2":
    "Add rules: one dimension and a list of values taken from the data. Rules are ANDed and the values inside a rule are ORed. Types are not cosmetic — a text column ruled with the number 1 is refused, because DuckDB would cast the column and «1», «01» and «1.0» would all match, handing three different sellers to one person.",
  "guide.scopes.step3":
    "«Proyectos donde aplica» answers project by project. One that declares every dimension the rules name shows its state and its row count; one missing a dimension shows «no aplica» and grants nothing there — which is either correct, or the dimension was never marked usable for scopes.",
  "guide.scopes.step4":
    "Editing the rules is impact-acknowledged. The dry run names the groups and the number of people the change reaches, and the save has to echo that list back, so nobody changes who sees what without having read the consequence. The answer then says in as many words that nothing was rebuilt.",
  "guide.scopes.step5":
    "«Reconstruir» cuts the file, for one project, now. It holds the tenant write lock while it runs and can outlive the proxy that carried the request — when it does, the source of truth is the builds list, not the failed request. The project's next reload rebuilds it too, including a reload that skips because the data did not change.",
  "guide.scopes.step6":
    "«Todo» is the reserved scope and has no file of its own: it resolves literally to the project's own file. It cannot be rebuilt and it cannot be edited into a restricted scope, and everybody carries it until somebody puts them in a group that carries another.",
  "guide.scopes.detail1":
    "Somebody in several groups reads the UNION of their scopes — unions widen, they never intersect. Somebody in exactly one group resolves to that group's scope itself and not to a copy of it, so their file already exists and no edit leaves them locked out while a rebuild is pending.",
  "guide.scopes.detail2":
    "When the union cannot be written as one rule set it is refused, naming both groups, instead of being merged. Merging «US and amazon» with «CL and meli» would hand the reader US-with-meli and CL-with-amazon: rows neither group granted, in a file with the right shape and a believable row count, with nothing downstream able to catch it.",
  "guide.scopes.detail3":
    "A failed build never blocks the project and never widens anybody: readers keep the previous file, the reload publishes as usual, and the only place it shows is the state column here. Somebody reading OLD numbers with no error on screen is what a failed rebuild looks like.",
  "guide.scopes.detail4":
    "Deleting a scope somebody resolves is refused, naming who resolves it, and a scope with no file answers 503 instead of the project's file. There is no fallback to the unrestricted data — that fallback is exactly the leak this whole design exists to prevent.",
  "guide.groups.title": "Groups",
  "guide.groups.intro":
    "A group is the only place access is written: one scope, one content matrix, and the people who inherit both.",
  "guide.groups.node1": "Group",
  "guide.groups.node1sub": "who it is for",
  "guide.groups.node2": "Scope",
  "guide.groups.node2sub": "which rows",
  "guide.groups.node3": "Content",
  "guide.groups.node3sub": "reports · sections",
  "guide.groups.node4": "Members",
  "guide.groups.node4sub": "people",
  "guide.groups.node5": "The person",
  "guide.groups.node5sub": "portal · mail · chat",
  "guide.groups.step1":
    "Groups are created here, in the builder, by Omnitok. The client's own admin assigns people to the groups that exist and never creates one, because creating a group is deciding what a role MEANS in that account.",
  "guide.groups.step2":
    "Tab «Ámbito»: pick the group's scope, exactly one. That single choice decides which rows every member of the group reads, in every project the scope applies to.",
  "guide.groups.step3":
    "Tab «Contenido»: the matrix of reports, sections and actions. A cell nobody set falls back to the role default rather than to «denied» — except «See it», which has no default at all: a report no group of theirs opens is not opened, and no role default rescues it. Somebody's permission is the OR of the explicit cells across all of their groups, so a deny in one group is not erased by a group that simply has no opinion.",
  "guide.groups.step4":
    "Tab «Miembros»: add and remove people. Adding somebody recomputes their effective scope in the same transaction, so the answer on that person's own screen is already right before you leave this one.",
  "guide.groups.step5":
    "The list shouts «no otorga nada» — a group without a single dashboard-view row. Whoever joins it opens no report at all, and in a plain SELECT over the table the group looks perfect. The member count travels beside that warning, so a group that is both empty and grants nothing is not quietly reduced to one of the two.",
  "guide.groups.detail1":
    "There are no individual permissions any more. An exception is a group of one — and a one-person group SURVIVES the person: when somebody leaves, the role does not leave with them, and their replacement joins a group that already carries the right access.",
  "guide.groups.detail2":
    "Several groups mean the union of their scopes and the OR of their permissions, never the cross product of the rules. Two groups of one country each see both countries; intersecting them would be nobody's rows at all.",
  "guide.groups.detail3":
    "The client's own administrator still sees everything, and a group does not restrict them. That bypass is deliberate and unchanged — an administrator who could not see all of their own data would be a different role, and that role does not exist.",
  "guide.people.title": "People",
  "guide.people.intro":
    "Every person is the union of their groups; this screen is where you read that union and check it before they log in.",
  "guide.people.node1": "Person",
  "guide.people.node1sub": "logs in or receives",
  "guide.people.node2": "Groups",
  "guide.people.node2sub": "one or more",
  "guide.people.node3": "Union",
  "guide.people.node3sub": "the effective scope",
  "guide.people.node4": "File",
  "guide.people.node4sub": "one per project",
  "guide.people.node5": "Portal",
  "guide.people.node5sub": "screen · mail · chat",
  "guide.people.step1":
    "Accesos › Personas lists everyone in this tenant — the people who log in and the addresses that only receive. Somebody is created here with a role and an e-mail that cannot be edited afterwards, because that address is the identity every audit row is written against.",
  "guide.people.step2":
    "Tab «Grupos»: assigning groups is the only way access is given. Nothing on this screen writes a permission onto a person — the matrix that used to live here is gone, and its rows belong to the groups the person is in.",
  "guide.people.step3":
    "«Ámbito efectivo» is read-only and computed: the card names the scope the person's groups resolve to and the groups it came from, and nothing else. The row count and the build state per project are NOT on it — they are on that scope's own page, in Accesos › Ámbitos, one click away. Open it and compare the count against the analyst's there. A count that does not match is the moment to stop, and this is the screen to stop on rather than after they log in.",
  "guide.people.step4":
    "Somebody in NO group reads «unknown» on that card, not «Everything»: no group reports a scope for them, and the card will not paint a confident label over a binding no group chose. They read nothing either way, because the content matrix answers no for every report. Those are two separate facts and the card keeps them apart — a scope is not a permission, it is a cut.",
  "guide.people.step5":
    "«Acceso al builder» is per person and never per group: those flags are Omnitok staff access to this console, while groups govern what a client's people see in the portal. A group that could carry them would let a tenant-facing object grant entry to the admin console.",
  "guide.people.step6":
    "Deactivating somebody takes effect on the NEXT REQUEST, not at their next login: the person's row is read on every request, so neither a group change nor a deactivation needs them to sign out. There is nothing to wait for and nothing to clear.",
  "guide.people.detail1":
    "Two kinds of identity live here: somebody who logs in, and a recipient who only receives mail and files. Both can have groups and both resolve an effective scope; what separates them is whether the portal lets them in — and that is now enforced rather than recorded: a delivery-only identity is refused an interactive session and is reachable only by the tokens that render its mail or look at it read-only.",
  "guide.people.detail2":
    "«Suscripciones» and «excepciones» appear once, as a note, and never as headline numbers: they count rows in the per-person tables this slice retires. They are shown at all so that a migration which missed somebody is visible from the screen.",
  "guide.people.detail3":
    "«Ver como» exists in two shapes and they are not the same tool. By SCOPE, on the builder's own preview: authorised against the caller's own row, honoured on six routes matched exactly and refused on every other, and it records what it showed before it binds anything. By PERSON, from the platform console: fifteen minutes, portal only, strictly read-only — every write under it is refused, naming the route — and its audit row is written BEFORE the token is signed, so a failed write means no token.",
  // ── Guía, chapter 6: the walk before granting (Plan J, J4) ──────
  "guide.verify.title": "Verify before granting",
  "guide.verify.intro":
    "The walk to make before a real person is bound to a restricted scope: build it, count it, look through their eyes, then read the trail.",
  "guide.verify.node1": "Build",
  "guide.verify.node1sub": "the scope's file",
  "guide.verify.node2": "Count",
  "guide.verify.node2sub": "against the analyst",
  "guide.verify.node3": "Look",
  "guide.verify.node3sub": "read-only · 15 min",
  "guide.verify.node4": "Mail",
  "guide.verify.node4sub": "as the recipient",
  "guide.verify.node5": "Trail",
  "guide.verify.node5sub": "who did what",
  "guide.verify.step1":
    "Build the file before anybody is in the group: Accesos › Ámbitos, open the scope, «Reconstruir» per project — or let the project's next reload do it. Then read the state column. «Construido» with a row count is the only state you may continue from; «no aplica» means this project declares none of the dimensions the rules name, so the scope grants nothing there.",
  "guide.verify.step2":
    "Compare that row count with the analyst's own number for the same cut, project by project. This is the gate: while the two disagree, nobody joins the group. A scope built with zero rows is an error on no screen — the dashboard is simply empty — and the usual cause is a value written one way in the data and another way in the rule.",
  "guide.verify.step3":
    "«Ver como este ámbito», in the builder's own preview, answers the cheap half without needing a person: the report renders from that scope's file, on six routes matched exactly and refused on every other one, and the audit row is written before anything is bound. Ask for it the way the per-person look is asked for — a header on those six preview calls; no screen sends it yet, so this is an API step today. Use it while the counts are still moving.",
  "guide.verify.step4":
    "«Ver como» a PERSON is the read-only crossing the platform console mints: fifteen minutes, portal only, and read-only for real — every write under it comes back refused with a sentence that names the route (one POST is exempt because it IS a read: the row drill, whose query does not fit in a URL). It is the only way to see what a delivery-only recipient gets, since that identity can never sign in. Today it is asked for through the console's API, which takes a read-only flag; the button is what is still missing.",
  "guide.verify.step5":
    "Ask the chat something the scope must NOT answer — a market outside the cut. Finding nothing is the right answer and a number is not, because the sandbox has that scope's file attached and no other. Two more things change here: a conversation started under another scope refuses to resume rather than replaying itself, and training examples stay withheld unless somebody marked them safe, so a restricted chat is deliberately duller.",
  "guide.verify.step6":
    "The mail is sent by the person, from their own screen: a look cannot press «Enviar», that POST is refused like every other. What changed underneath is whose eyes the screenshot is taken through. By default each addressee gets the dashboard captured with a token minted for them, so the picture shows their own slice; only an admin who chooses «My data scope» mails the picture of their own cut, and nobody else can choose it. The image is never the platform's unrestricted view. If the capture cannot show the dashboard, or shows error cards instead of data, the mail is not sent: the send fails and its owner is told why.",
  "guide.verify.step7":
    "Close by reading the trail, and know which one. Access changes — the group, its members, a scope's rules — land in Accesos › Auditoría with the actor, the before and the after. The per-person look does NOT: the platform console writes it into its own audit, as a crossing marked read-only. Two trails, because two different people are accountable for them.",
  "guide.verify.detail1":
    "A schedule that includes the AI analysis and belongs to a restricted person does send, and the narrative is written from that person's own file: the scope travels with the request the builder makes to the portal, where it is required rather than assumed, and the screenshot beside it is captured through the same identity — so the two halves of one mail describe one slice. What the mail will not do is go out unaccounted for: the portal echoes back which scope it read, and a missing or different echo — what a portal running an older release answers — fails the job instead of delivering it.",
  "guide.verify.detail2":
    "Insights are per scope and off until a group asks for them: a scope earns its own narrative only when somebody reading under it belongs to a group with the toggle on, and only in the projects whose file it actually built. The figure beside the toggle is arithmetic — reports × scopes × reloads — and the count measured over the last thirty days sits next to it so the two are never read as one.",
  "guide.verify.detail3":
    "Nothing about a mail is frozen at save time. The scope it renders under is read fresh when it fires, from the person's own row, so narrowing somebody's access reaches their next mail with nobody editing a schedule. Threshold and SQL alerts work the same way: both open that person's file, never the project's.",
  "guide.verify.detail4":
    "Every step here fails closed and none of them borrows a wider file. A scope with no build answers 503, a narrative missing for that scope is a refusal rather than the unrestricted one, and an owner whose row cannot be found stops the send. If a screen looks empty or a mail did not arrive, look for what is missing — no default quietly filled the gap.",
  "guide.refresh.title": "Auto-refresh & reconciliation",
  "guide.refresh.intro":
    "The cron reloads the OBT and a 3-way check gates every publish.",
  "guide.refresh.node1": "Cron",
  "guide.refresh.node1sub": "per project",
  "guide.refresh.node2": "Loader job",
  "guide.refresh.node2sub": "heartbeat ~5 s",
  "guide.refresh.node3": "Atomic swap",
  "guide.refresh.node3sub": "views rebuilt",
  "guide.refresh.node4": "Gate A↔B",
  "guide.refresh.node4sub": "golden vs engine",
  "guide.refresh.node5": "Portal",
  "guide.refresh.node5sub": "version pickup",
  "guide.refresh.step1":
    "Each project schedules its reload (cron + timezone) in the hub's Auto-refresh tab; Operations shows them all.",
  "guide.refresh.step2":
    "A run downloads the parquet, rebuilds the raw table and rematerialises every report's views in a staging swap.",
  "guide.refresh.step3":
    "Before the version bump, the reconciliation GATE compares golden SQL (A) against the live engine (B) for every covered element.",
  "guide.refresh.step4":
    "If the gate fails, the swap is discarded — the OLD data stays live, the job is marked failed and an alert email goes out.",
  "guide.refresh.step5":
    "After publish, phase 2 compares the engine against what the portal actually serves (B↔C) — alert-only, with cache rewarm.",
  "guide.refresh.detail1":
    "Golden manifests live per report (reconciliation.yaml); adding an element without an assertion BLOCKS the gate — no silent gaps.",
  "guide.refresh.detail2":
    "Jobs heartbeat every ~5 s; the reaper kills stale ones. Force-reap (Operations or hub) aborts a stuck job before a fresh run.",
  "guide.refresh.detail3":
    "The gate failing means data stayed consistent — never re-run blindly; read the per-element drawer first.",
  "guide.publish.title": "Publishing",
  "guide.publish.intro":
    "From draft edits to the portal, with history and revert.",
  "guide.publish.node1": "Draft",
  "guide.publish.node1sub": "autosave",
  "guide.publish.node2": "Validation",
  "guide.publish.node2sub": "SQL + layout",
  "guide.publish.node3": "Publish",
  "guide.publish.node3sub": "snapshot to history",
  "guide.publish.node4": "Portal",
  "guide.publish.node4sub": "~60 s pickup",
  "guide.publish.step1":
    "Save writes the report. A draft is not served; a published report changes in the portal with the save (the portal serves the published rows), so the save first passes the same checks as Publish. In a report group, what the group repeats is copied to the sibling reports in the same save.",
  "guide.publish.step2":
    "Publish validates SQL and layout (grid bounds, heights) — a failing validation blocks, loudly.",
  "guide.publish.step3":
    "Publishing snapshots the previous config to History — any snapshot can be reverted to in one click.",
  "guide.publish.step4":
    "The portal polls report_drafts every 60 s and picks the new config up without a restart.",
  "guide.publish.detail1":
    "The builder is the single source of truth — the portal never edits dashboard_config.",
  "guide.publish.detail2":
    "Backend-only fields (runtime_view_sql, …) are preserved on publish — the UI never sees or wipes them.",
  "guide.publish.detail3":
    "The shared preview (/preview/:key) renders the EXACT same DashboardBody the portal uses — what you see is what ships.",
  "guide.ai.title": "The AI and its improvement loop",
  "guide.ai.intro":
    "Training feeds the chat; issues feed the training back.",
  "guide.ai.node1": "Training",
  "guide.ai.node1sub": "DDL · docs · Q&A",
  "guide.ai.node2": "Chat",
  "guide.ai.node2sub": "per report",
  "guide.ai.node3": "Issues",
  "guide.ai.node3sub": "thumbs-down",
  "guide.ai.node4": "Triage",
  "guide.ai.node4sub": "AI Panorama",
  "guide.ai.node5": "Better AI",
  "guide.ai.node5sub": "promote to example",
  "guide.ai.step1":
    "Each report's Knowledge pane holds its schema docs and canonical Q&A examples — that IS the chat's training.",
  "guide.ai.step2":
    "The chat answers with SQL over the report's views; starter questions and CTAs are configured per report.",
  "guide.ai.step3":
    "A thumbs-down in the portal files an issue with the full conversation attached.",
  "guide.ai.step4":
    "AI Panorama queues issues across ALL reports — triage them there (resolve, dismiss, or promote).",
  "guide.ai.step5":
    "Promoting an issue turns the corrected answer into a training example — the single highest-leverage fix for chat quality.",
  "guide.ai.detail1":
    "Insights are separate from chat: specs + temporal window + snapshots, configured in the report's Insights pane.",
  "guide.ai.detail2":
    "Context notes live at PROJECT level and flow into every affected snapshot — regenerate affected ones after editing.",
  "guide.ai.detail3":
    "A bad chat answer is a training gap, not a prompt problem — add a canonical example before touching prompts.",
  // ── Plan J J2 — el riel de ayuda contextual ────────────────
  // Portado de la consola (console-frontend/components/HelpPanel.tsx).
  // La consola las llama `common.helpForThisScreen` / `help.contents` /
  // `help.reopen`; acá viven todas bajo `screenhelp.` porque es el
  // prefijo que escanea check_guide_keys.py, y un grupo que el gate no
  // mira es un grupo que se queda a medias en español sin que nadie se
  // entere (el i18next de esta app tiene fallbackLng: "en").
  "screenhelp.button": "What is this screen?",
  "screenhelp.forThisScreen": "Help for this screen",
  "screenhelp.contents": "Contents",
  "screenhelp.openGuide": "Open the guide",
  "screenhelp.dimensions.title": "Dimensions",
  "screenhelp.dimensions.body":
    "This screen defines the columns this project can be filtered by and can have access cut by — one definition serving both. A dimension is either a column the data already carries, a value translated through a sheet of a Master the client maintains, or one computed with a SQL expression over the row. The part that surprises everybody: declaring one changes nothing until the next reload, because the mapping and computed columns are written into the data by the reload itself.",
  // Plan J J3 — one topic per Accesos SCREEN, list and detail apart.
  // See ScreenHelp.tsx for why the detail routes do not reuse their
  // list's topic even though the gate would have accepted it.
  "screenhelp.scopes.title": "Scopes",
  "screenhelp.scopes.body":
    "This screen decides who reads which rows. A scope is a set of rules over dimensions — one dimension, one list of values, ANDed together — and each one is materialised as a real DuckDB file per project, so a project that does not declare a dimension the rules name grants nothing there rather than everything. Editing the rules retires the scope's files at once: everyone who reads through it gets an error (503) until somebody rebuilds it, or until the project's next reload.",
  "screenhelp.scope.title": "This scope",
  "screenhelp.scope.body":
    "One scope: its rules, the projects it applies to and every build it has had. The save is impact-acknowledged — the dry run names the groups and the people the change reaches, and the save has to echo them back — and the rebuild is synchronous, holds the tenant write lock and can outlive the proxy. When the request dies, the builds table below is the source of truth, not the error.",
  "screenhelp.groups.title": "Groups",
  "screenhelp.groups.body":
    "The list of groups. A group carries one scope and one content matrix and is the only place access is written; Omnitok creates them here and the client's own admin only assigns people to them. The state to look for is «no otorga nada» — a group with no dashboard-view row at all, which opens no report for its members and looks perfect in the database.",
  "screenhelp.group.title": "This group",
  "screenhelp.group.body":
    "This group's three tabs: who is in it, which rows it opens, and which reports and sections it grants. Changing the scope changes what every member reads on their next request, with no re-login — but only as far as their widest group allows: a person's scope is the UNION of their groups', and the union with «Everything» is «Everything», so a member who is also in an unrestricted group goes on reading everything. The «Resolves to» column says which is which, row by row: «reads everything» is a restriction this group is not applying. A cell left unset in the content matrix means the role default, not «denied» — except «See it», which has no default: with no row on it the report is not opened at all.",
  "screenhelp.groupAi.title": "This group's AI",
  "screenhelp.groupAi.body":
    "What the chat and the insights do for the people in this group. The insights toggle is a COST and it is off until somebody turns it on: with it on, every report that produces AI Analysis is narrated once more for this group's scope, on top of the unrestricted one, and the figure beside the switch is arithmetic — reports × scopes × reloads — with the measured count next to it so the two are never read as one. The context note is appended to the prompts its members reach. And under a restricted scope the chat keeps only the training examples somebody marked safe, so it answers with less than it does for an unrestricted reader — deliberately, not by accident. Nothing here grants access; it decides what the AI says to people who already have it.",
  "screenhelp.groupExperience.title": "This group's experience",
  "screenhelp.groupExperience.body":
    "What a member of this group starts with: the Home board they are provisioned with the first time they open the portal. Editing the board template re-syncs the members' boards and never touches a tile somebody added themselves; a tile a member cannot read comes back denied instead of vanishing, because provisioning decides what a person STARTS with and never what they may see. The other half of the column, a group default e-mail, has no form on this tab: nothing anywhere writes a member's schedule row from it, and a form for a template nobody delivers would tell an administrator they had scheduled something. The schedule people actually get is the one they set up for themselves in the portal.",
  "screenhelp.people.title": "People",
  "screenhelp.people.body":
    "Everyone in this tenant, whether they log in or only receive. The column that matters is the effective scope: it is the union of the person's groups — the WIDEST of them, so one unrestricted group makes every restriction they carry inert. It is computed and never typed by hand, and somebody in no group reads as «unknown» rather than as a scope, because no group reports one; they read nothing either way, since the content matrix answers no for every report.",
  "screenhelp.person.title": "This person",
  "screenhelp.person.body":
    "This person's groups, the effective scope those groups resolve to, and their builder access — which is staff access to this console and is per person on purpose, not per group. The scope card names the scope and the groups it came from, and nothing else: the row count and the build state per project live on the scope's own page, one click away. Read the union the way it resolves — the WIDEST group wins, so one unrestricted group makes every restriction this person carries inert. Nothing here writes a permission directly: access is given by adding them to a group.",
  "screenhelp.cache.title": "Cache",
  "screenhelp.cache.body":
    "What every reload computes before it publishes. Each reload warms the default view of every published report of its project, and of each group's filters, before the new data becomes visible; a report with «All combinations» on also warms every combination of its button filters. If any of it fails, the reload is NOT published: the previous data stays live with its cache complete, the run fails and an email goes out. Languages only count for reports whose SQL uses the language; the rest are computed once for all of them. The estimates come from each project's last reload and are recalculated as you switch; a report without a measurement says so instead of showing a number. Changes apply from each project's next reload.",
  "screenhelp.audit.title": "Audit trail",
  "screenhelp.audit.body":
    "Every access change made from Accesos, with who made it and when: scopes, groups, the content matrix, memberships, the role defaults and each recomputed effective scope. The row is written in the same transaction as the change, so the two land together or neither does — a change that could not be recorded did not happen. The one-shot migration that created the first groups is here too, filed as «automatic». What is NOT here is anything done outside this console: SQL run straight against the tenant database changes access and leaves no trace of it, so an action missing from this list was not made from Accesos, which is not the same as not made. Nothing is filtered in the browser — a view that quietly hid rows the server returned is how a reconstruction misses the one that mattered.",

  // Common UI atoms shared across inspector + list (iter 27)
  "common.copy": "Copy",
  "common.copied": "Copied",
  "common.expand": "Expand",
  "common.collapse": "Collapse",

  // Admin Prompts — iter 27 rename to "Global" framing
  "admin.prompts.titleGlobal": "Global AI Prompts",
  "admin.prompts.subtitleGlobal":
    "Fallback templates the portal uses by default for every report.",
  "admin.prompts.newBtnGlobal": "New global prompt",
  "admin.prompts.scopeBannerBodyIter27":
    "These are the GLOBAL prompts every report falls back to. Per-report overrides are edited in the report editor's AI tab (Knowledge → Advanced) — they do not appear here for editing.",
  "admin.prompts.overridesTitle": "Reports with active overrides",
  "admin.prompts.overridesHint":
    "Read-only summary. Edit each override from its report's AI tab.",
  "admin.prompts.editInAiTab": "Edit in AI tab",
  "admin.prompts.clientScopeReadonly": "client-scope · read-only here",
  "admin.prompts.overrideDraftMissing": "report not found — no link",

  // Inspector — iter 27 restructure
  "inspector.identity.title": "Identity",
  "inspector.sql.title": "SQL Query",
  "inspector.sql.empty": "Not set",
  "inspector.sql.notSet": "Not set",
  "inspector.sql.mode": "SQL display mode",
  "inspector.sql.template": "Template",
  "inspector.sql.live": "Live",
  "inspector.sql.live.loading": "Loading live SQL…",
  "inspector.sql.live.error": "Failed to load live SQL.",
  "inspector.behavior.title": "Behavior",
  "inspector.structural.title": "Structural (read-only)",
  "inspector.kpi.sqlHint":
    "Interpolated into the engine's KPI SELECT alongside the WHERE clause built from the active filters.",
  "inspector.kpi.previousSql": "Previous-period SQL",
  "inspector.kpi.sparklineSql": "Sparkline SQL",
  "inspector.kpi.expectedSql": "Expected-value SQL",
  "inspector.kpi.breakdownSql": "Breakdown SQL",
  "inspector.kpi.goalDirection": "Goal direction",
  "inspector.chart.rawSql": "Raw SQL",
  "inspector.chart.rawSqlHint":
    "Used when the chart needs a full SELECT (e.g. quarterly comparison tables). {where} and {filter:KEY} placeholders are substituted by the engine.",
  "inspector.table.sourceSql": "Source SQL",
  "inspector.table.sourceSqlHint":
    "Inner query the engine wraps with the active filter WHERE + sort/limit before fetching rows.",
  "inspector.text.sql": "SQL",
  "inspector.text.sqlHint":
    "Optional inline SQL the engine runs to interpolate {value} placeholders in the text.",
  "list.col.key": "Key",
  "list.col.name": "Display name",
  "list.col.table": "DuckDB table",
  "list.col.status": "Status",
  "list.col.updated": "Updated",
  "list.col.actions": "Actions",
  "list.status.draft": "Draft",
  "list.status.published": "Published",
  "list.delete.confirm":
    "Delete this report? This removes the draft and cannot be undone.",
  "list.create.title": "New report",
  "list.create.key": "Key (lowercase, underscores)",
  "list.create.keyInvalid": "lowercase letters / digits / underscores only — must start with a letter",
  "list.create.keyExists": "Key \"{key}\" already exists",
  "list.create.name": "Display name (required)",
  "list.create.table": "DuckDB table (optional)",
  "list.create.tableHint":
    "Leave it empty if the report takes its data from a source in the Data tab: the first source defines the table. Fill it in only to point at a table that already exists.",

  // Header / nav
  "header.signOut": "Sign out",
  "header.lang": "Language",
  "nav.reports": "Reports",
  "nav.reportsAndProjects": "Reports & Projects",
  "nav.templates": "Templates",

  // Templates page
  "templates.title": "Templates",
  "templates.subtitle": "Reusable starting points for new reports.",
  "templates.empty": "No templates yet. Built-in templates are seeded automatically by the backend.",
  "templates.noDescription": "(no description)",
  "templates.use": "Use",
  "templates.edit": "Edit",
  "templates.builtin": "Builtin",
  "templates.backToList": "Back to templates",
  "templates.deleteConfirm": "Delete template \"{title}\"? This cannot be undone.",
  "templates.stat.sections": "Sections",
  "templates.stat.items": "Items",
  "templates.stat.filters": "Filters",

  // Projects page
  "nav.projects": "Projects",
  "projects.title": "Projects",
  "projects.subtitle": "Group reports by project.",

  // Components gallery
  "nav.components": "Components",
  // App shell (v3 brand header, sidebar sections)
  "shell.label": "Builder",
  "shell.labelTenant": "Builder · {tenant}",
  "shell.toggleMenu": "Show or hide the menu",
  "nav.section.intelligence": "Intelligence",
  "nav.section.admin": "Administration",
  "nav.section.help": "Help",
  "gallery.title": "Components",
  "gallery.subtitle": "One visual base for the kit, the BI components and the report render. Every example is the real component with sample data.",
  "gallery.section.kpis": "KPI Cards",
  "gallery.section.chartsStatic": "Charts — Static",
  "gallery.section.chartsInteractive": "Charts — Interactive (hover for tooltips)",
  "gallery.section.tables": "Tables",
  "gallery.section.filters": "Filters",
  "gallery.section.dates": "Date Pickers",
  "gallery.section.analytics": "Analytics",
  "gallery.section.maps": "Geographic Maps",
  "gallery.toc.title": "On this page",
  "gallery.toc.kpis": "KPI Cards",
  "gallery.toc.chartsStatic": "Charts — Static",
  "gallery.toc.chartsInteractive": "Charts — Interactive",
  "gallery.toc.tables": "Tables",
  "gallery.toc.filters": "Filters",
  "gallery.toc.dates": "Date Pickers",
  "gallery.toc.analytics": "Analytics",
  "gallery.toc.maps": "Maps",
  "gallery.layout.grid": "Grid",
  "gallery.layout.full": "Full",
  "gallery.code.copy": "Copy",
  "gallery.code.copied": "Copied",
  "gallery.code.import": "Usage — Import",
  "gallery.toggle.tooltip": "Switch grid / full-width layout",
  "projects.new": "New project",
  "projects.edit.title": "Edit project",
  "projects.empty": "No projects yet.",
  "projects.slug": "Slug",
  "projects.slugInvalid": "lowercase letters / digits / hyphens / underscores only",
  "projects.slugLocked": "The slug can't be changed once the project exists: its data files, permissions and deliveries depend on it.",
  "projects.name": "Name",
  "projects.description": "Description",
  "projects.color": "Color",
  "projects.icon": "Icon",
  "projects.iconNone": "None",
  "projects.orderIndex": "Sidebar order",
  "projects.orderIndex.help":
    "Lower numbers appear first in the portal sidebar; projects with the same value sort by name.",
  "editor.chart.dataLabels.title": "Data labels & axis format",
  "editor.chart.dataLabels.show": "Show data labels",
  "editor.chart.dataLabels.formatLabel": "Format",
  "editor.chart.dataLabels.formatRaw": "Raw",
  "editor.chart.dataLabels.formatCompact": "Compact (K / M / B)",
  "editor.chart.dataLabels.formatCurrency": "Currency",
  "editor.chart.dataLabels.formatPercent": "Percent",
  "editor.chart.dataLabels.decimalsLabel": "Decimals",
  "editor.chart.dataLabels.axesTitle": "Axes",
  "editor.chart.dataLabels.axisX": "X axis format",
  "editor.chart.dataLabels.axisY": "Y axis format",

  "projects.toggles.title": "Sections",
  "projects.toggles.subtitle":
    "Pick which top-level sections this project exposes in the portal sidebar.",
  "projects.toggles.dashboard": "Dashboards",
  "projects.toggles.maestros": "Master data",
  "projects.toggles.files": "Files",
  "projects.toggles.requireOne": "At least one section must stay enabled.",

  "projects.form.tab.general": "General",
  "projects.form.tab.maestros": "Master data",
  "projects.form.tab.archivos": "Files",
  "projects.form.maestros.intro":
    "Appearance overrides + S3 prefixes for the project's Maestros page. Empty fields fall back to the portal's generic copy.",
  "projects.form.maestros.pageTitle": "Page title",
  "projects.form.maestros.pageTitlePlaceholder": "e.g. HP Catalogs",
  "projects.form.maestros.subtitle": "Subtitle",
  "projects.form.maestros.subtitlePlaceholder":
    "e.g. Reference data for this report",
  "projects.form.maestros.emptyMessage": "Empty-state message",
  "projects.form.maestros.emptyMessagePlaceholder":
    "Shown when no prefixes are configured",
  "projects.form.maestros.prefixesNote":
    "S3 prefixes for the master-data loader live in the JSONB column today and are managed by the loader pipeline. They will surface as an editable list here in a later iteration.",

  "projects.form.archivos.intro":
    "Appearance + S3 root + allowed types for the project's Archivos page. The portal uses these per project.",
  "projects.form.archivos.pageTitle": "Page title",
  "projects.form.archivos.pageTitlePlaceholder": "e.g. Quarterly downloads",
  "projects.form.archivos.subtitle": "Subtitle",
  "projects.form.archivos.subtitlePlaceholder":
    "e.g. Quarterly reports and exports",
  "projects.form.archivos.emptyMessage": "Empty-state message",
  "projects.form.archivos.emptyMessagePlaceholder":
    "Shown when the S3 listing under the root is empty",
  "projects.form.archivos.s3RootPath": "S3 root path",
  "projects.form.archivos.allowedTypes": "Allowed file types",
  "projects.form.archivos.allowedTypesNote":
    "When empty the portal accepts every type; click a chip to add/remove.",
  "projects.form.archivos.maxSize": "Max upload size (MB)",
  "projects.form.archivos.allowUpload":
    "Show the upload button on the Archivos page",
  // Project data source tab (iter 23)
  "projects.form.tab.data": "Data",
  // Loader / scheduler tab (iter 29 — moved from portal Settings → Operations)
  "projects.form.tab.loader": "Auto-refresh",
  "loader.loading": "Loading…",
  "loader.noDataSource":
    "Configure a data source on the Data tab first — the loader needs something to refresh.",
  "loader.scheduleTitle": "Schedule",
  "loader.enabled": "Enable automatic refresh",
  "loader.cron": "Cron expression",
  "loader.cronHelp":
    "Standard 5-field cron: minute hour day month weekday. Example: 0 3 * * * runs every day at 03:00.",
  "loader.timezone": "Timezone",
  "loader.timezoneHelp":
    "IANA name (UTC, America/Argentina/Buenos_Aires, …). Determines when the cron fires.",
  "loader.mode": "Refresh mode",
  "loader.mode.reload": "Full reload (recommended)",
  "loader.mode.incremental": "Incremental",
  "loader.mode.rebuild": "Rebuild",
  "loader.maxRuntime": "Max runtime (seconds)",
  "loader.maxRuntimeHelp":
    "Coming soon. Today there is no time limit: a refresh runs until it finishes or fails.",
  "loader.soon": "Coming soon",
  "loader.loadFailed": "The refresh schedule could not be loaded, so the form stays hidden: saving it now would overwrite the stored schedule with default values.",
  "loader.runNowTitle": "Manual refresh",
  "loader.runNowHelp":
    "Run the loader now using the saved schedule's mode. Blocks until complete or failed.",
  "loader.runNow": "Run now",
  "loader.runNowHint":
    "Idle. The Run now button enqueues a new reload and waits until it completes.",
  "loader.forceReap": "Force abort + run now",
  "loader.forceReapHelp":
    "A job is in flight. Force abort kills it (marks it failed) and starts a fresh reload.",
  "loader.forceReapConfirm":
    "This will mark the current pending/running job as failed and start a new reload. Continue?",
  "loader.recentJobsTitle": "Recent jobs",
  "loader.noJobs": "No jobs yet — schedule one or click Run now.",
  "loader.statusActive": "Scheduler active",
  "loader.statusIdle": "Scheduler idle",
  "loader.statusDisabled": "Scheduler disabled",
  "loader.statusLoadFailed": "Could not load the scheduler status: {error}",
  "loader.leaseHolder": "Leader",
  "loader.lastTick": "Last tick",
  "loader.recon.title": "Reconciliation detail",
  "loader.recon.jobLabel": "Job",
  "loader.recon.loading": "Loading reconciliation rows…",
  "loader.recon.empty": "No reconciliation data for this job.",
  "loader.recon.layerGate": "Gate (A·B)",
  "loader.recon.layerPortalCache": "Portal (B·C)",
  "loader.recon.layerPortalHttp": "Portal HTTP (B·C)",
  "loader.recon.colElement": "Element",
  "loader.recon.colLayer": "Layer",
  "loader.recon.colStatus": "Status",
  "loader.recon.colGolden": "Golden (A)",
  "loader.recon.colObserved": "Observed",
  "loader.recon.colDelta": "Δ",
  "loader.recon.noChecks": "No checks",
  "loader.recon.noChecksTitle": "No assertion was compared in this run.",
  "loader.recon.portalNotChecked": "Portal not checked: 0 assertions compared.",
  "projects.form.data.intro":
    "Configure ONE consolidated source for the whole project — an S3 parquet or DuckDB file, or a CSV / Excel file you upload here. Each report's data slice is then a SQL view over this raw — set the view body on the report's own Data tab.",
  "projects.form.data.kindLabel": "Kind",
  "projects.form.data.urlLabel": "S3 URL",
  "projects.form.data.tableLabel": "Target table (optional)",
  "projects.form.data.tableHint":
    "Defaults to project_raw_{slug}. Views reference this name in their SELECT.",
  "projects.form.data.aliasLabel": "Attach alias (optional)",
  "projects.form.data.saveBtn": "Save URL",
  "projects.form.data.reloadBtn": "Reload from S3",
  "projects.form.data.clearBtn": "Clear",
  "projects.form.data.clearConfirm":
    "Clear the project's data source? This drops the materialized raw table and resets audit fields. View-kind reports will fail until you reconfigure.",
  "projects.form.data.loadedAt": "Last loaded",
  "projects.form.data.rows": "Rows",
  "projects.form.data.columns": "Columns",
  "projects.form.data.tables": "Tables",
  "projects.form.data.columnsShort": "cols",
  "projects.form.data.schemaTitle": "Schema",
  "projects.form.data.catalogTitle": "Catalog",
  "projects.form.data.viewWarnings":
    "Reload succeeded but some dependent views failed to recreate:",
  // Track B1 — connections (project data tab)
  "projects.form.data.connectionLabel": "Connection",
  "projects.form.data.connectionNone": "(none)",
  "projects.form.data.connectionHint":
    "Credentials come from the selected connection. Required for postgres and api sources, and for any s3:// URL.",
  // Why "Save" is disabled. The rules themselves are right; before this
  // an operator saw only a dead button and had to read the source to
  // learn that an s3:// URL needs a connection.
  "projects.form.data.blockedUrl": "Enter the S3 URL of the file to load.",
  "projects.form.data.blockedParquetExt":
    "The URL must end in .parquet for a parquet source.",
  "projects.form.data.blockedDuckdbExt":
    "The URL must end in .duckdb for a duckdb (attach) source.",
  "projects.form.data.blockedTable":
    "Name the source table to copy out of the attached .duckdb file.",
  "projects.form.data.blockedUpload": "Upload the file first.",
  "projects.form.data.blockedSheet":
    "Pick which sheet of the workbook to load.",
  "projects.form.data.blockedConnection":
    "Pick a connection: an s3:// URL is read with its credentials — there is no anonymous access.",
  "projects.form.data.blockedNoConnections":
    "This project has no connection yet, and an s3:// URL is read with a connection's credentials — there is no anonymous access. Create one with “Add connection” above (a shared one from another project can be reused by pointing at the same secret reference).",
  "projects.form.data.attachTableLabel": "Source table inside the file",
  "projects.form.data.attachTableHint":
    "Required — the table to copy out of the attached .duckdb into project_raw_{slug}. There is no default.",
  // ── Plan C phase 4 — CSV / Excel uploaded as the project dataset ──
  "projects.form.data.kind.parquet": "Parquet in S3 (OBT)",
  "projects.form.data.kind.duckdb_attach": "DuckDB file (attach)",
  "projects.form.data.kind.postgres": "Postgres query",
  "projects.form.data.kind.api": "JSON API",
  "projects.form.data.kindSoon": "Coming soon",
  "projects.form.data.blockedKindSoon":
    "This project uses a {kind} source. This form cannot edit that source yet (coming soon), so it is read-only here; reloading still runs it as saved.",
  "projects.form.data.kindCsv": "CSV / TSV file (upload)",
  "projects.form.data.kindExcel": "Excel .xlsx file (upload)",
  "projects.form.data.saveUploadBtn": "Save file as source",
  "projects.form.data.reloadUploadBtn": "Reload project data",
  "projects.form.data.upload.title": "Uploaded file",
  "projects.form.data.upload.subtitle":
    "The project's whole dataset comes from this file. Accepted: .csv, .tsv and .xlsx, up to 100 MB.",
  "projects.form.data.upload.chooseBtn": "Choose a file…",
  "projects.form.data.upload.replaceBtn": "Choose a different file…",
  "projects.form.data.upload.uploading": "Uploading and inspecting…",
  // Single-brace interpolation per the builder's i18next config.
  "projects.form.data.upload.rowsRead":
    "{rows} rows · {columns} columns read",
  "projects.form.data.upload.stagedNote":
    "Uploaded but not published yet. Save the source, then reload the project data.",
  "projects.form.data.upload.sheetLabel": "Worksheet",
  "projects.form.data.upload.sheetPlaceholder": "Pick a worksheet…",
  "projects.form.data.upload.sheetNoWorkbook":
    "Upload an .xlsx file to list its worksheets.",
  "projects.form.data.upload.sheetHint":
    "Only this worksheet is loaded. Nothing else in the workbook is read. To use another one, pick it here, save, and reload.",
  "projects.form.data.upload.sheetRequired":
    "This workbook has more than one worksheet. Pick the one that holds the data — it is never guessed.",
  "projects.form.data.upload.delimiterLabel": "Field separator (optional)",
  "projects.form.data.upload.delimiterPlaceholder": "auto-detect",
  "projects.form.data.upload.delimiterHint":
    "Leave empty and the separator is detected from the file. Type one character to force it (\\t for tab).",
  "projects.form.data.upload.headerLabel": "First row holds the column names",
  "projects.form.data.upload.allVarcharLabel": "Read every column as text",
  "projects.form.data.upload.allVarcharHint":
    "Use this when a code is being read as a number and loses its leading zeros (postal codes, product IDs).",
  "projects.form.data.upload.guideTitle":
    "How to prepare and upload the file — step by step",
  "projects.form.data.upload.guideStep1":
    "Accepted formats are .csv, .tsv and .xlsx. The old .xls, macro workbooks (.xlsm), binary workbooks (.xlsb), OpenDocument (.ods) and Apple Numbers files are refused, because they cannot be read reliably.",
  "projects.form.data.upload.guideStep2":
    "If your file is one of those, convert it first — in Excel: File > Save As > Browse, and under 'Save as type' choose 'Excel Workbook (*.xlsx)' or 'CSV UTF-8 (Comma delimited) (*.csv)'. In Google Sheets: File > Download > 'Microsoft Excel (.xlsx)' or 'Comma-separated values (.csv)'. In LibreOffice Calc: File > Save As, and under 'File type' choose 'Excel 2007-365 (.xlsx)' or 'Text CSV'.",
  "projects.form.data.upload.guideStep3":
    "The first row must hold the column names, with no title rows, no merged cells and no blank rows above it. If your file genuinely has no header row, untick 'First row holds the column names' below and the columns will be named column0, column1, …",
  "projects.form.data.upload.guideStep4":
    "Maximum size: 100 MB. A larger file is refused — for those, use a parquet, DuckDB or Postgres connection instead, which stream from storage and have no size cap.",
  "projects.form.data.upload.guideStep5":
    "Click 'Choose a file…' above. The file is uploaded and inspected straight away, and you will see how many rows and columns were actually read. Nothing is published yet: what the portal serves has not changed.",
  "projects.form.data.upload.guideStep6":
    "Excel only: pick the worksheet. A workbook with a single sheet selects itself; one with several always asks, because loading the wrong tab would publish wrong numbers without any error. Only the chosen worksheet is read.",
  "projects.form.data.upload.guideStep7":
    "Click 'Save file as source', then 'Reload project data'. The reload is what publishes it — the table project_raw_{slug} is rebuilt and every report view on top of it is recreated.",
  "projects.form.data.upload.guideStep8":
    "To update the data later, upload a new file the same way. Each reload REPLACES the whole table — it is a full replace, never an append, so rows you delete from the file disappear from the dashboards. The old file is removed once the new one is saved.",
  "projects.form.data.upload.guideStep9":
    "If the new file is empty, unreadable, or produces zero rows, the reload fails and the data already published stays live and untouched. Keep the column names and types stable: if they change, the reload reports exactly which report view broke.",
  // ── Plan J J2 — Datos › Dimensiones ───────────────────────────
  "projects.data.sub.source": "Source",
  "projects.data.sub.dimensions": "Dimensions",
  "projects.data.sub.ariaLabel": "Data sections",
  "dimensions.title": "Dimensions",
  "dimensions.subtitle":
    "A column the data can be filtered and restricted by. It is defined once here and serves both.",
  "dimensions.add": "Add dimension",
  "dimensions.col.dimension": "Dimension",
  "dimensions.col.origin": "Origin",
  "dimensions.col.definition": "Definition",
  "dimensions.col.values": "Values",
  "dimensions.col.usedIn": "Used in",
  "dimensions.col.status": "Status",
  "dimensions.origin.column": "Column",
  "dimensions.origin.mapping": "Mapping",
  "dimensions.origin.computed": "Computed",
  "dimensions.def.mapping": "{match} → «{collection}» / {sheet}: {key} → {value}",
  "dimensions.def.noneHint":
    "This row carries no definition, so nothing can resolve it. It was not created from this screen.",
  "dimensions.status.ok": "Ready",
  "dimensions.status.needs_reload": "Pending reload",
  "dimensions.status.inert": "Enabled for nothing",
  "dimensions.status.schema_drift": "Schema mismatch",
  "dimensions.status.unresolvable": "Cannot be resolved",
  "dimensions.usableAs.filter": "Filter",
  "dimensions.usableAs.scope": "Scope",
  "dimensions.usableAs.none": "Not enabled for anything",
  "dimensions.usableAs.noneHint":
    "A dimension with no use enabled is invisible to filters and makes every scope that names it grant no access at all. Check «Scope» before you write a rule with it.",
  "dimensions.usedIn.scopes": "{n} scope(s)",
  "dimensions.usedIn.noneHint": "No access scope rules on this dimension yet.",
  "dimensions.values.n": "{n} distinct",
  "dimensions.values.view": "View values",
  "dimensions.values.unavailable": "Not in the data",
  "dimensions.values.unavailableHint":
    "The column this dimension resolves to is not in the live project file, so there is nothing to count. The Status column says why.",
  "dimensions.values.errorTitle": "The values could not be read",
  "dimensions.values.summary":
    "{shown} of {total} distinct values of «{column}» ({type}).",
  "dimensions.values.nulls":
    "{n} row(s) have no value. They belong to no restricted scope: an IN rule never matches an empty value.",
  "dimensions.values.notScopableTitle": "Not usable for access rules",
  "dimensions.values.notScopable":
    "These values are real and work as a filter, but a scope rule on a {type} column is refused when the scope file is built. For access, use a dimension of a text or numeric type.",
  "dimensions.values.rowCount": "{n} row(s)",
  "dimensions.values.nullValue": "(no value)",
  "dimensions.values.versionKey": "Read from version {key} of the project file.",
  "dimensions.detail.whyTitle": "Why this state",
  "dimensions.detail.notInStoredSchema":
    "The live file carries this column but the last reload did not record it in the project schema. The file is what decides, and nothing here reconciles the two.",
  "dimensions.actions.edit": "Edit",
  "dimensions.actions.delete": "Delete",
  "dimensions.delete.confirm":
    "Delete the dimension «{name}»? Any access scope ruling on it stops granting access to this project at the next reload.",
  "dimensions.error.title": "What the server said",
  "dimensions.empty.title": "No dimensions yet",
  "dimensions.empty.desc":
    "Nothing here can be filtered or restricted by anything yet. A dimension is one column: one that already exists, a value translated through a Master the client maintains, or one computed with SQL.",
  "dimensions.loaderUnavailable":
    "The last reload could not be checked, so a dimensions failure would not show up here: {message}",
  "dimensions.reloadFailed.title":
    "The last reload stopped at the dimension «{dimension}»",
  "dimensions.reloadFailed.titleGeneric":
    "The last reload stopped at the dimensions phase",
  "dimensions.reloadFailed.more": "See what the server said",
  "dimensions.reloadFailed.less": "Hide",
  "dimensions.reloadFailed.unmapped":
    "The Master named below does not cover every value in the data. Open Maestros for this project in the portal, add the missing rows to the sheet it names, and run the reload again. There is no «Other» bucket, on purpose, and meanwhile the previous data is still published.",
  "dimensions.reloadFailed.body":
    "Nothing was published and the previous data is still live. Fix the definition named below and run the reload again.",
  "dimensions.form.createTitle": "New dimension",
  "dimensions.form.editTitle": "Edit dimension",
  "dimensions.form.rejected": "The server refused this definition",
  "dimensions.form.keyLabel": "Key",
  "dimensions.form.keyHelp":
    "Lowercase letters, digits and underscores, starting with a letter. For a mapping or a computed dimension it becomes a real column of the data, and it is what access rules reference.",
  "dimensions.form.keyHelpEdit":
    "Renaming is allowed, but the server refuses it while an access scope that grants this project rules on the old key, and the refusal names those scopes.",
  "dimensions.form.keyInvalid":
    "The key must be lowercase letters, digits and underscores, and start with a letter.",
  "dimensions.form.labelLabel": "Name",
  "dimensions.form.labelHelp":
    "Both languages are required: a half-filled label renders as a raw key on one of the two screens.",
  "dimensions.form.labelIncomplete": "Fill the name in both languages.",
  "dimensions.form.originLabel": "Origin",
  "dimensions.form.originHelp":
    "A column already in the data, a value translated through a Master the client maintains, or one computed with SQL.",
  "dimensions.form.originHelpEdit":
    "The origin cannot be changed: it decides what the source column MEANS, while every existing access rule would keep pointing at the same key. Delete the dimension and create it again instead.",
  "dimensions.form.usableAsLabel": "Usable for",
  "dimensions.form.usableAsHelp":
    "What this dimension is offered for. Filters read it in the report editor; scopes rule on it to decide who sees which rows.",
  "dimensions.form.column.sourceLabel": "Source column",
  "dimensions.form.column.sourceHelp":
    "The list is what the last reload recorded. The server checks the LIVE file, so a column that is not on the list may still exist.",
  "dimensions.form.column.other": "Another column, not on the list…",
  "dimensions.form.column.otherLabel": "Column name",
  "dimensions.form.mapping.matchLabel": "Column to translate",
  "dimensions.form.mapping.matchHelp":
    "The raw column whose values are looked up in the Master sheet.",
  "dimensions.form.mapping.collectionLabel": "Master (slug)",
  "dimensions.form.mapping.collectionHelp":
    "The slug of a Master of THIS project. Another project's Master cannot be named from here.",
  "dimensions.form.mapping.sheetLabel": "Sheet (normalized name)",
  "dimensions.form.mapping.sheetHelp":
    "Lowercase, with spaces and hyphens turned into underscores.",
  "dimensions.form.mapping.keyColumnLabel": "Key column of the sheet",
  "dimensions.form.mapping.keyColumnHelp": "What the raw value is looked up BY.",
  "dimensions.form.mapping.valueColumnLabel": "Value column of the sheet",
  "dimensions.form.mapping.valueColumnHelp":
    "What the raw value BECOMES. It is written into the data as a new column named after the key above.",
  "dimensions.form.mapping.sameColumn":
    "The key and the value cannot be the same column: the mapping would be a column of copies.",
  "dimensions.form.mapping.noPickerTitle":
    "There is no picker for these three yet",
  "dimensions.form.mapping.noPicker":
    "Type the names as they appear in Maestros, in the portal. If one is wrong the server answers with the real list of Masters, sheets or columns it found, and that answer is shown here.",
  "dimensions.form.computed.exprLabel": "SQL expression",
  "dimensions.form.computed.exprHelp":
    "One expression over one row: no SELECT, no subquery, no window function and no aggregate. The server checks it against the real file before saving.",
  "dimensions.form.computed.unknownColumns":
    "These quoted columns are not in the recorded schema: {columns}",
  "dimensions.form.computed.caseMismatch":
    "«{used}» is written with different case than «{suggest}».",
  "projects.connections.title": "Connections",
  "projects.connections.intro":
    "Named upstreams this project can read from. A connection stores host / url and a POINTER to its credentials — never the credentials themselves.",
  "projects.connections.addBtn": "Add connection",
  "projects.connections.empty": "No connections yet.",
  "projects.connections.nameLabel": "Name",
  "projects.connections.kindLabel": "Kind",
  "projects.connections.configLabel": "Config (JSON, non-secret fields only)",
  "projects.connections.configInvalidJson": "Config is not valid JSON: {detail}",
  "projects.connections.secretRefLabel": "Secret reference",
  "projects.connections.secretRefHint":
    "env:NAME reads the BUILDER_SECRET_<TENANT>_NAME env var (a JSON object with all fields); there is no fallback to the unprefixed name. vault:tenants/<tenant>/connections/<name> reads the same JSON object from the secret store (write it with `python -m tenancy.secret_store set`). Leave empty only for sources that need no credentials.",
  "projects.connections.saveBtn": "Save",
  "projects.connections.cancelBtn": "Cancel",
  "projects.connections.editBtn": "Edit",
  "projects.connections.deleteBtn": "Delete",
  "projects.connections.testBtn": "Test",
  "projects.connections.testing": "Testing…",
  "projects.connections.testOk": "Connection OK",
  "projects.connections.testFailed": "Connection failed",
  "projects.connections.deleteConfirm":
    "Delete connection \"{name}\"? Any project data source pointing at it must be repointed first.",
  "projects.connections.noCredentialsNotice":
    "This form never accepts a password or token. Put the values in the environment variable named by the secret reference.",
  "projects.deleteConfirm":
    "Delete project \"{name}\"? Reports won't be deleted, just unlinked.",

  // Builder list — project grouping
  "list.group.noProject": "Ungrouped",
  "list.create.project": "Project",
  "list.create.project.none": "(none)",

  // Create draft modal — template picker
  "list.create.template": "Start from",
  "list.create.template.empty": "Empty (start from scratch)",

  // Builder editor
  // Date presets — the whole panel used to render its own dotted keys.
  // The code said `t("editor.preset.title") || "Presets"`, and i18next
  // returns the KEY for a miss, which is truthy, so the `||` arm never
  // ran and the admin read "editor.preset.title" on screen.
  "editor.preset.title": "Presets",
  "editor.preset.add": "Add preset",
  "editor.preset.empty":
    "No presets configured — add at least one for chips to appear in the portal.",
  "editor.preset.default": "default",
  "editor.preset.defaultHint": "Referenced as default_value",
  "editor.preset.resolves": "Resolves to:",
  "editor.preset.resolving": "Resolving…",
  "editor.preset.moveUp": "Move up",
  "editor.preset.moveDown": "Move down",
  "editor.preset.lengthDays": "Length (days)",
  "editor.preset.lengthMonths": "Length (months)",
  "editor.preset.kind.absolute_range": "Absolute range",
  "editor.preset.kind.relative_days": "Relative days",
  "editor.preset.kind.relative_months": "Relative months",
  "editor.preset.kind.hp_fiscal_q": "HP fiscal quarter",

  "editor.preview": "Preview",
  "editor.save": "Save",
  "editor.publish": "Publish",
  "editor.unpublish": "Unpublish",
  "editor.autosave": "Auto-save",
  "editor.dirty": "Unsaved",
  "editor.saved": "Saved",
  "editor.saveFailed": "Save failed",
  "editor.saveInProgress": "A save is already in progress. Wait for it to finish and try again.",
  "editor.unpublishFailed": "Unpublish failed",
  "editor.published": "Published",

  // Tabs
  "tab.dashboard": "Dashboard",
  "tab.data": "Data",
  "tab.ai": "AI",
  "tab.master": "Master Files",
  "tab.files": "Files",

  // Publish modal
  "publish.title": "Publish report",
  "publish.body":
    "Publishing makes the report visible to all hp-portal users on the next backend restart. The previous published version (if any) is replaced.",
  "publish.validating": "Validating against the DuckDB schema…",
  "publish.passed": "Validation passed — ready to publish.",
  "publish.success": "Published successfully. Closing…",
  "publish.warningsTitle": "warnings",
  "publish.errorsTitle": "Cannot publish — fix these issues first",
  "publish.confirm": "Publish",
  "publish.close": "Close",
  "publish.confirmAnyway": "Publish anyway",
  "views.checking": "Trying the saved AI Home views that read this report…",
  "views.checkFailed": "The saved AI Home views could not be tried.",
  "views.checkFailedBody": "The answer was: {error}. You can go ahead anyway, without knowing whether one breaks.",
  "views.none": "No saved AI Home view reads this report.",
  "views.ok_one": "The saved AI Home view that reads this report still works.",
  "views.ok_other": "The {count} saved AI Home views that read this report still work.",
  "views.brokenTitle_one": "1 saved AI Home view would stop working",
  "views.brokenTitle_other": "{count} saved AI Home views would stop working",
  "views.brokenBody_one": "We tried the view that reads this report with the new configuration, as its owner would open it. If you publish anyway, its owner sees the error when opening it.",
  "views.brokenBody_other": "We tried the {count} views that read this report with the new configuration, as each owner would open them. If you publish anyway, each owner sees the error when opening their view.",
  "views.brokenBodyLive_one": "We tried the view that reads this report with the new configuration, as its owner would open it. If you save anyway, its owner sees the error when opening it.",
  "views.brokenBodyLive_other": "We tried the {count} views that read this report with the new configuration, as each owner would open them. If you save anyway, each owner sees the error when opening their view.",
  "views.stillWork_one": "{count} more view still works",
  "views.stillWork_other": "{count} more views still work",
  "views.copy": "Copy the list",
  "views.copied": "Copied",
  "views.alreadyBroken_one": "1 view already failed before this change",
  "views.alreadyBroken_other": "{count} views already failed before this change",
  "views.colView": "Saved view",
  "views.colOwner": "Owner",
  "views.colFails": "What fails",
  "views.wholeView": "Does not open",
  "views.someElements_one": "{count} element",
  "views.someElements_other": "{count} elements",
  "views.saveTitle": "Save changes",
  "views.saveBody": "This report is published: saving puts the changes live for everyone.",
  "views.keepEditing": "Keep editing",
  "views.saveAnyway": "Save anyway",
  "views.publishGroupTitle": "Publish report group",
  "views.publishGroupBody": "Some reports of this group would break saved AI Home views when they go live.",
  "publish.unpublishConfirm":
    "Unpublish this report? It will disappear from hp-portal on the next backend restart.",

  // Table strings used by the DataTable / FilterBar copied from hp-portal.
  "table.title": "Data",
  "table.search": "Search…",
  "table.crossfilter.apply": "Filter report by this value",
  "filterbar.profile": "Profile",
  "filterbar.saveView": "Save view",
  "filterbar.soon": "soon",
  "filterbar.savedFilters": "Saved filters",
  "filterbar.skuList": "SKU list",
  "filterbar.skuListApplied": "SKU list · {n}",
  "filterbar.skuListCodes": "{n} codes",
  "filterbar.skuListUpload": "Upload a file…",
  "filterbar.skuListClear": "Remove the SKU list",
  "inspector.dashboard.filtersLayoutRows": "Rows",
  "table.action.filter": "Filter",
  "table.action.clear": "Clear filter",
  "table.total": "Total",
  "table.crossfilter.clear": "Clear this filter",
  "table.rowdrill.hint": "Click to expand the row detail",
  "table.rowdrill.open": "View detail",
  "table.rowdrill.close": "Close detail",
  "table.rowdrill.back": "Back",
  "table.rowdrill.detail": "Row detail",
  // Single-brace interpolation per the builder's i18next config.
  "table.rowdrill.invalidMode":
    "This table's row_drill_mode is '{value}' — it must be accordion or swap. Fix it in the builder.",
  "table.rowdrill.view.table": "Table",
  "table.rowdrill.view.chart": "Chart",
  "table.rowdrill.view.both": "Both",
  "table.rowdrill.view.label": "View",
  "table.export": "Export",
  "table.exportXlsx": "Export to XLSX",
  "table.exportCsvLatam": "Export to CSV (LatAm)",
  "table.exportCsvUs": "Export to CSV (US)",
  "table.exportFailed": "Export failed — no file was downloaded.",
  "table.exporting": "Exporting…",
  "table.exportXlsxAsCsv": "The table has more than 250,000 rows, so it was exported as CSV (LATAM) in a ZIP with one file per 250,000 rows instead of Excel.",
  "table.openLink": "View ↗",
  "table.fullscreen": "Fullscreen",
  "table.exitFullscreen": "Exit fullscreen",
  "table.chips.filter": "Quick filters",
  "table.chips.sort": "Sort by",
  "table.noData": "No data",
  "table.previous": "Previous",
  "table.next": "Next",
  // The shared ``DataTable`` component (byte-for-byte mirror with the
  // hp-portal) uses ``dashboard.table.*`` and ``common.*`` keys;
  // aliasing them here so the toolbar / empty-state / pagination
  // buttons don't render raw i18n keys on the preview.
  "dashboard.table.previous": "Previous",
  "dashboard.tabs.aria": "Report views",
  "dashboard.tabs.back": "Back to {tab}",
  "dashboard.glossary.open": "Glossary",
  "dashboard.glossary.title": "Glossary",
  "dashboard.glossary.close": "Close the glossary",
  "dashboard.scope.aria": "Active filters",
  "dashboard.scope.all": "All",
  "dashboard.scope.latest": "Latest day with data",
  "inspector.section.tab": "Tab",
  "inspector.section.tabNone": "(first tab)",
  "dashboard.longwait.label": "Still querying the data…",
  "chart.clickFilter": "Click to filter",
  "chart.shareBreakdown.of": "of",
  "dashboard.table.next": "Next",
  "dashboard.chart.calloutNoRow": "This block returned no row",
  "dashboard.chart.missingMetric": "The query returns no \"metric\" column (columns: {columns}). Alias the value as metric.",
  "dashboard.chart.aiGenerated": "AI",
  "dashboard.chart.aiGeneratedLong": "AI-generated",
  // Single-brace interpolation per the builder's i18next config.
  "dashboard.chart.unknownType": "Unknown chart type: {type}",
  "dashboard.chart.calendarDuplicateDay": "More than one row for the same day: {day}",
  "dashboard.kpi.previous": "Previous",
  "dashboard.error.charts": "Could not load the charts",
  "dashboard.error.texts": "Could not load the texts",
  "dashboard.error.drill": "Could not load the detail",
  "table.exportSheet": "Data",
  "dashboard.chart.bulletMissingTargetMax": "Every row of this chart needs a numeric target and a max greater than 0",
  "dashboard.chart.mapLoadError": "Couldn't load the map boundaries",
  "dashboard.chart.map.byCountry": "By country",
  "dashboard.chart.map.byRegion": "By region",
  "dashboard.chart.map.scale": "Scale",
  "dashboard.chart.map.low": "Low",
  "dashboard.chart.map.high": "High",
  "dashboard.chart.map.noData": "No data",
  "dashboard.chart.map.loading": "Loading map…",
  "dashboard.chart.calendar.less": "Less",
  "dashboard.chart.calendar.more": "More",
  "dashboard.kpi.anomalyExpected": "expected",
  "dashboard.chart.readoutLoading": "Generating the readout…",
  "dashboard.chart.readoutUnavailable": "The narrative readout is generated in the portal",
  "dashboard.portfolio.unavailable": "The saved-views portfolio is computed in the portal, for the signed-in user",
  "dashboard.portfolio.loading": "Loading your saved views…",
  "dashboard.portfolio.stale": "Showing the previous portfolio — refresh failed",
  "dashboard.portfolio.empty.title": "Save your first view",
  "dashboard.portfolio.empty.body": "Narrow the filters to a comparable group of products and save it. Each saved view becomes a card here, with the brand's position in it.",
  "dashboard.portfolio.empty.cta": "Open saved filters",
  "dashboard.portfolio.header.save": "Save current filters",
  "dashboard.portfolio.summary.winRate": "Win rate",
  "dashboard.portfolio.summary.winRateHint": "{lead} of {total} views led by the focus brand",
  "dashboard.portfolio.summary.behind": "Behind",
  "dashboard.portfolio.summary.behindHint": "{n} of {total} views",
  "dashboard.portfolio.summary.position": "Position",
  "dashboard.portfolio.summary.positionHint": "compared in {present} of {total} views",
  "dashboard.portfolio.summary.leaders": "Leadership across brands",
  "dashboard.portfolio.summary.leadersHint": "who leads each view",
  "dashboard.portfolio.summary.of": "of",
  "dashboard.portfolio.card.leader": "Leader: {brand}",
  "dashboard.portfolio.card.open": "Open this view →",
  "dashboard.portfolio.card.focus": "Focus",
  "dashboard.portfolio.card.noFilters": "Whole catalog",
  "dashboard.chart.readoutStale": "Showing the previous readout — refresh failed",
  // SWR per-block error surfacing (review #56 #2) — shared
  // BlockErrorCard / BlockStale (dashboard-render).
  "dashboard.blockError.title": "Couldn't load this data",
  "dashboard.blockError.retry": "Retry",
  "dashboard.blockError.stale": "Update failed — showing previous data",
  // Iter 31-C — the shared DataTable (dashboard-render) renders the
  // pagination footer via ``t("dashboard.table.showing", {from,to,total})``.
  // The portal i18n had this key; the builder didn't, so the footer
  // rendered the raw key "dashboard.table.showing". Single-brace
  // interpolation per the builder's i18next config (iter 23).
  "dashboard.table.showing": "Showing {from}-{to} of {total}",
  "common.export": "Export",
  "common.noData": "No data",
  "common.loadingData": "Loading data…",
  "table.showingOf": "Showing {from}-{to} of {total} {label}",
  "table.rowsLabel": "rows",
  "table.rows": "{count} rows",
  "table.count": "{count} products",
  "filterbar.title": "FILTERS",
  "filterbar.clearAll": "Clear all",
  "filterbar.pick": "Pick…",
  "filterbar.all": "All",
  "filterbar.locked": "locked",
  "filterbar.clear": "Clear",
  "filterbar.searchOptions": "Search…",
  "filterbar.noOptions": "No options.",
  "filterbar.nSelected": "{n} selected",
  "filterbar.relDays": "Last {length} days",
  "filterbar.relWeeks": "Last {length} weeks",
  "filterbar.relMonths": "Last {length} months",
  "filterbar.relYears": "Last {length} years",
  "filterbar.gte": "≥ {v}",
  "filterbar.lte": "≤ {v}",
  "filters.title": "Filters",
  "filters.removeChip": "Remove {label}",
  "filters.resetAll": "Clear all",
  "reportGroup.column": "Group",
  "reportGroup.backToProject": "Back to the project",
  "reportGroup.subtitle": "The reports of this group are the tabs of one page and share one set of filters.",
  "reportGroup.loadError": "The report group could not be loaded",
  "reportGroup.save": "Save",
  "reportGroup.saving": "Saving…",
  "reportGroup.saved": "Saved. Published reports of the group pick it up within a minute.",
  "reportGroup.publish": "Publish group",
  "reportGroup.publishing": "Publishing…",
  "reportGroup.published": "Every report of the group is published.",
  "reportGroup.publishPending": "Publish group · {count} pending",
  "reportGroup.pendingHint": "Every filter needs a decision for every report: uses it or not applicable.",
  "reportGroup.reports": "Reports",
  "reportGroup.reportsHint": "Their order is the order of the tabs.",
  "reportGroup.openReport": "Open {key} in the editor",
  "reportGroup.moveUp": "Move up",
  "reportGroup.moveDown": "Move down",
  "reportGroup.filters": "Group filters · {count}",
  "reportGroup.filtersHint": "In cascade order. Each report uses a filter at one of its columns, or it does not apply.",
  "reportGroup.filter": "Filter",
  "reportGroup.uses": "Uses column",
  "reportGroup.notApplicable": "Not applicable",
  "reportGroup.missing": "Missing",
  "reportGroup.cellAria": "Decision of {report} for the filter {filter}",
  "reportGroup.columnAria": "Column of {report} for the filter {filter}",
  "reportGroup.reasonAria": "Why {filter} does not apply to {report}",
  "reportGroup.reasonPlaceholder": "Reason (optional)",
  "reportGroup.legendUses": "the report filters by that column",
  "reportGroup.legendNotApplicable": "not applicable: an explicit decision, the value is kept for the other tabs",
  "reportGroup.legendMissing": "blocks the publish",
  "reportGroup.memberError": "This report is a tab of a report group and its filters are incomplete: {error}",
  "reportGroup.membersHint": "Every report of the project is a tab; a new report joins the group. To take reports out, show the project as a list:",
  "reportGroup.membersHintLink": "Project settings",
  "projectMode.title": "How the project shows its reports",
  "projectMode.description": "Tabs or a list in the sidebar, never both. A confirmed change reaches the portal within a minute.",
  "projectMode.tabs": "Tabs",
  "projectMode.tabsHint": "One page with every report as a tab and one set of filters for all of them.",
  "projectMode.list": "List in the sidebar",
  "projectMode.listHint": "Each report is a row of the portal sidebar, with its own filters.",
  "projectMode.current": "Current",
  "projectMode.openGroup": "Open the group",
  "projectMode.review": "Review the change",
  "projectMode.reviewing": "Reviewing…",
  "projectMode.reviewTitleTabs": "Switching to tabs:",
  "projectMode.reviewTitleList": "Switching to a list:",
  "projectMode.r.tabsReports": "the {count} reports become the tabs of group {group}, in the sidebar order",
  "projectMode.r.tabsFilters": "their filters move to the group: {count} ({filters})",
  "projectMode.r.notApplicable": "{report}: «not applicable» for {filters} (it did not have them)",
  "projectMode.r.savedViewsUp": "{count} saved view(s) move to the group",
  "projectMode.r.listReports": "each of the {count} reports gets back, as its own, the filters it uses",
  "projectMode.r.removedAction": "a button that opened another report is removed: {action}",
  "projectMode.r.savedViewsDown": "{count} saved view(s) go back to their report",
  "projectMode.r.trimmed": "filter values the report no longer has are dropped: {item}",
  "projectMode.r.lostAliases": "old links with ?tab= ({tabs}) will open the first report",
  "projectMode.reviewBlocked": "The change cannot be made:",
  "projectMode.confirm": "Confirm the change",
  "projectMode.applying": "Applying…",
  "projectMode.appliedTabs": "The project now shows its reports as tabs.",
  "projectMode.appliedList": "The project now shows its reports as a list in the sidebar.",
  "projectMode.loadError": "The mode of the project could not be loaded",
  "projectMode.mixedTitle": "This project mixes tabs and a list",
  "projectMode.mixedBody": "It has a report group next to reports outside it, from before the rule. Move reports to another project until each project is tabs or a list; then its mode can be chosen.",
  "projectMode.mixedGroups": "Groups",
  "projectMode.mixedLoose": "Reports outside a group",
  "filters.notApplicableIn": "not applicable in {report}",
  "filters.pinnedNotApplicable": "Not applicable in {report}: {filters}",
  "filters.phoneShow": "Show controls",
  "filters.phoneHide": "Hide controls",
  "filters.phoneChanged": "Filters changed: {count}",
  "filters.notApplicableLine": "Not applicable in {report}:",
  "filters.notApplicableKept": "Their values are kept for the other tabs.",
  "dashboard.group.orphanKeys": "These filters no longer exist and were not applied: {keys}",
  "filterbar.noMatches": "No matches.",
  "filterbar.searchPlaceholder": "Type to search…",
  "filterbar.narrowSearch": "Showing 200 of {count} — narrow your search.",
  "filterbar.typeToSearch": "Type {min}+ letters to search",
  "filterbar.dayPlaceholder": "yyyy-mm-dd",
  "filterbar.required": "Required",
  "filterbar.requiredHint":
    "Pick a value for the highlighted filter to load the dashboard.",

  // Date range picker — presets + custom modes
  // HP fiscal-quarter chip labels. Keep in sync with the picker's
  // PRESET_SPECS_BASE in packages/dashboard-render/src/DateRangePicker.tsx
  "daterange.preset.hp_all": "All",
  "daterange.preset.hp_q_actual": "Current Q",
  "daterange.preset.hp_last_q": "Last Q",
  "daterange.preset.hp_last_4_q": "Last 4 Q",
  "daterange.preset.hp_last_8_q": "Last 8 Q",
  // Cajón — per-report filter header layout
  "inspector.dashboard.filtersLayout": "Filters header",
  "inspector.dashboard.filtersLayoutBar": "Banner",
  "inspector.dashboard.filtersLayoutCajon": "Cajón",
  "inspector.dashboard.filtersLayoutBarHint":
    "Filters embedded in the header banner (the classic layout).",
  "inspector.dashboard.filtersLayoutCajonHint":
    "Chips row in the header that expands in place to edit the filters.",
  // Iter 26 — Advanced panel (runtime_view_sql)
  "inspector.dashboard.advanced": "Advanced",
  "inspector.dashboard.runtimeViewSql": "Runtime view SQL",
  "inspector.dashboard.runtimeViewSqlPlaceholder":
    "WITH _runtime_view AS (SELECT … FROM gating_fact_seller_weekly {where} …) SELECT * FROM _runtime_view",
  "inspector.dashboard.runtimeViewSqlHint":
    "SQL materialized as TEMP VIEW _runtime_view before KPIs/charts execute. Use {where} as the dashboard filter placeholder. Validate via report-builder skill before publishing.",
  "daterange.placeholder": "Pick a date range",
  "daterange.today": "Today",
  "daterange.yesterday": "Yesterday",
  "daterange.last_7_days": "Previous 7 days",
  "daterange.last_30_days": "Previous 30 days",
  "daterange.last_week": "Previous week",
  "daterange.last_month": "Previous month",
  "daterange.last_3_months": "Previous 3 months",
  "daterange.last_12_months": "Previous 12 months",
  "daterange.fixed": "Fixed date range…",
  "daterange.relative": "Relative date range…",
  "daterange.none": "(none)",
  "daterange.startDate": "Start date",
  "daterange.endDate": "End date",
  "daterange.apply": "Apply",
  "daterange.cancel": "Cancel",
  "daterange.back": "Back",
  "daterange.lastN": "Previous",
  "daterange.units.days": "days",
  "daterange.units.weeks": "weeks",
  "daterange.units.months": "months",
  "daterange.rangeSeparator": "→",
  "daterange.invalid": "Invalid range",
  // Dual-calendar popover strings (i18n audit 2026-07-14 — passed to
  // bi-components' DateRangePicker via its ``strings`` prop).
  "daterange.start": "Start",
  "daterange.end": "End",
  "daterange.pending": "pending",
  "daterange.clear": "Clear",
  "daterange.hintPickStart": "Click a day to pick the start date",
  "daterange.hintPickEnd": "Click a day to pick the end date",
  "daterange.goToStartMonth": "Go to the start date's month",
  "daterange.goToEndMonth": "Go to the end date's month",
  "daterange.startPending": "Start date pending",
  "daterange.endPending": "End date pending",

  // Date-range slider (type: "date_slider" filter)
  "dateslider.noData": "No date data available",
  "dateslider.from": "From",
  "dateslider.to": "To",
  // Numeric range filter (type: "range")
  "numrange.min": "Min",
  "numrange.max": "Max",
  "numrange.noData": "No values to set a range",
  "numrange.logScale": "log scale",
  "numrange.logUnusable": "A log scale needs values above 0",
  "numrange.histogramError": "Could not load the distribution",
  "numrange.remaining": "{n} of {total} left",
  "numrange.clear": "Remove threshold",

  // Common filter keys — these map filter.<key> to a display label.
  // Report configs can override per-filter with an explicit ``label``.
  "filter.country": "Country",
  "filter.marketplace": "Marketplace",
  "filter.seller": "Seller",
  "filter.account": "Account",
  "filter.category": "Category",
  "filter.seller_status": "Seller Status",
  "filter.in_listing": "In Listing",
  "filter.brand": "Brand",
  "filter.region": "Region",

  // Default placeholders shown by the preview
  "preview.defaultTableTitle": "Data",

  // ── KPI labels (slug → display) ──────────────────────────
  "kpi.total_annual_rev": "Total Annual Rev",
  "kpi.total_skus": "Total SKUs",
  "kpi.seller_status": "Seller Status",
  "kpi.avg_buy_box_rate": "Avg Buy Box Rate",
  "kpi.listed": "Listed %",
  "kpi.revenue_3m": "Revenue 3M",
  "kpi.1p_revenue_3m": "1P Revenue 3M",
  "kpi.3p_revenue_3m": "3P Revenue 3M",
  "kpi.week_start": "Week Start",
  "kpi.week_end": "Week End",

  // ── Chart titles (slug → display) ────────────────────────
  "chart.top_10_asins_by_annual_rev": "Top 10 ASINs by Annual Rev",
  "chart.revenue_1p_vs_3p": "Revenue 1P vs 3P",
  "chart.woh_vs_annual_rev_by_asin": "WOH vs Annual Rev by ASIN",
  "chart.avg_woh_by_category": "Avg WOH by Category",
  "chart.buy_box_rate_vs_1p_rev_by_asin": "Buy Box Rate vs 1P Rev % by ASIN",
  "chart.risk_score_by_asin": "Risk Score by ASIN",

  // ── Column labels (slug → display) ───────────────────────
  "column.sku": "SKU",
  "column.category": "Category",
  "column.listing_title": "Listing Title",
  "column.annual_rev": "Annual Rev",
  "column.rev_3m": "Rev 3M",
  "column.1p_rev_3m": "1P Rev 3M",
  "column.1p_rev": "1P Rev %",
  "column.3p_rev_3m": "3P Rev 3M",
  "column.3p_rev": "3P Rev %",
  "column.woh": "WOH",
  "column.1p": "1P",
  "column.mpa": "# MPA",
  "column.non_mpa": "# Non-MPA",
  "column.buy_box_rate": "Buy Box Rate",
  "column.asin": "ASIN",
  "column.risk_score": "Risk Score",
  "column.buy_box": "Buy Box %",

  // ── Section titles (slug → display) ──────────────────────
  "section.kpis_principales": "Key KPIs",
  "section.dashboard_explorer": "Dashboard Explorer",
  "section.diagnosticos_del_mercado": "Market Diagnostics",
  "sectionSubtitle.analisis_visual_de_cobertura_mix_1p_3p_y_riesgo_por_asin":
    "Visual analysis of coverage, 1P/3P mix, and risk per ASIN",

  // Preview modal
  "preview.title": "Preview · live data",
  "preview.close": "Close",
  "preview.filtersError": "Couldn't load the filter options: {err}",
  "preview.loadingCanvas": "Loading preview…",
  "preview.empty":
    "Add some sections in the editor to preview them here.",
  "preview.charts.title": "Diagnostics",
  "preview.charts.subtitle": "Visual analysis with the latest data.",
  "preview.tableSubtitle": "%{count} rows",

  // ── Editor chrome ────────────────────────────────────────
  // Inspector — report identity + hints
  "editor.inspector.reportIdentity": "Report identity",
  "editor.inspector.displayNameLabel": "Display name",
  "editor.inspector.displayNamePlaceholder": "My Report",
  "editor.inspector.duckdbTableLabel": "DuckDB table",
  "editor.inspector.duckdbTableHint": "Underlying table — drives column pickers in the editors.",
  "editor.inspector.duckdbTablePlaceholder": "my_table",
  "editor.inspector.descriptionLabel": "Description (optional)",
  "editor.inspector.descriptionPlaceholder": "What this report covers.",

  // Filter editor & list
  "editor.filters.globalTitle": "Global filters",
  "editor.filters.globalDesc": "Applied across every section unless overridden locally.",
  "editor.filters.sectionTitle": "Section filters",
  "editor.filters.sectionDesc": "Apply only to items inside this section. Merged with global filters at runtime.",
  "editor.filters.addBtn": "Add filter",
  "editor.filters.emptyMsg": "No filters yet.",
  "editor.filters.duplicateKeys": "Duplicate filter keys: {keys}. The engine will drop all but the first.",
  "editor.filter.unnamed": "(unnamed)",
  "editor.filter.duplicateBtn": "Duplicate filter",
  "editor.filter.removeBtn": "Remove filter",
  "editor.filter.keyLabel": "Key",
  "editor.filter.keyPlaceholder": "country",
  "editor.filter.keyValidation": "lowercase letters / digits / underscores only",
  "inspector.filter.selectionMode": "Selection",
  "inspector.filter.required": "Required selection",
  "inspector.filter.extraOptions": "Additional options (comma separated)",
  "inspector.filter.extraOptionsHint": "Keep supported values selectable even without data. This adds choices, never records or metrics.",
  "inspector.filter.dependencies": "Depends on",
  "inspector.filter.legacyCascade": "All other filters (legacy)",
  "inspector.filter.rootCascade": "None — first selection",
  "inspector.filter.dependenciesHint": "Only these filters narrow its options. Changing an upstream filter clears its descendants.",
  "inspector.filter.tabs": "Shown on tabs",
  "inspector.filter.tabsHint": "Uncheck a tab to hide this control there. The filter still applies to the data; only the tabs whose elements read it need to show it.",
  "inspector.sql.edit": "Edit SQL",
  "inspector.sql.apply": "Apply to preview",
  "inspector.sql.editHint": "Review the preview before saving. Saving a published report changes the live report.",
  "inspector.filter.checkboxDropdown": "Dropdown with checkboxes (multiple)",
  "inspector.filter.multipleDefault": "The default contains multiple values. Keep multiple selection or edit the default first.",
  "inspector.filter.fixedOptions": "{count} fixed options; the list does not follow the other filters.",
  "inspector.filter.useLiveOptions": "Use data values and cascade with other filters",
  "inspector.filter.liveOptions": "Options come from the data and follow the other filters.",
  "inspector.dashboard.snapshot": "View saved report snapshot",
  "inspector.dashboard.downloadSnapshot": "Download snapshot (JSON)",
  "editor.filter.typeLabel": "Type",
  "editor.filter.typeSelect": "Select",
  "editor.filter.typeAutocomplete": "Autocomplete",
  "editor.filter.typeMultiSelect": "Multi-select",
  "editor.filter.typeChip": "Chips (multi)",
  "editor.filter.typeRange": "Range slider",
  "editor.filter.typeSearch": "Search (text contains)",
  "editor.filter.typeDateRange": "Date range",
  "editor.filter.typeDateCalendar": "Single date",
  "editor.filter.typeDateRelative": "Relative period",
  "editor.filter.defaultDateHint": "Applied on first load (ISO date).",
  "editor.filter.jsonNoDefaultHint": "JSON-shaped filter — leave defaults blank; the runtime widget owns the input.",
  "editor.filter.columnLabel": "Column",
  "editor.filter.columnHint": "Pick from the report's table.",
  "editor.filter.columnHintTemporal": "Only DATE / TIMESTAMP columns are shown.",
  "editor.filter.defaultLabel": "Default value",
  "editor.filter.defaultNone": "(none)",
  "editor.filter.defaultPresetHint":
    "Applied on first load; the user can change it from the filter bar.",
  "editor.filter.lockedLabel": "Locked value",
  "editor.filter.lockedHint": "Fixed; the user can't change it.",
  "editor.filter.lockedNone": "(unlocked)",
  "editor.filter.requiredLabel": "Required (auto-pick a value on load)",

  // Diagram canvas
  "editor.diagram.addSection": "Add section",
  "editor.diagram.emptyTitle": "Empty dashboard",
  "editor.diagram.emptyHint": "Add your first section to start composing the report.",
  "editor.diagram.emptyHintReadOnly": "Sections and items are managed via Claude with the report-builder skill.",
  "editor.diagram.sectionFallback": "Section",
  "editor.diagram.itemsLabel": "items",
  "editor.diagram.readOnlyTitle": "Read-only.",
  "editor.diagram.readOnlyBody": "Structural changes (add/remove/reorder sections and items, item width) flow through Claude with the report-builder skill. Click an item to edit its content in the side panel.",
  "editor.diagram.readOnlyNote": "Dashboard canvas is read-only; edits go through the report-builder skill.",

  // Section block on canvas
  "editor.section.dragToReorder": "Drag to reorder",
  "editor.section.duplicateBtn": "Duplicate section",
  "editor.section.deleteBtn": "Delete section",
  "editor.section.renameBtn": "Rename section",
  "editor.section.addBtn": "Add",
  "editor.section.emptyHint": "Empty section. Add your first item:",
  "editor.section.emptyHintReadOnly": "Empty section. Add items via Claude with the report-builder skill.",
  "editor.section.deleteConfirm": "Delete section \"{title}\" and all its items?",
  "editor.section.itemCountSingular": "item",
  "editor.section.itemCountPlural": "items",

  // Element block on canvas
  "editor.element.dragToReorder": "Drag to reorder",
  "editor.element.duplicateBtn": "Duplicate",
  "editor.element.deleteBtn": "Delete",
  "editor.kpi.untitled": "Untitled KPI",
  "editor.chart.untitled": "Untitled chart",
  "editor.chart.typeSubtitle": "type: {type}",
  "editor.table.label": "Data table",
  "editor.table.columnSingular": "column",
  "editor.table.columnPlural": "columns",
  "editor.text.label": "Text block",

  // Section editor panel
  "editor.section.editingTitle": "Editing {element}",
  "editor.section.settingsTitle": "Section settings",
  "editor.section.dashboardSettingsTitle": "Dashboard settings",
  "editor.section.itemHint": "Per-element configuration.",
  "editor.section.sectionHint": "Section-level settings and local filters.",
  "editor.section.dashboardHint": "Pick a section or item on the canvas, or edit the dashboard-wide config below.",
  "editor.section.titleLabel": "Title",
  "editor.section.subtitleLabel": "Subtitle (optional)",
  "editor.section.itemsLabel": "Items in this section ({count})",
  "editor.section.noItemsHint": "No items yet — use the section header's \"Add\" menu.",
  "editor.item.widthLabel": "Width (grid columns)",
  "editor.item.rawPropsLabel": "Raw props (JSON)",
  "editor.item.duplicateBtn": "Duplicate",
  "editor.item.addAnotherBtn": "Add another",
  "editor.item.deleteBtn": "Delete",

  // KPI editor
  "editor.kpi.labelLabel": "Label",
  "editor.kpi.labelPlaceholder": "Total Annual Revenue",
  "editor.kpi.sqlLabel": "SQL expression",
  "editor.kpi.sqlHint": "Aggregate over the report's DuckDB table — wraps in SELECT … FROM table at run time.",
  "editor.kpi.sqlPlaceholder": "SUM(\"Annual Rev\")",
  "editor.kpi.formatLabel": "Format",
  "editor.kpi.accentColorLabel": "Accent color",
  "editor.kpi.accentColorHint": "Border swatch on the KPI card. Auto rotates by item position.",
  "editor.kpi.ignoreFiltersLabel": "Ignore filters (optional)",
  "editor.kpi.ignoreFiltersHint": "Filter keys this KPI should NOT respect — e.g. \"in_listing\" so a coverage KPI ignores user filtering.",
  "editor.kpi.ignoreFiltersPlaceholder": "Type a filter key…",
  "editor.kpi.ignoreFiltersEmptyPlaceholder": "Add filters first to suggest keys here…",
  // Phase A — KPI variants
  "editor.kpi.variantLabel": "Variant",
  "editor.kpi.variantHint": "Visual style for this KPI. Variants marked SQL need an extra query (sparkline / previous-period).",
  "editor.kpi.variantNeedsSqlHint": "Needs extra SQL configured below",
  "editor.kpi.variantCard": "Card",
  "editor.kpi.variantCardDesc": "Basic card with left-border accent (default)",
  "editor.kpi.variantSparkline": "Sparkline",
  "editor.kpi.variantSparklineDesc": "Value + small trend line + delta pill",
  "editor.kpi.variantRing": "Ring",
  "editor.kpi.variantRingDesc": "Value rendered inside a circular progress ring",
  "editor.kpi.variantBar": "Bar",
  "editor.kpi.variantBarDesc": "Value over a horizontal progress bar",
  "editor.kpi.variantHero": "Hero",
  "editor.kpi.variantHeroDesc": "Big card with brand gradient + optional icon",
  "editor.kpi.variantCompare": "Compare",
  "editor.kpi.variantCompareDesc": "Current vs previous period, auto delta",
  "editor.kpi.variantCompact": "Compact",
  "editor.kpi.variantCompactDesc": "Single-row layout for dense KPI lists",
  "editor.kpi.variantIconStat": "Icon stat",
  "editor.kpi.variantIconStatDesc": "Value with an icon in a tinted box",
  // Phase H — anomaly + score
  "editor.kpi.variantAnomaly": "Anomaly",
  "editor.kpi.variantAnomalyDesc": "Value with severity tone, expected baseline + description",
  "editor.kpi.variantScore": "Score",
  "editor.kpi.variantScoreDesc": "Score 0..max ring with optional breakdown bars",
  "editor.kpi.variantCore": "Core (DSA strip)",
  "editor.kpi.variantCoreDesc": "Signature-colour card: CORE badge, delta in pp vs previous_sql, subtitle and action band",
  "editor.kpi.severityLabel": "Severity",
  "editor.kpi.severityHint": "Drives the colour ramp (red / amber / purple).",
  "editor.kpi.severityCritical": "Critical (red)",
  "editor.kpi.severityWarning": "Warning (amber)",
  "editor.kpi.severityInfo": "Info (purple)",
  "editor.kpi.expectedSqlLabel": "Expected SQL",
  "editor.kpi.expectedSqlHint": "Optional aggregate for the baseline. Same scope as the main KPI; deviation % is computed automatically.",
  "editor.kpi.expectedSqlPlaceholder": "AVG(metric) OVER ()",
  "editor.kpi.descriptionLabel": "Description",
  "editor.kpi.descriptionHint": "Free-text blurb shown below the value. Keep it short.",
  "editor.kpi.descriptionPlaceholder": "Listing volume +320% vs trailing 30-day average.",
  "editor.kpi.scoreMaxHint": "Maximum score on the ring (defaults to 100).",
  "editor.kpi.breakdownSqlLabel": "Breakdown SQL",
  "editor.kpi.breakdownSqlHint": "SELECT returning rows shaped (label, value). Each row becomes a 0–100% breakdown bar.",
  "editor.kpi.breakdownSqlPlaceholder": "SELECT axis AS label, score AS value FROM scores GROUP BY axis",
  "editor.kpi.maxLabel": "Max value",
  "editor.kpi.maxHint": "Used by ring + bar variants to scale the progress display.",
  "editor.kpi.gradientLabel": "Gradient",
  "editor.kpi.gradientHint": "Background gradient for the hero variant.",
  "editor.kpi.gradientPrimary": "Primary (purple)",
  "editor.kpi.gradientAccent": "Accent (pink)",
  "editor.kpi.gradientPrimaryToAccent": "Primary → accent",
  "editor.kpi.gradientSuccess": "Success (green)",
  "editor.kpi.gradientWarning": "Warning (amber)",
  "editor.kpi.iconLabel": "Icon",
  "editor.kpi.iconHint": "Lucide icon shown next to the value.",
  "editor.kpi.iconNone": "None",
  "editor.kpi.iconAlert": "Alert",
  "editor.kpi.iconTrending": "Trending",
  "editor.kpi.iconDollar": "Dollar",
  "editor.kpi.iconUsers": "Users",
  "editor.kpi.iconPackage": "Package",
  "editor.kpi.iconCheck": "Check",
  "editor.kpi.iconZap": "Zap",
  "editor.kpi.iconTarget": "Target",
  "editor.kpi.iconBgLabel": "Icon background",
  "editor.kpi.iconBgHint": "Tailwind color word — e.g. primary tints the icon box morado.",
  "editor.kpi.iconBgPrimary": "Primary",
  "editor.kpi.iconBgAccent": "Accent",
  "editor.kpi.iconBgEmerald": "Emerald",
  "editor.kpi.iconBgAmber": "Amber",
  "editor.kpi.iconBgRose": "Rose",
  "editor.kpi.sparklineSqlLabel": "Sparkline SQL",
  "editor.kpi.sparklineSqlHint": "Returns an array of numbers (e.g. LIST(metric ORDER BY month)). Backend executes alongside the main KPI query.",
  "editor.kpi.sparklineSqlPlaceholder": "LIST(metric ORDER BY month)",
  "editor.kpi.previousSqlLabel": "Previous-period SQL",
  "editor.kpi.previousSqlHint": "Aggregate for the comparison baseline (e.g. SUM(metric) WHERE date < CURRENT_DATE - INTERVAL '1 month').",
  "editor.kpi.previousSqlPlaceholder": "SUM(metric) WHERE date < CURRENT_DATE - INTERVAL '1 month'",

  // Table editor
  "editor.table.defaultSortLabel": "Default sort column",
  "editor.table.defaultSortHint": "Quote when needed.",
  "editor.table.directionLabel": "Sort direction",
  "editor.table.descending": "Descending",
  "editor.table.ascending": "Ascending",
  "editor.table.pageSizeLabel": "Page size",
  "editor.table.densityLabel": "Row density",
  "editor.table.densityHint": "Controls vertical padding of the table rows in the preview.",
  "editor.table.densityAriaLabel": "Row density",
  "editor.table.densityCompact": "compact",
  "editor.table.densityNormal": "normal",
  "editor.table.densityComfortable": "comfortable",
  "editor.table.columnsLabel": "Columns ({count})",
  "editor.table.addColumnBtn": "Add column",
  "editor.table.noColumnsHint": "No columns yet. Add one to populate the table.",
  "editor.table.emptyLabel": "(empty)",
  "editor.table.columnLabel": "Column",
  "editor.table.labelLabel": "Label",
  "editor.table.labelPlaceholder": "Display name",
  "editor.table.formatLabel": "Format",
  "editor.table.widthLabel": "Width / truncate (optional)",
  "inspector.table.maxWidth": "Maximum column width",
  "inspector.table.autoWidth": "Automatic (full content)",
  "inspector.table.maxWidthHint": "Long text is shortened visually; hover to read it in full. Exports keep the full value.",
  "editor.table.widthPlaceholder": "auto · max-w-[200px] truncate",
  // Phase B — column-level cell renders
  "editor.table.renderLabel": "Cell render (optional)",
  "editor.table.renderHint": "How each cell in this column displays. Default uses the format above; richer variants override the cell visual.",
  "editor.table.renderDefault": "Default (text / number)",
  "editor.table.renderHeat": "Heat — tinted by min/max",
  "editor.table.renderSparkline": "Sparkline — array of numbers",
  "editor.table.renderStatus": "Status — colored badge",
  "editor.table.renderProgress": "Progress — 0–100 bar",
  "editor.table.renderCompare": "Compare — delta arrow",

  // Chart editor
  "editor.chart.titleLabel": "Title",
  "editor.chart.titlePlaceholder": "Top 10 ASINs by Revenue",
  "editor.chart.typeLabel": "Chart type",
  "editor.chart.dimensionLabel": "Dimension",
  "editor.chart.dimensionHint": "GROUP BY column.",
  "editor.chart.groupingHint": "Grouping column shared by every metric.",
  "editor.chart.metricLabel": "Metric",
  "editor.chart.metricHint": "Aggregate expression.",
  "editor.chart.metricPlaceholder": "SUM(\"Annual Rev\")",
  "editor.chart.xAxisLabel": "X axis",
  "editor.chart.yAxisLabel": "Y axis",
  "editor.chart.bubbleSizeLabel": "Bubble size",
  "editor.chart.labelColLabel": "Label column (optional)",
  "editor.chart.labelColHint": "Carried alongside x/y to label each point on hover.",
  "editor.chart.metricsLabel": "Metrics ({count})",
  "editor.chart.addMetricBtn": "Add metric",
  "editor.chart.groupedBarHint": "Add at least two metrics for a grouped bar chart.",
  "editor.chart.seriesLabel": "Series {idx}",
  "editor.chart.seriesLabelPlaceholder": "Series label",
  "editor.chart.metricsPlaceholder": "SUM(\"Rev 1P\")",
  "editor.chart.removeBtn": "Remove",
  "editor.chart.stackedLabel": "Stacked",
  "editor.chart.stackedHint": "Stack series instead of grouping side-by-side",
  "editor.chart.columnsLabel": "Columns ({count})",
  "editor.chart.addBtn": "Add",
  "editor.chart.noColumnsHint": "No columns yet — add at least one.",
  "editor.chart.columnLabel": "Column {idx}",
  "editor.chart.keyPlaceholder": "key (alias)",
  "editor.chart.labelPlaceholder": "Display label",
  "editor.chart.badgePlaceholder": "badge (e.g. \"risk_score\")",
  "editor.chart.sortLabel": "Sort expression",
  "editor.chart.sortHint": "ORDER BY for the mini-table.",
  "editor.chart.sortPlaceholder": "SUM(\"Rev\")",
  "editor.chart.moreOptions": "More chart options",
  "editor.chart.sharedSortLabel": "Sort",
  "editor.chart.sortDefault": "Default",
  "editor.chart.sortAsc": "Ascending",
  "editor.chart.sortDesc": "Descending",
  "editor.chart.limitLabel": "Limit (top N)",
  "editor.chart.limitNone": "(none)",
  "editor.chart.xFormatLabel": "X format hint",
  "editor.chart.yFormatLabel": "Y format hint",

  // Text editor
  "editor.text.contentLabel": "Markdown content",
  "editor.text.contentHint": "Renders inline on the dashboard.",
  "editor.text.contentPlaceholder": "## Section note\n\nWrite your context here.",

  // Chart type selector
  "editor.chart.typeBar": "Bar",
  "editor.chart.barDesc": "One series, vertical bars",
  "editor.chart.typeBarHorizontal": "Bar (h)",
  "editor.chart.barHDesc": "Horizontal bars",
  "editor.chart.typeLine": "Line",
  "editor.chart.lineDesc": "Trend over a dimension",
  "editor.chart.typePie": "Pie",
  "editor.chart.pieDesc": "Share of a whole",
  "editor.chart.typeGrouped": "Grouped",
  "editor.chart.groupedDesc": "Multiple metrics side by side",
  "editor.chart.typeScatter": "Scatter",
  "editor.chart.scatterDesc": "X / Y points",
  "editor.chart.typeBubble": "Bubble",
  "editor.chart.bubbleDesc": "X / Y / size points",
  "editor.chart.typeTable": "Table",
  "editor.chart.tableDesc": "Compact mini-table",
  // Phase 3 — advanced chart types
  "editor.chart.advancedGroup": "Advanced",
  "editor.chart.typeStackedBar": "Stacked bar",
  "editor.chart.stackedBarDesc": "Multiple metrics stacked into one bar",
  "editor.chart.typeStackedArea": "Stacked area",
  "editor.chart.stackedAreaDesc": "Time series areas stacked over time",
  "editor.chart.typeMultiLine": "Multi-line",
  "editor.chart.multiLineDesc": "Several lines on the same axis",
  "editor.chart.typeCombo": "Combo",
  "editor.chart.comboDesc": "Bar + line on dual Y axes",
  "editor.chart.typeRadar": "Radar",
  "editor.chart.radarDesc": "Compare metrics across categorical axes",
  "editor.chart.typeFunnel": "Funnel",
  "editor.chart.funnelDesc": "Stages of a conversion process",
  "editor.chart.typeTreemap": "Treemap",
  "editor.chart.treemapDesc": "Hierarchical share by area",
  "editor.chart.typeWaterfall": "Waterfall",
  "editor.chart.waterfallDesc": "Running total with positive/negative steps",
  "editor.chart.typeLollipop": "Lollipop",
  "editor.chart.lollipopDesc": "Bar variant emphasizing the value point",
  "editor.chart.typeBarTrend": "Bar + trend",
  "editor.chart.barTrendDesc": "Bars with a fitted regression line",
  "editor.chart.typeLineTrend": "Line + forecast",
  "editor.chart.lineTrendDesc": "Historical line plus projection",
  // Phase D — bespoke editor types
  "editor.chart.typeHeatmap": "Heatmap",
  "editor.chart.heatmapDesc": "Two-dim grid tinted by aggregated value",
  "editor.chart.typeBullet": "Bullet",
  "editor.chart.bulletDesc": "Per-row value vs target on a max scale",
  "editor.chart.heatmapRowLabel": "Row column",
  "editor.chart.heatmapRowHint": "Categorical column for the Y axis (rows of the matrix).",
  "editor.chart.heatmapColLabel": "Column column",
  "editor.chart.heatmapColHint": "Categorical column for the X axis (columns of the matrix).",
  "editor.chart.heatmapValueLabel": "Value (aggregate)",
  "editor.chart.heatmapValueHint": "Aggregate expression — runs as SUM/AVG/COUNT over each (row, col) cell.",
  "editor.chart.heatmapValuePlaceholder": "AVG(metric)",
  "editor.chart.bulletLabelLabel": "Label column",
  "editor.chart.bulletValueLabel": "Value (aggregate)",
  "editor.chart.bulletValuePlaceholder": "AVG(metric)",
  "editor.chart.bulletTargetLabel": "Target",
  "editor.chart.bulletTargetHint": "Constant or expression — e.g. 85 or AVG(target_col).",
  "editor.chart.bulletMaxLabel": "Max scale",
  "editor.chart.bulletMaxHint": "Constant or expression — defaults to 100 if blank.",
  // Phase E + F — geo + temporal-density
  "editor.chart.typeMap": "Map",
  "editor.chart.mapDesc": "Geo choropleth shaded by metric per region",
  "editor.chart.typeCalendarHeatmap": "Calendar",
  "editor.chart.calendarHeatmapDesc": "Year-long density grid by day",
  "editor.chart.mapVariantLabel": "Region",
  "editor.chart.mapVariantHint": "South America uses ISO country codes (CL, AR, BR…). Chile uses region codes (RM, V, VIII…).",
  "editor.chart.mapSouthAmerica": "South America",
  "editor.chart.mapChile": "Chile (regions)",
  "editor.chart.calendarYearLabel": "Year (optional)",
  "editor.chart.calendarYearHint": "Leave blank to infer from data. Otherwise YYYY.",
  // Phase G — timeline / gantt
  "editor.chart.typeTimeline": "Timeline",
  "editor.chart.timelineDesc": "Gantt-style phases with start, end and optional progress",
  "editor.chart.timelineLabelLabel": "Label column",
  "editor.chart.timelineLabelHint": "Column with the row label (e.g. project / phase / SKU).",
  "editor.chart.timelineStartLabel": "Start (index)",
  "editor.chart.timelineStartHint": "Numeric index into the months track (0 = first cell).",
  "editor.chart.timelineEndLabel": "End (index)",
  "editor.chart.timelineEndHint": "Numeric end index — must be > start.",
  "editor.chart.timelineProgressLabel": "Progress (0–1, optional)",
  "editor.chart.timelineProgressHint": "Fractional value 0..1 for the progress overlay. Blank = no overlay.",
  "editor.chart.timelineMonthsLabel": "Months / phase labels",
  "editor.chart.timelineMonthsHint": "Comma-separated labels for the X track. Defaults to Jan…Dec.",

  // Accent color picker
  "editor.color.accentLabel": "Accent color",
  "editor.color.autoTooltip": "Auto rotation by item index",
  "editor.color.auto": "Auto",
  "editor.color.red": "Red",
  "editor.color.blue": "Blue",
  "editor.color.emerald": "Emerald",
  "editor.color.purple": "Purple",
  "editor.color.amber": "Amber",
  "editor.color.cyan": "Cyan",
  "editor.color.pink": "Pink",
  "editor.color.indigo": "Indigo",

  // Column picker
  "editor.column.placeholder": "Pick a column…",
  "editor.column.loading": "Loading columns…",
  "editor.column.tableNotFound": "Table not found in DuckDB",
  "editor.column.noColumns": "No columns available — set the report's DuckDB table first.",

  // Format selector
  "editor.format.number": "Number",
  "editor.format.numberExample": "1,234",
  "editor.format.currency": "Currency",
  "editor.format.currencyExample": "$1,234",
  "editor.format.percent": "Percent",
  "editor.format.percentExample": "12.3%",
  "editor.format.text": "Text",
  "editor.format.textExample": "as-is",
  "editor.format.label": "Format",

  // SQL expression input
  "editor.sql.unknownColumnSingular": "Unknown column",
  "editor.sql.unknownColumnPlural": "Unknown columns",
  "editor.sql.columnNotInTable": "— not present in the report's DuckDB table.",
  "editor.sql.caseMismatch": "differs in case from",
  "editor.sql.fixButton": "Fix",
  "editor.sql.aggregateLabel": "Aggregate",
  "editor.sql.insertColumn": "Insert column",
  "editor.sql.moreColumns": "+{count} more",
  "editor.sql.noSemicolons": "Semicolons are not allowed.",
  "editor.sql.forbiddenKeyword": "Forbidden keyword: {keyword}",
  "editor.sql.valid": "Looks valid.",

  // AI tab — top tab strip (General AI / Chat AI / Insights AI)
  "editor.ai.tabs.ariaLabel": "AI configuration tabs",
  "editor.ai.tabs.general": "General AI",
  "editor.ai.tabs.chat": "Chat AI",
  "editor.translatePt.button": "Translate to PT",
  "editor.translatePt.busy": "Translating…",
  "editor.translatePt.error": "Could not translate: {message}",
  "editor.ai.tabs.insights": "Insights AI",
  // AI tab — Insights AI → Executions table (read-only snapshot history)
  "editor.ai.executions.title": "Executions",
  "editor.ai.executions.description":
    "Past insight generations for this report (the weekly snapshots the portal persists). Read-only.",
  "editor.ai.executions.loading": "Loading executions…",
  "editor.ai.executions.error": "Could not load executions",
  "editor.ai.executions.empty": "No insight executions yet for this report.",
  "editor.ai.executions.col.period": "Period",
  "editor.ai.executions.col.model": "Model",
  "editor.ai.executions.col.duration": "Duration",
  "editor.ai.executions.col.insights": "Insights",
  "editor.ai.executions.col.regenerated": "Regenerated",
  "editor.ai.executions.col.created": "Created",
  // AI tab — Insights section
  "editor.ai.insights.modelTitle": "Narrative model",
  "editor.ai.insights.modelDescription": "OpenAI model used to generate the AI Analysis narrative (Insights panel). Pick a per-report override when this report needs a different model than the portal default.",
  "editor.ai.insights.specsTitle": "Configured insights",
  "editor.ai.insights.specsDescription": "Define WHAT each insight should answer. Each spec produces one entry in the snapshot. Leave empty to let the LLM free-form 3 insights (legacy behaviour).",
  "editor.ai.insights.specsEmpty": "No insights configured. Click Add to define one — the LLM will still free-form 3 insights as a fallback.",
  "editor.ai.insights.addSpec": "Add insight",
  "editor.ai.insights.removeSpec": "Remove",
  "editor.ai.insights.spec.idLabel": "Spec ID",
  "editor.ai.insights.spec.idHint": "Stable identifier used to match insights across runs. Lowercase + underscores. Auto-suggests from the title.",
  "editor.ai.insights.spec.idPlaceholder": "yoy_revenue",
  "editor.ai.insights.spec.titleLabel": "Insight title (bilingual)",
  "editor.ai.insights.spec.titleHint": "Rendered verbatim as the insight card header in the portal. The LLM does NOT invent this — the spec wins.",
  "editor.ai.insights.spec.questionLabel": "What should the LLM look for (bilingual)",
  "editor.ai.insights.spec.questionHint": "Plain-language instruction. e.g. \"How did the NO seller count change vs 52 weeks ago?\" The LLM answers this using the data context.",
  "editor.ai.insights.spec.dataFocusLabel": "Data focus (optional)",
  "editor.ai.insights.spec.dataFocusHint": "KPI keys or chart titles the LLM should consider for this insight. Empty = the LLM uses all available data.",
  "editor.ai.insights.spec.dataFocusPlaceholder": "Type a KPI key or chart title and press Enter…",
  "editor.ai.insights.spec.priorityLabel": "Priority",
  "editor.ai.insights.spec.priorityHint": "Lower runs first. Leave blank to use insertion order.",
  "editor.ai.insights.spec.enabledLabel": "Active",
  // AI tab
  "editor.ai.titleLabel": "AI Setup",
  "editor.ai.identityDescription":
    "Global settings for the AI on this report. Toggle ``Enabled`` to control whether the ``+ Show AI Analysis`` button and the ``Chat AI`` sidebar appear in the portal. ``Focus areas`` are topics the LLM emphasizes in every answer about this report.",
  "editor.ai.enabledLabel": "Enable chat tooling and AI analysis for this report",
  "editor.ai.modelLabel": "Model",
  "editor.ai.modelCatalogError": "Couldn't load the model list: {err}",
  "editor.ai.focusAreasLabel": "Focus areas",
  "editor.ai.focusAreasHint": "Surface the topics the AI should prioritize when summarizing this report.",
  "editor.ai.focusAreasPlaceholder": "Type a focus area and press Enter…",
  "editor.ai.chatModel.title": "Chat model",
  "editor.ai.chatModel.description": "OpenAI model used by the chat sidebar (InsightAgent tool loop). Choose a per-report model only when the report needs a different one than the customer's.",
  "editor.ai.chatModel.label": "Model",
  "editor.ai.chatModel.hint": "Default = the customer's chat model, set in the platform console (AI models).",
  "editor.ai.model.useDefault": "Default",
  "editor.ai.model.useDefaultNamed": "Default ({model})",
  "editor.ai.model.outsideCatalog": "{model} (not in this customer's catalog)",
  "editor.ai.starterQuestions.title": "Starter questions",
  "editor.ai.starterQuestions.description": "Curated questions shown in the chat sidebar's empty state. Aim for 3-5 that demonstrate the report's most useful angles — mix data lookups with one actionable suggestion if it makes sense. Leave empty to fall back to the generic defaults.",
  "editor.ai.starterQuestions.empty": "No starter questions. Using the generic frontend defaults.",
  "editor.ai.starterQuestions.placeholder": "How many NO sellers do we have this week?",
  "editor.ai.starterQuestions.add": "Add starter question",
  "editor.ai.starterQuestions.remove": "Remove",
  "editor.ai.ctaCatalog.title": "Actionable CTAs",
  "editor.ai.ctaCatalog.description": "Templates surfaced as follow-up CTAs in the chat sidebar. Use placeholders like {seller_name}, {asin}, {sku}, {client} — the portal interpolates them from the last turn's data.",
  "editor.ai.ctaCatalog.empty": "No CTAs configured. Add one to start surfacing actionable suggestions.",
  "editor.ai.ctaCatalog.labelTemplate": "CTA label (template)",
  "editor.ai.ctaCatalog.labelTemplatePlaceholder": "Draftear email a {seller_name} con recomendaciones",
  "editor.ai.ctaCatalog.descriptionLabel": "Internal note (optional)",
  "editor.ai.ctaCatalog.descriptionPlaceholder": "Shown only in the builder UI, not to end users.",
  "editor.ai.ctaCatalog.add": "Add CTA",
  "editor.ai.ctaCatalog.remove": "Remove",
  "editor.ai.snapshots.title": "Insights cadence",
  "editor.ai.snapshots.description": "Configure how the portal generates the persisted AI analysis snapshots. Legacy reports run weekly; switch to the per-report temporal model to anchor at a different grain.",
  "editor.ai.snapshots.weekColumnLabel": "ISO week column",
  "editor.ai.snapshots.weekColumnHint": "DuckDB column the watcher uses to detect new data weeks. Defaults to \"week_iso\".",
  "editor.ai.snapshots.maxInsightsLabel": "Max insights per snapshot",
  "editor.ai.snapshots.maxInsightsHint": "Server clamps to 1–6. Default is 3.",
  "editor.ai.snapshots.priorOffsetsLabel": "Prior offsets (in weeks)",
  "editor.ai.snapshots.priorOffsetsHint": "Historical anchor offsets used to surface trend context to the LLM. Default is [1, 2, 4, 8, 13, 26, 52]. Type integers and press Enter.",
  "editor.ai.snapshots.temporalCtaDescription": "By default this report anchors insights to ISO weeks. Switch to the per-report temporal model to pick the date column, the grain (day / week / month / quarter / fiscal quarter) and how many periods of data + prior analyses feed each insight.",
  "editor.ai.snapshots.switchToTemporal": "Switch to per-report temporal config",
  "editor.ai.snapshots.temporalActiveBadge": "Per-report temporal model active",
  "editor.ai.snapshots.revertToWeekly": "Revert to legacy weekly",
  "editor.ai.snapshots.dateColumnLabel": "Date column",
  "editor.ai.snapshots.dateColumnHint": "DuckDB column that drives the period cadence. Temporal columns are listed first.",
  "editor.ai.snapshots.granularityLabel": "Granularity (atomicity)",
  "editor.ai.snapshots.granularityHint": "How insights are bucketed and anchored. \"Quarter\" is calendar; \"Fiscal quarter\" is the HP fiscal year (Nov→Oct).",
  "editor.ai.snapshots.dataLookbackLabel": "Data lookback (periods)",
  "editor.ai.snapshots.dataLookbackHint": "How many periods of data feed the completeness diagnostic. Minimum 1.",
  "editor.ai.snapshots.analysisLookbackLabel": "Analysis lookback (periods)",
  "editor.ai.snapshots.analysisLookbackHint": "How many prior analyses are injected as historical context. 0 = none.",
  "editor.ai.snapshots.columnsLoading": "Loading columns…",
  "editor.ai.snapshots.columnsError": "Couldn't load columns from DuckDB; you can still type a value below.",
  "editor.ai.snapshots.nonTemporalTag": "non-temporal",
  "editor.ai.snapshots.grain.day": "Day",
  "editor.ai.snapshots.grain.week": "Week",
  "editor.ai.snapshots.grain.month": "Month",
  "editor.ai.snapshots.grain.quarter": "Quarter (calendar)",
  "editor.ai.snapshots.grain.fiscalQuarter": "Fiscal quarter (HP)",
  "editor.ai.promptLabel": "AI Analysis prompt override",
  "editor.ai.promptHint": "Leave empty to use the per-report prompt fallback at configs/domains/_global/ai_analysis.j2.",
  "editor.ai.promptPlaceholder": "{{ report_display_name }}\nAnalyze the following data…",
  "editor.ai.promptTemplateLabel": "Template (Jinja2)",
  "editor.ai.promptOverrideInfo":
    "By default the portal's ``+ Show AI Analysis`` button uses the global ``ai_analysis`` prompt (configurable in /admin/prompts). This override REPLACES that prompt for THIS report only. Leave empty to fall back to the global.",
  "editor.ai.promptClearOverride": "Clear override (use global)",
  "editor.ai.promptNoOverride": "No override — using the global ai_analysis prompt.",
  "editor.ai.promptVarsLabel": "Available Jinja2 variables",
  "editor.ai.promptVarsHint":
    "The portal renders the template with these context variables. Reference them with ``{{ name }}`` syntax.",
  "editor.ai.docsLabel": "Training documents ({count})",
  "editor.ai.addDocBtn": "Add document",
  "editor.ai.noDocsHint": "No documents yet. Add markdown context that grounds the AI's answers (KPIs glossary, business rules, etc.).",
  "editor.ai.docNamePlaceholder": "document-name.md",
  "editor.ai.docContentPlaceholder": "Markdown content…",
  "editor.ai.examplesLabel": "Q&A examples ({count})",
  "editor.ai.addExampleBtn": "Add example",
  "editor.ai.noExamplesHint": "Few-shot examples that show the model the expected question→tool→answer pattern.",
  "editor.ai.exampleLabel": "Example {idx}",
  "editor.ai.questionLabel": "Question",
  "editor.ai.questionPlaceholder": "What is the total revenue for US?",
  "editor.ai.toolLabel": "Tool (optional)",
  "editor.ai.toolPlaceholder": "dashboard_explorer_kpis",
  "editor.ai.answerLabel": "Expected answer",
  "editor.ai.answerPlaceholder": "The total revenue for US is…",

  // Files tab
  "editor.files.titleLabel": "File uploads",
  "editor.files.s3PathLabel": "S3 root path",
  "editor.files.s3PathHint": "Where uploads land — usually 'hp/{report_key}/files'.",
  "editor.files.s3PathPlaceholder": "hp/dashboard_explorer/files",
  "editor.files.typesLabel": "Allowed file types",
  "editor.files.typesCountSingular": "type",
  "editor.files.typesCountPlural": "types",
  "editor.files.maxSizeLabel": "Max file size (MB)",
  "editor.files.pageSectionLabel": "Page appearance",
  "editor.files.pageTitleLabel": "Page title",
  "editor.files.pageTitleHint": "Shown at the top of the Files page. Leave blank to use the generic default.",
  "editor.files.pageTitlePlaceholder": "e.g. Regional downloads",
  "editor.files.pageSubtitleLabel": "Page subtitle",
  "editor.files.pageSubtitlePlaceholder": "e.g. Quarterly reports and exports",
  "editor.files.emptyMessageLabel": "Empty message",
  "editor.files.emptyMessageHint": "Shown when the S3 root has no files.",
  "editor.files.allowUploadLabel": "Allow upload",
  "editor.files.allowUploadHint": "Show an Upload button so users can add files to the root path.",

  // Master files tab
  "editor.master.titleLabel": "Master files ({count})",
  "editor.master.addPrefixBtn": "Add prefix",
  "editor.master.noPrefixesHint": "No master prefixes configured. Add one to map an S3 path the loader should pick up for this report.",
  "editor.master.prefixIndexLabel": "Prefix {idx}",
  "editor.master.removeBtn": "Remove",
  "editor.master.nameLabel": "Name",
  "editor.master.namePlaceholder": "US Catalogue",
  "editor.master.fileTypeLabel": "File type",
  "editor.master.prefixLabel": "S3 prefix",
  "editor.master.prefixHint": "Relative to the report's S3 bucket root.",
  "editor.master.prefixPlaceholder": "US/CATALOGUE",
  "editor.master.pageSectionLabel": "Page appearance",
  "editor.master.pageTitleLabel": "Page title",
  "editor.master.pageTitleHint": "Shown at the top of Maestros. Leave blank to use the generic default.",
  "editor.master.pageTitlePlaceholder": "e.g. HP Catalogues",
  "editor.master.pageSubtitleLabel": "Page subtitle",
  "editor.master.pageSubtitlePlaceholder": "e.g. Reference data for this report",
  "editor.master.pageEmptyLabel": "No-prefixes message",
  "editor.master.pageEmptyHint": "Shown on the Maestros page when no prefixes are configured.",
  "editor.master.prefixTitleLabel": "Card title",
  "editor.master.prefixTitleHint": "Overrides the Card header. Leave blank to use the prefix name.",
  "editor.master.prefixDescriptionLabel": "Description",
  "editor.master.prefixDescriptionHint": "Shown under the Card title.",
  "editor.master.prefixEmptyMessageLabel": "Empty message",
  "editor.master.prefixEmptyMessageHint": "Shown when the S3 listing is empty.",
  "editor.master.actionsLabel": "Allowed actions",
  "editor.master.actionsHint": "Toggle per-file buttons on hp-portal.",
  "editor.master.actionPreview": "Preview",
  "editor.master.actionDownload": "Download",
  "editor.master.actionReplace": "Replace",
  "editor.master.actionUpload": "Upload",

  // Report builder page
  "editor.dialog.deleteSection": "Delete the selected section?",
  "editor.error.loadDraft": "Failed to load draft \"{key}\".",

  // Publish modal
  "publish.warnings": "Warnings:",

  // Auth
  "auth.logoutConfirm": "Sign out of the builder?",
  "auth.sessionCheckFailed": "Couldn't verify your session. You are still signed in; retry in a moment.",

  // Draft history panel
  "history.title": "History",
  "history.empty": "No edits yet. The first snapshot will be captured when you save.",
  "history.revert": "Revert",
  "history.revertConfirm": "Revert the draft to this snapshot? A new history row will record the current state first.",
  "history.stat.sections": "Sec",
  "history.stat.kpis": "KPI",
  "history.stat.charts": "Ch",
  "history.stat.filters": "Filt",

  // KPICard drill footer + ignored-filters tooltip
  // (consumed by packages/dashboard-render/src/KPICard.tsx)
  "kpiCard.openAnalysis": "Open analysis",
  "kpiCard.closeAnalysis": "Close analysis",
  "kpiCard.ignoredFilters": "Ignores filters: {filters}",
  // Data tab (Phase 3 multi-source — keys consumed by DataTab.tsx)
  "editor.data.title": "Data sources",
  "editor.data.subtitle": "{n} source(s) materialized into this report's DuckDB",
  "editor.data.addSource": "Add data source",
  "editor.data.nav.aria": "Data source list",
  "editor.data.delete": "Delete",
  "editor.data.deleteConfirm": "Delete data source {name}?",
  "editor.data.cantDeleteLast":
    "A report must have at least one data source. Edit this one instead of deleting.",
  "editor.data.noSourceTitle": "No data source declared yet",
  "editor.data.noSourceHint":
    "Add a data source to materialize a table from the project's raw data.",
  "editor.data.source.editorTitle": "Data source",
  "editor.data.source.name": "Name",
  "editor.data.source.target": "Target table",
  "editor.data.source.targetHint":
    "Required: the DuckDB table this view lands on, and the one the report reads. Leave it as it comes and it takes the source's name plus _data.",
  "editor.data.source.sql": "View SQL",
  "editor.data.source.sqlHint":
    "SELECT body. The engine wraps this in CREATE OR REPLACE TABLE {target} AS <sql> when you click Apply.",
  "editor.data.source.description": "Description (for chat AI)",
  "editor.data.source.descriptionHint":
    "Markdown prose surfaced to the chat AI as the report's data context. Replaces the legacy DDL training slot.",
  "editor.data.source.preview": "Materialized columns",
  "editor.data.source.notMaterialized":
    "Table \"{name}\" doesn't exist in DuckDB yet. Click \"Apply view to DuckDB\" to materialize it.",
  "editor.data.source.sample": "Sample rows ({n})",
  "editor.data.source.schema": "Schema ({n} columns)",
  "editor.data.source.preview10": "Preview — first {n} rows",
  "editor.data.refresh": "Refresh",
  "editor.data.refreshTitle": "Refresh preview",
  "editor.data.save": "Save view SQL",
  "editor.data.apply": "Apply view to DuckDB",
  "editor.data.viewApplied": "Table {name} recreated in the draft. The published report changes when you publish.",
  "editor.data.viewApplyError": "Failed to recreate {name}: {err}",
  "editor.data.applyNeedsSave": "Unsaved changes: click “Save view SQL” and then Save (top right) before applying. Apply uses the saved SQL, not what is in the editor.",
  "editor.data.loading": "Loading…",
  // Legacy keys retained while older paths still reference them
  "editor.data.noTableTitle": "Set a DuckDB table name first",
  "editor.data.noTableHint":
    "In the Inspector → Identity section, type the lowercase snake_case name for this report's DuckDB table (e.g. 'metabase_data'). Then return here to load a parquet from S3.",
  "editor.data.loadTitle": "Load parquet from S3",
  "editor.data.loadHint":
    "Paste an s3:// URL to a parquet file. The builder downloads it to parquet_dir/{report_key}/ and materializes the table.",
  "editor.data.s3UrlPlaceholder": "s3://bucket/path/to/file.parquet",
  "editor.data.modeReplace": "Replace",
  "editor.data.modeAppend": "Append",
  "editor.data.loadBtn": "Load",
  "editor.data.loadSuccess": "Loaded {rows} rows in {ms}ms",
  "editor.data.dropBtn": "Drop table",
  "editor.data.dropConfirm": "Drop table \"{table}\" and delete its local parquet? This cannot be undone.",
  "editor.data.columnsTitle": "Columns ({count})",
  "editor.data.sampleTitle": "Sample rows (first 10)",
  "editor.data.emptyTable": "Table \"{table}\" doesn't exist yet. Load a parquet above to create it.",
  // View-kind editor (iter 23)
  "editor.data.viewSqlTitle": "View SQL",
  "editor.data.viewSqlHint":
    "SELECT statement over the project's raw fact table. The engine wraps this in CREATE OR REPLACE VIEW {viewName} AS <sql> on the next project reload.",
  "editor.data.viewSqlSaveBtn": "Save view SQL",
  "editor.data.viewSqlClean": "Saved",
  "editor.data.viewSqlDirty": "Unsaved changes",
  "editor.data.viewSqlReloadHint":
    "Save commits the SQL to the draft config. Apply re-runs CREATE OR REPLACE VIEW in DuckDB against the existing project source — no S3 re-download. Use the project's full Reload only when the underlying source has changed.",
  "editor.data.viewApplyBtn": "Apply view to DuckDB",
  // ``editor.data.viewApplied`` + ``editor.data.viewApplyError`` are now
  // defined once at the top of the Data tab block (Phase 3 multi-source).
  "editor.data.emptyView":
    "View \"{view}\" doesn't exist in DuckDB yet. Save the SQL and click Reload on the project's Data tab to create it.",

  // Preview empty-states
  "preview.noChartData": "No data for the current filter.",
  "preview.noKpiData": "—",
  "preview.rowDrill.label": "Row drill",
  "preview.notFoundTitle": "Report not found",
  "preview.notFoundHint":
    "The report key in the URL doesn't match any draft.",
  "preview.loadErrorTitle": "Couldn't load the report",

  // Editor · AI sub-tabs
  "editor.ai.subtabs.ddl": "DDL",
  "editor.ai.subtabs.docs": "Documentation",
  "editor.ai.subtabs.examples": "Examples",
  "editor.ai.subtabs.prompt": "Prompt",
  "editor.ai.loading": "Loading…",
  "editor.ai.saving": "Saving…",
  "editor.ai.saveBtn": "Save",
  "editor.ai.savedHint": "Saved.",
  "editor.ai.deleteConfirm": "Delete this row? This cannot be undone.",
  "editor.ai.noDDLHint": "No DDL yet. Add the CREATE TABLE statement so the AI knows the schema.",
  "editor.ai.addDDLBtn": "Add DDL row",
  "editor.ai.ddlContentPlaceholder": "CREATE TABLE …",
  "editor.ai.exampleNameLabel": "Label",
  "editor.ai.exampleSqlLabel": "SQL",
  "editor.ai.exampleSqlPlaceholder": "SELECT … FROM …",
  "editor.ai.exampleExplanationLabel": "Explanation (optional)",
  "editor.ai.exampleExplanationPlaceholder": "Why this query is the right answer.",

  // Admin · common
  "admin.common.loading": "Loading…",
  "admin.common.saving": "Saving…",
  "admin.common.save": "Save",
  "admin.common.cancel": "Cancel",
  "admin.common.back": "Back",
  "admin.common.delete": "Delete",
  "admin.common.select": "Select",
  "admin.common.noResults": "No results",

  // Admin · Nav
  "nav.admin.prompts": "Prompts",
  "nav.admin.subscribers": "Subscribers",

  // Admin · Prompts
  "admin.prompts.title": "Prompt templates",
  "admin.prompts.subtitle": "Jinja2 templates rendered by the chat agent and the AI-analysis endpoint.",
  "admin.prompts.scopeBannerTitle": "Global fallbacks",
  "admin.prompts.scopeBannerBody": "These templates are the global defaults used when a report has no override. Per-report AI prompts live in Reports → [report] → AI tab.",
  "admin.prompts.newBtn": "New prompt",
  "admin.prompts.empty": "No prompts yet.",
  "admin.prompts.notFound": "Prompt not found.",
  "admin.prompts.scope.all": "All",
  "admin.prompts.scope.global": "Global",
  "admin.prompts.scope.report": "Report",
  "admin.prompts.scope.client": "Client",
  "admin.prompts.col.name": "Name",
  "admin.prompts.col.scope": "Scope",
  "admin.prompts.col.key": "Target",
  "admin.prompts.col.version": "Version",
  "admin.prompts.col.active": "Active",
  "admin.prompts.col.updated": "Updated",
  "admin.prompts.activeLabel": "Active",
  "admin.prompts.savedHint": "Saved (now at v{version}).",
  "admin.prompts.shortcutHint": "Cmd/Ctrl+S to save.",
  "admin.prompts.deleteConfirm": "Delete this prompt template? It stops being served immediately.",
  "admin.prompts.create.title": "New prompt template",
  "admin.prompts.create.nameLabel": "Name",
  "admin.prompts.create.nameHint": "Lookup key, e.g. \"level1_system\". Must be unique within the chosen scope + target.",
  "admin.prompts.create.scopeLabel": "Scope",
  "admin.prompts.create.draftLabel": "Report draft",
  "admin.prompts.create.clientLabel": "Client key",
  "admin.prompts.create.templateLabel": "Initial template body",
  "admin.prompts.create.submit": "Create",

  // Admin · Subscribers
  "admin.subscribers.title": "Portal subscribers",
  "admin.subscribers.subtitle": "External portals consuming /api/v1/* with X-Builder-Token.",
  "admin.subscribers.newBtn": "New subscriber",
  "admin.subscribers.empty": "No subscribers yet. Create one and copy its token to wire a portal.",
  "admin.subscribers.col.name": "Name",
  "admin.subscribers.col.scope": "Scope",
  "admin.subscribers.col.active": "Active",
  "admin.subscribers.col.lastSeen": "Last seen",
  "admin.subscribers.col.actions": "Actions",
  "admin.subscribers.scopeAll": "All reports & projects",
  "admin.subscribers.active": "Active",
  "admin.subscribers.inactive": "Inactive",
  "admin.subscribers.revokeBtn": "Revoke",
  "admin.subscribers.revokeConfirm": "Revoke \"{name}\"? Its token stops working immediately.",
  "admin.subscribers.create.title": "New portal subscriber",
  "admin.subscribers.create.nameLabel": "Name",
  "admin.subscribers.create.descriptionLabel": "Description (optional)",
  "admin.subscribers.create.reportKeysLabel": "Allowed report keys",
  "admin.subscribers.create.reportKeysHint": "Leave empty to allow ALL reports.",
  "admin.subscribers.create.projectsLabel": "Allowed projects",
  "admin.subscribers.create.projectsHint": "Leave empty to allow ALL projects.",
  "admin.subscribers.create.submit": "Create",
  "admin.subscribers.token.title": "Token for {name}",
  "admin.subscribers.token.warning": "Copy this token now — it is shown only once and the server only stores its bcrypt hash.",
  "admin.subscribers.token.tokenLabel": "API token",
  "admin.subscribers.token.copyBtn": "Copy",
  "admin.subscribers.token.copied": "Copied",
  "admin.subscribers.token.usageHint": "Use as: X-Builder-Token: <token> on /api/v1/* requests from the portal.",
  "admin.subscribers.token.acknowledge": "I have stored this token securely.",
  "admin.subscribers.token.close": "Close",

  // ── Synonyms editor (iter 20) ──────────────────────────────
  "editor.synonyms.title": "Synonyms (optional)",
  "editor.synonyms.hint":
    "Alternative terms the chat will recognise as this column/KPI/chart/filter. Enter a term + Enter (or comma) to add a chip; Backspace removes the last.",
  "editor.synonyms.inputPlaceholder": "+ add synonym",
  "editor.synonyms.removeChip": "Remove",

  // ── Filter label (iter 19) ─────────────────────────────────
  "editor.filter.labelLabel": "Display label (optional)",
  "editor.filter.labelHint":
    "Bilingual chip label shown above the filter. Empty falls back to the key.",
  "editor.filter.labelPlaceholder": "Country",

  // ── Dashboard tab canvas + inspector (iter 21) ─────────────
  "canvas.readOnly.label": "Read-only render.",
  "canvas.readOnly.hint":
    "Structural changes (add/remove items, change types, etc.) flow through Claude with the report-builder skill. Click an item to inspect its details.",
  "canvas.expand": "Expand to full width",
  "canvas.collapse": "Restore 50/50 layout",

  "inspector.itemTitle": "{element}",
  "inspector.itemHint":
    "Inline editing is limited to bilingual labels + synonyms. Other fields are read-only.",
  "inspector.sectionFallback": "Section",
  "inspector.sectionHint":
    "Bilingual title + subtitle stay editable. Structural fields are read-only.",
  "inspector.dashboardTitle": "Dashboard",
  "inspector.dashboardHint":
    "Report identity + filter labels stay editable. Structural fields are read-only.",

  "inspector.kpi.editable": "Editable",
  "inspector.kpi.structural": "Configuration (read-only)",
  "inspector.kpi.sql": "SQL",
  "inspector.kpi.format": "Format",
  "inspector.kpi.variant": "Variant",
  "inspector.kpi.unit": "Unit",
  "inspector.kpi.ignoreFilters": "Ignored filters",
  "inspector.kpi.drillTarget": "Drill target",

  "inspector.chart.editable": "Editable",
  "inspector.chart.structural": "Configuration (read-only)",
  "inspector.chart.type": "Type",
  "inspector.chart.dimension": "Dimension",
  "inspector.chart.metric": "Metric",
  "inspector.chart.metrics": "Metrics",
  "inspector.chart.sort": "Sort",
  "inspector.chart.limit": "Limit",
  "inspector.chart.axes": "Axes",
  "inspector.chart.ignoreFilters": "Ignored filters",

  "inspector.table.editable": "Editable per column",
  "inspector.table.editableHint":
    "Bilingual header + synonyms per column. Format, render and structural fields stay read-only.",
  "inspector.table.structural": "Configuration (read-only)",
  "inspector.table.pageSize": "Page size",
  "inspector.table.sort": "Default sort",
  "inspector.table.ignoreFilters": "Ignored filters",
  "inspector.table.rowDrill": "Row drill target",
  "inspector.table.rowDrillMode": "Row detail display",
  "inspector.table.rowDrillMode.swap": "Replace in the cell (with slide) — default",
  "inspector.table.rowDrillMode.accordion": "Accordion under the row",
  "inspector.table.rowDrillModeHint":
    "Replace: the detail takes the table's place with a slide; Back restores it. Accordion: the row stays visible and the detail scrolls under it inside the table. The card never grows in either.",
  "inspector.table.crossFilter": "Cross-filter →",
  "inspector.table.crossFilterNone": "(none)",
  "inspector.table.noColumns": "No columns configured.",

  "inspector.text.editable": "Content",
  "inspector.text.contentLabel": "Markdown / plain text",
  "inspector.text.contentHint":
    "Author content shown in the dashboard. Renders verbatim — no structural fallback.",
  "inspector.text.contentPlaceholder": "Write Markdown or plain text…",

  "inspector.section.editable": "Section labels (editable)",
  "inspector.section.structural": "Section configuration (read-only)",
  "inspector.section.id": "ID",
  "inspector.section.kind": "Kind",
  "inspector.section.drillId": "Drill ID",
  "inspector.section.items": "Items ({count})",
  "inspector.section.filters": "Section filters ({count})",

  "inspector.dashboard.identityEditable": "Report identity (editable)",
  "inspector.dashboard.identityStructural": "Report identity (read-only)",
  "inspector.dashboard.key": "Key",
  "inspector.dashboard.duckdbTable": "DuckDB table",
  "inspector.dashboard.promptLanguage": "Prompt language",
  "inspector.dashboard.globalFilters": "Global filters ({count})",
  "inspector.dashboard.noFilters": "No global filters configured.",

  "inspector.filter.column": "Column",
  "inspector.filter.default": "Default value",
  "inspector.filter.locked": "Locked",
  "inspector.filter.sliderStep": "Slider step",
  "inspector.filter.sliderStep.day": "Day",
  "inspector.filter.sliderStep.week": "Week (Mondays)",
  "inspector.filter.sliderStep.month": "Month (1st)",
  "inspector.filter.sliderStepHint":
    "Snap granularity of the date slider handles. The emitted filter value is always a plain start/end date range.",
  "inspector.filter.rangeFormat": "Value format",
  "inspector.filter.rangeFormat.number": "Number",
  "inspector.filter.rangeFormat.percent": "Percent (column is a 0-1 fraction)",
  "inspector.filter.rangeFormat.currency": "Currency (USD)",
  "inspector.filter.rangeDefaultMin": "Default minimum",
  "inspector.filter.rangeDefaultMax": "Default maximum",
  "inspector.filter.rangeScale": "Slider scale",
  "inspector.filter.rangeScale.linear": "Linear",
  "inspector.filter.rangeScale.log": "Logarithmic (skewed values, e.g. revenue)",
  "inspector.filter.rangeHistogramTable": "Histogram table (id)",
  "inspector.filter.rangeHint":
    "Type the bounds as users see them (30 for 30 %). Leave a side empty to keep it open. The slider covers the column's full range.",

  "editor.report.displayNameLabel": "Display name (bilingual)",
  "editor.report.descriptionLabel": "Description (bilingual)",

  // ── Accesos: ámbitos, grupos, personas, auditoría (Plan J, J3) ──
  "access.tabs.label": "Access sections",
  "access.tabs.scopes": "Scopes",
  "access.tabs.groups": "Groups",
  "access.tabs.people": "People",
  "access.tabs.audit": "Audit",
  "access.common.errorTitle": "Something failed",
  "access.common.missingLabel": "This value has no label yet — it is shown raw.",
  "access.common.notComputed": "not computed",
  "access.common.notComputedHint": "Nobody has computed this yet. It is not zero.",
  "access.common.badDate": "unreadable date",
  "access.common.refresh": "Refresh",
  "access.common.prev": "Previous",
  "access.common.next": "Next",
  "access.scopes.title": "Scopes",
  "access.scopes.subtitle": "A scope is the cut of the data a person reads. Rules are ANDed and materialised as one file per scope and project.",
  "access.scopes.add": "New scope",
  "access.scopes.all": "Everything",
  "access.scopes.col.scope": "Scope",
  "access.scopes.col.rules": "Rules",
  "access.scopes.col.projects": "Projects",
  "access.scopes.col.usedBy": "Used by",
  "access.scopes.col.state": "State",
  "access.scopes.col.updated": "Updated",
  "access.scopes.appliesTo": "{n} with access · {m} without",
  "access.scopes.appliesToNone": "no project",
  "access.scopes.appliesToNoneHint": "It applies to no project, so it grants nothing anywhere.",
  "access.scopes.usedBy": "{n} group(s) · {people} person(s)",
  "access.scopes.usedByNone": "nobody",
  "access.scopes.usedByNoneHint": "No group carries it and nobody resolves through it.",
  "access.scopes.empty.title": "No scopes yet",
  "access.scopes.empty.desc": "Create one, attach it to a group, and the members of that group read only their slice.",
  "access.scopes.actions.detail": "Open",
  "access.scopes.actions.rebuild": "Rebuild",
  "access.scopes.actions.delete": "Delete",
  "access.scopes.state.built": "built",
  "access.scopes.state.noMapping": "empty (no match)",
  "access.scopes.state.building": "building",
  "access.scopes.state.failed": "failed",
  "access.scopes.state.notApplicable": "not applicable",
  "access.scopes.state.neverBuilt": "never built",
  "access.scopes.state.skippedConcurrent": "another build had it",
  "access.scopes.rules.chip": "{key}: {n} value(s)",
  "access.scopes.rules.all": "no rules — everything",
  "access.scopes.rules.allHint": "The reserved scope. It has no rules, no file and no rebuild.",
  "access.scopes.rules.none": "no rules",
  "access.scopes.rules.noneHint": "A scope with no rules cuts nothing. Add at least one.",
  "access.scopes.rules.andHint": "Every rule must match: they are combined with AND.",
  "access.scopes.rules.emptyTitle": "No rules yet",
  "access.scopes.rules.emptyDesc": "A scope with no rules cuts nothing and cannot be saved.",
  "access.scopes.rules.declaredIn": "declared in {n} project(s)",
  "access.scopes.rules.removeRule": "Remove",
  "access.scopes.rules.reference": "Read values from",
  "access.scopes.rules.referenceHelp": "A scope is tenant-wide; the values come from one project's live file. Two projects can hold different values for the same key.",
  "access.scopes.rules.addLabel": "Add a rule",
  "access.scopes.rules.addHelp": "Only keys declared usable for scoping are offered.",
  "access.scopes.rules.addPlaceholder": "Choose a dimension…",
  "access.scopes.rules.keyOption": "{key} — {n} project(s)",
  "access.scopes.rules.allKeysUsed": "Every available dimension is already used by a rule.",
  "access.scopes.rules.noKeysTitle": "No dimension can be scoped yet",
  "access.scopes.rules.noKeysDesc": "Declare a dimension as usable for scoping in a project's Data tab first.",
  "access.scopes.rules.orphanTitle": "This dimension is no longer available",
  "access.scopes.rules.orphanDesc": "No project declares «{key}» as usable for scoping any more. The rule is kept as it is; delete it or restore the dimension.",
  "access.scopes.values.noneSelected": "no value chosen yet",
  "access.scopes.values.remove": "Remove this value",
  "access.scopes.values.searchLabel": "Search values",
  "access.scopes.values.searchPlaceholder": "Type to filter…",
  "access.scopes.values.errorTitle": "The values could not be read",
  "access.scopes.values.summary": "Showing {shown} of {total} distinct values of {column} ({type}).",
  "access.scopes.values.nulls": "{n} row(s) have no value. They belong to no restricted scope.",
  "access.scopes.values.notScopableTitle": "This column cannot be scoped",
  "access.scopes.values.notScopable": "Its type ({type}) is refused when the scope is built. The values are fine for a filter.",
  "access.scopes.values.emptyTitle": "This column has no values",
  "access.scopes.values.noMatchTitle": "Nothing matches «{search}»",
  "access.scopes.values.noMatchDesc": "Only values that exist in the data can be chosen — there is no free text here on purpose.",
  "access.scopes.values.rowCount": "{n} row(s)",
  "access.scopes.values.range": "{from}–{to} of {total}",
  "access.scopes.form.createTitle": "New scope",
  "access.scopes.form.grantsNothingTitle": "Creating it grants nobody anything",
  "access.scopes.form.grantsNothing": "A new scope has no group, no member and no file until something builds it.",
  "access.scopes.form.id": "Identifier",
  "access.scopes.form.idHelp": "Lowercase, digits, hyphen and underscore. It IS the filename, so «us-north» and «us_north» are two different scopes.",
  "access.scopes.form.idRequired": "Type an identifier.",
  "access.scopes.form.idSpaces": "It cannot start or end with a space.",
  "access.scopes.form.idShape": "Start with a lowercase letter; then lowercase letters, digits, «-» or «_». Between 2 and 41 characters.",
  "access.scopes.form.name": "Name",
  "access.scopes.form.description": "Description",
  "access.scopes.form.descriptionHelp": "What this cut is, in the words the team uses.",
  "access.scopes.form.rulesTitle": "Rules",
  "access.scopes.delete.title": "Delete «{name}»",
  "access.scopes.delete.confirm": "Delete the scope and its files",
  "access.scopes.delete.blockedTitle": "It cannot be deleted",
  "access.scopes.delete.blocked": "{groups} group(s) carry it and {people} person(s) resolve through it. Move them first — there is no force option.",
  "access.scopes.delete.filesTitle": "The data files go too",
  "access.scopes.delete.files": "Its files go too, removed under the write lock. There is no undo: rebuilding later starts from zero.",
  "access.scopes.detail.identity": "Identity",
  "access.scopes.detail.identityDesc": "The identifier cannot change: it is the name of the file each project's slice is written to.",
  "access.scopes.detail.kind": "Kind",
  "access.scopes.detail.usedBy": "Used by",
  "access.scopes.detail.rules": "Rules",
  "access.scopes.detail.rulesDesc": "Saving RETIRES this scope's files and pointers on every project and deletes its AI snapshots, in the same request. Until it is rebuilt, everyone who reads through this scope gets an error (503) on every report — never the previous cut. If a reload holds the write lock, the save is refused and nothing changes.",
  "access.scopes.detail.notEditableTitle": "This scope is not editable",
  "access.scopes.detail.preview": "See the impact",
  "access.scopes.detail.mustPreviewTitle": "Look at the impact first",
  "access.scopes.detail.mustPreview": "The save has to name the groups it affects, so the impact is fetched before it is allowed.",
  "access.scopes.detail.impactTitle": "What this edit would change",
  "access.scopes.detail.rulesInvalid": "These rules are refused",
  "access.scopes.detail.impactWho": "{groups} group(s) and {people} person(s) resolve through this scope.",
  "access.scopes.detail.impactRows": "{before} rows now, {after} after",
  "access.scopes.detail.impactUnknown": "rows unknown ({reason})",
  "access.scopes.detail.noticeTitle": "What happened",
  "access.scopes.detail.retiredTitle": "Saved — readers blocked until the rebuild",
  "access.scopes.detail.retired": "The files were retired and the AI snapshots deleted. Everyone bound to this scope gets a 503 on every report until you rebuild it, project by project, below.",
  "access.scopes.detail.projects": "Projects",
  "access.scopes.detail.projectsDesc": "Whether the rules can cut each project, and what the last build left there.",
  "access.scopes.detail.col.project": "Project",
  "access.scopes.detail.col.state": "State",
  "access.scopes.detail.col.rows": "Rows",
  "access.scopes.detail.col.version": "Data version",
  "access.scopes.detail.col.built": "Last build",
  "access.scopes.detail.col.started": "Started",
  "access.scopes.detail.col.rebuild": "Rebuild",
  "access.scopes.detail.rebuild": "Rebuild",
  "access.scopes.detail.rebuilding": "Building…",
  "access.scopes.detail.rowsOf": "{rows} of {total}",
  "access.scopes.detail.noRowsHint": "There is no build row for this project yet. It is not zero rows.",
  "access.scopes.detail.noVersionHint": "No build has recorded which data file it read.",
  "access.scopes.detail.stuck": "STUCK",
  "access.scopes.detail.stuckTitle": "This build is stuck",
  "access.scopes.detail.builds": "Build history",
  "access.scopes.detail.buildsDesc": "{total} build(s), newest first.",
  "access.scopes.detail.buildsCapped": "Showing {shown} of {total}. The history continues.",
  "access.groups.title": "Groups",
  "access.groups.subtitle": "A group carries a scope and a content matrix. What a person may see is the OR of their groups.",
  "access.groups.add": "New group",
  "access.groups.col.group": "Group",
  "access.groups.col.scope": "Scope",
  "access.groups.col.members": "Members",
  "access.groups.col.content": "Content",
  "access.groups.col.health": "State",
  "access.groups.col.updated": "Updated",
  "access.groups.contentCount": "{reports} report(s) · {projects} project cell(s)",
  "access.groups.scopeAllHint": "Members of this group read the whole data set.",
  "access.groups.health.grantsNothing": "opens nothing",
  "access.groups.health.noMembers": "nobody in it",
  "access.groups.health.ok": "in use",
  "access.groups.empty.title": "No groups yet",
  "access.groups.empty.desc": "A person with no group sees no report. Create the first one and give it a scope.",
  "access.groups.actions.detail": "Open",
  "access.groups.actions.delete": "Delete",
  "access.groups.base.chip": "Base",
  "access.groups.base.chipTitle": "The base group: it opens every published report and all the data. It is never deleted and its scope does not change.",
  "access.groups.base.contentTitle": "Every published report",
  "access.groups.base.contentBody": "This group opens every published report, including the ones published later. Its actions are fixed: see the report, its data, the chat, the AI analysis, and view + download of master files and files. It does not include sending by e-mail nor scheduling sends. To give someone a different set, put them in a subgroup.",
  "access.groups.tab.groups": "Groups",
  "access.groups.tab.defaults": "Role defaults",
  "access.groups.tab.members": "Members",
  "access.groups.tab.scope": "Scope",
  "access.groups.tab.content": "Content",
  "access.groups.form.createTitle": "New group",
  "access.groups.form.emptyTitle": "It starts empty and grants nothing",
  "access.groups.form.empty": "Add people and open reports afterwards. Both states are reported in the list with a word.",
  "access.groups.form.name": "Name",
  "access.groups.form.nameHelp": "It has to be unique, and it is what the audit trail shows.",
  "access.groups.form.description": "Description",
  "access.groups.form.scope": "Scope",
  "access.groups.form.scopeHelp": "It has no default on purpose: a group exists to carry a restriction.",
  "access.groups.form.scopePlaceholder": "Choose a scope…",
  "access.groups.form.scopeAllOption": "Everything (no restriction)",
  "access.groups.form.scopeAllTitle": "This group does not restrict anything",
  "access.groups.form.scopeAllWarn": "Its members read the whole data set of every project they can open — and so does anybody put in it later, whatever their other groups restrict: the union with «Everything» is «Everything».",
  "access.groups.delete.title": "Delete «{name}»",
  "access.groups.delete.confirm": "Delete the group",
  "access.groups.delete.body": "{members} person(s) lose the {reports} report(s) this group opens.",
  "access.groups.delete.scopeNote": "The scope itself is not deleted — only this group and its cells.",
  "access.groups.members.add": "Add people",
  "access.groups.members.addTitle": "Add people to the group",
  "access.groups.members.addCount": "Add {count}",
  "access.groups.members.addNote": "Adding somebody can change the scope they resolve to, and the new file may not exist yet. It changes NOTHING for a person with the administrator role: they read everything whatever their groups say, so a restriction written here would quietly not apply to them.",
  "access.groups.members.searchLabel": "Search by name or e-mail",
  "access.groups.members.noneOfferable": "Nobody left to add",
  "access.groups.members.noneOfferableDesc": "Everybody in the list is already a member.",
  "access.groups.members.col.person": "Person",
  "access.groups.members.col.role": "Role",
  "access.groups.members.col.status": "Status",
  "access.groups.members.col.effective": "Resolves to",
  "access.groups.members.col.added": "Added",
  "access.groups.members.union": "union of {n} scopes",
  "access.groups.members.scope.unrestricted": "reads everything",
  "access.groups.members.scope.unrestrictedHint": "Their union resolves to «Everything», so this group's cut decides nothing for them. One unrestricted group is enough — the union with «Everything» is «Everything». To make this restriction bite, take them out of the group that carries the unrestricted scope.",
  "access.groups.members.scope.elsewhere": "decided elsewhere",
  "access.groups.members.scope.elsewhereHint": "Their union resolves to a scope that is not this group's, so what they read is decided by another of their groups. Open the person to see which groups it came from.",
  "access.groups.members.scope.holds": "this group's cut",
  "access.groups.members.scope.holdsHint": "Their union resolves to exactly this group's scope, so the cut on this page is the one they read through.",
  "access.groups.members.remove": "Remove from the group",
  "access.groups.members.footnote": "The whole membership is listed, never a page of it: an access decision read off a shortened list is the worst thing this screen could do.",
  "access.groups.members.noticeTitle": "What this change does",
  "access.groups.scope.title": "Scope of this group",
  "access.groups.scope.desc": "Changing it changes what every member reads, from the next request on — as far as their widest group allows. A person's scope is the union of their groups', and the union with «Everything» is «Everything».",
  "access.groups.scope.openScope": "Open the scope",
  "access.groups.scope.projectsDesc": "Where this scope has a file, and where it does not.",
  "access.groups.scope.warningTitle": "This costs somebody access",
  "access.groups.content.desc": "Cells with no row fall through to the person's other groups, and then to the role defaults — except «See it», which has no default at all: a report no group of theirs opens is not opened.",
  "access.groups.content.hint": "Click a cell to cycle: default → granted → denied → default.",
  "access.groups.content.save": "Save ({count})",
  "access.groups.content.entitled": "This group opens {n} report(s). A report with no «see it» cell is not opened at all.",
  "access.groups.content.projectLevel": "Project level",
  "access.groups.content.projectLevelBadge": "master files and files",
  "access.groups.content.projectSectionsHint": "These two sections are granted for the whole project, not per report.",
  "access.groups.content.noProject": "Without a project",
  "access.groups.content.granted": "granted",
  "access.groups.content.denied": "denied",
  "access.groups.content.defaultTitle": "No row here. The role default decides, and it says: {value}. «See it» is the exception: it has no default, so with no row the report is not opened.",
  "access.groups.content.grantedTitle": "Granted here. It also wins over a denial in another group.",
  "access.groups.content.entitlementTitle": "This is the cell that OPENS the report. Without it the other cells grant nothing.",
  "access.groups.content.denyIsWeak": "Denied here — but a grant in ANOTHER group of the same person wins. To revoke, remove the grant everywhere.",
  "access.groups.content.unknownDefault": "No role default row exists for {section}.{action}, so nobody knows what an empty cell means here.",
  "access.defaults.subtitle": "What an empty cell of a group's content matrix means. Only the «user» role is editable.",
  "access.defaults.save": "Save ({count})",
  "access.defaults.readonly": "read only",
  "access.defaults.adminNote": "The admin role bypasses every check, so these cells decide nothing.",
  "access.defaults.missingTitle": "Some defaults have no row",
  "access.defaults.missing": "Nobody knows what an empty cell means for: {cells}. Set them so the group matrix can say.",
  "access.defaults.grantedTitle": "Granted by default",
  "access.defaults.deniedTitle": "Denied by default",
  "access.defaults.unknownCell": "There is no row for {section}.{action}. That is unknown, not denied.",
  "access.people.title": "People",
  "access.people.subtitle": "Who signs in, which groups they belong to, and the scope those groups resolve to.",
  "access.people.add": "New person",
  "access.people.count": "{count} person(s)",
  "access.people.allRoles": "Every role",
  "access.people.col.person": "Person",
  "access.people.col.role": "Role",
  "access.people.col.groups": "Groups",
  "access.people.col.effective": "Scope",
  "access.people.col.builder": "Builder",
  "access.people.col.lastLogin": "Last login",
  "access.people.lastLogin.window": "{n} in the last 30 days",
  "access.people.lastLogin.noneSince": "No logins since {date}",
  "access.people.lastLogin.noneYet": "No logins recorded yet",
  "access.people.col.status": "Status",
  "access.people.active": "active",
  "access.people.inactive": "inactive",
  "access.people.builderNone": "no access",
  "access.people.builderFull": "full",
  "access.people.builderPartial": "partial",
  "access.people.groupCount": "{n} group(s)",
  "access.people.noGroups": "no group",
  "access.people.noGroupsHint": "A person in no group is granted no report by any group.",
  "access.people.partialTitle": "This list is incomplete",
  "access.people.partial": "The membership of these groups could not be read: {groups}. The group counts below are a lower bound.",
  "access.people.empty.title": "Nobody here",
  "access.people.empty.desc": "Create the first person and put them in a group.",
  "access.people.actions.detail": "Open",
  "access.people.actions.reset": "Reset the password",
  "access.people.actions.delete": "Delete",
  "access.people.legacyCounts": "Legacy rows still on this person: {subs} subscription(s) and {overrides} override(s). They are read by the old permission system and are being retired.",
  "access.people.identity": "Identity",
  "access.people.identityDesc": "The e-mail is the identity and does not change here.",
  "access.people.form.name": "Name",
  "access.people.form.email": "E-mail",
  "access.people.form.emailFixed": "It cannot be changed from this console.",
  "access.people.form.role": "Role",
  "access.people.form.roleHelp": "An admin bypasses every permission check, so groups decide nothing for them.",
  "access.people.form.status": "Status",
  "access.people.form.password": "Password",
  "access.people.form.passwordHelp": "At least 8 characters. It is shown once and there is no e-mail to send it with.",
  "access.people.form.passwordWarning": "Copy it before closing: it cannot be shown again.",
  "access.people.form.generate": "Generate",
  "access.people.form.createTitle": "New person — step {step} of {total}",
  "access.people.form.next": "Next",
  "access.people.form.back": "Back",
  "access.people.form.fillRequired": "Fill in the name, the e-mail and a password of at least 8 characters.",
  "access.people.form.groupsTitle": "Groups are the only thing you can give",
  "access.people.form.groupsHint": "There are no per-person permissions any more: what somebody sees comes from their groups.",
  "access.people.form.noGroupsTitle": "There are no groups yet",
  "access.people.form.noGroupsHint": "Create one first, or this person will sign in and see nothing.",
  "access.people.form.noPickTitle": "No group chosen",
  "access.people.form.noPick": "They can sign in and will see no report at all.",
  "access.people.groups.title": "Groups",
  "access.people.groups.desc": "Everything this person sees comes from here.",
  "access.people.groups.col.group": "Group",
  "access.people.groups.col.scope": "Scope",
  "access.people.groups.join": "Add to groups",
  "access.people.groups.joinTitle": "Add this person to groups",
  "access.people.groups.joinCount": "Add to {count}",
  "access.people.groups.joinNote": "This is how «same access as somebody else» is expressed now: shared membership, which survives the other person leaving.",
  "access.people.groups.leave": "Remove from the group",
  "access.people.groups.noneOfferable": "Already in every group",
  "access.people.groups.noneOfferableDesc": "There is no other group to add them to.",
  "access.people.effective.title": "Resulting scope",
  "access.people.effective.eyebrow": "Computed from the groups",
  "access.people.effective.name": "union of {n} group(s)",
  "access.people.effective.derived": "This identifier is derived from the rules of the groups above. It is recomputed on every membership change.",
  "access.people.effective.allHint": "No restriction: this person reads the whole data set. There are TWO ways to be here and this chip does not tell them apart: an unrestricted group (the union with «Everything» is «Everything»), or the administrator role, which reads everything whatever their groups say. Either way, a restricted group they are also in decides nothing.",
  "access.people.effective.unknown": "unknown",
  "access.people.effective.unknownHint": "No group reports a scope for this person, so this console cannot say which one they resolve to.",
  "access.people.effective.noGroups": "They are in no group, so no group grants them any report.",
  "access.people.effective.orphanHint": "Their binding names a scope none of their groups carries. Something wrote it by hand, or the recompute has not caught up.",
  "access.people.effective.computed": "This is computed from the person's groups. To change it, change the groups.",
  "access.people.effective.editGroups": "Edit the groups",
  "access.people.builder.title": "Builder access",
  "access.people.builder.desc": "Omnitok staff access to this console. It is not a group setting and never will be.",
  "access.people.reset.title": "Reset the password",
  "access.people.reset.prompt": "A new password is generated for {name} and shown once.",
  "access.people.reset.generate": "Generate",
  "access.people.reset.newPassword": "New password",
  "access.people.reset.warning": "Copy it before closing: it cannot be shown again, and there is no e-mail to send it with.",
  "access.people.reset.done": "Done",
  "access.people.delete.title": "Delete «{name}»",
  "access.people.delete.confirm": "Delete the person",
  "access.people.delete.body": "They are in {groups} group(s) and will not be able to sign in.",
  "access.people.delete.soft": "It is a soft delete: the row and its memberships stay, so reactivating restores what they had.",
  "access.audit.title": "Audit",
  "access.audit.subtitle": "Every access change made from Accesos, with who made it and what it was before.",
  "access.audit.startsHereTitle": "The trail starts here",
  "access.audit.startsHere": "Changes made before this feature shipped were not recorded. An empty result for an older date means «not recorded», never «nothing happened».",
  "access.audit.filter.action": "Action",
  "access.audit.filter.actor": "Who",
  "access.audit.filter.target": "Object",
  "access.audit.filter.limit": "Rows",
  "access.audit.apply": "Apply",
  "access.audit.limitRange": "Rows: a whole number between 1 and {max}.",
  "access.audit.col.when": "When",
  "access.audit.col.who": "Who",
  "access.audit.col.actorKind": "Console",
  "access.audit.col.action": "Action",
  "access.audit.col.target": "Object",
  "access.audit.col.detail": "Detail",
  "access.audit.view": "See it",
  "access.audit.atCap": "Showing {limit} of {total}. The trail continues — ask for more rows.",
  "access.audit.empty.title": "Nothing recorded for this filter",
  "access.audit.empty.desc": "Widen the filter, or ask for more rows.",
  "access.audit.detailTitle": "What changed",
  "access.audit.actorId": "Identifier of who acted",
  "access.audit.actorGone": "the person no longer exists; the e-mail survives",
  "access.audit.before": "Before",
  "access.audit.after": "After",
  "access.audit.noBefore": "It did not exist before.",
  "access.audit.noAfter": "It does not exist any more.",
  "access.audit.actor.providerAdmin": "builder",
  "access.audit.actor.tenantAdmin": "client console",
  "access.audit.actor.platform": "platform",
  "access.audit.actor.system": "automatic",
  "access.audit.action.dimensionCreate": "dimension created",
  "access.audit.action.dimensionUpdate": "dimension edited",
  "access.audit.action.dimensionDelete": "dimension deleted",
  "access.audit.action.scopeCreate": "scope created",
  "access.audit.action.scopeUpdate": "scope edited",
  "access.audit.action.scopeDelete": "scope deleted",
  "access.audit.action.scopeRebuild": "scope rebuilt",
  "access.audit.action.groupCreate": "group created",
  "access.audit.action.groupUpdate": "group edited",
  "access.audit.action.groupDelete": "group deleted",
  "access.audit.action.groupPermissionSet": "content changed",
  "access.audit.action.groupMemberAdd": "added to a group",
  "access.audit.action.groupMemberRemove": "removed from a group",
  "access.audit.action.userEffectiveScopeChange": "scope recomputed",
  "access.audit.action.roleDefaultUpdate": "role defaults changed",
  "access.audit.action.userCreate": "person created",
  "access.audit.action.userRoleChange": "role changed",
  "access.audit.action.userDeactivate": "deactivated",
  "access.audit.action.userReactivate": "reactivated",
  "access.audit.action.userLoginAllowedChange": "sign-in changed",
  "access.audit.action.userDelete": "person removed",
  "access.audit.action.userPasswordReset": "password reset",
  "access.audit.action.userBuilderAccess": "builder access changed",
  "access.audit.action.viewAsScopePreview": "scope previewed",

  // ── Accesos → Grupos → AI y Experiencia (Plan J, J4) ──────────────
  //
  // Every CONTROL named in these strings saves and is read by
  // something; the strings that describe no control say what decides
  // the thing instead of offering a dial for it. That is the whole
  // split, and it is why `access.experience.mail.*` is four strings and
  // not thirty: nothing reads a group's mail template, so the tab
  // answers the question rather than rendering a form for it.
  "access.groups.tab.ai": "AI",
  "access.groups.tab.experience": "Experience",
  "access.ai.save": "Save the AI settings",
  "access.ai.saveEffect": "Both settings take effect on the next request the people in this group make: the note reaches their chat and their e-mails, and the analysis is generated on the next reload.",
  "access.ai.insights.title": "AI Analysis for this group",
  "access.ai.insights.desc": "The analysis is generated per SCOPE, not per group: two groups carrying the same scope read the same paragraphs. Switching this on asks for one under this group's scope. It is the cost lever, and the cost is beside the switch.",
  "access.ai.insights.switch": "Generate AI Analysis under this group's scope",
  "access.ai.insights.switchHelp": "Off until somebody turns it on: charging a customer N times the generations because a group row was created is not a default anybody chose.",
  "access.ai.note.title": "Group context note",
  "access.ai.note.desc": "Background you write for the people who read through this group. It reaches the chat, the AI Analysis and the report e-mails, and it orients the model — it grants nothing.",
  "access.ai.note.label": "Note",
  "access.ai.note.help": "Plain prose: «this team only looks at Amazon US», «an order counts when it ships». What a question can read is decided by the data file, so a note asking for something outside the scope simply returns nothing.",
  "access.ai.note.tooLong": "{n} characters, and the limit is {max}. Nothing is cut for you: this note is added whole to every prompt the people in this group reach, so half a sentence would go with it. Shorten it and save.",
  "access.ai.note.sharedTitle": "Where else this note is read",
  "access.ai.note.shared": "The AI Analysis is stored per SCOPE, not per group, so two groups carrying the same scope both read a paragraph written with their two notes joined. In the chat and in a person's e-mail each reader gets the notes of their own groups.",
  "access.ai.reach.title": "How far the chat reaches",
  "access.ai.reach.desc": "Not a setting. The chat reads the same file the asker's scope opens, so its reach is the scope and nothing on this page can widen it.",
  "access.ai.reach.projectWide": "Questions across a whole project",
  "access.ai.reach.projectWideBody": "Offered automatically in projects with two or more published reports, for the whole customer. There is no per-group switch for it today, so this tab does not show one.",
  "access.ai.examples.title": "Training examples withheld from this scope",
  "access.ai.examples.desc": "An example is withheld from a restricted scope unless its author marked it safe. The corpus that already exists starts withheld, on purpose: an example quoting real figures from Mexico would teach them to a group that only sees the United States.",
  "access.ai.examples.unknown": "count unknown",
  "access.ai.examples.unknownHint": "No read endpoint returns the safe-for-restricted-scopes flag, so nobody can count them from here. A zero would say nothing is being withheld, which is the opposite of the default.",
  "access.ai.examples.body": "The number is written to the chat log on every restricted answer. The flag that lets an example through is report_training.scope_safe, and the builder is the side that will write it — no editor exposes it yet.",
  "access.ai.verbosity.title": "How long the answers are",
  "access.ai.verbosity.desc": "There is no length setting per group.",
  "access.ai.verbosity.body": "The product's one length control — short, medium or long — belongs to the automatic send a person sets up for themselves in the portal, and it is theirs to choose. A dial per group here would write a setting nothing reads.",
  "access.ai.cost.title": "Cost of AI Analysis per scope",
  "access.ai.cost.tenantDesc": "Every project of this customer, at the reload cadence you give. The per-month figures are arithmetic about a month that has not happened; the measured column is a plain count of the analyses actually written in the last 30 days.",
  "access.ai.cost.beforeTitle": "What switching it on costs",
  "access.ai.cost.beforeBody": "One more scope over the projects where this group's scope has a file. The figure is the same in both directions: off, it is what you would add; on, it is what you would save.",
  "access.ai.cost.unrestricted": "This group carries the unrestricted scope, so the switch adds nothing: the unrestricted analysis is already generated for every project, and it is the one its members read.",
  "access.ai.cost.runs": "Reloads per month",
  "access.ai.cost.runsHelp": "How many times a month these projects reload their data — 4 for a weekly load, about 30 for a nightly one. It has no default because the analysis regenerates when the data changes, not on a calendar, and only you know that schedule.",
  "access.ai.cost.runsRange": "A whole number between {min} and {max}. Nothing is assumed for you.",
  "access.ai.cost.compute": "Estimate",
  "access.ai.cost.notYet": "Fill in the reloads per month to see the figure. Until then there is none: an invented cadence would turn a guess into a number on screen.",
  "access.ai.cost.adds": "Switching it on adds about {n} AI Analysis generations a month.",
  "access.ai.cost.saves": "Switching it off saves about {n} AI Analysis generations a month.",
  "access.ai.cost.tenantTotal": "About {n} AI Analysis generations a month across every project.",
  "access.ai.cost.partial": "Partial: {n} project(s) could not be estimated and are not in the total.",
  "access.ai.cost.alreadyGenerating": "This scope already gets an analysis in: {projects}. There its share of the cost is already being spent — by this group or by another one carrying the same scope.",
  "access.ai.cost.basis.estimate": "estimate",
  "access.ai.cost.col.project": "Project",
  "access.ai.cost.col.reports": "Reports with analysis",
  "access.ai.cost.col.scopes": "Scopes that get one",
  "access.ai.cost.col.perMonth": "Per month",
  "access.ai.cost.col.measured": "Written in 30 days",
  "access.ai.cost.unavailable": "not estimated",
  "access.ai.cost.unavailableHint": "This project's estimate did not come back. The reason is under the table, whole. A zero here would be a real and different answer.",
  "access.ai.cost.failedTitle": "«{project}» could not be estimated",
  "access.ai.cost.projectsFailedTitle": "The project list could not be read",
  "access.ai.cost.noProjects": "This group's scope has no file in any project, so there is nothing to narrate under it.",
  "access.ai.cost.noProjectsTenant": "This customer has no projects.",
  "access.experience.grantsNothingTitle": "Nothing on this tab grants anything",
  "access.experience.grantsNothing": "A tile handed to a member is still resolved against their own permissions and their own scope on every read, and comes back denied when they cannot open it. This tab decides what a person STARTS with, never what they may see.",
  "access.experience.save": "Save the profile",
  "access.experience.clear": "Clear the profile",
  "access.experience.board.title": "Home board template",
  "access.experience.board.desc": "What a member's Home looks like the first time they open it. When the template changes their board is re-synced — and a tile they added themselves is never touched.",
  "access.experience.board.resync": "Re-sync members' boards",
  "access.experience.board.resyncHelp": "On: the group's tiles are replaced on every board read and everything the person added stays. Off: the template is applied once, when the person has no board yet, and never again.",
  "access.experience.board.document": "Tiles, as JSON",
  "access.experience.board.documentHelp": "An array of widget objects, the same shape a saved Home board has. At most {max}, each with its own id, and no image tiles — an image belongs to one person's uploads and would be broken for everybody else. The builder has no board canvas, so the template is edited as the document it is; the preview below is the real placement.",
  "access.experience.board.count": "{n} of {max} tiles.",
  "access.experience.board.overCapTitle": "Too many tiles",
  "access.experience.board.overCap": "The portal refuses a board over {max} tiles, so nothing here is trimmed to fit: take some out and save again.",
  "access.experience.board.badJson": "This is not valid JSON: {detail}",
  "access.experience.board.notAnArray": "The document has to be an array of tiles, even when it holds one.",
  "access.experience.board.notAnObject": "Item {position} is not an object. Every tile is a widget object.",
  "access.experience.board.layoutError": "This layout cannot be placed",
  "access.experience.board.preview": "Preview",
  "access.experience.board.previewHelp": "Twelve columns, in array order — the same placement the member's board runs, vertical stretch included.",
  "access.experience.board.previewEmpty": "No tiles yet.",
  "access.experience.board.noTiles": "No tiles in the template. A member with no template gets an empty Home and builds their own.",
  "access.experience.board.col.position": "#",
  "access.experience.board.positionHint": "The index the server's refusals name — «tile 2 of the board template».",
  "access.experience.board.col.kind": "Kind",
  "access.experience.board.col.title": "Title",
  "access.experience.board.col.source": "Report or project",
  "access.experience.board.col.size": "Size",
  "access.experience.board.col.id": "Id",
  "access.experience.board.noTitle": "no title",
  "access.experience.board.noKind": "no kind",
  "access.experience.board.noKindHint": "A tile with no kind cannot be read by the portal's widget schema, and the group's whole template will be refused.",
  "access.experience.board.noId": "no id",
  "access.experience.board.noIdHint": "The id is what tells a member's board which tiles came from the group and which they added themselves. Without it the tile cannot be re-synced or kept apart from their own.",
  "access.experience.board.badSize": "bad size",
  "access.experience.board.badSizeHint": "Width and height have to be numbers: 1 to 12 columns, 1 to 40 rows.",
  "access.experience.mail.title": "Default e-mail",
  "access.experience.mail.desc": "The plan gives a group a scheduled report every member receives. It is not configured here, because nothing sends it yet.",
  "access.experience.mail.body": "The column can hold a mail template and the server still validates one, but no code anywhere writes a member's schedule row from it — that needs an origin column on the schedules table, beside the schedules API in the portal. So there is no form here. A form would let somebody set a cron, read a success and believe they had scheduled something, and a warning above it does not undo that. Until the writer exists, what people get is the schedule they set up for themselves in the portal. One thing the schema settles regardless: a group template has no recipient list and cannot have one — a group default is rendered once per member, under that member's own scope, and addressed to that member, so there is no way to express «mail this group's report to somebody else».",
  "access.experience.mail.storedTitle": "This group carries a stored mail template",
  "access.experience.mail.stored": "Somebody wrote one into the column by hand. It is kept exactly as it is when you save this tab, and it is still not delivered to anybody. «Clear the profile» removes it along with the board template.",
  "access.experience.threshold.title": "Threshold alerts",
  "access.experience.threshold.desc": "Not a group setting, and they cannot be.",
  "access.experience.threshold.body": "A threshold alert carries state per row — the last version it saw, whether it has already fired — and that only means something in a schedule a person wrote and can edit, so it lives on that person's schedule and not here. What Plan J did add is that when one of those fires it reads the owner's scope file, so the number it compares is one the owner is entitled to see.",
  // Tasks: the report flag of the shared table (portal only).
  "tasks.mark.flag": "Report",
  "tasks.mark.reported": "Reported",
  "tasks.mark.resolved": "Resolved",
  "tasks.mark.column": "Report",
  "tasks.mark.wholeChart": "The whole chart",
  "tasks.mark.pickPoint": "Pick a point",
  "tasks.mark.pickHint": "Click the point you want to report",
  "tasks.mark.pickCancel": "Cancel (Esc)",
};

const ES: StringTable = {
  // Component gallery (Fase D, look v3, 2026-10-24).
  "gallery.count": "{entries} ejemplos · {components} componentes",
  "gallery.search": "Buscar componente, tabla, filtro…",
  "gallery.clearSearch": "Limpiar búsqueda",
  "gallery.animations": "Animaciones",
  "gallery.replay": "Reiniciar ejemplos",
  "gallery.examples": "{count} ejemplos",
  "gallery.sampleData": "Datos de ejemplo · interacción opcional",
  "gallery.searchResults": "Resultados de la búsqueda",
  "gallery.backToFamily": "← Ver toda la familia",
  "gallery.empty.title": "No encontramos ese componente",
  "gallery.empty.hint": "Prueba con «KPI», «Tabla», «Fecha» o «Botón».",
  "gallery.component": "Componente",
  "gallery.composition": "Composición",
  "gallery.composedOf": "Formada por {parts}",
  "gallery.preview": "Vista previa",
  "gallery.interactive.on": "Interacción activa",
  "gallery.interactive.off": "Interacción desactivada",
  "gallery.interactive.aria": "Interacción de {title}",
  "gallery.expand": "Desplegar {label}",
  "gallery.categoriesNav": "Categorías de componentes",
  "gallery.copyImport": "Copiar import",
  "gallery.copied": "Copiado",
  "gallery.source.ui": "UI kit",
  "gallery.source.bi": "Componentes BI",
  "gallery.source.render": "Render de reportes",
  "gallery.source.admin": "UI de operador",
  "gallery.demoError": "No se pudo mostrar este componente: {message}",
  "gallery.missing": "Exports del kit sin ejemplo: {names}",
  "gallery.unknownCategory": "No existe la categoría «{category}».",
  "gallery.exploreData": "Explorar datos",
  "gallery.noData": "Sin dato",
  "gallery.group.system": "Sistema",
  "gallery.group.data": "Datos y reportes",
  "gallery.group.composition": "Composición",
  "gallery.category.foundation": "Fundamentos",
  "gallery.category.navigation": "Navegación",
  "gallery.category.controls": "Controles",
  "gallery.category.surfaces": "Tarjetas y estados",
  "gallery.category.feedback": "Mensajes y paneles",
  "gallery.category.layout": "Estructura de la app",
  "gallery.category.drag": "Arrastrar y cargar",
  "gallery.category.kpis": "Indicadores KPI",
  "gallery.category.charts": "Gráficos",
  "gallery.category.tables": "Tablas",
  "gallery.category.filters": "Filtros y selección",
  "gallery.category.dates": "Fechas y períodos",
  "gallery.category.measures": "Medidores y contexto",
  "gallery.category.maps": "Mapas",
  "gallery.category.compositions": "Composiciones de reporte",
  "gallery.category.motion-page": "Movimiento · página",
  "gallery.category.transitions": "Movimiento · elementos",
  "gallery.category.brand": "Tokens del motor",
  "gallery.foundation.palette.title": "El lenguaje visual",
  "gallery.foundation.palette.text": "Navy para orientar, fucsia para actuar, el color del módulo para leer los datos.",
  "gallery.foundation.usage.ink": "Navegación y títulos",
  "gallery.foundation.usage.pink": "Acción y marca",
  "gallery.foundation.usage.violet": "Controles y series",
  "gallery.foundation.usage.module": "Color del módulo",
  "gallery.foundation.usage.canvas": "Fondo de la aplicación",
  "gallery.foundation.usage.border": "Divisores y tarjetas",
  "gallery.foundation.type.title": "Tipografía",
  "gallery.foundation.type.sample": "Los datos, más claros.",
  "gallery.foundation.type.text": "Inter para controles y lectura. Nunito Sans para títulos e indicadores. Texto secundario con contraste suficiente para leerlo.",
  "gallery.foundation.surfaces.title": "Superficies y espacio",
  "gallery.foundation.radius.field": "Campo",
  "gallery.foundation.radius.control": "Control",
  "gallery.foundation.radius.card": "Tarjeta",
  "gallery.foundation.radius.panel": "Panel",
  "gallery.foundation.inContext": "Componentes en contexto",
  "common.cancel": "Cancelar",
  "common.save": "Guardar",
  "common.delete": "Eliminar",
  "common.create": "Crear",
  "common.edit": "Editar",
  "common.loading": "Cargando…",
  "drill.title": "Análisis",
  "drill.noData": "Sin datos para este análisis.",
  "drill.closeAnalysis": "Cerrar análisis",
  "drill.close": "Cerrar",
  "drill.fromKpi": "↑ desde KPI",
  "table.rowDrill.label": "↳ Detalle de fila",
  "table.close": "Cerrar",
  "common.error": "Algo salió mal",
  "common.retry": "Reintentar",
  "common.missingLabel":
    "Este valor todavía no tiene etiqueta — se muestra lo que mandó el backend.",
  "common.confirm": "Confirmar",

  "login.title": "Digital Shelf Analytics",
  "login.subtitle": "Inicia sesión para gestionar definiciones de reportes",
  "login.email": "Correo",
  "login.password": "Contraseña",
  "login.submit": "Iniciar sesión",
  "login.invalid": "Correo o contraseña incorrectos",

  "list.title": "Reportes y Proyectos",
  "list.subtitle":
    "Cada reporte pertenece a un proyecto. Crea proyectos y luego agrégales reportes.",
  "list.new": "Nuevo reporte",
  "list.newProject": "Nuevo proyecto",
  "list.addReport": "Reporte",
  "list.noReportsInProject":
    "Todavía no hay reportes en este proyecto. Usa \"+ reporte\" para agregar uno.",
  "list.reorderHint":
    "Arrastra las filas para ordenar el sidebar del portal — se refleja en ~1 minuto.",
  "list.reorderSaved": "Orden guardado.",
  "list.reorderError": "No se pudo guardar el orden — se restauró el anterior.",
  "list.dragHandle": "Arrastrar para reordenar",
  "list.empty":
    "Todavía no hay proyectos. Haz clic en \"+ Nuevo Proyecto\" para crear el primero.",

  // PR-0 builder reorg — home de proyectos + hub del proyecto
  "home.title": "Proyectos",
  "home.subtitle":
    "Un card por proyecto — los reportes viven adentro de cada proyecto.",
  "home.search": "Buscar proyecto…",
  "home.reports": "reportes",
  "home.published": "publicados",
  "home.unassigned": "Sin clasificar",
  "home.unassigned.desc": "Borradores que todavía no pertenecen a ningún proyecto.",
  "hub.back": "Proyectos",
  "hub.tab.reports": "Reportes",
  "hub.tab.config": "Configuración",
  "common.saved": "Guardado",

  // PR-1 builder reorg — icon picker con búsqueda + icono por reporte + tarjeta de alcance
  "iconPicker.search": "Buscar icono…",
  "iconPicker.count": "{total} iconos",
  "iconPicker.countFiltered": "{shown} de {total} iconos",
  "iconPicker.noResults": "Ningún icono coincide.",
  "list.create.icon": "Icono (opcional)",
  "editor.report.iconLabel": "Icono",
  "hub.scope.title": "Qué se configura dónde",
  "hub.scope.projectLabel": "Proyecto",
  "hub.scope.projectItems":
    "Datos (OBT), auto-refresh, maestros, archivos, accesos, icono/color.",
  "hub.scope.reportLabel": "Reporte",
  "hub.scope.reportItems":
    "Dashboard, vistas SQL, conocimiento IA, chat, insights, snapshots.",

  // PR-2 builder reorg — la IA del reporte repensada (pills + salud)
  "editor.ai.panes.overview": "Resumen",
  "editor.ai.panes.knowledge": "Conocimiento",
  "editor.ai.panes.chat": "Chat",
  "editor.ai.panes.insights": "Insights",
  "editor.ai.overview.enabledTitle": "IA activa en este reporte",
  "editor.ai.overview.enabledHint":
    "El portal muestra Chat + AI Analysis a los usuarios suscriptos (según sus permisos).",
  "editor.ai.overview.knowledgeDesc":
    "Lo que la IA sabe de este reporte — alimenta al chat Y a los insights. Se configura una sola vez.",
  "editor.ai.overview.chatDesc":
    "La superficie conversacional del sidebar del portal.",
  "editor.ai.overview.insightsDesc":
    "El análisis narrativo (AI Analysis), regenerado con cada período nuevo.",
  "editor.ai.overview.knowledgeCounts": "{docs} docs · {examples} ejemplos",
  "editor.ai.overview.focusCount": "{count} áreas de foco",
  "editor.ai.overview.promptOverrideOn": "Prompt override: activo",
  "editor.ai.overview.promptOverrideOff": "Prompt override: usa el global",
  "editor.ai.overview.modelDefault": "Modelo: default global",
  "editor.ai.overview.modelOverride": "Modelo: {model}",
  "editor.ai.overview.chatCounts":
    "{questions} preguntas de arranque · {ctas} CTAs",
  "editor.ai.overview.specCount": "{count} specs de insights",
  "editor.ai.overview.temporalMode": "Ventana temporal por reporte",
  "editor.ai.overview.weeklyMode": "Modo semanal legacy",
  "editor.ai.overview.maxInsights": "máx. {count} insights por snapshot",
  "editor.ai.overview.contextNotesHint":
    "Las notas de contexto (eventos del negocio) viven a nivel PROYECTO y aplican a todos sus reportes.",
  "editor.ai.overview.open": "Abrir",
  "editor.ai.aiHomeTime.title": "Home con IA: tiempo",
  "editor.ai.aiHomeRef.title": "Home con IA: KPIs y gráficos de referencia",
  "editor.ai.aiHomeRef.description": "Las medidas oficiales de este reporte. Cuando alguien pide una, el Home con IA usa este SQL tal cual en vez de escribir uno, y su revisión de IA nunca lo reescribe.",
  "editor.ai.aiHomeRef.placeholders": "Tiempo: {asOf} (el último período) y {since} (el inicio del rango), nunca una fecha escrita. Filtros del dashboard: marca cada columna con {filter} en un WHERE; una marca que ningún filtro toma se lee TRUE.",
  "editor.ai.aiHomeRef.add": "Agregar",
  "editor.ai.aiHomeRef.fromExample": "Desde un ejemplo Q&A",
  "editor.ai.aiHomeRef.fromExampleHint": "Reemplaza cualquier fecha escrita por los placeholders de tiempo.",
  "editor.ai.aiHomeRef.noExamples": "Este reporte todavía no tiene ejemplos SQL.",
  "editor.ai.aiHomeRef.empty": "Sin referencias: el Home con IA diseña cada elemento.",
  "editor.ai.aiHomeRef.untitled": "Sin título",
  "editor.ai.aiHomeRef.titleLabel": "Título",
  "editor.ai.aiHomeRef.kindLabel": "Tipo",
  "editor.ai.aiHomeRef.formatLabel": "Formato",
  "editor.ai.aiHomeRef.format.number": "Número",
  "editor.ai.aiHomeRef.format.percent": "Porcentaje (0..1)",
  "editor.ai.aiHomeRef.format.currency": "Moneda",
  "editor.ai.aiHomeRef.scopeSafe": "Válido con ámbitos restringidos",
  "editor.ai.aiHomeRef.test": "Probar con el último período",
  "editor.ai.aiHomeRef.testing": "Corriendo…",
  "editor.ai.aiHomeRef.runs": "Corre · {value}",
  "editor.ai.aiHomeRef.rows_one": "{count} fila",
  "editor.ai.aiHomeRef.rows_other": "{count} filas",
  "editor.ai.aiHomeRef.fails": "No corre",
  "editor.ai.aiHomeRef.filters": "filtros que acepta: {columns}",
  "editor.ai.aiHomeRef.noFilters": "no acepta filtros del dashboard",
  "editor.ai.aiHomeRef.remove": "Quitar",
  "editor.ai.aiHomeRef.publishNote": "Publicar corre cada referencia: una que no corre lo bloquea.",
  "editor.ai.aiHomeTime.baseTitle": "Home con IA: tiempo de la tabla base",
  "editor.ai.aiHomeTime.baseDescription": "Con una regla, el Home con IA puede usar toda la data del proyecto (también su tabla base). Sin regla, usa solo los reportes.",
  "editor.ai.aiHomeTime.description": "Cómo lee las fechas de este reporte el Home con IA. Siempre relativo a la última data: un dashboard guardado que se abre mañana muestra la data de mañana.",
  "editor.ai.aiHomeTime.missing": "Obligatorio: sin esto el reporte no se publica y no aparece en el Home con IA.",
  "editor.ai.aiHomeTime.modeLabel": "Cómo lee el tiempo",
  "editor.ai.aiHomeTime.modeHint": "Último período para reportes de foto del día (stock, disponibilidad); rango para reportes que se leen en el tiempo.",
  "editor.ai.aiHomeTime.modeUnset": "Elige…",
  "editor.ai.aiHomeTime.mode.day": "Último período",
  "editor.ai.aiHomeTime.mode.range": "Rango de períodos recientes",
  "editor.ai.aiHomeTime.mode.none": "Sin dimensión de tiempo",
  "editor.ai.aiHomeTime.dateColumnLabel": "Columna de fecha",
  "editor.ai.aiHomeTime.dateColumnHint": "Una fecha, o una semana o mes ISO como texto (week_iso, month_iso).",
  "editor.ai.aiHomeTime.anchorLabel": "Tabla ancla",
  "editor.ai.aiHomeTime.anchorHint": "Su último valor de la columna de fecha es el último período.",
  "editor.ai.aiHomeTime.periodsLabel": "Períodos",
  "editor.ai.aiHomeTime.periodsHint": "Cuántos períodos lee una tendencia (último período) o cada elemento (rango).",
  "editor.ai.aiHomeTime.completenessToggle": "Preguntar antes de armar si el último período está incompleto",
  "editor.ai.aiHomeTime.completenessHint": "Incompleto = menos filas que el porcentaje de abajo respecto de la mediana de los períodos anteriores.",
  "editor.ai.aiHomeTime.completenessOff": "Apagado: el último período se muestra siempre como está.",
  "editor.ai.aiHomeTime.minRatioLabel": "Mínimo respecto de un período normal (%)",
  "editor.ai.aiHomeTime.baselineLabel": "Períodos anteriores comparados",
  "editor.ai.advanced": "Avanzado",
  "editor.ai.blocks.what": "Qué analiza",
  "editor.ai.blocks.when": "Cómo y cuándo",
  "editor.ai.blocks.results": "Resultados",

  // PR-3 builder reorg — usuarios y permisos
  "nav.admin.users": "Usuarios y permisos",
  "nav.admin.access": "Accesos",
  "adminUsers.title": "Usuarios y permisos",
  "adminUsers.subtitle":
    "Cuentas del portal, suscripciones a reportes y la matriz de permisos.",
  "adminUsers.tab.users": "Usuarios",
  "adminUsers.tab.defaults": "Defaults de rol",
  "adminUsers.create": "Nuevo usuario",
  "adminUsers.allRoles": "Todos los roles",
  "adminUsers.usersCount": "{count} usuarios",
  "adminUsers.table.name": "Nombre",
  "adminUsers.table.email": "Correo",
  "adminUsers.table.role": "Rol",
  "adminUsers.table.subscriptions": "Suscripciones",
  "adminUsers.table.overrides": "Excepciones",
  "adminUsers.table.builder": "Builder",
  "adminUsers.table.status": "Estado",
  "adminUsers.table.actions": "Acciones",
  "adminUsers.builderFull": "total",
  "adminUsers.builderPartial": "parcial",
  "adminUsers.builderFlag.can_enter": "Entrar al builder",
  "adminUsers.builderFlag.edit_projects": "Editar proyectos y reportes",
  "adminUsers.builderFlag.publish": "Publicar",
  "adminUsers.builderFlag.manage_ai": "IA (Panorama, prompts, issues)",
  "adminUsers.builderFlag.operations": "Operaciones (loader)",
  "adminUsers.builderFlag.manage_users": "Usuarios y permisos",
  "adminUsers.noUsers": "Ningún usuario coincide.",
  "adminUsers.allProjects": "todos los reportes",
  "adminUsers.subsCount": "{count} reportes",
  "adminUsers.overridesCount": "{count} personalizados",
  "adminUsers.active": "Activo",
  "adminUsers.inactive": "Inactivo",
  "adminUsers.permissions": "Permisos",
  "adminUsers.resetPassword": "Restablecer contraseña",
  "adminUsers.confirmDelete":
    "¿Eliminar el usuario \"{name}\"? La cuenta se desactiva (soft delete).",
  "adminUsers.deleted": "Usuario eliminado",
  "adminUsers.created": "Usuario creado",
  "adminUsers.updated": "Usuario actualizado",
  "adminUsers.passwordResetDone": "Contraseña restablecida",
  "adminUsers.prev": "Anterior",
  "adminUsers.next": "Siguiente",
  "adminUsers.createTitle": "Nuevo usuario",
  "adminUsers.stepOf": "paso {current} de {total}",
  "adminUsers.fillRequired":
    "Completa nombre, correo y una contraseña de al menos 8 caracteres.",
  "adminUsers.password": "Contraseña",
  "adminUsers.passwordMin": "Mínimo 8 caracteres.",
  "adminUsers.generate": "Generar",
  "adminUsers.back": "Atrás",
  "adminUsers.skip": "Omitir",
  "adminUsers.initialAccessHelp":
    "Elige los reportes publicados a los que este usuario arranca suscrito.",
  "adminUsers.initialAccessNote":
    "Las suscripciones se pueden ajustar después en Permisos.",
  "adminUsers.noReports": "No hay reportes publicados.",
  "adminUsers.editTitle": "Editar usuario",
  "adminUsers.cloneFromAnother": "Clonar permisos de otro usuario…",
  "adminUsers.cloneSource": "Copiar suscripciones + overrides de",
  "adminUsers.cloneSelectUser": "Elegir usuario…",
  "adminUsers.applyClone": "Aplicar",
  "adminUsers.cloneConfirm":
    "¿Reemplazar las suscripciones y overrides de {name} con las del usuario origen?",
  "adminUsers.resetPrompt":
    "¿Generar una contraseña nueva para {name}? La actual deja de funcionar al instante.",
  "adminUsers.newPassword": "Contraseña nueva",
  "adminUsers.passwordWarning":
    "Se muestra una sola vez — no hay envío por correo (sin SMTP). Cópiala ahora y compártela por un canal seguro.",
  "adminUsers.done": "Listo",
  "adminUsers.saveCount": "Guardar ({count})",
  "adminUsers.defaults.subtitle":
    "Matriz base a la que cae cada rol cuando el usuario no tiene un override explícito.",
  "adminUsers.defaults.adminNote":
    "El rol admin saltea todos los chequeos de permisos — esta tarjeta es solo informativa.",
  "adminUsers.defaults.readonly": "solo lectura",
  "adminUsers.perms.title": "Permisos — {name}",
  "adminUsers.perms.action.view": "Verlo",
  "adminUsers.perms.action.view_data": "Ver los datos",
  "adminUsers.perms.action.run_chat": "Usar el chat",
  "adminUsers.perms.action.run_ai_analysis": "Generar el análisis IA",
  "adminUsers.perms.action.send_report": "Enviar por correo",
  "adminUsers.perms.action.schedule_send": "Programar envíos automáticos",
  "adminUsers.perms.action.upload": "Subir",
  "adminUsers.perms.action.download": "Descargar",
  "adminUsers.perms.action.delete": "Eliminar",
  "adminUsers.perms.subscribedOn": "Suscrito — clic para quitar",
  "adminUsers.perms.subscribedOff": "Sin suscribir — clic para agregar",
  "adminUsers.perms.subscriptions": "Suscripciones",
  "adminUsers.perms.subscriptionsHint":
    "Las pills activas son los reportes que este usuario ve. Clic para alternar — guarda al instante.",
  "adminUsers.perms.overridesTitle": "Overrides por reporte",
  "adminUsers.perms.noSubscriptions":
    "Sin suscripciones — activa primero un reporte arriba.",
  "adminUsers.perms.quickActions": "Acciones rápidas",
  "adminUsers.perms.applyDefaults": "Aplicar defaults del rol",
  "adminUsers.perms.clearAll": "Limpiar todos los overrides",
  "adminUsers.perms.copyFrom": "Copiar de otro usuario…",
  "adminUsers.perms.confirmClearAll":
    "¿Quitar todos los overrides explícitos? El usuario vuelve a los defaults puros del rol.",
  "adminUsers.perms.noOverridesToClear": "No hay overrides para limpiar.",
  "adminUsers.perms.adminNote":
    "Los admins saltean todos los chequeos — la matriz aplica solo al rol user.",
  "adminUsers.perms.legend":
    "El clic cicla cada celda: default (·) → concedido (✓) → denegado (✕).",
  "adminUsers.perms.builderAccessTitle": "Acceso al builder",
  "adminUsers.perms.builderAccessHint":
    "Qué puede hacer este usuario dentro de ESTA consola. Se guarda con cada toggle.",
  "adminUsers.perms.builderAdminNote":
    "Los admins siempre tienen acceso total al builder.",
  "adminUsers.perms.projectLevel": "Maestros y Files",
  "adminUsers.perms.projectLevelBadge": "nivel proyecto",
  "adminUsers.perms.projectSectionsHint":
    "Estas celdas aplican a todos los reportes del proyecto.",
  "adminUsers.perms.noProject": "Sin proyecto",
  "adminUsers.perms.subscribed": "(suscrito)",
  "adminUsers.perms.defaultTitle": "por defecto = {value}",
  "adminUsers.perms.granted": "concedido",
  "adminUsers.perms.denied": "denegado",
  "hub.tab.access": "Accesos",
  "hub.access.title": "Quién ve este proyecto",
  "hub.access.user": "Usuario",
  "hub.access.role": "Rol",
  "hub.access.role.admin": "Admin",
  "hub.access.role.user": "Usuario",
  "hub.access.reports": "Reportes",
  "hub.access.reportsOf": "{count} de {total} reportes",
  "hub.access.overrides": "Excepciones",
  "hub.access.edit": "Editar →",
  "hub.access.empty":
    "Ningún usuario está suscrito a los reportes de este proyecto todavía.",
  "hub.access.adminNote":
    "Los admins saltean todos los chequeos y no aparecen acá.",

  // PR-4 builder reorg — panorama IA
  "common.close": "Cerrar",
  "nav.admin.panorama": "Panorama IA",
  "hub.tab.ia": "IA del proyecto",
  "editor.ai.panes.issues": "Issues",

  "panorama.title": "Panorama IA",
  "panorama.subtitle":
    "Vista cross-proyecto de los issues del chat, snapshots IA y prompts globales.",
  "panorama.tab.overview": "Resumen",
  "panorama.tab.issues": "Cola de issues",
  "panorama.tab.prompts": "Prompts globales",
  "panorama.kpi.open": "Issues abiertos",
  "panorama.kpi.week": "Esta semana",
  "panorama.kpi.topReport": "Top reporte",
  "panorama.kpi.topHeuristic": "Top heurística",
  "panorama.chart.title": "Issues por semana",
  "panorama.chart.subtitle": "Últimos 60 días",
  "panorama.map.title": "Dónde se trabaja cada cosa",
  "panorama.map.report": "Reporte",
  "panorama.map.reportItems":
    "Issues, snapshots y conocimiento IA viven en el editor de cada reporte (tab IA).",
  "panorama.map.project": "Proyecto",
  "panorama.map.projectItems":
    "Las notas de contexto y el conocimiento del proyecto viven en el hub del proyecto (tab IA del proyecto).",
  "panorama.map.global": "Global",
  "panorama.map.globalItems":
    "Los prompts globales y la cola cross-proyecto de issues viven acá.",

  "issues.filter.report": "Reporte",
  "issues.filter.allReports": "Todos los reportes",
  "issues.filter.status": "Estado",
  "issues.filter.allStatuses": "Todos los estados",
  "issues.filter.heuristic": "Heurística",
  "issues.filter.allHeuristics": "Todas las heurísticas",
  "issues.total": "{count} incidencias",
  "issues.countAll": "{count} en total · {open} abiertos",
  "issues.countFiltered": "{count} coinciden con el filtro",
  "issues.table.id": "ID",
  "issues.table.created": "Creado",
  "issues.table.report": "Reporte",
  "issues.table.heuristic": "Heurística",
  "issues.table.question": "Pregunta",
  "issues.table.status": "Estado",
  "issues.empty": "Ningún issue coincide con los filtros actuales.",
  "issues.pageOf": "Página {page} de {total}",
  "issues.prev": "Anterior",
  "issues.next": "Siguiente",
  "issues.status.new": "Nuevo",
  "issues.status.in_review": "En revisión",
  "issues.status.resolved": "Resuelto",
  "issues.status.dismissed": "Descartado",
  "issues.heuristic.tool_execution_error": "Error de tool",
  "issues.heuristic.empty_result": "Resultado vacío",
  "issues.heuristic.max_iterations": "Máx. iteraciones",
  "issues.heuristic.empty_response": "Respuesta vacía",
  "issues.heuristic.llm_cannot_answer": "El LLM no puede responder",
  "issues.heuristic.user_reformulation": "Reformulación del usuario",
  "issues.heuristic.user_thumbs_down": "Pulgar abajo del usuario",
  "issues.drawer.title": "Issue #{id}",
  "issues.drawer.question": "Pregunta",
  "issues.drawer.response": "Respuesta del asistente",
  "issues.drawer.heuristicDetail": "Detalle de la heurística",
  "issues.drawer.toolCalls": "Llamadas a herramientas ({count})",
  "issues.drawer.resolutionNotes": "Notas de resolución",
  "issues.action.promote": "Promover a ejemplo de training",
  "issues.action.resolve": "Marcar resuelto",
  "issues.action.dismiss": "Descartar",
  "issues.action.reopen": "Reabrir",
  "issues.promote.question": "Pregunta (como se va a guardar)",
  "issues.promote.sql": "SQL (se valida en el server)",
  "issues.promote.save": "Guardar ejemplo de training",
  "issues.promote.required": "La pregunta y el SQL son obligatorios.",
  "issues.notes": "Notas (opcional)",
  "issues.resolve.known_limitation": "Limitación conocida",
  "issues.resolve.added_doc": "Documentación agregada",
  "issues.resolve.wont_fix": "No se corrige",
  "issues.dismiss.wont_fix": "No se corrige",
  "issues.dismiss.duplicate": "Duplicado",
  "issues.dismiss.known_limitation": "Limitación conocida",

  "snapshots.title": "Snapshots",
  "snapshots.description":
    "Snapshots persistidos del AI Analysis de este reporte. Regenerar, backfill y el watcher corren en el runtime del portal.",
  "snapshots.upToDate": "al día — {week}",
  "snapshots.trigger": "Ejecutar watcher",
  "snapshots.triggerDone": "Watcher ejecutado — {count} snapshots creados",
  "snapshots.backfill": "Backfill…",
  "snapshots.empty": "Todavía no hay snapshots para este reporte.",
  "snapshots.col.week": "Semana",
  "snapshots.col.summary": "Resumen",
  "snapshots.col.insights": "Insights",
  "snapshots.col.feedback": "Feedback",
  "snapshots.col.regen": "Regenerar",
  "snapshots.col.actions": "Acciones",
  "snapshots.view": "Ver",
  "snapshots.regenerate": "Regenerar",
  "snapshots.regenConfirm":
    "¿Regenerar este snapshot? Corre la llamada LLM completa en el portal y sobrescribe el contenido.",
  "snapshots.detail.regenerated": "regenerado × {count}",
  "snapshots.detail.filters": "Filtros aplicados",
  "snapshots.detail.feedback": "Resumen de feedback",
  "snapshots.detail.comments": "comentarios",
  "snapshots.detail.execSummary": "Resumen ejecutivo",
  "snapshots.detail.insights": "Insights ({count})",
  "snapshots.backfill.title": "Backfill de snapshots",
  "snapshots.backfill.help":
    "Genera los snapshots faltantes de {key}. Deja ambas fechas vacías para usar los defaults del portal.",
  "snapshots.backfill.start": "Desde",
  "snapshots.backfill.end": "Hasta",
  "snapshots.backfill.run": "Iniciar",
  "snapshots.backfill.running": "Corriendo…",
  "snapshots.backfill.done": "Listo — {created} snapshots nuevos",
  "snapshots.backfill.failed": "Falló: {error}",
  "snapshots.backfill.progress": "{processed} / {total}",

  "projectIA.title": "IA del proyecto",
  "projectIA.subtitle":
    "Notas de contexto y conocimiento a nivel proyecto, compartidos por todos los reportes del proyecto.",
  "projectIA.back": "IA del proyecto",
  "projectIA.row.notes": "Notas de contexto",
  "projectIA.row.notesDesc":
    "Eventos del negocio que la IA lee al generar cada snapshot.",
  "projectIA.row.column_docs": "Docs de columnas",
  "projectIA.row.column_docsDesc":
    "Documentación a nivel columna que el chat de cada reporte ve como contexto.",
  "projectIA.row.synonyms": "Sinónimos",
  "projectIA.row.synonymsDesc":
    "Términos del negocio mapeados a valores de columnas para que el chat entienda la jerga.",
  "projectIA.row.example": "Ejemplos del proyecto",
  "projectIA.row.exampleDesc":
    "Ejemplos Q&A del proyecto entero, encima del training propio de cada reporte.",
  "projectIA.activeCount": "{count} activas",
  "projectIA.activeCountAtLeast": "{count}+ activas",
  "projectIA.truncatedHint":
    "El endpoint de notas devuelve como máximo 500 filas, y esta pantalla recién después las filtra por reporte. La lista está cortada y el número es un piso, no un total.",
  "projectIA.rowCount": "{count} filas",
  "projectIA.manage": "Gestionar",

  "notes.new": "Nueva nota",
  "notes.filter.active": "Activas",
  "notes.filter.archived": "Archivadas",
  "notes.filter.all": "Todas",
  "notes.filter.report": "Reporte",
  "notes.filter.allReports": "Todos los reportes",
  "notes.search": "Buscar título o cuerpo…",
  "notes.col.title": "Título",
  "notes.col.reports": "Reportes",
  "notes.col.slice": "Recorte",
  "notes.col.window": "Ventana",
  "notes.col.visibility": "Visibilidad",
  "notes.col.actions": "Acciones",
  "notes.visibility.internal": "interna",
  "notes.visibility.mentionable": "mencionable",
  "notes.archived": "archivada",
  "notes.empty": "Todavía no hay notas de contexto para este proyecto.",
  "notes.archive": "Archivar",
  "notes.archiveConfirm":
    "¿Archivar esta nota? Los snapshots históricos conservan su rastro de auditoría.",
  "notes.regen": "Regenerar afectados",
  "notes.modal.createTitle": "Nueva nota",
  "notes.modal.editTitle": "Editar nota",
  "notes.field.title": "Título",
  "notes.field.body": "Detalle del evento",
  "notes.field.reports": "Reportes afectados",
  "notes.field.dateStart": "Desde (opcional)",
  "notes.field.dateEnd": "Hasta (opcional)",
  "notes.field.filters": "Filtros descriptivos (opcional)",
  "notes.addFilter": "Agregar filtro",
  "notes.filtersHint":
    "Describen DÓNDE pasó el evento. El LLM los lee como contexto, no como criterio de match.",
  "notes.field.referenceAllowed":
    "Permitir que el LLM mencione esta nota al cliente",
  "notes.referenceAllowedHint":
    "Si lo desmarcas, el LLM usa la nota como contexto interno pero NO la menciona en su narrativa. Útil para temas sensibles (legales, financieros).",
  "notes.errors.titleRequired": "El título es obligatorio.",
  "notes.errors.bodyRequired": "El cuerpo es obligatorio.",
  "notes.errors.reportRequired": "Selecciona al menos un reporte.",
  "notes.errors.dateOrder":
    "La fecha de inicio debe ser anterior a la de fin.",
  "notes.regen.title": "Regenerar snapshots afectados",
  "notes.regen.prompt":
    "{count} snapshots existentes solapan la ventana de fechas de esta nota. ¿Regenerarlos ahora para que el LLM los rehaga con el nuevo contexto?",
  "notes.regen.costHint":
    "Cada snapshot dispara una llamada al LLM. Es seguro re-ejecutar — los snapshots solo se sobrescriben.",
  "notes.regen.start": "Regenerar",
  "notes.regen.none": "Sin snapshots para regenerar",
  "notes.regen.later": "Más tarde",
  "notes.regen.done": "Listo — {count} regenerados",
  "notes.regen.failed": "Falló: {error}",

  "knowledge.add": "Agregar fila",
  "knowledge.empty": "Todavía no hay filas.",
  "knowledge.modal.addTitle": "Nueva fila",
  "knowledge.modal.editTitle": "Editar fila",
  "knowledge.field.name": "Nombre",
  "knowledge.field.content": "Contenido",
  "knowledge.contentRequired": "El contenido es obligatorio.",
  "knowledge.deleteConfirm": "¿Eliminar esta fila?",

  // ── Plan C 2.1+2.3 — administrador de integraciones unificado ──
  // Una seccion, dos pestanas, sobre una idea: la credencial se
  // registra una vez y se reusa. Los pasos numerados por canal NO estan
  // aca - los sirve builder/integrations_catalog.py para que existan
  // una sola vez.
  "nav.admin.integrations": "Integraciones",
  "nav.admin.sendPolicy": "Envío de reportes",
  "sendPolicy.title": "Envío de reportes por usuarios del portal",
  "sendPolicy.subtitle":
    "La beta de \"Enviar por correo\" del portal: a quién se puede enviar, cuánto, y con qué credencial. Apagada por defecto; una lista de dominios vacía la mantiene apagada igual. Cada intento de envío queda auditado abajo.",
  "sendPolicy.enabled": "Habilitar envío de reportes (beta)",
  "sendPolicy.neverConfigured": "Nunca configurada",
  "sendPolicy.needDomain": "Para habilitar hace falta al menos un dominio permitido — agrega uno abajo antes de guardar.",
  "sendPolicy.needCredential": "Para habilitar hace falta una credencial de envío — elige una abajo (regístrala primero en Integraciones).",
  "sendPolicy.loadFailed": "No se pudo cargar la política de envío, así que el formulario queda oculto: guardarlo ahora sobrescribiría la política guardada con valores vacíos.",
  "sendPolicy.domains": "Dominios de destinatarios permitidos",
  "sendPolicy.domainsHint": "Un destinatario fuera de estos dominios se rechaza, nombrando la dirección. Lista vacía = función apagada.",
  "sendPolicy.removeDomain": "Quitar {domain}",
  "sendPolicy.maxRecipients": "Máx. destinatarios por envío",
  "sendPolicy.quota": "Envíos diarios por usuario",
  "sendPolicy.quotaNone": "sin cuota",
  "sendPolicy.quotaHint": "Vacío = sin cuota (una decisión explícita, no un default).",
  "sendPolicy.credential": "Credencial de envío (email)",
  "sendPolicy.credentialNone": "Ninguna elegida",
  "sendPolicy.audit.title": "Auditoría de envíos",
  "sendPolicy.audit.total": "{n} intentos registrados",
  "sendPolicy.audit.empty": "Todavía no hay intentos de envío.",
  "sendPolicy.audit.pageOf": "Página {page} de {pages}",
  "sendPolicy.audit.prev": "Anterior",
  "sendPolicy.audit.next": "Siguiente",
  "sendPolicy.outcome.queued": "En cola",
  "sendPolicy.outcome.collapsed": "Unido a otro envío",
  "sendPolicy.outcome.schedule_queued": "Encolado por un schedule",
  "sendPolicy.outcome.capture_token": "Token de captura emitido",
  "sendPolicy.outcome.prompt_blocked": "Bloqueado por el guardián",
  "sendPolicy.outcome.sent": "Enviado",
  "sendPolicy.outcome.failed": "Falló",
  "sendPolicy.outcome.dead": "Falló (reintentos agotados)",
  "sendPolicy.outcome.reaped": "Desconocido (se interrumpió)",
  "sendPolicy.outcome.schedule_skipped": "No se envió (saltado)",
  "sendPolicy.outcome.schedule_failed": "No se envió (falló antes de enviar)",
  "sendPolicy.outcome.unknown":
    "Este resultado todavía no tiene etiqueta — se muestra lo que registró el backend.",
  "sendPolicy.audit.when": "Cuándo",
  "sendPolicy.audit.who": "Quién",
  "sendPolicy.audit.report": "Reporte",
  "sendPolicy.audit.recipients": "Destinatarios",
  "sendPolicy.audit.extras": "Llevó",
  "sendPolicy.audit.image": "imagen",
  "sendPolicy.audit.outcome": "Resultado",
  "delivery.field.credential": "Credencial del canal",
  "delivery.credential.pick": "Elige una credencial…",
  "delivery.credential.legacy":
    "Credencial configurada por el operador (heredada)",
  "delivery.credential.notNeeded": "No hace falta",
  "delivery.credential.noneAvailable":
    "Todavía no hay credenciales de {channel}. Registra una en Integraciones — el valor se guarda una sola vez y lo reusa cada envío que la elija.",
  "delivery.credential.goToIntegrations": "Abrir Integraciones",
  "delivery.error.credential": "Elige una credencial para este canal.",
  "integrations.title": "Integraciones",
  "integrations.introShort":
    "Canales de comunicación y fuentes de datos en un solo lugar. Una credencial se registra una vez acá y la reusa cada suscripción o proyecto que la apunte.",
  "operations.jobStatus.pending": "Pendiente",
  "operations.jobStatus.queued": "En cola",
  "operations.jobStatus.running": "Corriendo",
  "operations.jobStatus.completed": "Completado",
  "operations.jobStatus.failed": "Falló",
  "operations.jobStatus.skipped": "Salteado",
  "integrations.intro":
    "Canales de comunicación y fuentes de datos en un solo lugar. Una credencial se registra una vez acá y la reusa cada suscripción o proyecto que la apunte - así rotarla es una sola acción y no una por consumidor. Los valores se guardan cifrados y no se vuelven a mostrar.",
  "integrations.tabs.channels": "Canales",
  "integrations.tabs.sources": "Fuentes de datos",
  "integrations.comingSoon": "Próximamente",
  "integrations.cancelBtn": "Cancelar",
  "integrations.deleteBtn": "Eliminar",
  "integrations.testBtn": "Probar",
  "integrations.testing": "Probando...",
  "integrations.testOk": "OK",
  "integrations.testFailed": "El upstream dijo que no",
  "integrations.usedBy": "En uso por {count}:",
  "integrations.guide.title": "Lo que haces en tu propio sistema",
  "integrations.guide.warnings": "Conviene saberlo antes de empezar",
  "integrations.guide.docsLink": "Documentación oficial",
  "integrations.secret.configured": "Configurado",
  "integrations.secret.notConfigured": "Sin valor guardado",
  "integrations.secret.notNeeded": "No necesita credencial",
  "integrations.secret.fromEnv": "Valor en una variable de entorno",
  "integrations.secret.setBtn": "Cargar valor",
  "integrations.secret.replaceBtn": "Reemplazar",
  "integrations.secret.saveBtn": "Guardar valor",
  "integrations.secret.saving": "Guardando...",
  "integrations.secret.stored":
    "Valor guardado para {name}. No se vuelve a mostrar.",
  "integrations.secret.firstNotice":
    "El valor se guarda cifrado y nunca vuelve a esta pantalla - ni a este formulario, ni a un log, ni al detalle de un job.",
  "integrations.secret.replaceNotice":
    "Ya hay un valor guardado. Guardar lo REEMPLAZA; no hay nada que editar porque no se puede volver a leer. La referencia no cambia, así que cada suscripción o proyecto que la use toma el valor nuevo en su próxima corrida.",
  "integrations.backend.title":
    "El backend de secretos todavía no está prendido",
  "integrations.backend.intro":
    "Hasta que lo esté, los valores de credenciales no se pueden guardar desde esta pantalla. Es la única parte de integraciones que no se puede hacer desde la web: el backend al que escribe un formulario web tiene que habilitarse con variables de deploy. Los pasos exactos:",
  "integrations.channels.addBtn": "Agregar credencial",
  "integrations.channels.nameLabel": "Nombre",
  "integrations.channels.nameHint":
    "Pasa a ser parte del path del secreto, así que acepta minúsculas, dígitos, '.', '_' y '-'. Después no se puede cambiar - el path se construye con él.",
  "integrations.channels.descriptionLabel": "Descripción (opcional)",
  "integrations.channels.createBtn": "Registrar",
  "integrations.channels.unused": "Todavía no la usa ninguna suscripción.",
  "integrations.channels.deleteConfirm":
    "¿Eliminar la credencial '{name}' y su valor guardado? Se rechaza mientras una suscripción la siga usando.",
  "integrations.sources.title": "Conexiones a fuentes de datos",
  "integrations.sources.intro":
    "Una conexión puede estar compartida con todos los proyectos de este cliente, o pertenecer a uno. Compartirla es lo que hace posible 'un warehouse, una credencial' en vez de una copia por proyecto.",
  "integrations.sources.empty": "Todavía no hay conexiones.",
  "integrations.sources.shared": "Compartida",
  "integrations.sources.ownedBy": "Solo {slug}",
  "integrations.sources.shareBtn": "Compartir con todos los proyectos",
  "integrations.sources.unused": "Todavía no la apunta ningún proyecto.",
  "integrations.sources.deleteConfirm":
    "¿Eliminar la conexión '{name}'? Se rechaza mientras la fuente de datos de un proyecto la siga apuntando.",
  // Migración 035 — una conexión con dueño compartida con un conjunto
  // elegido de proyectos, el punto medio entre "solo el dueño" y "todos".
  "integrations.sources.alsoShared": "+ {count} más",
  "integrations.sources.grantBtn": "Compartir con…",
  "integrations.sources.grantTitle":
    "Dejar que estos proyectos también usen '{name}'",
  "integrations.sources.grantHint":
    "El dueño ({slug}) siempre puede. Compartir con todos los proyectos es el botón de al lado. Destildar un proyecto cuya fuente de datos todavía depende de esta conexión se rechaza, nombrando el proyecto.",
  "integrations.sources.grantNoOthers": "Todavía no hay otro proyecto.",
  "integrations.sources.grantSave": "Guardar compartir",
  "integrations.sources.grantSaved": "Compartir de '{name}' guardado.",
  // ── PR-6: Operaciones cockpit + Guía del builder ──────────────
  "nav.admin.operations": "Operaciones",
  // «Caché» page (Administración → Caché, 2026-10-26).
  "nav.admin.cache": "Caché",
  "cache.title": "Caché",
  "cache.subtitle": "Qué se calcula antes de publicar cada recarga, para que los reportes abran desde el caché.",
  "cache.day.title": "Precalentado del día (estimación)",
  "cache.day.breakdown": "Valores por defecto {defaults} · todas las combinaciones +{extra} · {languages} idioma(s)",
  "cache.day.note": "Estimación con la última recarga de cada proyecto, {workers} en paralelo, ámbito «todos».",
  "cache.day.unmeasured": "{count} reporte(s) todavía sin datos: no entran al total.",
  "cache.locales.title": "Idiomas a precalentar",
  "cache.locale.en": "Inglés",
  "cache.locale.es": "Español",
  "cache.locale.pt": "Portugués (Brasil)",
  "cache.locales.help": "Solo cuentan para los reportes que usan el idioma en su SQL. Un idioma sin marcar se calcula la primera vez que alguien lo abre y desde ahí queda en caché.",
  "cache.alert.title": "Alerta por correo",
  "cache.alert.label": "Si el precalentado de un proyecto pasa de (minutos)",
  "cache.alert.help": "No frena la publicación. Si el precalentado falla, la recarga no se publica y el correo sale siempre.",
  "cache.settings.save": "Guardar",
  "cache.settings.saved": "Guardado. Rige desde la próxima recarga de cada proyecto.",
  "cache.settings.needOneLocale": "Marca al menos un idioma.",
  "cache.settings.badMinutes": "El umbral tiene que ser un número entero de minutos entre 1 y {max}.",
  "cache.option.saved": "Guardado. Rige desde la próxima recarga de {project}.",
  "cache.option.yes": "Sí",
  "cache.option.no": "No",
  "cache.option.aria": "Precalentar todas las combinaciones de {report}",
  "cache.option.followsOn": "Sigue a los tabs: sí",
  "cache.option.followsOff": "Sigue a los tabs: no",
  "cache.groupOptions": "Opciones del grupo {group}",
  "cache.readsLocale": "usa el idioma",
  "cache.col.report": "Reporte",
  "cache.col.buttons": "Filtros-botón y combinaciones",
  "cache.col.option": "Todas las combinaciones",
  "cache.col.cost": "Suma por recarga",
  "cache.noData": "sin datos todavía",
  "cache.ifEnabled": "si se activa",
  "cache.last.none": "Sin precalentado todavía",
  "cache.last.failed": "Falló {when}: quedan los datos anteriores",
  "cache.last.overThreshold": "Pasó el umbral {when} · {dur}",
  "cache.last.complete": "Completo {when} · {dur}",
  "cache.project.desc": "{count} reporte(s) con filtros-botón · {dur} por recarga",
  "cache.without.title": "{count} proyecto(s) sin filtros-botón",
  "cache.without.desc": "Solo se precalientan los valores por defecto · {dur} por día",
  "cache.broken.title": "Reportes publicados que no se pueden armar: cada recarga de su proyecto falla hasta corregirlos",
  "cache.empty.title": "Ningún reporte tiene filtros-botón",
  "cache.empty.desc": "Cada recarga precalienta la vista por defecto de todos los reportes.",
  "nav.guide": "Guía del builder",
  "common.refresh": "Actualizar",
  // Delivery (track B2)
  "hub.tab.delivery": "Envíos",
  // ── Track B3 — chat embebible para sitios de terceros ─────────
  "hub.tab.embed": "Embed",
  "common.remove": "Quitar",
  "embed.form.title": "Emitir una clave de embed",
  "embed.form.help":
    "Una clave de embed permite que un visitante ANÓNIMO de un sitio externo converse con un reporte de este proyecto. Queda acotada a un cliente + un contexto de chat, y solo se puede llamar desde los orígenes exactos que listes.",
  "embed.form.submit": "Emitir clave",
  "embed.form.incomplete":
    "Etiqueta, cliente, contexto de chat y al menos un origen son obligatorios.",
  "embed.field.label": "Etiqueta",
  "embed.field.labelPlaceholder": "página de marketing de cliente.example",
  "embed.field.clientKey": "Clave de cliente",
  "embed.field.chatContextKey": "Contexto de chat",
  "embed.field.chatContextPick": "Elige un contexto de chat…",
  "embed.field.origins": "Orígenes permitidos",
  "embed.field.originAdd": "Agregar origen",
  "embed.field.originsHelp":
    "Valores exactos scheme://host[:port], uno por chip. Sin wildcards: '*' y 'null' se rechazan, y http://localhost:8899 es un origen DISTINTO de http://127.0.0.1:8899.",
  "embed.field.maxToolIterations": "Presupuesto de tools por respuesta",
  "embed.field.maxToolIterationsHelp":
    "1-8. Más bajo que el default autenticado (8) a propósito — un turno anónimo debe costar menos.",
  "embed.field.dailyQuota": "Cuota diaria de mensajes",
  "embed.field.dailyQuotaHelp":
    "Vacío = SIN cuota. Una clave sin medir en una página con tráfico consume crédito de LLM hasta que se corta.",
  "embed.field.allowFreeSql": "Permitir SQL libre (apagado por defecto)",
  "embed.field.allowFreeSqlWarning":
    "Peligro: habilita que el modelo corra SELECTs arbitrarios sobre el DuckDB de este proyecto, guiado por una pregunta que CUALQUIER visitante del sitio del cliente puede escribir. Déjalo apagado salvo que tengas una razón concreta.",
  "embed.error.badOrigin":
    "No es un origen válido. Usa scheme://host[:port] sin path, query ni wildcard.",
  "embed.issued.title": "Clave emitida",
  "embed.issued.warning":
    "Cópiala ahora — solo se guarda un hash, así que no se puede volver a mostrar. Una clave perdida se reemite, no se recupera.",
  "embed.issued.key": "Clave de embed (texto plano)",
  "embed.issued.snippet": "Snippet listo para pegar",
  "embed.list.empty": "Todavía no hay claves de embed para este proyecto.",
  "embed.col.label": "Clave",
  "embed.col.scope": "Alcance",
  "embed.col.origins": "Orígenes",
  "embed.col.policy": "Política",
  "embed.col.usage": "Uso",
  // Single-brace interpolation — ver nota EN.
  "embed.col.iterations": "{n} rondas de tools",
  "embed.col.total": "{n} en total",
  "embed.status.active": "Activa",
  "embed.status.revoked": "Revocada",
  "embed.badge.freeSql": "SQL libre ACTIVO",
  "embed.quota.none": "sin cuota",
  "embed.action.revoke": "Revocar",
  "embed.action.enableFreeSql": "Activar SQL libre",
  "embed.action.disableFreeSql": "Desactivar SQL libre",
  "delivery.title": "Envíos programados",
  "delivery.subtitle":
    "Envía los insights IA de este proyecto a un canal según un horario. Las credenciales se eligen de Integraciones, nunca se escriben acá.",
  "delivery.empty": "Todavía no hay envíos programados.",
  "delivery.noJobs": "Todavía no hubo intentos de envío.",
  "delivery.channel.webhook": "Webhook",
  "delivery.channel.email": "Email",
  "delivery.channel.slack": "Slack",
  "delivery.channel.teams": "Teams",
  "delivery.field.name": "Nombre",
  "delivery.field.report": "Reporte",
  "delivery.field.channel": "Canal",
  "delivery.field.cron": "Cron",
  "delivery.field.timezone": "Zona horaria",
  "delivery.field.locale": "Idioma",
  "delivery.locale.en": "Inglés",
  "delivery.locale.es": "Español",
  "delivery.field.recipients": "Destinatarios",
  "delivery.field.subjectPrefix": "Prefijo del asunto",
  "delivery.field.url": "URL del webhook",
  "delivery.field.mode": "Modo",
  "delivery.field.slackChannel": "Canal de Slack",
  "delivery.field.maxContentAge": "Antigüedad máxima (días)",
  "delivery.field.onMissingContent": "Si falta contenido",
  "delivery.field.enabled": "Activo",
  "delivery.col.nextRun": "Próxima corrida",
  "delivery.col.lastStatus": "Último estado",
  "delivery.col.schedule": "Programación",
  // ── Plan F, F0 — el asistente que reemplazó al form plano cron+ref ──
  "delivery.wizard.titleNew": "Nuevo envío programado",
  "delivery.wizard.titleEdit": "Editar envío programado",
  "delivery.wizard.step.what": "Qué mando",
  "delivery.wizard.step.who": "A quién",
  "delivery.wizard.step.when": "Cuándo",
  "delivery.wizard.step.review": "Revisar y probar",
  "delivery.wizard.back": "Atrás",
  "delivery.wizard.next": "Siguiente",
  "delivery.wizard.saveAndTest": "Guardar y probar",
  "delivery.wizard.savedTestFailed": "Se guardó, pero la prueba falló:",
  "delivery.wizard.namePlaceholder": "Resumen semanal para el equipo",
  "delivery.schedule.kind.daily": "Todos los días",
  "delivery.schedule.kind.weekly": "Cada semana",
  "delivery.schedule.kind.monthly": "Cada mes",
  "delivery.schedule.kind.custom": "Personalizada (cron)",
  "delivery.schedule.time": "Hora",
  "delivery.schedule.monthDay": "Día del mes",
  "delivery.schedule.monthDayHint":
    "Los meses que no tienen ese día se saltean — el día 31 no dispara en abril.",
  "delivery.schedule.summary.daily": "Todos los días a las {time}",
  "delivery.schedule.summary.weekly":
    "Cada semana, los {days}, a las {time}",
  "delivery.schedule.summary.monthly":
    "El día {day} de cada mes, a las {time}",
  "delivery.schedule.summary.custom": "Programación personalizada: {cron}",
  "delivery.weekday.0": "domingo",
  "delivery.weekday.1": "lunes",
  "delivery.weekday.2": "martes",
  "delivery.weekday.3": "miércoles",
  "delivery.weekday.4": "jueves",
  "delivery.weekday.5": "viernes",
  "delivery.weekday.6": "sábado",
  "delivery.error.weekdays": "Elige al menos un día.",
  "delivery.advanced.title": "Avanzado",
  "delivery.advanced.cronHint":
    "Editar el cron pasa la programación a modo personalizado; el texto se guarda tal cual.",
  "delivery.slackMode.incomingWebhook": "Webhook entrante (recomendado)",
  "delivery.slackMode.botToken": "Bot + canal",
  "delivery.review.enabledHint":
    "Déjalo apagado hasta que una prueba se vea bien — se puede activar en cualquier momento desde esta pantalla.",
  "delivery.review.testOk": "No se envió nada",
  "delivery.teams.guide.title": "Cómo obtener la URL de Workflow de Teams",
  "delivery.teams.guide.doneInTeams": "Se hace en Microsoft Teams, no acá",
  "delivery.teams.guide.retiredNotice":
    "Si ya tienes una dirección de webhook de Teams que contiene webhook.office.com, está muerta. Microsoft apagó los connectors de Office 365 entre el 18 y el 22 de mayo de 2026 y esas direcciones no van a entregar nunca más. Nuestro envío las rechaza y explica por qué. Hay que crear un Workflow.",
  "delivery.teams.guide.intro":
    "Solo lo puede crear alguien del canal de Teams de destino, dentro de tu propio tenant de Microsoft. Pásale estos pasos al dueño del canal - toma alrededor de un minuto - y pídele la dirección que sale al final.",
  "delivery.teams.guide.step1":
    "En Microsoft Teams, abre el canal que tiene que recibir el reporte. Tiene que ser un canal dentro de un equipo, no un chat uno a uno.",
  "delivery.teams.guide.step2":
    "Pasa el mouse sobre el nombre del canal en la lista de la izquierda y haz clic en el botón ... (Más opciones) que aparece al lado.",
  "delivery.teams.guide.step3":
    "Elige Workflows. Teams abre una galería de plantillas de workflow.",
  "delivery.teams.guide.step4":
    'Elige la plantilla "Send webhook alerts to a channel". Su disparador se llama "When a Teams webhook request is received".',
  "delivery.teams.guide.step5":
    "Teams muestra con qué cuenta de Microsoft va a correr el workflow - esa cuenta queda como dueña, y eso importa (ver abajo). Confirma el equipo y el canal, y guarda.",
  "delivery.teams.guide.step6":
    "Copia la dirección https que muestra el workflow al crearse. Cópiala antes de cerrar el diálogo: con eso solo alcanza para postear en ese canal, así que trátala como una credencial.",
  "delivery.teams.guide.step7":
    "Entrégale esa dirección al operador de la plataforma, que la setea como valor de {envVar} en el contenedor del builder. Este formulario guarda una referencia, nunca el valor de una credencial.",
  // Plan C 2.2: con un backend de secretos escribible, el ultimo paso
  // dejo de ser "pídele a un operador que setee una env var". Convive
  // con el viejo, no lo reemplaza, porque el panel de Envios sigue
  // apuntando a env vars para las referencias anteriores a la 033.
  "delivery.teams.guide.step7Web":
    "Pega esa dirección en el campo de arriba y guarda. Se guarda cifrada y no se vuelve a mostrar nunca - si necesitas cambiarla, la reemplazas.",
  "delivery.teams.guide.refNote":
    "La URL del Workflow se registra como credencial de Teams en Integraciones; los envíos programados después la eligen de una lista. Ya nadie escribe una referencia a mano.",
  "delivery.teams.guide.warnTitle": "Dos cosas que después muerden",
  "delivery.teams.guide.warnOwner":
    "El workflow le pertenece a la PERSONA que lo creó, no al canal. Si esa persona se va de la organización o pierde su licencia, los reportes dejan de llegar sin aviso. Apenas lo crees, abre el workflow en Power Automate y agrega co-owners.",
  "delivery.teams.guide.warnLimits":
    "Microsoft rechaza mensajes de más de 28 KB y aplica throttling por encima de 4 requests por segundo. Un reporte programado es un mensaje, así que esto solo importa cuando muchos envíos disparan en el mismo minuto - escalona los minutos del cron.",
  "delivery.teams.guide.unavailableTitle": "Lo que este camino NO da",
  "delivery.teams.guide.noBotIdentity":
    "No hay nombre ni ícono de remitente propios: los mensajes llegan bajo la app Workflows.",
  "delivery.teams.guide.noInteractive":
    "No hay botones interactivos dentro del mensaje. La tarjeta lleva un link que abre el reporte completo en el portal.",
  "delivery.teams.guide.docsLink":
    "El anuncio de Microsoft sobre el retiro de los connectors",
  "delivery.onMissing.fail": "Fallar el envío (recomendado)",
  "delivery.onMissing.triggerWatcher":
    "Pedir al portal una pasada del watcher",
  "delivery.status.pending": "Pendiente",
  "delivery.status.running": "Corriendo",
  "delivery.status.sent": "Enviado",
  "delivery.sentMeaning":
    "Enviado significa que el servidor de correo aceptó el mensaje. Si una dirección no existe, el rebote puede llegar minutos después y no se refleja aquí.",
  "delivery.status.failed": "Falló",
  "delivery.status.dead": "Descartado",
  "delivery.status.disabled": "Inactivo",
  "delivery.action.new": "Nuevo envío",
  "delivery.action.test": "Probar",
  "delivery.action.sendNow": "Enviar ahora",
  "delivery.action.retry": "Reintentar",
  "delivery.action.delete": "Borrar",
  "delivery.action.history": "Historial",
  "delivery.confirmSendNow":
    "¿Enviar este envío ahora mismo, al destino real?",
  "delivery.confirmDelete":
    // Ver nota EN: interpolación de UNA sola llave en esta instancia.
    "¿Borrar el envío {name}? Su historial se conserva.",
  "delivery.sendNowResult": "El envío terminó con estado: {status}",
  "delivery.test.title":
    "Vista previa - datos al {date} ({locale}). No se envió nada.",
  "delivery.error.cron":
    "El cron debe tener 5 campos (minuto hora día mes día-semana).",
  "delivery.error.url": "Debe empezar con https://",
  "delivery.error.recipients": "Se requiere al menos un destinatario.",
  "delivery.error.slackChannel": "El canal es requerido en modo bot_token.",
  "delivery.recent.title": "Envíos recientes",
  "delivery.recent.empty": "Todavía no hay envíos.",
  "operations.title": "Operaciones",
  "operations.subtitle":
    "Scheduler global, programación por proyecto y corridas vivas — el detalle (historial, drawer de reconciliación) vive en el tab Auto-refresh de cada proyecto.",
  "operations.scheduler.title": "Scheduler",
  "operations.scheduler.enabled": "activo",
  "operations.scheduler.disabled": "pausado",
  // Tres estados, tres PALABRAS — ver nota EN.
  "operations.scheduler.running": "corriendo",
  "operations.scheduler.noLeader": "sin líder",
  "operations.scheduler.paused": "en pausa",
  "operations.scheduler.lease": "Lease",
  "operations.scheduler.lastTick": "Último tick",
  "operations.scheduler.pause": "Pausar scheduler",
  "operations.scheduler.resume": "Reanudar scheduler",
  "operations.scheduler.confirmDisable":
    "¿Pausar el scheduler global? Ningún cron va a disparar hasta reanudarlo (Run now manual sigue funcionando). Un restart del pod lo re-activa.",
  "operations.scheduler.note":
    "Kill-switch a nivel proceso. La intención persistida es el cron de cada proyecto — pausar acá frena todos los schedules a la vez.",
  "operations.projects.title": "Programación por proyecto",
  "operations.projects.project": "Proyecto",
  "operations.projects.schedule": "Programación",
  "operations.projects.lastRun": "Última corrida",
  "operations.projects.nextRun": "Próxima",
  "operations.projects.manualOnly": "solo manual",
  "operations.projects.empty": "Todavía no hay proyectos.",
  "operations.projects.hubLink": "hub",
  "operations.runNow": "Ejecutar ahora",
  "operations.runStarted": "Reload iniciado para {slug}.",
  "operations.forceReap": "Forzar recolección",
  "operations.forceReapTitle":
    "Aborta primero el job en vuelo y arranca un reload nuevo",
  "operations.confirmForceReap":
    "¿Forzar el aborto del job en vuelo de {slug} y arrancar un reload nuevo? Úsalo solo cuando un job está genuinamente trabado.",
  "operations.jobs.title": "Corridas recientes",
  "operations.jobs.hint": "cross-proyecto, más nuevas primero",
  "operations.jobs.status": "Estado",
  "operations.jobs.phase": "Fase",
  "operations.jobs.started": "Inicio",
  "operations.jobs.duration": "Duración",
  "operations.jobs.recon": "Reconciliación",
  "operations.jobs.empty": "Todavía no hay corridas.",
  // ── Operaciones › Ámbitos de datos (Plan J, J6) ────────────────────
  // Single-brace interpolation — ver nota EN.
  "operations.scopes.title": "Ámbitos de datos",
  "operations.scopes.summary": "{scopes} ámbito(s) en {projects} proyecto(s) · {size} en disco · {free} libres",
  "operations.scopes.stuckCount": "{count} trabado(s)",
  "operations.scopes.failedCount": "{count} con fallo",
  "operations.scopes.sizeIncomplete": "Total en disco incompleto — sin medir: {projects}",
  "operations.scopes.col.project": "Proyecto",
  "operations.scopes.col.scope": "Ámbito",
  "operations.scopes.col.status": "Último build",
  "operations.scopes.col.freshness": "Vigencia",
  "operations.scopes.col.rows": "Filas",
  "operations.scopes.col.size": "Tamaño",
  "operations.scopes.col.lock": "Lock tomado",
  "operations.scopes.col.when": "Cuándo",
  "operations.scopes.status.built": "Construido",
  "operations.scopes.status.building": "Construyendo",
  "operations.scopes.status.failed": "Falló",
  "operations.scopes.status.noMapping": "Corte vacío",
  "operations.scopes.status.notApplicable": "No aplica",
  "operations.scopes.status.neverBuilt": "Nunca se construyó",
  "operations.scopes.status.stuck": "Trabado",
  "operations.scopes.stuckHint":
    "Lleva construyendo más allá del umbral y bloquea todo build nuevo de este ámbito en este proyecto. Nada lo destraba solo: el comando exacto está en el historial de builds, en Accesos › Ámbitos.",
  "operations.scopes.unknownStatus":
    "Este estado todavía no tiene etiqueta: se muestra el valor crudo de la base.",
  "operations.scopes.freshnessHint":
    "Si el ámbito se cortó del archivo del proyecto que está vivo ahora mismo.",
  "operations.scopes.fresh": "Al día",
  "operations.scopes.stale": "Desactualizado",
  "operations.scopes.lockHint":
    "Tiempo medido adentro del lock de escritura del cliente, y cuánto de eso no explican los tiempos por paso.",
  "operations.scopes.notMeasured": "sin medir",
  "operations.scopes.gap": "{pct}% sin explicar",
  "operations.scopes.empty.title": "Todavía no se construyó ningún ámbito",
  "operations.scopes.empty.body":
    "Un ámbito recibe su archivo en la próxima recarga del proyecto, o desde Accesos › Ámbitos › Reconstruir.",
  "operations.scopes.orphans.title": "Ámbitos derivados que ya nadie resuelve",
  "operations.scopes.orphans.body":
    "Sus archivos siguen en disco y nada los borra automáticamente. Compáralos con el listado de archivos antes de borrar algo a mano.",
  "operations.scopes.gaps.title": "Lo que estos números no cubren",
  "operations.scopes.gaps.body":
    "Léelos con estos límites presentes: cada uno es una propiedad del código actual, no de esta pantalla.",
  "operations.scopes.gaps.noTimings":
    "Un build que falla no registra ningún tiempo, así que toda cifra de lock acá describe solo builds exitosos, y los holds más largos son justamente los que fallan.",
  "operations.scopes.gaps.noEnd":
    "Solo un build exitoso recibe fecha de término, así que una duración derivada de ella dejaría afuera, en silencio, a todos los fallidos y vacíos.",
  "operations.scopes.gaps.noElapsed":
    "El reloj de un ámbito suelto no se guarda: sobrevive solo en el job del loader de la recarga que lo construyó.",
  "operations.scopes.gaps.untranslated":
    "Este límite todavía no tiene texto traducido: se muestra el del backend.",
  "guide.title": "Guía del builder",
  "guide.subtitle":
    "Cómo funciona la plataforma, capítulo por capítulo — de la data cruda a quién puede leer qué filas.",
  "guide.detailsTitle": "Detalles que importan",
  "guide.data.title": "Datos y vistas",
  "guide.data.intro":
    "Del parquet en S3 a los elementos que renderea cada dashboard.",
  "guide.data.node1": "S3",
  "guide.data.node1sub": "parquet (OBT)",
  "guide.data.node2": "Loader",
  "guide.data.node2sub": "descarga + swap",
  "guide.data.node3": "DuckDB",
  "guide.data.node3sub": "un archivo por proyecto",
  "guide.data.node4": "Vistas",
  "guide.data.node4sub": "por reporte",
  "guide.data.node5": "Elementos",
  "guide.data.node5sub": "KPIs · charts · tablas",
  "guide.data.step1":
    "El proyecto declara UNA fuente de datos (el parquet OBT en S3) en su tab Datos.",
  "guide.data.step2":
    "El loader lo descarga y escribe la tabla raw en el DuckDB propio del proyecto con un swap atómico.",
  "guide.data.step3":
    "Cada reporte declara SUS PROPIAS vistas sobre el OBT (sources[] del config) — el reload las re-materializa todas.",
  "guide.data.step4":
    "Cada elemento (KPI, chart, tabla, filtro) corre SQL contra las vistas del reporte — nunca contra la tabla raw directo.",
  "guide.data.step5":
    "El portal lee el mismo archivo DuckDB y recoge versiones nuevas automáticamente vía la key de versión per-proyecto.",
  "guide.data.detail1":
    "Cada reporte es dueño de sus vistas, aunque dupliquen las de otro — autonomía sobre deduplicación (regla durable del equipo).",
  "guide.data.detail2":
    "Un piso de fecha hardcodeado en el SQL de una vista corta historia en silencio — las vistas llevan TODA la data; la ventana la hacen los filtros.",
  "guide.data.detail3":
    "Todo lo derivado de la data de un proyecto abre la conexión per-proyecto (slug) — la global es el blindspot clásico.",
  // ── Plan J J2 — capítulo 2 de la Guía: Dimensiones ───────────
  "guide.dimensions.title": "Dimensiones",
  "guide.dimensions.intro":
    "Una sola definición de una columna — los filtros del reporte la ofrecen y los ámbitos de acceso cortan por ella.",
  "guide.dimensions.node1": "Tabla raw",
  "guide.dimensions.node1sub": "la del proyecto",
  "guide.dimensions.node2": "Dimensión",
  "guide.dimensions.node2sub": "columna · mapeo · SQL",
  "guide.dimensions.node3": "Enriquecimiento",
  "guide.dimensions.node3sub": "en la recarga",
  "guide.dimensions.node4": "Vistas",
  "guide.dimensions.node4sub": "ven la columna",
  "guide.dimensions.node5": "La usan",
  "guide.dimensions.node5sub": "filtros y ámbitos",
  "guide.dimensions.step1":
    "Las dimensiones viven en Proyecto › Datos › Dimensiones. Una dimensión es UNA definición de una columna: los filtros del reporte la ofrecen y los ámbitos de acceso cortan por ella, así que no hay una segunda lista que mantener sincronizada. Declararla no cambia nada hasta la próxima recarga.",
  "guide.dimensions.step2":
    "Origen «columna»: elige una columna que la tabla raw ya trae. Nombra esa columna de forma explícita —nunca se deduce de la clave de la dimensión— y marca «Ámbito» en «Usable para», porque el default es no servir para nada.",
  "guide.dimensions.step3":
    "Origen «mapeo»: elige el Maestro, la pestaña, la columna clave y la columna valor. Esa pestaña la mantiene el CLIENTE (país → región comercial, vendedor → cartera KAM), y todo valor que esté en los datos tiene que tener su fila ahí.",
  "guide.dimensions.step4":
    "Origen «calculada»: una sola expresión SELECT sobre las columnas de la propia fila — la marca 2P, el precio en cuotas, la definición de 1P/3P que cambia por país. Sin subconsulta, sin función de ventana y sin agregado: el servidor la prueba contra el archivo real antes de guardarla.",
  "guide.dimensions.step5":
    "Recarga. La fase enrich_dimensions agrega a la tabla raw una columna real por cada dimensión de mapeo y calculada — adentro de la transacción de escritura, antes de que se arme la primera vista — así ninguna vista cambia su SQL y los ámbitos la ven. Las fases del job y su result_json son donde lees qué hizo.",
  "guide.dimensions.step6":
    "Cuando avisa que hay valores sin mapeo: la fase se detuvo, no se publicó nada, los datos anteriores siguen vivos, y el mensaje nombra los valores, cuántas filas cubren y qué pestaña no los tiene. Agrega esas filas al Maestro y vuelve a correr la recarga.",
  "guide.dimensions.detail1":
    "Se define una vez y se usa dos: la misma dimensión alimenta los filtros del reporte y los ámbitos de acceso. No hay una segunda lista que mantener sincronizada y, por lo tanto, no hay forma de que las dos se contradigan.",
  "guide.dimensions.detail2":
    "El enriquecimiento reescribe la tabla raw en el lugar y corre ANTES de la primera vista. Ese orden es todo el diseño: una columna agregada después de las vistas es invisible para ellas y para el predicado del ámbito, que resuelve sobre un DESCRIBE de la tabla raw del archivo del proyecto.",
  "guide.dimensions.detail3":
    "No hay bucket «Otros» y el valor nunca queda en NULL. Un valor de los datos sin fila en el Maestro hace fallar la fase, nombrando el valor y cuántas filas cubre — una fila que se cayera en silencio de todos los ámbitos no la vería nadie.",
  "guide.dimensions.detail4":
    "Una dimensión que nadie marcó como usable para ámbitos hace que todo ámbito que la nombre resuelva «no aplica» en este proyecto, y nada en pantalla parece roto. El default es fail-closed, no «sin definir»: hay que marcarlo.",
  // ── Guía, capítulos 3-5: accesos (Plan J, J3) ────────────────────
  "guide.scopes.title": "Ámbitos",
  "guide.scopes.intro":
    "Quién lee qué filas, decidido por un archivo real por proyecto — nunca por un WHERE que alguien se puede olvidar.",
  "guide.scopes.node1": "Reglas",
  "guide.scopes.node1sub": "dimensión = valores",
  "guide.scopes.node2": "Predicado",
  "guide.scopes.node2sub": "AND · IN",
  "guide.scopes.node3": "Archivo",
  "guide.scopes.node3sub": "uno por proyecto",
  "guide.scopes.node4": "Lector",
  "guide.scopes.node4sub": "abre solo ese",
  "guide.scopes.node5": "Estado",
  "guide.scopes.node5sub": "listo o cerrado",
  "guide.scopes.step1":
    "Los ámbitos viven en Accesos › Ámbitos y son del tenant entero, no de un proyecto. El id ES el nombre del archivo — de 1 a 40 caracteres de [a-z0-9_-] — y nunca se normaliza, así que us-amazon y us_amazon son dos ámbitos distintos. El nombre es lo que lee la gente. Crearlo todavía no mueve nada en el disco.",
  "guide.scopes.step2":
    "Agrega reglas: una dimensión y una lista de valores tomados de los datos. Las reglas se combinan con Y, y los valores de una misma regla con O. Los tipos no son cosméticos: una columna de texto con la regla escrita como el número 1 se rechaza, porque DuckDB castearía la columna y «1», «01» y «1.0» entrarían todos, entregando tres vendedores distintos a una sola persona.",
  "guide.scopes.step3":
    "«Proyectos donde aplica» responde proyecto por proyecto. El que declara todas las dimensiones que nombran las reglas muestra su estado y su cantidad de filas; al que le falta una muestra «no aplica» y ahí no otorga nada — lo que puede ser correcto, o puede ser que la dimensión nunca se marcó como usable para ámbitos.",
  "guide.scopes.step4":
    "Editar las reglas exige reconocer el impacto. El ensayo nombra los grupos y la cantidad de personas a las que llega el cambio, y el guardado tiene que devolver esa lista, así nadie cambia quién ve qué sin haber leído la consecuencia. La respuesta después dice con todas las letras que no se reconstruyó nada.",
  "guide.scopes.step5":
    "«Reconstruir» corta el archivo, de un proyecto, ahora. Toma el lock de escritura del tenant mientras corre y puede durar más que el proxy que trajo el pedido — cuando eso pasa, la fuente de verdad es la lista de builds y no el pedido que falló. La próxima recarga del proyecto también lo reconstruye, incluida la recarga que se saltea porque los datos no cambiaron.",
  "guide.scopes.step6":
    "«Todo» es el ámbito reservado y no tiene archivo propio: resuelve literalmente al archivo del proyecto. No se puede reconstruir ni se puede editar hasta volverlo restringido, y lo lleva todo el mundo hasta que alguien lo mete en un grupo que carga otro.",
  "guide.scopes.detail1":
    "Quien está en varios grupos lee la UNIÓN de sus ámbitos — las uniones amplían, nunca intersectan. Quien está en un solo grupo resuelve al ámbito de ese grupo y no a una copia, así que su archivo ya existe y ninguna edición lo deja afuera mientras espera una reconstrucción.",
  "guide.scopes.detail2":
    "Cuando la unión no se puede escribir como un solo juego de reglas, se rechaza nombrando los dos grupos, en vez de fusionarse. Fusionar «US y amazon» con «CL y meli» le entregaría al lector US-con-meli y CL-con-amazon: filas que ningún grupo otorgó, en un archivo con la forma correcta y una cantidad de filas creíble, sin nada más abajo que pueda detectarlo.",
  "guide.scopes.detail3":
    "Un build fallido nunca bloquea el proyecto y nunca le amplía el acceso a nadie: los lectores se quedan con el archivo anterior, la recarga publica como siempre, y el único lugar donde se ve es la columna de estado de esta pantalla. Alguien leyendo números VIEJOS sin ningún error en pantalla es como se ve una reconstrucción que falló.",
  "guide.scopes.detail4":
    "Borrar un ámbito que alguien resuelve se rechaza, nombrando a quién lo resuelve, y un ámbito sin archivo responde 503 en vez del archivo del proyecto. No hay fallback a los datos sin restricción — ese fallback es justo la fuga que todo este diseño evita.",
  "guide.groups.title": "Grupos",
  "guide.groups.intro":
    "Un grupo es el único lugar donde se escribe el acceso: un ámbito, una matriz de contenido, y las personas que heredan los dos.",
  "guide.groups.node1": "Grupo",
  "guide.groups.node1sub": "para quién",
  "guide.groups.node2": "Ámbito",
  "guide.groups.node2sub": "qué filas",
  "guide.groups.node3": "Contenido",
  "guide.groups.node3sub": "reportes · secciones",
  "guide.groups.node4": "Miembros",
  "guide.groups.node4sub": "personas",
  "guide.groups.node5": "La persona",
  "guide.groups.node5sub": "portal · correo · chat",
  "guide.groups.step1":
    "Los grupos se crean acá, en el builder, y los crea Omnitok. El admin del cliente asigna personas a los grupos que ya existen y nunca crea uno, porque crear un grupo es decidir qué SIGNIFICA un rol en esa cuenta.",
  "guide.groups.step2":
    "Pestaña «Ámbito»: elige el ámbito del grupo, uno solo. Esa única decisión define qué filas lee cada miembro del grupo, en todos los proyectos donde el ámbito aplica.",
  "guide.groups.step3":
    "Pestaña «Contenido»: la matriz de reportes, secciones y acciones. Una celda que nadie tocó cae al default del rol y no a «denegado» — salvo «Verlo», que no tiene default: un reporte que ningún grupo suyo abre no se abre, y ningún predeterminado por rol lo rescata. El permiso de una persona es el O de las celdas explícitas de todos sus grupos, así que una denegación en un grupo no la borra otro grupo que simplemente no opina.",
  "guide.groups.step4":
    "Pestaña «Miembros»: agrega y quita personas. Agregar a alguien recalcula su ámbito efectivo en la misma transacción, así que la respuesta en la pantalla de esa persona ya es la correcta antes de que salgas de esta.",
  "guide.groups.step5":
    "La lista grita «no otorga nada» — un grupo sin una sola fila de dashboard-view. Quien entra a ese grupo no abre ningún reporte, y en un SELECT sobre la tabla el grupo se ve perfecto. La cantidad de miembros viaja al lado de ese aviso, así un grupo que además está vacío no queda reducido a una sola de las dos cosas.",
  "guide.groups.detail1":
    "Ya no hay permisos individuales. Una excepción es un grupo de una sola persona — y un grupo de una persona SOBREVIVE a la persona: cuando alguien se va, el rol no se va con ella, y su reemplazo entra a un grupo que ya lleva el acceso correcto.",
  "guide.groups.detail2":
    "Varios grupos son la unión de sus ámbitos y el O de sus permisos, nunca el producto cruzado de las reglas. Dos grupos de un país cada uno ven los dos países; intersectarlos no sería las filas de nadie.",
  "guide.groups.detail3":
    "El administrador del propio cliente sigue viendo todo, y un grupo no lo restringe. Ese bypass es deliberado y no cambió — un administrador que no pudiera ver toda su propia data sería otro rol, y ese rol no existe.",
  "guide.people.title": "Personas",
  "guide.people.intro":
    "Cada persona es la unión de sus grupos; esta pantalla es donde se lee esa unión y se revisa antes de que la persona entre.",
  "guide.people.node1": "Persona",
  "guide.people.node1sub": "entra o recibe",
  "guide.people.node2": "Grupos",
  "guide.people.node2sub": "uno o varios",
  "guide.people.node3": "Unión",
  "guide.people.node3sub": "el ámbito efectivo",
  "guide.people.node4": "Archivo",
  "guide.people.node4sub": "uno por proyecto",
  "guide.people.node5": "Portal",
  "guide.people.node5sub": "pantalla · correo · chat",
  "guide.people.step1":
    "Accesos › Personas lista a todo el mundo de este tenant — quienes entran y las direcciones que solo reciben. Una persona se crea acá, con un rol y un correo que después no se edita, porque esa dirección es la identidad contra la que se escribe cada fila de auditoría.",
  "guide.people.step2":
    "Pestaña «Grupos»: asignar grupos es la única forma de dar acceso. Nada en esta pantalla escribe un permiso sobre una persona — la matriz que antes vivía acá ya no está, y sus filas son de los grupos donde la persona está.",
  "guide.people.step3":
    "«Ámbito efectivo» es de solo lectura y es calculado: la tarjeta nombra el ámbito al que resuelven los grupos de la persona y de qué grupos salió, y nada más. La cantidad de filas y el estado del build por proyecto NO están ahí — están en la página de ese ámbito, en Accesos › Ámbitos, a un clic. Ábrela y compara la cantidad contra la del analista ahí. Una cantidad que no coincide es el momento de frenar, y esta es la pantalla donde frenar, no después de que la persona entre.",
  "guide.people.step4":
    "A quien no está en NINGÚN grupo esa tarjeta le muestra «desconocido», no «Todo»: ningún grupo reporta un ámbito para esa persona, y la tarjeta no pinta una etiqueta segura sobre un vínculo que ningún grupo eligió. Igual no lee nada, porque la matriz de contenido responde que no para todos los reportes. Son dos hechos distintos y la tarjeta los mantiene separados: un ámbito no es un permiso, es un recorte.",
  "guide.people.step5":
    "«Acceso al builder» es por persona y nunca por grupo: esos permisos son el acceso del equipo de Omnitok a esta consola, mientras que los grupos gobiernan lo que ve la gente del cliente en el portal. Un grupo que pudiera cargarlos dejaría que un objeto del cliente diera entrada a la consola de administración.",
  "guide.people.step6":
    "Desactivar a alguien pega en la SIGUIENTE REQUEST, no en su próximo ingreso: la fila de la persona se lee en cada request, así que ni un cambio de grupo ni una desactivación necesitan que cierre sesión. No hay nada que esperar y nada que limpiar.",
  "guide.people.detail1":
    "Acá viven dos clases de identidad: alguien que entra, y alguien que solo recibe correos y archivos. Los dos pueden tener grupos y los dos resuelven un ámbito efectivo; lo que los separa es si el portal los deja entrar — y eso ya se aplica, no solo se anota: a una identidad de solo entregas se le niega la sesión interactiva y solo la alcanzan los tokens que arman su correo o la miran de solo lectura.",
  "guide.people.detail2":
    "«Suscripciones» y «excepciones» aparecen una sola vez, como nota, y nunca como números destacados: cuentan filas de las tablas por persona que este avance retira. Se muestran justamente para que una migración que se salteó a alguien se vea desde la pantalla.",
  "guide.people.detail3":
    "«Ver como» existe en dos formas y no son la misma herramienta. Por ÁMBITO, en la vista previa del propio builder: se autoriza contra la fila de quien lo pide, se honra en seis rutas que se comparan exactas y se rechaza en todas las demás, y registra lo que mostró antes de vincular nada. Por PERSONA, desde la consola de plataforma: quince minutos, solo en el portal, estrictamente de solo lectura —cada escritura se rechaza, nombrando la ruta— y su fila de auditoría se escribe ANTES de firmar el token, así que si esa escritura falla no hay token.",
  // ── Guía, capítulo 6: el recorrido previo a dar acceso (J4) ─────
  "guide.verify.title": "Verificar antes de dar acceso",
  "guide.verify.intro":
    "El recorrido que se hace antes de dejar a una persona real atada a un ámbito restringido: construirlo, contar, mirar con sus ojos y leer el rastro.",
  "guide.verify.node1": "Construir",
  "guide.verify.node1sub": "el archivo del ámbito",
  "guide.verify.node2": "Contar",
  "guide.verify.node2sub": "contra el analista",
  "guide.verify.node3": "Ver como",
  "guide.verify.node3sub": "solo lectura · 15 min",
  "guide.verify.node4": "Correo",
  "guide.verify.node4sub": "como quien lo recibe",
  "guide.verify.node5": "Rastro",
  "guide.verify.node5sub": "quién hizo qué",
  "guide.verify.step1":
    "Construye el archivo antes de que haya nadie en el grupo: Accesos › Ámbitos, abre el ámbito y «Reconstruir» por proyecto — o deja que lo haga la próxima recarga. Después lee la columna de estado. «Construido» con una cantidad de filas es el único estado desde el que se puede seguir; «no aplica» quiere decir que ese proyecto no declara ninguna de las dimensiones que nombran las reglas, así que ahí el ámbito no otorga nada.",
  "guide.verify.step2":
    "Compara esa cantidad de filas con el número que tiene el analista para el mismo corte, proyecto por proyecto. Ese es el filtro: mientras los dos no coincidan, no entra nadie al grupo. Un ámbito construido con cero filas no es un error en ninguna pantalla —el tablero queda vacío, nada más— y la causa habitual es un valor escrito de una forma en los datos y de otra en la regla.",
  "guide.verify.step3":
    "«Ver como este ámbito», en la vista previa del propio builder, contesta la mitad barata sin necesitar una persona: el reporte se arma desde el archivo de ese ámbito, en seis rutas que se comparan exactas y se rechazan en cualquier otra, y la fila de auditoría se escribe antes de vincular nada. Se pide igual que la mirada por persona —una cabecera en esas seis llamadas de la vista previa—; ninguna pantalla la manda todavía, así que hoy es un paso por API. Úsalo mientras las cantidades todavía se están moviendo.",
  "guide.verify.step4":
    "«Ver como» una PERSONA es el cruce de solo lectura que emite la consola de plataforma: quince minutos, solo en el portal, y de solo lectura en serio — cada escritura que salga de ahí vuelve rechazada con una frase que nombra la ruta (hay un POST exento porque ES una lectura: el drill de fila, cuya consulta no entra en una URL). Es la única manera de ver lo que recibe una identidad de solo entregas, porque esa identidad no puede iniciar sesión nunca. Hoy se pide por la API de la consola, que acepta una marca de solo lectura; lo que falta es el botón.",
  "guide.verify.step5":
    "Hazle al chat una pregunta que el ámbito NO deba contestar: un mercado que quedó fuera del corte. Que no encuentre nada es la respuesta correcta; un número no lo es, porque el sandbox tiene atado el archivo de ese ámbito y ningún otro. Cambian dos cosas más: una conversación empezada bajo otro ámbito se niega a retomarse en vez de repetirse, y los ejemplos de entrenamiento quedan retenidos salvo que alguien los haya marcado seguros, así que un chat restringido es más pobre a propósito.",
  "guide.verify.step6":
    "El correo lo manda la persona, desde su propia pantalla: una mirada no puede apretar «Enviar», ese POST se rechaza como todos. Lo que cambió por debajo es con qué ojos se saca la foto. Por defecto, a cada destinatario se le captura el dashboard con un token emitido para él, así que la imagen muestra su propio corte; solo un admin que elige «Mi ámbito de datos» manda la foto de su propio corte, y nadie más puede elegirlo. La imagen nunca es la vista sin restricción de la plataforma. Si la captura no logra mostrar el dashboard, o muestra tarjetas de error en vez de datos, el correo no se envía: el envío falla y su dueño recibe el motivo.",
  "guide.verify.step7":
    "Cierra leyendo el rastro, y ten claro cuál. Los cambios de acceso —el grupo, sus integrantes, las reglas de un ámbito— quedan en Accesos › Auditoría con el actor, el antes y el después. La mirada por persona NO: la escribe la consola de plataforma en su propia auditoría, como un cruce marcado de solo lectura. Son dos rastros porque hay dos responsables distintos.",
  "guide.verify.detail1":
    "Un envío programado que incluye el análisis de IA y es de una persona restringida sí sale, y la narrativa se escribe desde el archivo de esa persona: el ámbito viaja con el pedido que el builder le hace al portal, donde es obligatorio y no se supone, y la imagen que va al lado se captura con esa misma identidad — así las dos mitades de un correo cuentan el mismo corte. Lo que no va a pasar es que salga sin poder explicarse: el portal devuelve de vuelta con qué ámbito lo armó, y si esa respuesta falta o no coincide —que es lo que contesta un portal desplegado antes que el builder— el envío falla en vez de entregarse.",
  "guide.verify.detail2":
    "Los insights son por ámbito y están apagados hasta que un grupo los pida: un ámbito se gana su propia narrativa solo si alguien que lee bajo él está en un grupo con el interruptor encendido, y solo en los proyectos cuyo archivo llegó a construirse. La cifra que acompaña al interruptor es aritmética —reportes × ámbitos × recargas— y al lado va lo medido en los últimos treinta días, para que nadie lea una cosa por la otra.",
  "guide.verify.detail3":
    "En un correo no hay nada congelado desde que se guardó. El ámbito con el que se arma se lee fresco al momento de disparar, de la fila de la persona, así que recortarle el acceso alcanza a su próximo correo sin que nadie edite un envío. Las alertas por umbral y por SQL funcionan igual: las dos abren el archivo de esa persona, nunca el del proyecto.",
  "guide.verify.detail4":
    "Cada paso de acá falla cerrado y ninguno toma prestado un archivo más amplio. Un ámbito sin construir responde 503, una narrativa que falta para ese ámbito es un rechazo y no la versión sin restricción, y un dueño cuya fila no aparece detiene el envío. Si una pantalla se ve vacía o un correo no llegó, busca qué falta: no hay un valor por defecto que haya tapado el hueco.",
  "guide.refresh.title": "Auto-refresh y reconciliación",
  "guide.refresh.intro":
    "El cron recarga el OBT y un chequeo 3-way gatea cada publicación.",
  "guide.refresh.node1": "Cron",
  "guide.refresh.node1sub": "por proyecto",
  "guide.refresh.node2": "Job del loader",
  "guide.refresh.node2sub": "heartbeat ~5 s",
  "guide.refresh.node3": "Swap atómico",
  "guide.refresh.node3sub": "vistas rebuild",
  "guide.refresh.node4": "Gate A↔B",
  "guide.refresh.node4sub": "golden vs engine",
  "guide.refresh.node5": "Portal",
  "guide.refresh.node5sub": "pickup de versión",
  "guide.refresh.step1":
    "Cada proyecto programa su reload (cron + timezone) en el tab Auto-refresh del hub; Operaciones los muestra todos.",
  "guide.refresh.step2":
    "Una corrida descarga el parquet, reconstruye la tabla raw y re-materializa las vistas de todos los reportes en un swap de staging.",
  "guide.refresh.step3":
    "Antes del bump de versión, el GATE de reconciliación compara SQL golden (A) contra el engine vivo (B) por cada elemento cubierto.",
  "guide.refresh.step4":
    "Si el gate falla, el swap se descarta — la data VIEJA queda viva, el job queda failed y sale un email de alerta.",
  "guide.refresh.step5":
    "Después de publicar, la fase 2 compara el engine contra lo que el portal realmente sirve (B↔C) — solo alerta, con rewarm de cache.",
  "guide.refresh.detail1":
    "Los manifests golden viven por reporte (reconciliation.yaml); agregar un elemento sin assertion BLOQUEA el gate — sin gaps silenciosos.",
  "guide.refresh.detail2":
    "Los jobs laten cada ~5 s; el reaper mata los stale. Force-reap (Operaciones o hub) aborta un job trabado antes de una corrida nueva.",
  "guide.refresh.detail3":
    "Que el gate falle significa que la data quedó consistente — nunca re-correr a ciegas; leer primero el drawer por elemento.",
  "guide.publish.title": "Publicar",
  "guide.publish.intro":
    "De la edición del draft al portal, con historial y revert.",
  "guide.publish.node1": "Draft",
  "guide.publish.node1sub": "autosave",
  "guide.publish.node2": "Validación",
  "guide.publish.node2sub": "SQL + layout",
  "guide.publish.node3": "Publish",
  "guide.publish.node3sub": "snapshot al historial",
  "guide.publish.node4": "Portal",
  "guide.publish.node4sub": "pickup ~60 s",
  "guide.publish.step1":
    "Guardar escribe el reporte. Un borrador no se sirve; un reporte publicado cambia en el portal con el guardado (el portal sirve las filas publicadas), por eso el guardado pasa antes las mismas validaciones que Publicar. En un grupo de reportes, lo que el grupo repite se copia a los reportes hermanos en el mismo guardado.",
  "guide.publish.step2":
    "Publicar valida SQL y layout (bounds de la grilla, alturas) — una validación que falla bloquea, ruidosamente.",
  "guide.publish.step3":
    "Publicar snapshotea el config anterior al Historial — cualquier snapshot se puede revertir en un click.",
  "guide.publish.step4":
    "El portal poll-ea report_drafts cada 60 s y recoge el config nuevo sin restart.",
  "guide.publish.detail1":
    "El builder es la única fuente de verdad — el portal nunca edita dashboard_config.",
  "guide.publish.detail2":
    "Los fields backend-only (runtime_view_sql, …) se preservan al publicar — la UI nunca los ve ni los pisa.",
  "guide.publish.detail3":
    "La vista compartida (/preview/:key) renderea EXACTAMENTE el mismo DashboardBody del portal — lo que ves es lo que sale.",
  "guide.ai.title": "La IA y su loop de mejora",
  "guide.ai.intro":
    "El training alimenta el chat; los issues realimentan el training.",
  "guide.ai.node1": "Training",
  "guide.ai.node1sub": "DDL · docs · Q&A",
  "guide.ai.node2": "Chat",
  "guide.ai.node2sub": "por reporte",
  "guide.ai.node3": "Issues",
  "guide.ai.node3sub": "thumbs-down",
  "guide.ai.node4": "Triage",
  "guide.ai.node4sub": "Panorama IA",
  "guide.ai.node5": "Mejor IA",
  "guide.ai.node5sub": "promover a ejemplo",
  "guide.ai.step1":
    "El pane Conocimiento de cada reporte guarda sus docs de schema y ejemplos Q&A canónicos — ESO es el training del chat.",
  "guide.ai.step2":
    "El chat responde con SQL sobre las vistas del reporte; preguntas de arranque y CTAs se configuran por reporte.",
  "guide.ai.step3":
    "Un thumbs-down en el portal crea un issue con la conversación completa adjunta.",
  "guide.ai.step4":
    "Panorama IA encola los issues de TODOS los reportes — el triage se hace ahí (resolver, descartar o promover).",
  "guide.ai.step5":
    "Promover un issue convierte la respuesta corregida en un ejemplo de training — la palanca de mayor impacto para la calidad del chat.",
  "guide.ai.detail1":
    "Los Insights son aparte del chat: specs + ventana temporal + snapshots, configurados en el pane Insights del reporte.",
  "guide.ai.detail2":
    "Las notas de contexto viven a nivel PROYECTO y fluyen a cada snapshot afectado — regenera los afectados después de editar.",
  "guide.ai.detail3":
    "Una mala respuesta del chat es un gap de training, no un problema de prompt — agrega un ejemplo canónico antes de tocar prompts.",
  // ── Plan J J2 — el riel de ayuda contextual ────────────────
  "screenhelp.button": "¿Qué es esta pantalla?",
  "screenhelp.forThisScreen": "Ayuda de esta pantalla",
  "screenhelp.contents": "Contenido",
  "screenhelp.openGuide": "Abrir la guía",
  "screenhelp.dimensions.title": "Dimensiones",
  "screenhelp.dimensions.body":
    "Esta pantalla define las columnas por las que este proyecto se puede filtrar y por las que se puede cortar el acceso — una sola definición sirve para las dos cosas. Una dimensión es una columna que los datos ya traen, un valor traducido por una pestaña de un Maestro que mantiene el cliente, o una calculada con una expresión SQL sobre la fila. Lo que sorprende a todo el mundo: declararla no cambia nada hasta la próxima recarga, porque las columnas de mapeo y calculadas las escribe la recarga misma.",
  "screenhelp.scopes.title": "Ámbitos",
  "screenhelp.scopes.body":
    "Esta pantalla decide quién lee qué filas. Un ámbito es un conjunto de reglas sobre dimensiones —una dimensión, una lista de valores, combinadas con Y— y cada uno se materializa en un archivo DuckDB real por proyecto, así que un proyecto que no declara alguna dimensión que las reglas nombran no otorga nada ahí, en vez de otorgar todo. Editar las reglas retira los archivos del ámbito en el acto: todas las personas que leen por él reciben un error (503) hasta que alguien lo reconstruya, o hasta la próxima recarga del proyecto.",
  "screenhelp.scope.title": "Este ámbito",
  "screenhelp.scope.body":
    "Un ámbito: sus reglas, los proyectos donde aplica y todos los builds que tuvo. El guardado exige reconocer el impacto —el ensayo nombra los grupos y las personas a las que llega el cambio, y el guardado tiene que devolverlos— y la reconstrucción es sincrónica, toma el lock de escritura del tenant y puede durar más que el proxy. Cuando el pedido se muere, la fuente de verdad es la tabla de builds de abajo, no el error.",
  "screenhelp.groups.title": "Grupos",
  "screenhelp.groups.body":
    "La lista de grupos. Un grupo carga un ámbito y una matriz de contenido, y es el único lugar donde se escribe el acceso; Omnitok los crea acá y el admin del cliente solo les asigna personas. El estado que hay que buscar es «no otorga nada» — un grupo sin ninguna fila de dashboard-view, que no le abre ningún reporte a sus miembros y en la base se ve perfecto.",
  "screenhelp.group.title": "Este grupo",
  "screenhelp.group.body":
    "Las tres pestañas de este grupo: quién está adentro, qué filas abre, y qué reportes y secciones otorga. Cambiar el ámbito cambia lo que lee cada miembro en su request siguiente, sin volver a entrar — pero solo hasta donde permita su grupo más amplio: el ámbito de una persona es la UNIÓN de los de sus grupos, y la unión con «Todo» es «Todo», así que quien también esté en un grupo sin restricción sigue leyendo todo. La columna «Resuelve a» dice cuál es cuál, fila por fila: «lee todo» es una restricción que este grupo no está aplicando. Una celda que quedó sin marcar en la matriz de contenido significa el default del rol, no «denegado» — salvo «Verlo», que no tiene default: sin fila ahí, el reporte no se abre.",
  "screenhelp.groupAi.title": "La IA de este grupo",
  "screenhelp.groupAi.body":
    "Qué hacen el chat y los insights para la gente de este grupo. El interruptor de insights es un COSTO y está apagado hasta que alguien lo prenda: encendido, cada reporte que produce Análisis de IA se narra una vez más para el ámbito de este grupo, además de la versión sin restricción, y la cifra que acompaña al interruptor es aritmética —reportes × ámbitos × recargas— con lo medido al lado, para que nadie lea una cosa por la otra. La nota de contexto se agrega a los prompts que llegan a sus integrantes. Y bajo un ámbito restringido el chat conserva solo los ejemplos de entrenamiento que alguien marcó como seguros, así que responde con menos que para un lector sin restricción — a propósito, no por accidente. Nada de acá otorga acceso: decide qué les dice la IA a quienes ya lo tienen.",
  "screenhelp.groupExperience.title": "La experiencia de este grupo",
  "screenhelp.groupExperience.body":
    "Con qué arranca alguien de este grupo: el tablero de Inicio que se le arma la primera vez que abre el portal. Editar la plantilla del tablero vuelve a sincronizar los tableros de los integrantes y nunca toca una tarjeta que alguien agregó por su cuenta; una tarjeta que un integrante no puede leer vuelve como denegada en vez de desaparecer, porque el aprovisionamiento decide con qué EMPIEZA una persona y nunca qué puede ver. La otra mitad de la columna, el correo predeterminado del grupo, no tiene formulario en esta pestaña: nada escribe la fila de envío de un integrante a partir de ella, y un formulario para una plantilla que nadie entrega le diría a un administrador que programó algo. El envío que la gente recibe de verdad es el que se arma en el portal.",
  "screenhelp.people.title": "Personas",
  "screenhelp.people.body":
    "Todo el mundo de este tenant, tanto quien entra como quien solo recibe. La columna que importa es el ámbito efectivo: es la unión de los grupos de la persona —el MÁS AMPLIO de ellos, así que un solo grupo sin restricción deja inertes todas las que tenga—, calculada y nunca escrita a mano. A quien no está en ningún grupo se le muestra «desconocido» en vez de un ámbito, porque ningún grupo reporta uno; igual no lee nada, porque la matriz de contenido responde que no para todos los reportes.",
  "screenhelp.person.title": "Esta persona",
  "screenhelp.person.body":
    "Los grupos de esta persona, el ámbito efectivo al que resuelven esos grupos, y su acceso al builder — que es acceso del equipo a esta consola y es por persona a propósito, no por grupo. La tarjeta del ámbito nombra el ámbito y los grupos de los que salió, y nada más: la cantidad de filas y el estado del build por proyecto están en la página del ámbito, a un clic. Lee la unión como resuelve de verdad: gana el grupo MÁS AMPLIO, así que un solo grupo sin restricción deja inertes todas las que tenga esta persona. Nada de acá escribe un permiso directo: el acceso se da agregando a la persona a un grupo.",
  "screenhelp.cache.title": "Caché",
  "screenhelp.cache.body":
    "Qué se calcula antes de publicar cada recarga. Cada recarga precalienta la vista por defecto de cada reporte publicado de su proyecto, y los filtros de cada grupo, antes de que los datos nuevos se vean; un reporte con «Todas las combinaciones» prendido precalienta además cada combinación de sus filtros-botón. Si algo falla, la recarga NO se publica: quedan los datos anteriores con su caché completo, la corrida falla y sale un correo. Los idiomas solo cuentan para los reportes cuyo SQL usa el idioma; el resto se calcula una vez para todos. Las estimaciones salen de la última recarga de cada proyecto y se recalculan al cambiar un interruptor; un reporte sin medición lo dice en vez de mostrar un número. Los cambios rigen desde la próxima recarga de cada proyecto.",
  "screenhelp.audit.title": "Registro de auditoría",
  "screenhelp.audit.body":
    "Cada cambio de acceso hecho desde Accesos, con quién lo hizo y cuándo: ámbitos, grupos, la matriz de contenido, las membresías, los predeterminados por rol y cada ámbito efectivo recalculado. La fila se escribe en la misma transacción que el cambio, así que las dos cosas quedan juntas o no queda ninguna: un cambio que no se pudo registrar no ocurrió. La migración de una sola vez que creó los primeros grupos también está acá, marcada como «automático». Lo que NO está es lo que se hace fuera de esta consola: un SQL corrido directo contra la base del cliente cambia el acceso y no deja rastro acá, así que una acción que falta en esta lista no se hizo desde Accesos, que no es lo mismo que no se hizo. Nada se filtra en el navegador — una vista que escondiera en silencio filas que el servidor devolvió es así como una reconstrucción se pierde justo la fila que importaba.",

  // Atomos UI compartidos inspector + list (iter 27)
  "common.copy": "Copiar",
  "common.copied": "Copiado",
  "common.expand": "Expandir",
  "common.collapse": "Colapsar",

  // Admin Prompts — iter 27 rename a framing "Global"
  "admin.prompts.titleGlobal": "Prompts AI Globales",
  "admin.prompts.subtitleGlobal":
    "Plantillas por defecto que el portal usa para cada reporte como fallback.",
  "admin.prompts.newBtnGlobal": "Nuevo prompt global",
  "admin.prompts.scopeBannerBodyIter27":
    "Estos son los prompts GLOBALES a los que cada reporte hace fallback. Los overrides por reporte se editan en el tab IA del reporte (Conocimiento → Avanzado) — no aparecen acá para edición.",
  "admin.prompts.overridesTitle": "Reportes con overrides activos",
  "admin.prompts.overridesHint":
    "Resumen sólo lectura. Edita cada override desde el tab IA de su reporte.",
  "admin.prompts.editInAiTab": "Editar en el tab IA",
  "admin.prompts.clientScopeReadonly": "alcance cliente · sólo lectura acá",
  "admin.prompts.overrideDraftMissing": "reporte no encontrado — sin enlace",

  // Inspector — restructure iter 27
  "inspector.identity.title": "Identidad",
  "inspector.sql.title": "Query SQL",
  "inspector.sql.empty": "Sin definir",
  "inspector.sql.notSet": "Sin definir",
  "inspector.sql.mode": "Modo de visualización del SQL",
  "inspector.sql.template": "Plantilla",
  "inspector.sql.live": "Real",
  "inspector.sql.live.loading": "Cargando SQL real…",
  "inspector.sql.live.error": "No se pudo cargar el SQL real.",
  "inspector.behavior.title": "Comportamiento",
  "inspector.structural.title": "Estructural (sólo lectura)",
  "inspector.kpi.sqlHint":
    "Se interpola en el SELECT del KPI que arma el engine junto al WHERE de los filtros activos.",
  "inspector.kpi.previousSql": "SQL período anterior",
  "inspector.kpi.sparklineSql": "SQL sparkline",
  "inspector.kpi.expectedSql": "SQL valor esperado",
  "inspector.kpi.breakdownSql": "SQL desglose",
  "inspector.kpi.goalDirection": "Dirección de meta",
  "inspector.chart.rawSql": "SQL crudo",
  "inspector.chart.rawSqlHint":
    "Se usa cuando el chart necesita un SELECT completo (ej. tablas de comparación trimestral). Los placeholders {where} y {filter:KEY} los substituye el engine.",
  "inspector.table.sourceSql": "SQL de origen",
  "inspector.table.sourceSqlHint":
    "Query interna que el engine envuelve con el WHERE de filtros activos + sort/limit antes de traer las filas.",
  "inspector.text.sql": "SQL",
  "inspector.text.sqlHint":
    "SQL inline opcional que el engine corre para interpolar placeholders {value} dentro del texto.",
  "list.col.key": "Clave",
  "list.col.name": "Nombre",
  "list.col.table": "Tabla DuckDB",
  "list.col.status": "Estado",
  "list.col.updated": "Actualizado",
  "list.col.actions": "Acciones",
  "list.status.draft": "Borrador",
  "list.status.published": "Publicado",
  "list.delete.confirm":
    "¿Eliminar este reporte? Se borra el borrador y no se puede revertir.",
  "list.create.title": "Nuevo reporte",
  "list.create.key": "Clave (minúsculas, guiones bajos)",
  "list.create.keyInvalid": "solo minúsculas / dígitos / guiones bajos — debe empezar con una letra",
  "list.create.keyExists": "La clave «{key}» ya existe",
  "list.create.name": "Nombre visible (obligatorio)",
  "list.create.table": "Tabla DuckDB (opcional)",
  "list.create.tableHint":
    "Déjalo vacío si el reporte toma sus datos de una fuente de la pestaña Datos: la primera fuente define la tabla. Complétalo solo para apuntar a una tabla que ya existe.",

  "header.signOut": "Cerrar sesión",
  "header.lang": "Idioma",
  "nav.reports": "Reportes",
  "nav.reportsAndProjects": "Reportes y Proyectos",
  "nav.templates": "Plantillas",

  "templates.title": "Plantillas",
  "templates.subtitle": "Puntos de partida reutilizables para nuevos reportes.",
  "templates.empty": "Todavía no hay plantillas. Las plantillas integradas se cargan automáticamente.",
  "templates.noDescription": "(sin descripción)",
  "templates.use": "Usar",
  "templates.edit": "Editar",
  "templates.builtin": "Integrada",
  "templates.backToList": "Volver a plantillas",
  "templates.deleteConfirm": "¿Eliminar la plantilla \"{title}\"? No se puede revertir.",
  "templates.stat.sections": "Secciones",
  "templates.stat.items": "Elementos",
  "templates.stat.filters": "Filtros",

  "nav.projects": "Proyectos",
  "projects.title": "Proyectos",
  "projects.subtitle": "Agrupa reportes por proyecto.",

  // Components gallery
  "nav.components": "Componentes",
  // App shell (v3 brand header, sidebar sections)
  "shell.label": "Builder",
  "shell.labelTenant": "Builder · {tenant}",
  "shell.toggleMenu": "Mostrar u ocultar el menú",
  "nav.section.intelligence": "Inteligencia",
  "nav.section.admin": "Administración",
  "nav.section.help": "Ayuda",
  "gallery.title": "Componentes",
  "gallery.subtitle": "Una base visual para el kit, los componentes BI y el render de reportes. Cada ejemplo es el componente real con datos de ejemplo.",
  "gallery.section.kpis": "Tarjetas KPI",
  "gallery.section.chartsStatic": "Gráficos — Estáticos",
  "gallery.section.chartsInteractive": "Gráficos — Interactivos (hover para tooltips)",
  "gallery.section.tables": "Tablas",
  "gallery.section.filters": "Filtros",
  "gallery.section.dates": "Selectores de Fecha",
  "gallery.section.analytics": "Analítica",
  "gallery.section.maps": "Mapas Geográficos",
  "gallery.toc.title": "En esta página",
  "gallery.toc.kpis": "Tarjetas KPI",
  "gallery.toc.chartsStatic": "Gráficos — Estáticos",
  "gallery.toc.chartsInteractive": "Gráficos — Interactivos",
  "gallery.toc.tables": "Tablas",
  "gallery.toc.filters": "Filtros",
  "gallery.toc.dates": "Selectores de Fecha",
  "gallery.toc.analytics": "Analítica",
  "gallery.toc.maps": "Mapas",
  "gallery.layout.grid": "Grilla",
  "gallery.layout.full": "Completo",
  "gallery.code.copy": "Copiar",
  "gallery.code.copied": "Copiado",
  "gallery.code.import": "Uso — Import",
  "gallery.toggle.tooltip": "Cambiar layout grilla / full",
  "projects.new": "Nuevo proyecto",
  "projects.edit.title": "Editar proyecto",
  "projects.empty": "Todavía no hay proyectos.",
  "projects.slug": "Slug",
  "projects.slugInvalid": "solo minúsculas / dígitos / guiones / guiones bajos",
  "projects.slugLocked": "El slug no se puede cambiar una vez creado el proyecto: sus archivos de datos, permisos y entregas dependen de él.",
  "projects.name": "Nombre",
  "projects.description": "Descripción",
  "projects.color": "Color",
  "projects.icon": "Ícono",
  "projects.iconNone": "Ninguno",
  "projects.orderIndex": "Orden en el sidebar",
  "projects.orderIndex.help":
    "Los números más bajos aparecen primero en el sidebar del portal; con el mismo valor se ordena por nombre.",
  "editor.chart.dataLabels.title": "Etiquetas de datos y formato de ejes",
  "editor.chart.dataLabels.show": "Mostrar etiquetas",
  "editor.chart.dataLabels.formatLabel": "Formato",
  "editor.chart.dataLabels.formatRaw": "Sin formato",
  "editor.chart.dataLabels.formatCompact": "Compacto (K / M / B)",
  "editor.chart.dataLabels.formatCurrency": "Moneda",
  "editor.chart.dataLabels.formatPercent": "Porcentaje",
  "editor.chart.dataLabels.decimalsLabel": "Decimales",
  "editor.chart.dataLabels.axesTitle": "Ejes",
  "editor.chart.dataLabels.axisX": "Formato eje X",
  "editor.chart.dataLabels.axisY": "Formato eje Y",

  "projects.toggles.title": "Secciones",
  "projects.form.tab.general": "General",
  "projects.form.tab.maestros": "Maestros",
  "projects.form.tab.archivos": "Archivos",
  "projects.form.maestros.intro":
    "Apariencia + prefijos S3 para la página Maestros del proyecto. Vacío → el portal usa los textos genéricos.",
  "projects.form.maestros.pageTitle": "Título de la página",
  "projects.form.maestros.pageTitlePlaceholder": "ej. Catálogos HP",
  "projects.form.maestros.subtitle": "Subtítulo",
  "projects.form.maestros.subtitlePlaceholder":
    "ej. Datos de referencia de este reporte",
  "projects.form.maestros.emptyMessage": "Mensaje cuando está vacío",
  "projects.form.maestros.emptyMessagePlaceholder":
    "Aparece cuando no hay prefijos configurados",
  "projects.form.maestros.prefixesNote":
    "Los prefijos S3 del loader de master data viven hoy en la columna JSONB y los gestiona el pipeline de loader. En una iteración futura van a aparecer acá como lista editable.",
  "projects.form.archivos.intro":
    "Apariencia + ruta raíz S3 + tipos permitidos para la página Archivos del proyecto.",
  "projects.form.archivos.pageTitle": "Título de la página",
  "projects.form.archivos.pageTitlePlaceholder": "ej. Descargas regionales",
  "projects.form.archivos.subtitle": "Subtítulo",
  "projects.form.archivos.subtitlePlaceholder":
    "ej. Reportes trimestrales y exports",
  "projects.form.archivos.emptyMessage": "Mensaje cuando está vacío",
  "projects.form.archivos.emptyMessagePlaceholder":
    "Aparece cuando la ruta S3 no tiene archivos",
  "projects.form.archivos.s3RootPath": "Ruta raíz S3",
  "projects.form.archivos.allowedTypes": "Tipos de archivo permitidos",
  "projects.form.archivos.allowedTypesNote":
    "Vacío = el portal acepta cualquier tipo. Click en un chip para agregar/quitar.",
  "projects.form.archivos.maxSize": "Tamaño máximo (MB)",
  "projects.form.archivos.allowUpload":
    "Mostrar el botón de subir archivos en la página",
  // Project data source tab (iter 23)
  "projects.form.tab.data": "Datos",
  // Loader / scheduler tab (iter 29 — movido del portal Settings → Operations)
  "projects.form.tab.loader": "Auto-actualización",
  "loader.loading": "Cargando…",
  "loader.noDataSource":
    "Primero configura una fuente de datos en la pestaña Data — el loader necesita algo que refrescar.",
  "loader.scheduleTitle": "Programación",
  "loader.enabled": "Habilitar refresh automático",
  "loader.cron": "Expresión cron",
  "loader.cronHelp":
    "Cron estándar de 5 campos: minuto hora día mes día-semana. Ejemplo: 0 3 * * * corre cada día a las 03:00.",
  "loader.timezone": "Zona horaria",
  "loader.timezoneHelp":
    "Nombre IANA (UTC, America/Argentina/Buenos_Aires, …). Define cuándo dispara el cron.",
  "loader.mode": "Modo de refresh",
  "loader.mode.reload": "Recarga completa (recomendado)",
  "loader.mode.incremental": "Incremental",
  "loader.mode.rebuild": "Reconstruir",
  "loader.maxRuntime": "Tiempo máx (segundos)",
  "loader.maxRuntimeHelp":
    "Pronto. Hoy no hay tiempo máximo: una actualización corre hasta que termina o falla.",
  "loader.soon": "Pronto",
  "loader.loadFailed": "No se pudo cargar la programación de actualización, así que el formulario queda oculto: guardarlo ahora sobrescribiría la programación guardada con valores por defecto.",
  "loader.runNowTitle": "Refresh manual",
  "loader.runNowHelp":
    "Correr el loader ahora usando el modo del schedule guardado. Bloquea hasta completar o fallar.",
  "loader.runNow": "Correr ahora",
  "loader.runNowHint":
    "Idle. El botón Correr ahora encola un nuevo reload y espera a que termine.",
  "loader.forceReap": "Forzar aborto + correr ahora",
  "loader.forceReapHelp":
    "Hay un job en curso. Forzar aborto lo mata (lo marca como fallido) y lanza un reload nuevo.",
  "loader.forceReapConfirm":
    "Esto marcará el job pending/running actual como fallido y lanzará un reload nuevo. ¿Continuar?",
  "loader.recentJobsTitle": "Jobs recientes",
  "loader.noJobs": "Sin jobs todavía — programa uno o toca Correr ahora.",
  "loader.statusActive": "Scheduler activo",
  "loader.statusIdle": "Scheduler en idle",
  "loader.statusDisabled": "Scheduler deshabilitado",
  "loader.statusLoadFailed": "No se pudo cargar el estado del scheduler: {error}",
  "loader.leaseHolder": "Líder",
  "loader.lastTick": "Último tick",
  "loader.recon.title": "Detalle de reconciliación",
  "loader.recon.jobLabel": "Job",
  "loader.recon.loading": "Cargando filas de reconciliación…",
  "loader.recon.empty": "Sin datos de reconciliación para este job.",
  "loader.recon.layerGate": "Gate (A·B)",
  "loader.recon.layerPortalCache": "Portal (B·C)",
  "loader.recon.layerPortalHttp": "Portal HTTP (B·C)",
  "loader.recon.colElement": "Elemento",
  "loader.recon.colLayer": "Capa",
  "loader.recon.colStatus": "Estado",
  "loader.recon.colGolden": "Golden (A)",
  "loader.recon.colObserved": "Observado",
  "loader.recon.colDelta": "Δ",
  "loader.recon.noChecks": "Sin verificaciones",
  "loader.recon.noChecksTitle": "En esta corrida no se comparó ninguna aserción.",
  "loader.recon.portalNotChecked": "Portal sin verificar: 0 aserciones comparadas.",
  "projects.form.data.intro":
    "Configura UNA sola fuente consolidada para todo el proyecto — un parquet o DuckDB en S3, o un archivo CSV / Excel que subes acá. Cada reporte después es una vista SQL sobre este raw — el SELECT lo defines en el tab Data del reporte.",
  "projects.form.data.kindLabel": "Tipo",
  "projects.form.data.urlLabel": "URL de S3",
  "projects.form.data.tableLabel": "Tabla destino (opcional)",
  "projects.form.data.tableHint":
    "Por defecto project_raw_{slug}. Las vistas referencian este nombre en su SELECT.",
  "projects.form.data.aliasLabel": "Alias de ATTACH (opcional)",
  "projects.form.data.saveBtn": "Guardar URL",
  "projects.form.data.reloadBtn": "Recargar desde S3",
  "projects.form.data.clearBtn": "Limpiar",
  "projects.form.data.clearConfirm":
    "¿Limpiar la fuente de datos del proyecto? Drop-ea la tabla raw materializada y resetea los campos de auditoría. Las vistas dependientes van a fallar hasta que reconfigure.",
  "projects.form.data.loadedAt": "Última carga",
  "projects.form.data.rows": "Filas",
  "projects.form.data.columns": "Columnas",
  "projects.form.data.tables": "Tablas",
  "projects.form.data.columnsShort": "cols",
  "projects.form.data.schemaTitle": "Esquema",
  "projects.form.data.catalogTitle": "Catálogo",
  "projects.form.data.viewWarnings":
    "Recarga exitosa pero algunas vistas dependientes fallaron al recrearse:",
  // Track B1 — conexiones (tab Data del proyecto)
  "projects.form.data.connectionLabel": "Conexión",
  "projects.form.data.connectionNone": "(ninguna)",
  "projects.form.data.connectionHint":
    "Las credenciales vienen de la conexión seleccionada. Obligatoria para fuentes postgres y api, y para cualquier URL s3://.",
  // Por qué "Guardar" está deshabilitado. Las reglas están bien; antes
  // de esto el operador veía solo un botón muerto y tenía que leer el
  // código para enterarse de que una URL s3:// necesita una conexión.
  "projects.form.data.blockedUrl": "Ingresa la URL de S3 del archivo a cargar.",
  "projects.form.data.blockedParquetExt":
    "La URL tiene que terminar en .parquet para una fuente parquet.",
  "projects.form.data.blockedDuckdbExt":
    "La URL tiene que terminar en .duckdb para una fuente duckdb (attach).",
  "projects.form.data.blockedTable":
    "Indica la tabla de origen a copiar desde el archivo .duckdb adjunto.",
  "projects.form.data.blockedUpload": "Sube el archivo primero.",
  "projects.form.data.blockedSheet":
    "Elige qué hoja del workbook cargar.",
  "projects.form.data.blockedConnection":
    "Elige una conexión: una URL s3:// se lee con sus credenciales — no hay acceso anónimo.",
  "projects.form.data.blockedNoConnections":
    "Este proyecto todavía no tiene ninguna conexión, y una URL s3:// se lee con las credenciales de una conexión — no hay acceso anónimo. Crea una con “Agregar conexión” acá arriba (puedes reusar la de otro proyecto apuntando a la misma referencia de secreto).",
  "projects.form.data.attachTableLabel": "Tabla de origen dentro del archivo",
  "projects.form.data.attachTableHint":
    "Obligatoria — la tabla a copiar desde el .duckdb adjunto hacia project_raw_{slug}. No hay default.",
  // ── Plan C fase 4 — CSV / Excel subido como dataset del proyecto ──
  "projects.form.data.kind.parquet": "Parquet en S3 (OBT)",
  "projects.form.data.kind.duckdb_attach": "Archivo DuckDB (attach)",
  "projects.form.data.kind.postgres": "Consulta Postgres",
  "projects.form.data.kind.api": "API JSON",
  "projects.form.data.kindSoon": "Pronto",
  "projects.form.data.blockedKindSoon":
    "Este proyecto usa una fuente {kind}. Este formulario todavía no puede editar esa fuente (pronto), así que aquí es de solo lectura; la recarga la sigue ejecutando tal como está guardada.",
  "projects.form.data.kindCsv": "Archivo CSV / TSV (subir)",
  "projects.form.data.kindExcel": "Archivo Excel .xlsx (subir)",
  "projects.form.data.saveUploadBtn": "Guardar archivo como fuente",
  "projects.form.data.reloadUploadBtn": "Recargar datos del proyecto",
  "projects.form.data.upload.title": "Archivo subido",
  "projects.form.data.upload.subtitle":
    "Todo el dataset del proyecto sale de este archivo. Se aceptan .csv, .tsv y .xlsx, hasta 100 MB.",
  "projects.form.data.upload.chooseBtn": "Elegir un archivo…",
  "projects.form.data.upload.replaceBtn": "Elegir otro archivo…",
  "projects.form.data.upload.uploading": "Subiendo e inspeccionando…",
  // Single-brace interpolation — ver nota EN.
  "projects.form.data.upload.rowsRead":
    "{rows} filas · {columns} columnas leídas",
  "projects.form.data.upload.stagedNote":
    "Subido pero todavía no publicado. Guarda la fuente y después recarga los datos del proyecto.",
  "projects.form.data.upload.sheetLabel": "Hoja",
  "projects.form.data.upload.sheetPlaceholder": "Elige una hoja…",
  "projects.form.data.upload.sheetNoWorkbook":
    "Sube un archivo .xlsx para ver sus hojas.",
  "projects.form.data.upload.sheetHint":
    "Se carga solo esta hoja. El resto del libro no se lee. Para usar otra, elígela acá, guarda y recarga.",
  "projects.form.data.upload.sheetRequired":
    "Este libro tiene más de una hoja. Elige la que tiene los datos — nunca se adivina.",
  "projects.form.data.upload.delimiterLabel": "Separador de campos (opcional)",
  "projects.form.data.upload.delimiterPlaceholder": "detección automática",
  "projects.form.data.upload.delimiterHint":
    "Déjalo vacío y el separador se detecta del archivo. Escribe un solo carácter para forzarlo (\\t para tabulación).",
  "projects.form.data.upload.headerLabel":
    "La primera fila tiene los nombres de las columnas",
  "projects.form.data.upload.allVarcharLabel": "Leer todas las columnas como texto",
  "projects.form.data.upload.allVarcharHint":
    "Úsalo cuando un código se lee como número y pierde los ceros iniciales (códigos postales, IDs de producto).",
  "projects.form.data.upload.guideTitle":
    "Cómo preparar y subir el archivo — paso a paso",
  "projects.form.data.upload.guideStep1":
    "Los formatos aceptados son .csv, .tsv y .xlsx. Los .xls viejos, los libros con macros (.xlsm), los binarios (.xlsb), OpenDocument (.ods) y los archivos de Apple Numbers se rechazan, porque no se pueden leer de forma confiable.",
  "projects.form.data.upload.guideStep2":
    "Si tu archivo es alguno de esos, conviértelo primero — en Excel: Archivo > Guardar como > Examinar, y en «Tipo» elige «Libro de Excel (*.xlsx)» o «CSV UTF-8 (delimitado por comas) (*.csv)». En Google Sheets: Archivo > Descargar > «Microsoft Excel (.xlsx)» o «Valores separados por comas (.csv)». En LibreOffice Calc: Archivo > Guardar como, y en «Tipo de archivo» elige «Excel 2007-365 (.xlsx)» o «Texto CSV».",
  "projects.form.data.upload.guideStep3":
    "La primera fila tiene que tener los nombres de las columnas, sin filas de título, sin celdas combinadas y sin filas en blanco arriba. Si tu archivo realmente no tiene encabezado, desmarca «La primera fila tiene los nombres de las columnas» acá abajo y las columnas se van a llamar column0, column1, …",
  "projects.form.data.upload.guideStep4":
    "Tamaño máximo: 100 MB. Un archivo más grande se rechaza — para esos usa una conexión parquet, DuckDB o Postgres, que leen desde el storage y no tienen tope de tamaño.",
  "projects.form.data.upload.guideStep5":
    "Haz clic en «Elegir un archivo…» arriba. El archivo se sube y se inspecciona en el momento, y vas a ver cuántas filas y columnas se leyeron realmente. Todavía no se publica nada: lo que sirve el portal no cambió.",
  "projects.form.data.upload.guideStep6":
    "Solo Excel: elige la hoja. Un libro con una sola hoja se resuelve solo; uno con varias siempre pregunta, porque cargar la pestaña equivocada publicaría números equivocados sin ningún error. Se lee únicamente la hoja elegida.",
  "projects.form.data.upload.guideStep7":
    "Haz clic en «Guardar archivo como fuente» y después en «Recargar datos del proyecto». La recarga es lo que publica: se reconstruye la tabla project_raw_{slug} y se recrean todas las vistas de los reportes que dependen de ella.",
  "projects.form.data.upload.guideStep8":
    "Para actualizar los datos más adelante, sube un archivo nuevo de la misma forma. Cada recarga REEMPLAZA la tabla entera — es un reemplazo completo, nunca un agregado, así que las filas que borres del archivo desaparecen de los dashboards. El archivo anterior se elimina cuando se guarda el nuevo.",
  "projects.form.data.upload.guideStep9":
    "Si el archivo nuevo está vacío, no se puede leer o da cero filas, la recarga falla y los datos ya publicados quedan vivos e intactos. Mantén estables los nombres y tipos de las columnas: si cambian, la recarga te dice exactamente qué vista de qué reporte se rompió.",
  // ── Plan J J2 — Datos › Dimensiones ───────────────────────────
  "projects.data.sub.source": "Fuente",
  "projects.data.sub.dimensions": "Dimensiones",
  "projects.data.sub.ariaLabel": "Secciones de Datos",
  "dimensions.title": "Dimensiones",
  "dimensions.subtitle":
    "Una columna por la que los datos se pueden filtrar y restringir. Se define una sola vez acá y sirve para las dos cosas.",
  "dimensions.add": "Agregar dimensión",
  "dimensions.col.dimension": "Dimensión",
  "dimensions.col.origin": "Origen",
  "dimensions.col.definition": "Definición",
  "dimensions.col.values": "Valores",
  "dimensions.col.usedIn": "Se usa en",
  "dimensions.col.status": "Estado",
  "dimensions.origin.column": "Columna",
  "dimensions.origin.mapping": "Mapeo",
  "dimensions.origin.computed": "Calculada",
  "dimensions.def.mapping": "{match} → «{collection}» / {sheet}: {key} → {value}",
  "dimensions.def.noneHint":
    "Esta fila no trae definición, así que nada puede resolverla. No se creó desde esta pantalla.",
  "dimensions.status.ok": "Lista",
  "dimensions.status.needs_reload": "Falta recargar",
  "dimensions.status.inert": "Sin uso habilitado",
  "dimensions.status.schema_drift": "Desajuste de esquema",
  "dimensions.status.unresolvable": "No se puede resolver",
  "dimensions.usableAs.filter": "Filtro",
  "dimensions.usableAs.scope": "Ámbito",
  "dimensions.usableAs.none": "No habilitada para nada",
  "dimensions.usableAs.noneHint":
    "Una dimensión sin ningún uso habilitado es invisible para los filtros y hace que todo ámbito que la nombre no otorgue ningún acceso. Marca «Ámbito» antes de escribir una regla con ella.",
  "dimensions.usedIn.scopes": "{n} ámbito(s)",
  "dimensions.usedIn.noneHint":
    "Todavía ningún ámbito de acceso escribe reglas con esta dimensión.",
  "dimensions.values.n": "{n} distintos",
  "dimensions.values.view": "Ver valores",
  "dimensions.values.unavailable": "No está en los datos",
  "dimensions.values.unavailableHint":
    "La columna a la que apunta esta dimensión no está en el archivo del proyecto, así que no hay nada que contar. La columna Estado dice por qué.",
  "dimensions.values.errorTitle": "No se pudieron leer los valores",
  "dimensions.values.summary":
    "{shown} de {total} valores distintos de «{column}» ({type}).",
  "dimensions.values.nulls":
    "{n} fila(s) no tienen valor. No pertenecen a ningún ámbito restringido: una regla IN nunca coincide con un valor vacío.",
  "dimensions.values.notScopableTitle": "No sirve para reglas de acceso",
  "dimensions.values.notScopable":
    "Estos valores son reales y sirven como filtro, pero una regla de ámbito sobre una columna {type} se rechaza al construir el archivo del ámbito. Para acceso, usa una dimensión de tipo texto o numérico.",
  "dimensions.values.rowCount": "{n} fila(s)",
  "dimensions.values.nullValue": "(sin valor)",
  "dimensions.values.versionKey":
    "Leído de la versión {key} del archivo del proyecto.",
  "dimensions.detail.whyTitle": "Por qué este estado",
  "dimensions.detail.notInStoredSchema":
    "El archivo en vivo tiene esta columna, pero la última recarga no la registró en el esquema del proyecto. El archivo es el que decide, y nada acá empareja los dos.",
  "dimensions.actions.edit": "Editar",
  "dimensions.actions.delete": "Eliminar",
  "dimensions.delete.confirm":
    "¿Eliminar la dimensión «{name}»? Todo ámbito de acceso que escriba reglas con ella deja de otorgar acceso a este proyecto en la próxima recarga.",
  "dimensions.error.title": "Lo que respondió el servidor",
  "dimensions.empty.title": "Todavía no hay dimensiones",
  "dimensions.empty.desc":
    "Todavía no hay ninguna columna por la que filtrar ni restringir. Una dimensión es una columna: una que ya existe, un valor traducido con un Maestro que mantiene el cliente, o una calculada con SQL.",
  "dimensions.loaderUnavailable":
    "No se pudo revisar la última recarga, así que un fallo de dimensiones no se vería acá: {message}",
  "dimensions.reloadFailed.title":
    "La última recarga se detuvo en la dimensión «{dimension}»",
  "dimensions.reloadFailed.titleGeneric":
    "La última recarga se detuvo en la fase de dimensiones",
  "dimensions.reloadFailed.more": "Ver lo que respondió el servidor",
  "dimensions.reloadFailed.less": "Ocultar",
  "dimensions.reloadFailed.unmapped":
    "El Maestro que se nombra abajo no cubre todos los valores de los datos. Abre Maestros de este proyecto en el portal, agrega las filas que faltan en la hoja que se nombra y corre la recarga de nuevo. No hay una bolsa de «Otros», a propósito, y mientras tanto los datos anteriores siguen publicados.",
  "dimensions.reloadFailed.body":
    "No se publicó nada y los datos anteriores siguen vigentes. Corrige la definición que se nombra abajo y corre la recarga de nuevo.",
  "dimensions.form.createTitle": "Nueva dimensión",
  "dimensions.form.editTitle": "Editar dimensión",
  "dimensions.form.rejected": "El servidor rechazó esta definición",
  "dimensions.form.keyLabel": "Clave",
  "dimensions.form.keyHelp":
    "Minúsculas, dígitos y guiones bajos, empezando con una letra. Para una dimensión de mapeo o calculada se vuelve una columna real de los datos, y es lo que nombran las reglas de acceso.",
  "dimensions.form.keyHelpEdit":
    "Se puede renombrar, pero el servidor lo rechaza mientras un ámbito de acceso que otorga este proyecto escriba reglas con la clave anterior, y el rechazo nombra esos ámbitos.",
  "dimensions.form.keyInvalid":
    "La clave debe tener minúsculas, dígitos y guiones bajos, y empezar con una letra.",
  "dimensions.form.labelLabel": "Nombre",
  "dimensions.form.labelHelp":
    "Los dos idiomas son obligatorios: una etiqueta a medias se muestra como una clave cruda en una de las dos pantallas.",
  "dimensions.form.labelIncomplete": "Completa el nombre en los dos idiomas.",
  "dimensions.form.originLabel": "Origen",
  "dimensions.form.originHelp":
    "Una columna que ya está en los datos, un valor traducido con un Maestro que mantiene el cliente, o una calculada con SQL.",
  "dimensions.form.originHelpEdit":
    "El origen no se puede cambiar: define qué SIGNIFICA la columna de origen, mientras que toda regla de acceso existente seguiría apuntando a la misma clave. En su lugar, elimina la dimensión y vuelve a crearla.",
  "dimensions.form.usableAsLabel": "Se puede usar para",
  "dimensions.form.usableAsHelp":
    "Para qué se ofrece esta dimensión. Los filtros la leen en el editor de reportes; los ámbitos escriben reglas con ella para decidir quién ve qué filas.",
  "dimensions.form.column.sourceLabel": "Columna de origen",
  "dimensions.form.column.sourceHelp":
    "La lista es lo que registró la última recarga. El servidor revisa el archivo EN VIVO, así que una columna que no esté en la lista igual puede existir.",
  "dimensions.form.column.other": "Otra columna, fuera de la lista…",
  "dimensions.form.column.otherLabel": "Nombre de la columna",
  "dimensions.form.mapping.matchLabel": "Columna que se traduce",
  "dimensions.form.mapping.matchHelp":
    "La columna cruda cuyos valores se buscan en la hoja del Maestro.",
  "dimensions.form.mapping.collectionLabel": "Maestro (slug)",
  "dimensions.form.mapping.collectionHelp":
    "El slug de un Maestro de ESTE proyecto. Desde acá no se puede nombrar el Maestro de otro proyecto.",
  "dimensions.form.mapping.sheetLabel": "Hoja (nombre normalizado)",
  "dimensions.form.mapping.sheetHelp":
    "En minúsculas, con espacios y guiones convertidos en guiones bajos.",
  "dimensions.form.mapping.keyColumnLabel": "Columna clave de la hoja",
  "dimensions.form.mapping.keyColumnHelp":
    "Aquello POR LO QUE se busca el valor crudo.",
  "dimensions.form.mapping.valueColumnLabel": "Columna de valor de la hoja",
  "dimensions.form.mapping.valueColumnHelp":
    "En lo que se CONVIERTE el valor crudo. Se escribe en los datos como una columna nueva con el nombre de la clave de arriba.",
  "dimensions.form.mapping.sameColumn":
    "La clave y el valor no pueden ser la misma columna: el mapeo sería una columna de copias.",
  "dimensions.form.mapping.noPickerTitle":
    "Todavía no hay selector para estos tres",
  "dimensions.form.mapping.noPicker":
    "Escribe los nombres tal como aparecen en Maestros, en el portal. Si alguno está mal, el servidor responde con la lista real de Maestros, hojas o columnas que encontró, y esa respuesta se muestra acá.",
  "dimensions.form.computed.exprLabel": "Expresión SQL",
  "dimensions.form.computed.exprHelp":
    "Una expresión sobre una sola fila: sin SELECT, sin subconsulta, sin función de ventana y sin agregación. El servidor la revisa contra el archivo real antes de guardar.",
  "dimensions.form.computed.unknownColumns":
    "Estas columnas entre comillas no están en el esquema registrado: {columns}",
  "dimensions.form.computed.caseMismatch":
    "«{used}» está escrita con distinta capitalización que «{suggest}».",
  "projects.connections.title": "Conexiones",
  "projects.connections.intro":
    "Orígenes con nombre que este proyecto puede leer. Una conexión guarda host / url y un PUNTERO a sus credenciales — nunca las credenciales.",
  "projects.connections.addBtn": "Agregar conexión",
  "projects.connections.empty": "Todavía no hay conexiones.",
  "projects.connections.nameLabel": "Nombre",
  "projects.connections.kindLabel": "Tipo",
  "projects.connections.configLabel": "Config (JSON, solo campos no secretos)",
  "projects.connections.configInvalidJson": "La config no es JSON válido: {detail}",
  "projects.connections.secretRefLabel": "Referencia al secreto",
  "projects.connections.secretRefHint":
    "env:NOMBRE lee la env var BUILDER_SECRET_<TENANT>_NOMBRE (un objeto JSON con todos los campos); no hay fallback al nombre sin prefijo. vault:tenants/<tenant>/connections/<nombre> lee ese mismo objeto JSON del secret store (se carga con `python -m tenancy.secret_store set`). Déjala vacía solo para fuentes que no necesitan credenciales.",
  "projects.connections.saveBtn": "Guardar",
  "projects.connections.cancelBtn": "Cancelar",
  "projects.connections.editBtn": "Editar",
  "projects.connections.deleteBtn": "Eliminar",
  "projects.connections.testBtn": "Probar",
  "projects.connections.testing": "Probando…",
  "projects.connections.testOk": "Conexión OK",
  "projects.connections.testFailed": "La conexión falló",
  "projects.connections.deleteConfirm":
    "¿Eliminar la conexión \"{name}\"? Cualquier data source que la apunte debe reapuntarse primero.",
  "projects.connections.noCredentialsNotice":
    "Este formulario nunca acepta una contraseña ni un token. Pon los valores en la variable de entorno que nombra la referencia al secreto.",
  "projects.toggles.subtitle":
    "Elige qué secciones top-level expone el proyecto en el sidebar del portal.",
  "projects.toggles.dashboard": "Dashboards",
  "projects.toggles.maestros": "Maestros",
  "projects.toggles.files": "Archivos",
  "projects.toggles.requireOne":
    "Al menos una sección debe quedar habilitada.",
  "projects.deleteConfirm":
    "¿Eliminar el proyecto \"{name}\"? Los reportes no se borran, solo se desvinculan.",

  "list.group.noProject": "Sin proyecto",
  "list.create.project": "Proyecto",
  "list.create.project.none": "(ninguno)",

  "list.create.template": "Partir desde",
  "list.create.template.empty": "Vacío (desde cero)",

  "editor.preset.title": "Presets",
  "editor.preset.add": "Agregar preset",
  "editor.preset.empty":
    "No hay presets configurados — agrega al menos uno para que aparezcan chips en el portal.",
  "editor.preset.default": "por defecto",
  "editor.preset.defaultHint": "Se referencia como default_value",
  "editor.preset.resolves": "Resuelve a:",
  "editor.preset.resolving": "Resolviendo…",
  "editor.preset.moveUp": "Subir",
  "editor.preset.moveDown": "Bajar",
  "editor.preset.lengthDays": "Largo (días)",
  "editor.preset.lengthMonths": "Largo (meses)",
  "editor.preset.kind.absolute_range": "Rango absoluto",
  "editor.preset.kind.relative_days": "Días relativos",
  "editor.preset.kind.relative_months": "Meses relativos",
  "editor.preset.kind.hp_fiscal_q": "Trimestre fiscal HP",

  "editor.preview": "Previsualizar",
  "editor.save": "Guardar",
  "editor.publish": "Publicar",
  "editor.unpublish": "Despublicar",
  "editor.autosave": "Auto-guardar",
  "editor.dirty": "Sin guardar",
  "editor.saved": "Guardado",
  "editor.saveFailed": "Error al guardar",
  "editor.saveInProgress": "Ya hay un guardado en curso. Espera a que termine y vuelve a intentarlo.",
  "editor.unpublishFailed": "Error al despublicar",
  "editor.published": "Publicado",

  "tab.dashboard": "Dashboard",
  "tab.data": "Datos",
  "tab.ai": "IA",
  "tab.master": "Maestros",
  "tab.files": "Archivos",

  "publish.title": "Publicar reporte",
  "publish.body":
    "Publicar lo hace visible para todos los usuarios del hp-portal en el próximo reinicio del backend. La versión publicada anterior (si la hay) se reemplaza.",
  "publish.validating": "Validando contra el esquema DuckDB…",
  "publish.passed": "Validación OK — listo para publicar.",
  "publish.success": "Publicado correctamente. Cerrando…",
  "publish.warningsTitle": "advertencias",
  "publish.errorsTitle": "No se puede publicar — soluciona estos errores primero",
  "publish.confirm": "Publicar",
  "publish.close": "Cerrar",
  "publish.confirmAnyway": "Publicar igual",
  "views.checking": "Probando las vistas guardadas del Home con IA que leen este reporte…",
  "views.checkFailed": "No se pudieron probar las vistas guardadas del Home con IA.",
  "views.checkFailedBody": "La respuesta fue: {error}. Puedes seguir igual, sin saber si alguna se rompe.",
  "views.none": "Ninguna vista guardada del Home con IA lee este reporte.",
  "views.ok_one": "La vista guardada del Home con IA que lee este reporte sigue funcionando.",
  "views.ok_other": "Las {count} vistas guardadas del Home con IA que leen este reporte siguen funcionando.",
  "views.brokenTitle_one": "1 vista guardada del Home con IA dejaría de funcionar",
  "views.brokenTitle_other": "{count} vistas guardadas del Home con IA dejarían de funcionar",
  "views.brokenBody_one": "Probamos la vista que lee este reporte con la configuración nueva, como la abriría su dueño. Si publicas igual, su dueño verá el error al abrirla.",
  "views.brokenBody_other": "Probamos las {count} vistas que leen este reporte con la configuración nueva, como las abriría cada dueño. Si publicas igual, cada dueño verá el error al abrir su vista.",
  "views.brokenBodyLive_one": "Probamos la vista que lee este reporte con la configuración nueva, como la abriría su dueño. Si guardas igual, su dueño verá el error al abrirla.",
  "views.brokenBodyLive_other": "Probamos las {count} vistas que leen este reporte con la configuración nueva, como las abriría cada dueño. Si guardas igual, cada dueño verá el error al abrir su vista.",
  "views.stillWork_one": "{count} vista más sigue funcionando",
  "views.stillWork_other": "{count} vistas más siguen funcionando",
  "views.copy": "Copiar la lista",
  "views.copied": "Copiada",
  "views.alreadyBroken_one": "1 vista ya fallaba antes de este cambio",
  "views.alreadyBroken_other": "{count} vistas ya fallaban antes de este cambio",
  "views.colView": "Vista guardada",
  "views.colOwner": "Dueño",
  "views.colFails": "Qué falla",
  "views.wholeView": "No abre",
  "views.someElements_one": "{count} elemento",
  "views.someElements_other": "{count} elementos",
  "views.saveTitle": "Guardar cambios",
  "views.saveBody": "Este reporte está publicado: al guardar, los cambios quedan en vivo para todos.",
  "views.keepEditing": "Seguir editando",
  "views.saveAnyway": "Guardar igual",
  "views.publishGroupTitle": "Publicar grupo de reportes",
  "views.publishGroupBody": "Algunos reportes de este grupo romperían vistas guardadas del Home con IA al quedar en vivo.",
  "publish.unpublishConfirm":
    "¿Despublicar este reporte? Desaparecerá del hp-portal en el próximo reinicio.",

  "table.title": "Datos",
  "table.search": "Buscar…",
  "table.crossfilter.apply": "Filtrar el reporte por este valor",
  "filterbar.profile": "Perfil",
  "filterbar.saveView": "Guardar vista",
  "filterbar.soon": "próx.",
  "filterbar.savedFilters": "Filtros guardados",
  "filterbar.skuList": "Lista de SKU",
  "filterbar.skuListApplied": "Lista de SKU · {n}",
  "filterbar.skuListCodes": "{n} códigos",
  "filterbar.skuListUpload": "Subir archivo…",
  "filterbar.skuListClear": "Quitar la lista de SKU",
  "inspector.dashboard.filtersLayoutRows": "Filas",
  "table.action.filter": "Filtrar",
  "table.action.clear": "Quitar filtro",
  "table.total": "Total",
  "table.crossfilter.clear": "Quitar este filtro",
  "table.rowdrill.hint": "Click para expandir el detalle de la fila",
  "table.rowdrill.open": "Ver detalle",
  "table.rowdrill.close": "Cerrar detalle",
  "table.rowdrill.back": "Volver",
  "table.rowdrill.detail": "Detalle de fila",
  // Single-brace interpolation per the builder's i18next config.
  "table.rowdrill.invalidMode":
    "El row_drill_mode de esta tabla es '{value}' y debe ser accordion o swap. Corrígelo en el builder.",
  "table.rowdrill.view.table": "Tabla",
  "table.rowdrill.view.chart": "Gráfico",
  "table.rowdrill.view.both": "Ambos",
  "table.rowdrill.view.label": "Vista",
  "table.export": "Exportar",
  "table.exportXlsx": "Exportar a XLSX",
  "table.exportCsvLatam": "Exportar a CSV (LatAm)",
  "table.exportCsvUs": "Exportar a CSV (US)",
  "table.exportFailed": "No se pudo exportar; no se descargó ningún archivo.",
  "table.exporting": "Exportando…",
  "table.exportXlsxAsCsv": "La tabla tiene más de 250.000 filas, así que se exportó en CSV (LATAM) en lugar de Excel, en un ZIP con un archivo cada 250.000 filas.",
  "table.openLink": "Ver ↗",
  "table.fullscreen": "Pantalla completa",
  "table.exitFullscreen": "Salir de pantalla completa",
  "table.chips.filter": "Filtros rápidos",
  "table.chips.sort": "Ordenar por",
  "table.noData": "Sin datos",
  "table.previous": "Anterior",
  "table.next": "Siguiente",
  "dashboard.table.previous": "Anterior",
  "dashboard.tabs.aria": "Vistas del reporte",
  "dashboard.tabs.back": "Volver a {tab}",
  "dashboard.glossary.open": "Glosario",
  "dashboard.glossary.title": "Glosario",
  "dashboard.glossary.close": "Cerrar el glosario",
  "dashboard.scope.aria": "Filtros activos",
  "dashboard.scope.all": "Todos",
  "dashboard.scope.latest": "Último día con datos",
  "inspector.section.tab": "Tab",
  "inspector.section.tabNone": "(primera tab)",
  "dashboard.longwait.label": "Consultando los datos…",
  "chart.clickFilter": "Clic para filtrar",
  "chart.shareBreakdown.of": "de",
  "dashboard.table.next": "Siguiente",
  "dashboard.chart.calloutNoRow": "Este bloque no devolvió ninguna fila",
  "dashboard.chart.missingMetric": "La consulta no devuelve la columna \"metric\" (columnas: {columns}). Usa el alias metric para el valor.",
  "dashboard.chart.aiGenerated": "IA",
  "dashboard.chart.aiGeneratedLong": "Generado por IA",
  // Single-brace interpolation — ver nota EN.
  "dashboard.chart.unknownType": "Tipo de gráfico desconocido: {type}",
  "dashboard.chart.calendarDuplicateDay": "Hay más de una fila para el mismo día: {day}",
  "dashboard.kpi.previous": "Anterior",
  "dashboard.error.charts": "No se pudieron cargar los gráficos",
  "dashboard.error.texts": "No se pudieron cargar los textos",
  "dashboard.error.drill": "No se pudo cargar el detalle",
  "table.exportSheet": "Datos",
  "dashboard.chart.bulletMissingTargetMax": "Cada fila de este gráfico necesita una meta numérica y un máximo mayor que 0",
  "dashboard.chart.mapLoadError": "No se pudieron cargar los límites del mapa",
  "dashboard.chart.map.byCountry": "Por país",
  "dashboard.chart.map.byRegion": "Por región",
  "dashboard.chart.map.scale": "Escala",
  "dashboard.chart.map.low": "Bajo",
  "dashboard.chart.map.high": "Alto",
  "dashboard.chart.map.noData": "Sin datos",
  "dashboard.chart.map.loading": "Cargando mapa…",
  "dashboard.chart.calendar.less": "Menos",
  "dashboard.chart.calendar.more": "Más",
  "dashboard.kpi.anomalyExpected": "esperado",
  "dashboard.chart.readoutLoading": "Generando el análisis…",
  "dashboard.chart.readoutUnavailable": "El análisis narrativo se genera en el portal",
  "dashboard.portfolio.unavailable": "El portafolio de vistas guardadas se calcula en el portal, para el usuario que inició sesión",
  "dashboard.portfolio.loading": "Cargando tus vistas guardadas…",
  "dashboard.portfolio.stale": "Mostrando el portafolio anterior — la actualización falló",
  "dashboard.portfolio.empty.title": "Guarda tu primera vista",
  "dashboard.portfolio.empty.body": "Acota los filtros a un grupo comparable de productos y guárdalo. Cada vista guardada aparece aquí como una tarjeta, con la posición de la marca en ella.",
  "dashboard.portfolio.empty.cta": "Abrir filtros guardados",
  "dashboard.portfolio.header.save": "Guardar filtros actuales",
  "dashboard.portfolio.summary.winRate": "Win rate",
  "dashboard.portfolio.summary.winRateHint": "{lead} de {total} vistas con la marca foco líder",
  "dashboard.portfolio.summary.behind": "Atrás",
  "dashboard.portfolio.summary.behindHint": "{n} de {total} vistas",
  "dashboard.portfolio.summary.position": "Posición",
  "dashboard.portfolio.summary.positionHint": "con comparación en {present} de {total} vistas",
  "dashboard.portfolio.summary.leaders": "Liderazgo entre marcas",
  "dashboard.portfolio.summary.leadersHint": "quién lidera cada vista",
  "dashboard.portfolio.summary.of": "de",
  "dashboard.portfolio.card.leader": "Líder: {brand}",
  "dashboard.portfolio.card.open": "Abrir esta vista →",
  "dashboard.portfolio.card.focus": "Foco",
  "dashboard.portfolio.card.noFilters": "Todo el catálogo",
  "dashboard.chart.readoutStale": "Mostrando el análisis anterior — falló la actualización",
  // SWR per-block error surfacing (review #56 #2)
  "dashboard.blockError.title": "No se pudieron cargar estos datos",
  "dashboard.blockError.retry": "Reintentar",
  "dashboard.blockError.stale":
    "Error al actualizar — mostrando datos anteriores",
  // Iter 31-C — ver nota EN. Single-brace interpolation.
  "dashboard.table.showing": "Mostrando {from}-{to} de {total}",
  "common.export": "Exportar",
  "common.noData": "Sin datos",
  "common.loadingData": "Cargando datos…",
  "table.showingOf": "Mostrando {from}-{to} de {total} {label}",
  "table.rowsLabel": "filas",
  "table.rows": "{count} filas",
  "table.count": "{count} productos",
  "filterbar.title": "FILTROS",
  "filterbar.clearAll": "Limpiar todo",
  "filterbar.pick": "Elegir…",
  "filterbar.all": "Todos",
  "filterbar.locked": "bloqueado",
  "filterbar.clear": "Limpiar",
  "filterbar.searchOptions": "Buscar…",
  "filterbar.searchPlaceholder": "Buscar…",
  "filterbar.noOptions": "Sin opciones.",
  "filterbar.nSelected": "{n} seleccionados",
  "filterbar.relDays": "Últimos {length} días",
  "filterbar.relWeeks": "Últimas {length} semanas",
  "filterbar.relMonths": "Últimos {length} meses",
  "filterbar.relYears": "Últimos {length} años",
  "filterbar.gte": "≥ {v}",
  "filterbar.lte": "≤ {v}",
  "filters.title": "Filtros",
  "filters.removeChip": "Quitar {label}",
  "filters.resetAll": "Limpiar todo",
  "reportGroup.column": "Grupo",
  "reportGroup.backToProject": "Volver al proyecto",
  "reportGroup.subtitle": "Los reportes de este grupo son los tabs de una página y comparten un solo juego de filtros.",
  "reportGroup.loadError": "No se pudo cargar el grupo de reportes",
  "reportGroup.save": "Guardar",
  "reportGroup.saving": "Guardando…",
  "reportGroup.saved": "Guardado. Los reportes publicados del grupo lo toman en menos de un minuto.",
  "reportGroup.publish": "Publicar grupo",
  "reportGroup.publishing": "Publicando…",
  "reportGroup.published": "Todos los reportes del grupo quedaron publicados.",
  "reportGroup.publishPending": "Publicar grupo · {count} pendiente(s)",
  "reportGroup.pendingHint": "Cada filtro necesita una decisión en cada reporte: lo usa o no aplica.",
  "reportGroup.reports": "Reportes",
  "reportGroup.reportsHint": "Su orden es el orden de los tabs.",
  "reportGroup.openReport": "Abrir {key} en el editor",
  "reportGroup.moveUp": "Subir",
  "reportGroup.moveDown": "Bajar",
  "reportGroup.filters": "Filtros del grupo · {count}",
  "reportGroup.filtersHint": "En el orden de la cascada. Cada reporte usa un filtro en una de sus columnas, o no le aplica.",
  "reportGroup.filter": "Filtro",
  "reportGroup.uses": "Usa la columna",
  "reportGroup.notApplicable": "No aplica",
  "reportGroup.missing": "Falta",
  "reportGroup.cellAria": "Decisión de {report} para el filtro {filter}",
  "reportGroup.columnAria": "Columna de {report} para el filtro {filter}",
  "reportGroup.reasonAria": "Por qué {filter} no aplica en {report}",
  "reportGroup.reasonPlaceholder": "Motivo (opcional)",
  "reportGroup.legendUses": "el reporte filtra por esa columna",
  "reportGroup.legendNotApplicable": "no aplica: decisión explícita, el valor se mantiene para los otros tabs",
  "reportGroup.legendMissing": "bloquea el publish",
  "reportGroup.memberError": "Este reporte es un tab de un grupo de reportes y sus filtros están incompletos: {error}",
  "reportGroup.membersHint": "Todos los reportes del proyecto son tabs; un reporte nuevo se suma al grupo. Para sacar reportes, muestra el proyecto como lista:",
  "reportGroup.membersHintLink": "Configuración del proyecto",
  "projectMode.title": "Cómo muestra sus reportes el proyecto",
  "projectMode.description": "Tabs o lista en el sidebar, nunca las dos. Un cambio confirmado llega al portal en menos de un minuto.",
  "projectMode.tabs": "Tabs",
  "projectMode.tabsHint": "Una página con todos los reportes como tabs y un solo juego de filtros para todos.",
  "projectMode.list": "Lista en el sidebar",
  "projectMode.listHint": "Cada reporte es una fila del sidebar del portal, con sus propios filtros.",
  "projectMode.current": "Actual",
  "projectMode.openGroup": "Abrir el grupo",
  "projectMode.review": "Revisar el cambio",
  "projectMode.reviewing": "Revisando…",
  "projectMode.reviewTitleTabs": "Al pasar a tabs:",
  "projectMode.reviewTitleList": "Al pasar a lista:",
  "projectMode.r.tabsReports": "los {count} reportes pasan a ser los tabs del grupo {group}, en el orden del sidebar",
  "projectMode.r.tabsFilters": "sus filtros pasan al grupo: {count} ({filters})",
  "projectMode.r.notApplicable": "{report}: «no aplica» en {filters} (no los tenía)",
  "projectMode.r.savedViewsUp": "{count} vista(s) guardada(s) pasan al grupo",
  "projectMode.r.listReports": "cada uno de los {count} reportes recupera como propios los filtros que usa",
  "projectMode.r.removedAction": "se quita un botón que abría otro reporte: {action}",
  "projectMode.r.savedViewsDown": "{count} vista(s) guardada(s) vuelven a su reporte",
  "projectMode.r.trimmed": "se quitan valores de filtros que el reporte ya no tiene: {item}",
  "projectMode.r.lostAliases": "los enlaces viejos con ?tab= ({tabs}) abrirán el primer reporte",
  "projectMode.reviewBlocked": "No se puede hacer el cambio:",
  "projectMode.confirm": "Confirmar el cambio",
  "projectMode.applying": "Aplicando…",
  "projectMode.appliedTabs": "El proyecto ahora muestra sus reportes como tabs.",
  "projectMode.appliedList": "El proyecto ahora muestra sus reportes como lista en el sidebar.",
  "projectMode.loadError": "No se pudo leer el modo del proyecto",
  "projectMode.mixedTitle": "Este proyecto mezcla tabs y lista",
  "projectMode.mixedBody": "Tiene un grupo de reportes junto a reportes que no están en él, de antes de la regla. Mueve reportes a otro proyecto hasta que cada proyecto sea tabs o lista; ahí se puede elegir su modo.",
  "projectMode.mixedGroups": "Grupos",
  "projectMode.mixedLoose": "Reportes fuera de grupo",
  "filters.notApplicableIn": "no aplica en {report}",
  "filters.pinnedNotApplicable": "No aplica en {report}: {filters}",
  "filters.phoneShow": "Ver controles",
  "filters.phoneHide": "Ocultar controles",
  "filters.phoneChanged": "Filtros cambiados: {count}",
  "filters.notApplicableLine": "No aplican en {report}:",
  "filters.notApplicableKept": "Sus valores se mantienen para los otros tabs.",
  "dashboard.group.orphanKeys": "Estos filtros ya no existen y no se aplicaron: {keys}",
  "filterbar.noMatches": "Sin coincidencias.",
  "filterbar.narrowSearch": "Mostrando 200 de {count} — refina tu búsqueda.",
  "filterbar.typeToSearch": "Escribe {min}+ letras para buscar",
  "filterbar.dayPlaceholder": "aaaa-mm-dd",
  "filterbar.required": "Requerido",
  "filterbar.requiredHint":
    "Elige un valor para el filtro marcado para cargar el dashboard.",

  // Date range picker — presets + custom modes
  // HP fiscal-quarter chip labels (ES). Keep in sync with EN block.
  "daterange.preset.hp_all": "Todo",
  "daterange.preset.hp_q_actual": "Q actual",
  "daterange.preset.hp_last_q": "Último Q",
  "daterange.preset.hp_last_4_q": "Últimos 4 Q",
  "daterange.preset.hp_last_8_q": "Últimos 8 Q",
  // Cajón — layout del header de filtros por reporte
  "inspector.dashboard.filtersLayout": "Header de filtros",
  "inspector.dashboard.filtersLayoutBar": "Banner",
  "inspector.dashboard.filtersLayoutCajon": "Cajón",
  "inspector.dashboard.filtersLayoutBarHint":
    "Filtros embebidos en el banner del header (el layout clásico).",
  "inspector.dashboard.filtersLayoutCajonHint":
    "Fila de chips en el header que se expande en el lugar para editar los filtros.",
  "inspector.dashboard.advanced": "Avanzado",
  "inspector.dashboard.runtimeViewSql": "SQL de runtime view",
  "inspector.dashboard.runtimeViewSqlPlaceholder":
    "WITH _runtime_view AS (SELECT … FROM gating_fact_seller_weekly {where} …) SELECT * FROM _runtime_view",
  "inspector.dashboard.runtimeViewSqlHint":
    "SQL materializado como TEMP VIEW _runtime_view antes que se ejecuten KPIs/charts. Usa {where} como placeholder del filtro. Validar con el skill report-builder antes de publicar.",
  "daterange.placeholder": "Elige un rango de fechas",
  "daterange.today": "Hoy",
  "daterange.yesterday": "Ayer",
  "daterange.last_7_days": "7 días anteriores",
  "daterange.last_30_days": "30 días anteriores",
  "daterange.last_week": "Semana anterior",
  "daterange.last_month": "Mes anterior",
  "daterange.last_3_months": "3 meses anteriores",
  "daterange.last_12_months": "12 meses anteriores",
  "daterange.fixed": "Rango de fechas fijo…",
  "daterange.relative": "Rango de fechas relativo…",
  "daterange.none": "(ninguno)",
  "daterange.startDate": "Fecha desde",
  "daterange.endDate": "Fecha hasta",
  "daterange.apply": "Aplicar",
  "daterange.cancel": "Cancelar",
  "daterange.back": "Volver",
  "daterange.lastN": "Últimos",
  "daterange.units.days": "días",
  "daterange.units.weeks": "semanas",
  "daterange.units.months": "meses",
  "daterange.rangeSeparator": "→",
  "daterange.invalid": "Rango inválido",
  // Dual-calendar popover strings (i18n audit 2026-07-14 — passed to
  // bi-components' DateRangePicker via its ``strings`` prop).
  "daterange.start": "Inicio",
  "daterange.end": "Fin",
  "daterange.pending": "pendiente",
  "daterange.clear": "Limpiar",
  "daterange.hintPickStart": "Haz clic para seleccionar la fecha de inicio",
  "daterange.hintPickEnd": "Haz clic para seleccionar la fecha de fin",
  "daterange.goToStartMonth": "Ir al mes de la fecha de inicio",
  "daterange.goToEndMonth": "Ir al mes de la fecha de fin",
  "daterange.startPending": "Fecha de inicio pendiente",
  "daterange.endPending": "Fecha de fin pendiente",

  // Date-range slider (type: "date_slider" filter)
  "dateslider.noData": "Sin datos de fechas",
  "dateslider.from": "Desde",
  "dateslider.to": "Hasta",
  "numrange.min": "Mín.",
  "numrange.max": "Máx.",
  "numrange.noData": "Sin valores para acotar",
  "numrange.logScale": "escala log",
  "numrange.logUnusable": "La escala log necesita valores mayores que 0",
  "numrange.histogramError": "No se pudo cargar la distribución",
  "numrange.remaining": "Quedan {n} de {total}",
  "numrange.clear": "Quitar umbral",

  "filter.country": "País",
  "filter.marketplace": "Marketplace",
  "filter.seller": "Vendedor",
  "filter.account": "Cuenta",
  "filter.category": "Categoría",
  "filter.seller_status": "Estado del vendedor",
  "filter.in_listing": "En listado",
  "filter.brand": "Marca",
  "filter.region": "Región",

  "preview.defaultTableTitle": "Datos",

  "kpi.total_annual_rev": "Ingresos anuales totales",
  "kpi.total_skus": "SKUs totales",
  "kpi.seller_status": "Estado del vendedor",
  "kpi.avg_buy_box_rate": "Buy Box promedio",
  "kpi.listed": "Listado %",
  "kpi.revenue_3m": "Ingresos 3M",
  "kpi.1p_revenue_3m": "Ingresos 1P 3M",
  "kpi.3p_revenue_3m": "Ingresos 3P 3M",
  "kpi.week_start": "Semana inicio",
  "kpi.week_end": "Semana fin",

  "chart.top_10_asins_by_annual_rev": "Top 10 ASINs por ingresos anuales",
  "chart.revenue_1p_vs_3p": "Ingresos 1P vs 3P",
  "chart.woh_vs_annual_rev_by_asin": "WOH vs ingresos anuales por ASIN",
  "chart.avg_woh_by_category": "WOH promedio por categoría",
  "chart.buy_box_rate_vs_1p_rev_by_asin":
    "Buy Box vs % de ingresos 1P por ASIN",
  "chart.risk_score_by_asin": "Puntaje de riesgo por ASIN",

  "column.sku": "SKU",
  "column.category": "Categoría",
  "column.listing_title": "Título del listing",
  "column.annual_rev": "Ingresos anuales",
  "column.rev_3m": "Ingresos 3M",
  "column.1p_rev_3m": "Ingresos 1P 3M",
  "column.1p_rev": "1P %",
  "column.3p_rev_3m": "Ingresos 3P 3M",
  "column.3p_rev": "3P %",
  "column.woh": "WOH",
  "column.1p": "1P",
  "column.mpa": "# MPA",
  "column.non_mpa": "# No-MPA",
  "column.buy_box_rate": "Buy Box",
  "column.asin": "ASIN",
  "column.risk_score": "Puntaje de riesgo",
  "column.buy_box": "Buy Box %",

  "section.kpis_principales": "KPIs principales",
  "section.dashboard_explorer": "Dashboard Explorer",
  "section.diagnosticos_del_mercado": "Diagnósticos del Mercado",
  "sectionSubtitle.analisis_visual_de_cobertura_mix_1p_3p_y_riesgo_por_asin":
    "Análisis visual de cobertura, mix 1P/3P y riesgo por ASIN",

  "preview.title": "Vista previa · datos en vivo",
  "preview.close": "Cerrar",
  "preview.filtersError": "No se pudieron cargar las opciones de filtro: {err}",
  "preview.loadingCanvas": "Cargando vista previa…",
  "preview.empty":
    "Agrega secciones en el editor para previsualizarlas aquí.",
  "preview.charts.title": "Diagnósticos",
  "preview.charts.subtitle": "Análisis visual con los últimos datos.",
  "preview.tableSubtitle": "%{count} filas",

  // ── Editor chrome (ES) ──────────────────────────────────
  "editor.inspector.reportIdentity": "Identidad del reporte",
  "editor.inspector.displayNameLabel": "Nombre visible",
  "editor.inspector.displayNamePlaceholder": "Mi reporte",
  "editor.inspector.duckdbTableLabel": "Tabla DuckDB",
  "editor.inspector.duckdbTableHint": "Tabla subyacente — alimenta los column pickers de los editores.",
  "editor.inspector.duckdbTablePlaceholder": "mi_tabla",
  "editor.inspector.descriptionLabel": "Descripción (opcional)",
  "editor.inspector.descriptionPlaceholder": "De qué trata este reporte.",

  "editor.filters.globalTitle": "Filtros globales",
  "editor.filters.globalDesc": "Se aplican a todas las secciones salvo que se sobrescriban localmente.",
  "editor.filters.sectionTitle": "Filtros de la sección",
  "editor.filters.sectionDesc": "Se aplican solo a los ítems de esta sección. Se combinan con los globales en runtime.",
  "editor.filters.addBtn": "Agregar filtro",
  "editor.filters.emptyMsg": "Todavía no hay filtros.",
  "editor.filters.duplicateKeys": "Claves duplicadas: {keys}. El engine descarta todos menos el primero.",
  "editor.filter.unnamed": "(sin nombre)",
  "editor.filter.duplicateBtn": "Duplicar filtro",
  "editor.filter.removeBtn": "Eliminar filtro",
  "editor.filter.keyLabel": "Clave",
  "editor.filter.keyPlaceholder": "country",
  "editor.filter.keyValidation": "solo minúsculas / dígitos / guiones bajos",
  "inspector.filter.selectionMode": "Selección",
  "inspector.filter.required": "Selección obligatoria",
  "inspector.filter.extraOptions": "Opciones adicionales (separadas por coma)",
  "inspector.filter.extraOptionsHint": "Permite seleccionar valores admitidos aunque aún no tengan datos. Agrega opciones, nunca registros ni métricas.",
  "inspector.filter.dependencies": "Depende de",
  "inspector.filter.legacyCascade": "Todos los otros filtros (anterior)",
  "inspector.filter.rootCascade": "Ninguno — primera selección",
  "inspector.filter.dependenciesHint": "Solo estos filtros limitan sus opciones. Cambiar uno anterior limpia los filtros dependientes.",
  "inspector.filter.tabs": "Visible en pestañas",
  "inspector.filter.tabsHint": "Desmarca una pestaña para ocultar este control ahí. El filtro sigue aplicándose a los datos; solo necesitan mostrarlo las pestañas cuyos elementos lo leen.",
  "inspector.sql.edit": "Editar SQL",
  "inspector.sql.apply": "Aplicar a la vista previa",
  "inspector.sql.editHint": "Revisa la vista previa antes de guardar. Guardar un reporte publicado modifica el reporte visible.",
  "inspector.filter.checkboxDropdown": "Dropdown con casillas (múltiple)",
  "inspector.filter.multipleDefault": "El valor por defecto contiene varios valores. Mantén la selección múltiple o edita primero el valor por defecto.",
  "inspector.filter.fixedOptions": "{count} opciones fijas; la lista no depende de los otros filtros.",
  "inspector.filter.useLiveOptions": "Usar valores de los datos y cascada con otros filtros",
  "inspector.filter.liveOptions": "Las opciones vienen de los datos y dependen de los otros filtros.",
  "inspector.dashboard.snapshot": "Ver respaldo del reporte guardado",
  "inspector.dashboard.downloadSnapshot": "Descargar respaldo (JSON)",
  "editor.filter.typeLabel": "Tipo",
  "editor.filter.typeSelect": "Selector simple",
  "editor.filter.typeAutocomplete": "Autocompletar",
  "editor.filter.typeMultiSelect": "Selección múltiple",
  "editor.filter.typeChip": "Chips (múltiple)",
  "editor.filter.typeRange": "Slider de rango",
  "editor.filter.typeSearch": "Búsqueda (contiene texto)",
  "editor.filter.typeDateRange": "Rango de fechas",
  "editor.filter.typeDateCalendar": "Día único",
  "editor.filter.typeDateRelative": "Período relativo",
  "editor.filter.defaultDateHint": "Aplicado al cargar (formato ISO).",
  "editor.filter.jsonNoDefaultHint": "Filtro con encoding JSON — deja defaults vacíos; el widget runtime maneja el input.",
  "editor.filter.columnLabel": "Columna",
  "editor.filter.columnHint": "Elige desde la tabla del reporte.",
  "editor.filter.columnHintTemporal": "Solo se muestran columnas DATE / TIMESTAMP.",
  "editor.filter.defaultLabel": "Valor por defecto",
  "editor.filter.defaultNone": "(ninguno)",
  "editor.filter.defaultPresetHint":
    "Se aplica al cargar; el usuario puede cambiarlo desde la barra de filtros.",
  "editor.filter.lockedLabel": "Valor bloqueado",
  "editor.filter.lockedHint": "Fijo; el usuario no lo puede cambiar.",
  "editor.filter.lockedNone": "(desbloqueado)",
  "editor.filter.requiredLabel": "Requerido (auto-selecciona un valor al cargar)",

  "editor.diagram.addSection": "Agregar sección",
  "editor.diagram.emptyTitle": "Dashboard vacío",
  "editor.diagram.emptyHint": "Agrega tu primera sección para empezar a componer el reporte.",
  "editor.diagram.emptyHintReadOnly": "Las secciones e ítems se gestionan vía Claude con el skill report-builder.",
  "editor.diagram.sectionFallback": "Sección",
  "editor.diagram.itemsLabel": "ítems",
  "editor.diagram.readOnlyTitle": "Solo lectura.",
  "editor.diagram.readOnlyBody": "Los cambios estructurales (agregar/quitar/reordenar secciones e ítems, ancho de ítems) se hacen vía Claude con el skill report-builder. Haz click en un ítem para editar su contenido en el panel lateral.",
  "editor.diagram.readOnlyNote": "El canvas del dashboard es de sólo lectura; las ediciones pasan por el skill report-builder.",

  "editor.section.dragToReorder": "Arrastrar para reordenar",
  "editor.section.duplicateBtn": "Duplicar sección",
  "editor.section.deleteBtn": "Eliminar sección",
  "editor.section.renameBtn": "Renombrar sección",
  "editor.section.addBtn": "Agregar",
  "editor.section.emptyHint": "Sección vacía. Agrega tu primer ítem:",
  "editor.section.emptyHintReadOnly": "Sección vacía. Agrega ítems vía Claude con el skill report-builder.",
  "editor.section.deleteConfirm": "¿Eliminar la sección \"{title}\" y todos sus ítems?",
  "editor.section.itemCountSingular": "ítem",
  "editor.section.itemCountPlural": "ítems",

  "editor.element.dragToReorder": "Arrastrar para reordenar",
  "editor.element.duplicateBtn": "Duplicar",
  "editor.element.deleteBtn": "Eliminar",
  "editor.kpi.untitled": "KPI sin título",
  "editor.chart.untitled": "Chart sin título",
  "editor.chart.typeSubtitle": "tipo: {type}",
  "editor.table.label": "Tabla de datos",
  "editor.table.columnSingular": "columna",
  "editor.table.columnPlural": "columnas",
  "editor.text.label": "Bloque de texto",

  "editor.section.editingTitle": "Editando {element}",
  "editor.section.settingsTitle": "Configuración de la sección",
  "editor.section.dashboardSettingsTitle": "Configuración del dashboard",
  "editor.section.itemHint": "Configuración por elemento.",
  "editor.section.sectionHint": "Configuración a nivel sección y filtros locales.",
  "editor.section.dashboardHint": "Elige una sección o ítem en el canvas, o edita la configuración global del dashboard abajo.",
  "editor.section.titleLabel": "Título",
  "editor.section.subtitleLabel": "Subtítulo (opcional)",
  "editor.section.itemsLabel": "Ítems en esta sección ({count})",
  "editor.section.noItemsHint": "Todavía no hay ítems — usa el menú \"Agregar\" del header de la sección.",
  "editor.item.widthLabel": "Ancho (columnas del grid)",
  "editor.item.rawPropsLabel": "Props raw (JSON)",
  "editor.item.duplicateBtn": "Duplicar",
  "editor.item.addAnotherBtn": "Agregar otro",
  "editor.item.deleteBtn": "Eliminar",

  "editor.kpi.labelLabel": "Etiqueta",
  "editor.kpi.labelPlaceholder": "Ingresos anuales totales",
  "editor.kpi.sqlLabel": "Expresión SQL",
  "editor.kpi.sqlHint": "Agregación sobre la tabla DuckDB del reporte — se envuelve en SELECT … FROM tabla en runtime.",
  "editor.kpi.sqlPlaceholder": "SUM(\"Annual Rev\")",
  "editor.kpi.formatLabel": "Formato",
  "editor.kpi.accentColorLabel": "Color de acento",
  "editor.kpi.accentColorHint": "Borde coloreado de la card. Rota automáticamente según la posición del ítem.",
  "editor.kpi.ignoreFiltersLabel": "Ignorar filtros (opcional)",
  "editor.kpi.ignoreFiltersHint": "Claves de filtros que este KPI debe ignorar — ej. \"in_listing\" para que una métrica de cobertura no responda al filtrado del usuario.",
  "editor.kpi.ignoreFiltersPlaceholder": "Escribe una clave de filtro…",
  // Phase A — variantes KPI
  "editor.kpi.variantLabel": "Variante",
  "editor.kpi.variantHint": "Estilo visual del KPI. Las variantes marcadas SQL requieren una consulta extra (sparkline / período anterior).",
  "editor.kpi.variantNeedsSqlHint": "Requiere SQL extra abajo",
  "editor.kpi.variantCard": "Card",
  "editor.kpi.variantCardDesc": "Card básica con acento lateral (default)",
  "editor.kpi.variantSparkline": "Sparkline",
  "editor.kpi.variantSparklineDesc": "Valor + mini-línea de tendencia + pill de delta",
  "editor.kpi.variantRing": "Anillo",
  "editor.kpi.variantRingDesc": "Valor dentro de un anillo de progreso",
  "editor.kpi.variantBar": "Barra",
  "editor.kpi.variantBarDesc": "Valor sobre una barra de progreso horizontal",
  "editor.kpi.variantHero": "Hero",
  "editor.kpi.variantHeroDesc": "Card grande con gradiente + ícono opcional",
  "editor.kpi.variantCompare": "Comparar",
  "editor.kpi.variantCompareDesc": "Actual vs anterior, delta auto",
  // Phase H — anomaly + score
  "editor.kpi.variantAnomaly": "Anomalía",
  "editor.kpi.variantAnomalyDesc": "Valor con severidad, baseline esperada + descripción",
  "editor.kpi.variantScore": "Score",
  "editor.kpi.variantScoreDesc": "Score 0..max anillo con barras de breakdown opcionales",
  "editor.kpi.variantCore": "Core (tira DSA)",
  "editor.kpi.variantCoreDesc": "Tarjeta en color firma: badge CORE, delta en pp contra previous_sql, subtítulo y franja de acción",
  "editor.kpi.severityLabel": "Severidad",
  "editor.kpi.severityHint": "Determina el color (rojo / ámbar / morado).",
  "editor.kpi.severityCritical": "Crítica (rojo)",
  "editor.kpi.severityWarning": "Advertencia (ámbar)",
  "editor.kpi.severityInfo": "Info (morado)",
  "editor.kpi.expectedSqlLabel": "SQL esperado",
  "editor.kpi.expectedSqlHint": "Agregado opcional para el baseline. Mismo scope que el KPI; el % de desviación se calcula automático.",
  "editor.kpi.expectedSqlPlaceholder": "AVG(metric) OVER ()",
  "editor.kpi.descriptionLabel": "Descripción",
  "editor.kpi.descriptionHint": "Texto libre debajo del valor. Mantenlo corto.",
  "editor.kpi.descriptionPlaceholder": "Volumen de listings +320% vs promedio últimos 30 días.",
  "editor.kpi.scoreMaxHint": "Score máximo en el anillo (default 100).",
  "editor.kpi.breakdownSqlLabel": "SQL breakdown",
  "editor.kpi.breakdownSqlHint": "SELECT que devuelve filas (label, value). Cada fila es una barra de 0–100%.",
  "editor.kpi.breakdownSqlPlaceholder": "SELECT axis AS label, score AS value FROM scores GROUP BY axis",
  "editor.kpi.variantCompact": "Compact",
  "editor.kpi.variantCompactDesc": "Fila compacta para listas densas",
  "editor.kpi.variantIconStat": "Icon stat",
  "editor.kpi.variantIconStatDesc": "Valor con ícono en caja teñida",
  "editor.kpi.maxLabel": "Valor máximo",
  "editor.kpi.maxHint": "Usado por las variantes ring + bar para escalar el progreso.",
  "editor.kpi.gradientLabel": "Gradiente",
  "editor.kpi.gradientHint": "Fondo en gradiente para la variante hero.",
  "editor.kpi.gradientPrimary": "Primary (morado)",
  "editor.kpi.gradientAccent": "Accent (rosa)",
  "editor.kpi.gradientPrimaryToAccent": "Primario → acento",
  "editor.kpi.gradientSuccess": "Éxito (verde)",
  "editor.kpi.gradientWarning": "Atención (ámbar)",
  "editor.kpi.iconLabel": "Ícono",
  "editor.kpi.iconHint": "Ícono Lucide al lado del valor.",
  "editor.kpi.iconNone": "Ninguno",
  "editor.kpi.iconAlert": "Alerta",
  "editor.kpi.iconTrending": "Tendencia",
  "editor.kpi.iconDollar": "Dólar",
  "editor.kpi.iconUsers": "Usuarios",
  "editor.kpi.iconPackage": "Paquete",
  "editor.kpi.iconCheck": "Tilde",
  "editor.kpi.iconZap": "Rayo",
  "editor.kpi.iconTarget": "Objetivo",
  "editor.kpi.iconBgLabel": "Fondo del ícono",
  "editor.kpi.iconBgHint": "Color Tailwind — ej. primary tiñe el ícono de morado.",
  "editor.kpi.iconBgPrimary": "Primario",
  "editor.kpi.iconBgAccent": "Acento",
  "editor.kpi.iconBgEmerald": "Esmeralda",
  "editor.kpi.iconBgAmber": "Ámbar",
  "editor.kpi.iconBgRose": "Rosa",
  "editor.kpi.sparklineSqlLabel": "SQL del sparkline",
  "editor.kpi.sparklineSqlHint": "Devuelve un array de números (ej: LIST(metric ORDER BY month)). El backend lo ejecuta junto con la query principal del KPI.",
  "editor.kpi.sparklineSqlPlaceholder": "LIST(metric ORDER BY month)",
  "editor.kpi.previousSqlLabel": "SQL del período anterior",
  "editor.kpi.previousSqlHint": "Agregado de comparación (ej: SUM(metric) WHERE date < CURRENT_DATE - INTERVAL '1 month').",
  "editor.kpi.previousSqlPlaceholder": "SUM(metric) WHERE date < CURRENT_DATE - INTERVAL '1 month'",
  "editor.kpi.ignoreFiltersEmptyPlaceholder": "Agrega filtros primero para sugerir claves aquí…",

  "editor.table.defaultSortLabel": "Columna de sort por defecto",
  "editor.table.defaultSortHint": "Pon comillas cuando haga falta.",
  "editor.table.directionLabel": "Dirección de sort",
  "editor.table.descending": "Descendente",
  "editor.table.ascending": "Ascendente",
  "editor.table.pageSizeLabel": "Tamaño de página",
  "editor.table.densityLabel": "Densidad de fila",
  "editor.table.densityHint": "Controla el padding vertical de las filas en la preview.",
  "editor.table.densityAriaLabel": "Densidad de fila",
  "editor.table.densityCompact": "compacta",
  "editor.table.densityNormal": "normal",
  "editor.table.densityComfortable": "cómoda",
  "editor.table.columnsLabel": "Columnas ({count})",
  "editor.table.addColumnBtn": "Agregar columna",
  "editor.table.noColumnsHint": "Todavía no hay columnas. Agrega una para poblar la tabla.",
  "editor.table.emptyLabel": "(vacío)",
  "editor.table.columnLabel": "Columna",
  "editor.table.labelLabel": "Etiqueta",
  "editor.table.labelPlaceholder": "Nombre visible",
  "editor.table.formatLabel": "Formato",
  "editor.table.widthLabel": "Ancho / truncado (opcional)",
  "inspector.table.maxWidth": "Ancho máximo de columna",
  "inspector.table.autoWidth": "Automático (contenido completo)",
  "inspector.table.maxWidthHint": "El texto largo se acorta visualmente; pasa el cursor para leerlo completo. La exportación conserva el valor íntegro.",
  "editor.table.widthPlaceholder": "auto · max-w-[200px] truncate",
  // Phase B — renders por columna
  "editor.table.renderLabel": "Render de celda (opcional)",
  "editor.table.renderHint": "Cómo se ve cada celda. Default usa el formato; las variantes más ricas reemplazan la visualización.",
  "editor.table.renderDefault": "Default (texto / número)",
  "editor.table.renderHeat": "Heat — teñido por min/max",
  "editor.table.renderSparkline": "Sparkline — array de números",
  "editor.table.renderStatus": "Estado — badge de color",
  "editor.table.renderProgress": "Progreso — barra 0–100",
  "editor.table.renderCompare": "Comparar — flecha delta",

  "editor.chart.titleLabel": "Título",
  "editor.chart.titlePlaceholder": "Top 10 ASINs por ingresos",
  "editor.chart.typeLabel": "Tipo de chart",
  "editor.chart.dimensionLabel": "Dimensión",
  "editor.chart.dimensionHint": "Columna del GROUP BY.",
  "editor.chart.groupingHint": "Columna de agrupamiento compartida por todas las métricas.",
  "editor.chart.metricLabel": "Métrica",
  "editor.chart.metricHint": "Expresión agregada.",
  "editor.chart.metricPlaceholder": "SUM(\"Annual Rev\")",
  "editor.chart.xAxisLabel": "Eje X",
  "editor.chart.yAxisLabel": "Eje Y",
  "editor.chart.bubbleSizeLabel": "Tamaño de burbuja",
  "editor.chart.labelColLabel": "Columna de etiqueta (opcional)",
  "editor.chart.labelColHint": "Acompaña x/y para etiquetar cada punto en el hover.",
  "editor.chart.metricsLabel": "Métricas ({count})",
  "editor.chart.addMetricBtn": "Agregar métrica",
  "editor.chart.groupedBarHint": "Agrega al menos dos métricas para un grouped bar chart.",
  "editor.chart.seriesLabel": "Serie {idx}",
  "editor.chart.seriesLabelPlaceholder": "Etiqueta de la serie",
  "editor.chart.metricsPlaceholder": "SUM(\"Rev 1P\")",
  "editor.chart.removeBtn": "Eliminar",
  "editor.chart.stackedLabel": "Apilado",
  "editor.chart.stackedHint": "Apilar las series en vez de agruparlas lado a lado",
  "editor.chart.columnsLabel": "Columnas ({count})",
  "editor.chart.addBtn": "Agregar",
  "editor.chart.noColumnsHint": "Todavía no hay columnas — agrega al menos una.",
  "editor.chart.columnLabel": "Columna {idx}",
  "editor.chart.keyPlaceholder": "key (alias)",
  "editor.chart.labelPlaceholder": "Etiqueta visible",
  "editor.chart.badgePlaceholder": "badge (ej. \"risk_score\")",
  "editor.chart.sortLabel": "Expresión de sort",
  "editor.chart.sortHint": "ORDER BY de la mini-tabla.",
  "editor.chart.sortPlaceholder": "SUM(\"Rev\")",
  "editor.chart.moreOptions": "Más opciones del chart",
  "editor.chart.sharedSortLabel": "Orden",
  "editor.chart.sortDefault": "Por defecto",
  "editor.chart.sortAsc": "Ascendente",
  "editor.chart.sortDesc": "Descendente",
  "editor.chart.limitLabel": "Límite (top N)",
  "editor.chart.limitNone": "(ninguno)",
  "editor.chart.xFormatLabel": "Hint de formato X",
  "editor.chart.yFormatLabel": "Hint de formato Y",

  "editor.text.contentLabel": "Contenido markdown",
  "editor.text.contentHint": "Se renderiza inline en el dashboard.",
  "editor.text.contentPlaceholder": "## Nota de sección\n\nEscribe tu contexto aquí.",

  "editor.chart.typeBar": "Barras",
  "editor.chart.barDesc": "Una serie, barras verticales",
  "editor.chart.typeBarHorizontal": "Barras (h)",
  "editor.chart.barHDesc": "Barras horizontales",
  "editor.chart.typeLine": "Línea",
  "editor.chart.lineDesc": "Tendencia sobre una dimensión",
  "editor.chart.typePie": "Torta",
  "editor.chart.pieDesc": "Participación del total",
  "editor.chart.typeGrouped": "Agrupado",
  "editor.chart.groupedDesc": "Múltiples métricas lado a lado",
  "editor.chart.typeScatter": "Scatter",
  "editor.chart.scatterDesc": "Puntos X / Y",
  "editor.chart.typeBubble": "Burbuja",
  "editor.chart.bubbleDesc": "Puntos X / Y / tamaño",
  "editor.chart.typeTable": "Tabla",
  "editor.chart.tableDesc": "Mini-tabla compacta",
  // Phase 3 — tipos avanzados
  "editor.chart.advancedGroup": "Avanzados",
  "editor.chart.typeStackedBar": "Barras apiladas",
  "editor.chart.stackedBarDesc": "Múltiples métricas apiladas en una barra",
  "editor.chart.typeStackedArea": "Área apilada",
  "editor.chart.stackedAreaDesc": "Series temporales apiladas en áreas",
  "editor.chart.typeMultiLine": "Multi-línea",
  "editor.chart.multiLineDesc": "Varias líneas sobre el mismo eje",
  "editor.chart.typeCombo": "Combo",
  "editor.chart.comboDesc": "Barra + línea con doble eje Y",
  "editor.chart.typeRadar": "Radar",
  "editor.chart.radarDesc": "Compara métricas a lo largo de ejes categóricos",
  "editor.chart.typeFunnel": "Embudo",
  "editor.chart.funnelDesc": "Etapas de un proceso de conversión",
  "editor.chart.typeTreemap": "Treemap",
  "editor.chart.treemapDesc": "Participación jerárquica por área",
  "editor.chart.typeWaterfall": "Cascada",
  "editor.chart.waterfallDesc": "Acumulado con pasos positivos/negativos",
  "editor.chart.typeLollipop": "Lollipop",
  "editor.chart.lollipopDesc": "Variante de barra que resalta el valor",
  "editor.chart.typeBarTrend": "Barra + tendencia",
  "editor.chart.barTrendDesc": "Barras con regresión lineal ajustada",
  "editor.chart.typeLineTrend": "Línea + proyección",
  "editor.chart.lineTrendDesc": "Histórico más proyección",
  // Phase D — editores bespoke
  "editor.chart.typeHeatmap": "Heatmap",
  "editor.chart.heatmapDesc": "Grilla 2D teñida por valor agregado",
  "editor.chart.typeBullet": "Bullet",
  "editor.chart.bulletDesc": "Valor vs meta sobre escala máxima",
  "editor.chart.heatmapRowLabel": "Columna fila",
  "editor.chart.heatmapRowHint": "Columna categórica para el eje Y (filas).",
  "editor.chart.heatmapColLabel": "Columna columna",
  "editor.chart.heatmapColHint": "Columna categórica para el eje X (columnas).",
  "editor.chart.heatmapValueLabel": "Valor (agregado)",
  "editor.chart.heatmapValueHint": "Expresión agregada — corre SUM/AVG/COUNT por cada celda (row, col).",
  "editor.chart.heatmapValuePlaceholder": "AVG(metric)",
  "editor.chart.bulletLabelLabel": "Columna etiqueta",
  "editor.chart.bulletValueLabel": "Valor (agregado)",
  "editor.chart.bulletValuePlaceholder": "AVG(metric)",
  "editor.chart.bulletTargetLabel": "Meta",
  "editor.chart.bulletTargetHint": "Constante o expresión — ej. 85 o AVG(target_col).",
  "editor.chart.bulletMaxLabel": "Máximo",
  "editor.chart.bulletMaxHint": "Constante o expresión — default 100 si vacío.",
  // Phase E + F — geo + densidad temporal
  "editor.chart.typeMap": "Mapa",
  "editor.chart.mapDesc": "Coropleta geográfica teñida por métrica por región",
  "editor.chart.typeCalendarHeatmap": "Calendario",
  "editor.chart.calendarHeatmapDesc": "Grilla de densidad por día durante un año",
  "editor.chart.mapVariantLabel": "Región",
  "editor.chart.mapVariantHint": "Sudamérica usa códigos ISO de país (CL, AR, BR…). Chile usa códigos de región (RM, V, VIII…).",
  "editor.chart.mapSouthAmerica": "Sudamérica",
  "editor.chart.mapChile": "Chile (regiones)",
  "editor.chart.calendarYearLabel": "Año (opcional)",
  "editor.chart.calendarYearHint": "Vacío para inferir del dato. Si no, YYYY.",
  // Phase G — timeline / gantt
  "editor.chart.typeTimeline": "Línea de tiempo",
  "editor.chart.timelineDesc": "Fases gantt con inicio, fin y progreso opcional",
  "editor.chart.timelineLabelLabel": "Columna etiqueta",
  "editor.chart.timelineLabelHint": "Columna con el label de cada fila (ej. proyecto / fase / SKU).",
  "editor.chart.timelineStartLabel": "Inicio (índice)",
  "editor.chart.timelineStartHint": "Índice numérico en la pista de meses (0 = primera celda).",
  "editor.chart.timelineEndLabel": "Fin (índice)",
  "editor.chart.timelineEndHint": "Índice numérico de fin — debe ser > inicio.",
  "editor.chart.timelineProgressLabel": "Progreso (0–1, opcional)",
  "editor.chart.timelineProgressHint": "Valor 0..1 para el overlay de progreso. Vacío = sin overlay.",
  "editor.chart.timelineMonthsLabel": "Meses / etiquetas de fase",
  "editor.chart.timelineMonthsHint": "Etiquetas separadas por coma para la pista X. Default Jan…Dec.",

  "editor.color.accentLabel": "Color de acento",
  "editor.color.autoTooltip": "Rotación automática por índice de ítem",
  "editor.color.auto": "Automático",
  "editor.color.red": "Rojo",
  "editor.color.blue": "Azul",
  "editor.color.emerald": "Esmeralda",
  "editor.color.purple": "Púrpura",
  "editor.color.amber": "Ámbar",
  "editor.color.cyan": "Cian",
  "editor.color.pink": "Rosa",
  "editor.color.indigo": "Índigo",

  "editor.column.placeholder": "Elige una columna…",
  "editor.column.loading": "Cargando columnas…",
  "editor.column.tableNotFound": "Tabla no encontrada en DuckDB",
  "editor.column.noColumns": "Sin columnas disponibles — configura primero la tabla DuckDB del reporte.",

  "editor.format.number": "Número",
  "editor.format.numberExample": "1.234",
  "editor.format.currency": "Moneda",
  "editor.format.currencyExample": "$1.234",
  "editor.format.percent": "Porcentaje",
  "editor.format.percentExample": "12,3%",
  "editor.format.text": "Texto",
  "editor.format.textExample": "tal cual",
  "editor.format.label": "Formato",

  "editor.sql.unknownColumnSingular": "Columna desconocida",
  "editor.sql.unknownColumnPlural": "Columnas desconocidas",
  "editor.sql.columnNotInTable": "— no existe en la tabla DuckDB del reporte.",
  "editor.sql.caseMismatch": "difiere en mayúsculas/minúsculas de",
  "editor.sql.fixButton": "Arreglar",
  "editor.sql.aggregateLabel": "Agregación",
  "editor.sql.insertColumn": "Insertar columna",
  "editor.sql.moreColumns": "+{count} más",
  "editor.sql.noSemicolons": "No se permiten punto y coma.",
  "editor.sql.forbiddenKeyword": "Palabra reservada prohibida: {keyword}",
  "editor.sql.valid": "Parece válido.",

  // AI tab — barra de tabs superior (General AI / Chat AI / Insights AI)
  "editor.ai.tabs.ariaLabel": "Pestañas de configuración de IA",
  "editor.ai.tabs.general": "IA General",
  "editor.ai.tabs.chat": "IA Chat",
  "editor.translatePt.button": "Traducir a PT",
  "editor.translatePt.busy": "Traduciendo…",
  "editor.translatePt.error": "No se pudo traducir: {message}",
  "editor.ai.tabs.insights": "IA Insights",
  // AI tab — Insights AI → tabla de ejecuciones (historial read-only)
  "editor.ai.executions.title": "Ejecuciones",
  "editor.ai.executions.description":
    "Generaciones de insights pasadas de este reporte (los snapshots semanales que persiste el portal). Solo lectura.",
  "editor.ai.executions.loading": "Cargando ejecuciones…",
  "editor.ai.executions.error": "No se pudieron cargar las ejecuciones",
  "editor.ai.executions.empty": "Todavía no hay ejecuciones de insights para este reporte.",
  "editor.ai.executions.col.period": "Período",
  "editor.ai.executions.col.model": "Modelo",
  "editor.ai.executions.col.duration": "Duración",
  "editor.ai.executions.col.insights": "Insights",
  "editor.ai.executions.col.regenerated": "Regeneraciones",
  "editor.ai.executions.col.created": "Creado",
  // AI tab — Insights section
  "editor.ai.insights.modelTitle": "Modelo de narrativa",
  "editor.ai.insights.modelDescription": "Modelo de OpenAI que genera la narrativa del panel de Análisis IA (Insights). Elige un override per-report sólo cuando el reporte necesita un modelo distinto al default del portal.",
  "editor.ai.insights.specsTitle": "Insights configurados",
  "editor.ai.insights.specsDescription": "Define QUÉ debe responder cada insight. Cada spec produce una entrada en el snapshot. Deja vacío para que el LLM free-formee 3 insights (comportamiento legacy).",
  "editor.ai.insights.specsEmpty": "Sin insights configurados. Haz click en Agregar para definir uno — el LLM igual genera 3 insights libres como fallback.",
  "editor.ai.insights.addSpec": "Agregar insight",
  "editor.ai.insights.removeSpec": "Eliminar",
  "editor.ai.insights.spec.idLabel": "ID del spec",
  "editor.ai.insights.spec.idHint": "Identificador estable usado para matchear insights entre runs. Minúsculas + underscores. Se auto-sugiere desde el título.",
  "editor.ai.insights.spec.idPlaceholder": "yoy_revenue",
  "editor.ai.insights.spec.titleLabel": "Título del insight (bilingüe)",
  "editor.ai.insights.spec.titleHint": "Se renderea tal cual como header de la card en el portal. El LLM NO inventa — el spec gana.",
  "editor.ai.insights.spec.questionLabel": "Qué debe buscar el LLM (bilingüe)",
  "editor.ai.insights.spec.questionHint": "Instrucción en lenguaje natural. Ej.: \"¿Cómo cambió la cantidad de sellers NO vs hace 52 semanas?\" El LLM responde usando el contexto de data.",
  "editor.ai.insights.spec.dataFocusLabel": "Foco de datos (opcional)",
  "editor.ai.insights.spec.dataFocusHint": "KPI keys o títulos de charts que el LLM debe considerar para este insight. Vacío = el LLM usa toda la data disponible.",
  "editor.ai.insights.spec.dataFocusPlaceholder": "Escribe un KPI key o chart title y presiona Enter…",
  "editor.ai.insights.spec.priorityLabel": "Prioridad",
  "editor.ai.insights.spec.priorityHint": "Menor corre primero. Vacío = orden de inserción.",
  "editor.ai.insights.spec.enabledLabel": "Activo",
  "editor.ai.titleLabel": "Configuración IA",
  "editor.ai.identityDescription":
    "Configuración global del AI para este reporte. El toggle ``Enabled`` controla si el botón ``+ Mostrar Análisis IA`` y el sidebar ``Chat AI`` aparecen en el portal. ``Focus areas`` son temas que el LLM enfatiza en cualquier respuesta sobre este reporte.",
  "editor.ai.enabledLabel": "Habilitar herramientas de chat y análisis IA para este reporte",
  "editor.ai.modelLabel": "Modelo",
  "editor.ai.modelCatalogError": "No se pudo cargar la lista de modelos: {err}",
  "editor.ai.focusAreasLabel": "Áreas de foco",
  "editor.ai.focusAreasHint": "Resalta los temas que la IA debería priorizar al resumir este reporte.",
  "editor.ai.focusAreasPlaceholder": "Escribe un área y presiona Enter…",
  "editor.ai.chatModel.title": "Modelo del chat",
  "editor.ai.chatModel.description": "Modelo de OpenAI que usa el sidebar de chat (loop del InsightAgent). Elige un modelo propio del reporte solo cuando necesita uno distinto al del cliente.",
  "editor.ai.chatModel.label": "Modelo",
  "editor.ai.chatModel.hint": "Por defecto = el modelo de chat del cliente, configurado en la consola de plataforma (Modelos IA).",
  "editor.ai.model.useDefault": "Por defecto",
  "editor.ai.model.useDefaultNamed": "Por defecto ({model})",
  "editor.ai.model.outsideCatalog": "{model} (no está en el catálogo de este cliente)",
  "editor.ai.starterQuestions.title": "Preguntas iniciales",
  "editor.ai.starterQuestions.description": "Preguntas curadas que se muestran en el estado vacío del chat. Apunta a 3-5 que demuestren los ángulos más útiles del reporte — mezcla búsquedas de datos con una sugerencia accionable si tiene sentido. Deja vacío para usar los defaults genéricos del frontend.",
  "editor.ai.starterQuestions.empty": "Sin preguntas iniciales. Usando los defaults genéricos del frontend.",
  "editor.ai.starterQuestions.placeholder": "¿Cuántos sellers NO tenemos esta semana?",
  "editor.ai.starterQuestions.add": "Agregar pregunta inicial",
  "editor.ai.starterQuestions.remove": "Eliminar",
  "editor.ai.ctaCatalog.title": "CTAs accionables",
  "editor.ai.ctaCatalog.description": "Plantillas surfaceadas como CTAs de follow-up en el sidebar del chat. Usa placeholders como {seller_name}, {asin}, {sku}, {client} — el portal los interpola desde la data del último turno.",
  "editor.ai.ctaCatalog.empty": "No hay CTAs configurados. Agrega uno para empezar a sugerir acciones concretas.",
  "editor.ai.ctaCatalog.labelTemplate": "Label del CTA (template)",
  "editor.ai.ctaCatalog.labelTemplatePlaceholder": "Draftear email a {seller_name} con recomendaciones",
  "editor.ai.ctaCatalog.descriptionLabel": "Nota interna (opcional)",
  "editor.ai.ctaCatalog.descriptionPlaceholder": "Visible solo en la UI del builder, no para usuarios finales.",
  "editor.ai.ctaCatalog.add": "Agregar CTA",
  "editor.ai.ctaCatalog.remove": "Eliminar",
  "editor.ai.snapshots.title": "Cadencia de insights",
  "editor.ai.snapshots.description": "Configura cómo el portal genera los snapshots de análisis IA persistidos. Los reportes legacy corren semanal; cambia al modelo temporal por reporte para anclar a otra granularidad.",
  "editor.ai.snapshots.weekColumnLabel": "Columna ISO de semana",
  "editor.ai.snapshots.weekColumnHint": "Columna de DuckDB que el watcher usa para detectar nuevas semanas de datos. Default: \"week_iso\".",
  "editor.ai.snapshots.maxInsightsLabel": "Máx. insights por snapshot",
  "editor.ai.snapshots.maxInsightsHint": "El server clampea a 1–6. Default: 3.",
  "editor.ai.snapshots.priorOffsetsLabel": "Offsets históricos (semanas)",
  "editor.ai.snapshots.priorOffsetsHint": "Offsets de anclas históricas que el LLM ve como contexto de tendencia. Default: [1, 2, 4, 8, 13, 26, 52]. Tipea enteros y presiona Enter.",
  "editor.ai.snapshots.temporalCtaDescription": "Por default este reporte ancla los insights a semanas ISO. Cambia al modelo temporal por reporte para elegir la columna de fecha, la granularidad (día / semana / mes / trimestre / trimestre fiscal) y cuántos períodos de datos + análisis previos alimentan cada insight.",
  "editor.ai.snapshots.switchToTemporal": "Cambiar a config temporal por reporte",
  "editor.ai.snapshots.temporalActiveBadge": "Modelo temporal por reporte activo",
  "editor.ai.snapshots.revertToWeekly": "Volver a semanal (legacy)",
  "editor.ai.snapshots.dateColumnLabel": "Columna de fecha",
  "editor.ai.snapshots.dateColumnHint": "Columna de DuckDB que define la cadencia del período. Las columnas temporales aparecen primero.",
  "editor.ai.snapshots.granularityLabel": "Granularidad (atomicidad)",
  "editor.ai.snapshots.granularityHint": "Cómo se agrupan y anclan los insights. \"Trimestre\" es calendario; \"Trimestre fiscal\" es el año fiscal HP (Nov→Oct).",
  "editor.ai.snapshots.dataLookbackLabel": "Lookback de datos (períodos)",
  "editor.ai.snapshots.dataLookbackHint": "Cuántos períodos de datos alimentan el diagnóstico de completeness. Mínimo 1.",
  "editor.ai.snapshots.analysisLookbackLabel": "Lookback de análisis (períodos)",
  "editor.ai.snapshots.analysisLookbackHint": "Cuántos análisis previos se inyectan como contexto histórico. 0 = ninguno.",
  "editor.ai.snapshots.columnsLoading": "Cargando columnas…",
  "editor.ai.snapshots.columnsError": "No se pudieron cargar las columnas de DuckDB; igual puedes tipear un valor abajo.",
  "editor.ai.snapshots.nonTemporalTag": "no-temporal",
  "editor.ai.snapshots.grain.day": "Día",
  "editor.ai.snapshots.grain.week": "Semana",
  "editor.ai.snapshots.grain.month": "Mes",
  "editor.ai.snapshots.grain.quarter": "Trimestre (calendario)",
  "editor.ai.snapshots.grain.fiscalQuarter": "Trimestre fiscal (HP)",
  "editor.ai.promptLabel": "Override del prompt AI Analysis",
  "editor.ai.promptHint": "Dejar vacío para usar el prompt por defecto en configs/domains/_global/ai_analysis.j2.",
  "editor.ai.promptPlaceholder": "{{ report_display_name }}\nAnaliza los siguientes datos…",
  "editor.ai.promptTemplateLabel": "Template (Jinja2)",
  "editor.ai.promptOverrideInfo":
    "Por defecto el botón ``+ Mostrar Análisis IA`` del portal usa el prompt global ``ai_analysis`` (configurable en /admin/prompts). Este override REEMPLAZA ese prompt SÓLO para este reporte. Dejar vacío para usar el global.",
  "editor.ai.promptClearOverride": "Limpiar override (usar global)",
  "editor.ai.promptNoOverride": "Sin override — usando el prompt global ai_analysis.",
  "editor.ai.promptVarsLabel": "Variables Jinja2 disponibles",
  "editor.ai.promptVarsHint":
    "El portal renderea el template con estas variables de contexto. Referéncialas con sintaxis ``{{ name }}``.",
  "editor.ai.docsLabel": "Documentos de entrenamiento ({count})",
  "editor.ai.addDocBtn": "Agregar documento",
  "editor.ai.noDocsHint": "Todavía no hay documentos. Agrega contexto markdown que fundamente las respuestas (glosario de KPIs, reglas de negocio, etc.).",
  "editor.ai.docNamePlaceholder": "nombre-del-documento.md",
  "editor.ai.docContentPlaceholder": "Contenido markdown…",
  "editor.ai.examplesLabel": "Ejemplos Q&A ({count})",
  "editor.ai.addExampleBtn": "Agregar ejemplo",
  "editor.ai.noExamplesHint": "Ejemplos few-shot que le muestran al modelo el patrón esperado pregunta→herramienta→respuesta.",
  "editor.ai.exampleLabel": "Ejemplo {idx}",
  "editor.ai.questionLabel": "Pregunta",
  "editor.ai.questionPlaceholder": "¿Cuáles son los ingresos totales de US?",
  "editor.ai.toolLabel": "Herramienta (opcional)",
  "editor.ai.toolPlaceholder": "dashboard_explorer_kpis",
  "editor.ai.answerLabel": "Respuesta esperada",
  "editor.ai.answerPlaceholder": "Los ingresos totales de US son…",

  "editor.files.titleLabel": "Subida de archivos",
  "editor.files.s3PathLabel": "Ruta raíz S3",
  "editor.files.s3PathHint": "Dónde caen los uploads — usualmente 'hp/{report_key}/files'.",
  "editor.files.s3PathPlaceholder": "hp/dashboard_explorer/files",
  "editor.files.typesLabel": "Tipos de archivo permitidos",
  "editor.files.typesCountSingular": "tipo",
  "editor.files.typesCountPlural": "tipos",
  "editor.files.maxSizeLabel": "Tamaño máximo (MB)",
  "editor.files.pageSectionLabel": "Apariencia de la página",
  "editor.files.pageTitleLabel": "Título de la página",
  "editor.files.pageTitleHint": "Mostrado en la parte superior de la página Files. Déjalo vacío para usar el texto genérico.",
  "editor.files.pageTitlePlaceholder": "ej. Descargas regionales",
  "editor.files.pageSubtitleLabel": "Subtítulo",
  "editor.files.pageSubtitlePlaceholder": "ej. Reportes trimestrales y exports",
  "editor.files.emptyMessageLabel": "Mensaje vacío",
  "editor.files.emptyMessageHint": "Mostrado cuando no hay archivos en la ruta S3.",
  "editor.files.allowUploadLabel": "Permitir subir",
  "editor.files.allowUploadHint": "Muestra un botón para que el usuario suba archivos al path raíz.",

  "editor.master.titleLabel": "Archivos maestros ({count})",
  "editor.master.addPrefixBtn": "Agregar prefijo",
  "editor.master.noPrefixesHint": "No hay prefijos master configurados. Agrega uno para mapear un path S3 que el loader tome para este reporte.",
  "editor.master.prefixIndexLabel": "Prefijo {idx}",
  "editor.master.removeBtn": "Eliminar",
  "editor.master.nameLabel": "Nombre",
  "editor.master.namePlaceholder": "Catálogo US",
  "editor.master.fileTypeLabel": "Tipo de archivo",
  "editor.master.prefixLabel": "Prefijo S3",
  "editor.master.prefixHint": "Relativo al root del bucket S3 del reporte.",
  "editor.master.prefixPlaceholder": "US/CATALOGUE",
  "editor.master.pageSectionLabel": "Apariencia de la página",
  "editor.master.pageTitleLabel": "Título de la página",
  "editor.master.pageTitleHint": "Mostrado en la parte superior de Maestros. Déjalo vacío para usar el texto genérico.",
  "editor.master.pageTitlePlaceholder": "ej. Catálogos HP",
  "editor.master.pageSubtitleLabel": "Subtítulo",
  "editor.master.pageSubtitlePlaceholder": "ej. Datos de referencia de este reporte",
  "editor.master.pageEmptyLabel": "Mensaje sin prefijos",
  "editor.master.pageEmptyHint": "Mostrado en Maestros cuando no hay prefijos configurados.",
  "editor.master.prefixTitleLabel": "Título del Card",
  "editor.master.prefixTitleHint": "Sobrescribe el header del Card. Déjalo vacío para usar el nombre del prefijo.",
  "editor.master.prefixDescriptionLabel": "Descripción",
  "editor.master.prefixDescriptionHint": "Mostrada bajo el título del Card.",
  "editor.master.prefixEmptyMessageLabel": "Mensaje vacío",
  "editor.master.prefixEmptyMessageHint": "Mostrado cuando el listado S3 está vacío.",
  "editor.master.actionsLabel": "Acciones permitidas",
  "editor.master.actionsHint": "Activa o desactiva los botones por archivo en hp-portal.",
  "editor.master.actionPreview": "Vista previa",
  "editor.master.actionDownload": "Descargar",
  "editor.master.actionReplace": "Reemplazar",
  "editor.master.actionUpload": "Subir",

  "editor.dialog.deleteSection": "¿Eliminar la sección seleccionada?",
  "editor.error.loadDraft": "Error al cargar el draft \"{key}\".",

  "publish.warnings": "Advertencias:",

  "auth.logoutConfirm": "¿Cerrar sesión del builder?",
  "auth.sessionCheckFailed": "No se pudo verificar tu sesión. Tu sesión sigue iniciada; reintenta en un momento.",

  "history.title": "Historial",
  "history.empty": "Todavía no hay ediciones. El primer snapshot se captura al guardar.",
  "history.revert": "Restaurar",
  "history.revertConfirm": "¿Restaurar el draft a este snapshot? El estado actual quedará registrado como nueva entrada del historial.",
  "history.stat.sections": "Sec",
  "history.stat.kpis": "KPI",
  "history.stat.charts": "Graf",
  "history.stat.filters": "Filt",

  // KPICard drill footer + ignored-filters tooltip — ES
  "kpiCard.openAnalysis": "Ver análisis",
  "kpiCard.closeAnalysis": "Cerrar análisis",
  "kpiCard.ignoredFilters": "Ignora los filtros: {filters}",
  // Data tab (Phase 3 multi-source ES) — espejado al EN dict.
  "editor.data.title": "Fuentes de datos",
  "editor.data.subtitle": "{n} fuente(s) materializada(s) en el DuckDB del reporte",
  "editor.data.addSource": "Agregar fuente de datos",
  "editor.data.nav.aria": "Lista de fuentes de datos",
  "editor.data.delete": "Eliminar",
  "editor.data.deleteConfirm": "¿Eliminar la fuente {name}?",
  "editor.data.cantDeleteLast":
    "Un reporte necesita al menos una fuente de datos. Edita ésta en lugar de eliminarla.",
  "editor.data.noSourceTitle": "Aún no hay fuente de datos declarada",
  "editor.data.noSourceHint":
    "Agrega una fuente para materializar una tabla a partir del raw del proyecto.",
  "editor.data.source.editorTitle": "Fuente de datos",
  "editor.data.source.name": "Nombre",
  "editor.data.source.target": "Tabla destino",
  "editor.data.source.targetHint":
    "Obligatoria: la tabla DuckDB donde aterriza esta vista, y la que lee el reporte. Si la dejas como viene, toma el nombre de la fuente más _data.",
  "editor.data.source.sql": "SQL de la vista",
  "editor.data.source.sqlHint":
    "Cuerpo SELECT. El engine lo envuelve en CREATE OR REPLACE TABLE {target} AS <sql> al hacer Apply.",
  "editor.data.source.description": "Descripción (para chat AI)",
  "editor.data.source.descriptionHint":
    "Markdown que se inyecta al chat AI como contexto de los datos. Reemplaza el slot DDL del módulo de training.",
  "editor.data.source.preview": "Columnas materializadas",
  "editor.data.source.notMaterialized":
    "La tabla \"{name}\" todavía no existe en DuckDB. Haz clic en \"Aplicar vista a DuckDB\" para materializarla.",
  "editor.data.source.sample": "Filas de muestra ({n})",
  "editor.data.source.schema": "Esquema ({n} columnas)",
  "editor.data.source.preview10": "Vista previa — primeras {n} filas",
  "editor.data.refresh": "Actualizar",
  "editor.data.refreshTitle": "Actualizar la vista previa",
  "editor.data.save": "Guardar SQL",
  "editor.data.apply": "Aplicar vista a DuckDB",
  "editor.data.viewApplied": "Tabla {name} recreada en el borrador. El reporte publicado cambia al publicar.",
  "editor.data.viewApplyError": "No se pudo recrear {name}: {err}",
  "editor.data.applyNeedsSave": "Hay cambios sin guardar: haz clic en «Guardar SQL» y luego en Guardar (arriba a la derecha) antes de aplicar. Aplicar usa el SQL guardado, no el del editor.",
  "editor.data.loading": "Cargando…",
  "editor.data.noTableTitle": "Configura primero el nombre de la tabla DuckDB",
  "editor.data.noTableHint":
    "En Inspector → Identidad, escribe el nombre snake_case de la tabla DuckDB de este reporte (ej. 'metabase_data'). Después vuelve aquí para cargar un parquet desde S3.",
  "editor.data.loadTitle": "Cargar parquet desde S3",
  "editor.data.loadHint":
    "Pega una URL s3:// apuntando a un archivo parquet. El builder lo descarga a parquet_dir/{report_key}/ y materializa la tabla.",
  "editor.data.s3UrlPlaceholder": "s3://bucket/path/al/archivo.parquet",
  "editor.data.modeReplace": "Reemplazar",
  "editor.data.modeAppend": "Agregar",
  "editor.data.loadBtn": "Cargar",
  "editor.data.loadSuccess": "Cargadas {rows} filas en {ms}ms",
  "editor.data.dropBtn": "Borrar tabla",
  "editor.data.dropConfirm": "¿Borrar la tabla \"{table}\" y eliminar su parquet local? No se puede revertir.",
  "editor.data.columnsTitle": "Columnas ({count})",
  "editor.data.sampleTitle": "Filas de muestra (primeras 10)",
  "editor.data.emptyTable": "La tabla \"{table}\" no existe todavía. Carga un parquet arriba para crearla.",
  // Editor view-kind (iter 23)
  "editor.data.viewSqlTitle": "SQL de la view",
  "editor.data.viewSqlHint":
    "SELECT sobre la tabla raw del proyecto. El engine lo envuelve en CREATE OR REPLACE VIEW {viewName} AS <sql> en la próxima recarga del proyecto.",
  "editor.data.viewSqlSaveBtn": "Guardar SQL",
  "editor.data.viewSqlClean": "Guardado",
  "editor.data.viewSqlDirty": "Cambios sin guardar",
  "editor.data.viewSqlReloadHint":
    "Save guarda la SQL en el draft. Apply re-corre CREATE OR REPLACE VIEW en DuckDB contra la fuente actual del proyecto — sin re-descarga de S3. Usa el Reload completo del proyecto solo cuando cambió la fuente subyacente.",
  "editor.data.viewApplyBtn": "Aplicar vista a DuckDB",
  // ``editor.data.viewApplied`` + ``editor.data.viewApplyError`` are defined
  // once at the top of the Data tab block (Phase 3 multi-source).
  "editor.data.emptyView":
    "La vista \"{view}\" no existe en DuckDB todavía. Guarda la SQL y click Reload en el tab Data del proyecto para crearla.",

  "preview.noChartData": "Sin datos para el filtro actual.",
  "preview.noKpiData": "—",
  "preview.rowDrill.label": "Drill de fila",
  "preview.notFoundTitle": "Reporte no encontrado",
  "preview.notFoundHint":
    "La clave del reporte en la URL no coincide con ningún draft.",
  "preview.loadErrorTitle": "No se pudo cargar el reporte",

  // Editor · sub-tabs IA
  "editor.ai.subtabs.ddl": "DDL",
  "editor.ai.subtabs.docs": "Documentación",
  "editor.ai.subtabs.examples": "Ejemplos",
  "editor.ai.subtabs.prompt": "Prompt",
  "editor.ai.loading": "Cargando…",
  "editor.ai.saving": "Guardando…",
  "editor.ai.saveBtn": "Guardar",
  "editor.ai.savedHint": "Guardado.",
  "editor.ai.deleteConfirm": "¿Eliminar esta fila? No se puede deshacer.",
  "editor.ai.noDDLHint": "Sin DDL todavía. Agrega el CREATE TABLE para que la IA conozca el esquema.",
  "editor.ai.addDDLBtn": "Agregar DDL",
  "editor.ai.ddlContentPlaceholder": "CREATE TABLE …",
  "editor.ai.exampleNameLabel": "Etiqueta",
  "editor.ai.exampleSqlLabel": "SQL",
  "editor.ai.exampleSqlPlaceholder": "SELECT … FROM …",
  "editor.ai.exampleExplanationLabel": "Explicación (opcional)",
  "editor.ai.exampleExplanationPlaceholder": "Por qué esta query es la respuesta correcta.",

  // Admin · común
  "admin.common.loading": "Cargando…",
  "admin.common.saving": "Guardando…",
  "admin.common.save": "Guardar",
  "admin.common.cancel": "Cancelar",
  "admin.common.back": "Volver",
  "admin.common.delete": "Eliminar",
  "admin.common.select": "Seleccionar",
  "admin.common.noResults": "Sin resultados",

  // Admin · Nav
  "nav.admin.prompts": "Prompts",
  "nav.admin.subscribers": "Suscriptores",

  // Admin · Prompts
  "admin.prompts.title": "Plantillas de prompt",
  "admin.prompts.subtitle": "Plantillas Jinja2 renderizadas por el agente de chat y el endpoint de análisis IA.",
  "admin.prompts.scopeBannerTitle": "Defaults globales",
  "admin.prompts.scopeBannerBody": "Estas plantillas son los defaults globales que se usan cuando un reporte no tiene override. Los prompts AI per-reporte viven en Reportes → [reporte] → tab IA.",
  "admin.prompts.newBtn": "Nuevo prompt",
  "admin.prompts.empty": "Aún no hay prompts.",
  "admin.prompts.notFound": "Prompt no encontrado.",
  "admin.prompts.scope.all": "Todos",
  "admin.prompts.scope.global": "Global",
  "admin.prompts.scope.report": "Reporte",
  "admin.prompts.scope.client": "Cliente",
  "admin.prompts.col.name": "Nombre",
  "admin.prompts.col.scope": "Alcance",
  "admin.prompts.col.key": "Objetivo",
  "admin.prompts.col.version": "Versión",
  "admin.prompts.col.active": "Activo",
  "admin.prompts.col.updated": "Actualizado",
  "admin.prompts.activeLabel": "Activo",
  "admin.prompts.savedHint": "Guardado (ahora en v{version}).",
  "admin.prompts.shortcutHint": "Cmd/Ctrl+S para guardar.",
  "admin.prompts.deleteConfirm": "¿Eliminar esta plantilla? Dejará de servirse inmediatamente.",
  "admin.prompts.create.title": "Nueva plantilla de prompt",
  "admin.prompts.create.nameLabel": "Nombre",
  "admin.prompts.create.nameHint": "Clave de búsqueda, p. ej. \"level1_system\". Debe ser única dentro del alcance + objetivo.",
  "admin.prompts.create.scopeLabel": "Alcance",
  "admin.prompts.create.draftLabel": "Draft de reporte",
  "admin.prompts.create.clientLabel": "Clave de cliente",
  "admin.prompts.create.templateLabel": "Cuerpo inicial de la plantilla",
  "admin.prompts.create.submit": "Crear",

  // Admin · Suscriptores
  "admin.subscribers.title": "Suscriptores del portal",
  "admin.subscribers.subtitle": "Portales externos que consumen /api/v1/* con X-Builder-Token.",
  "admin.subscribers.newBtn": "Nuevo suscriptor",
  "admin.subscribers.empty": "Aún no hay suscriptores. Crea uno y copia su token para conectar un portal.",
  "admin.subscribers.col.name": "Nombre",
  "admin.subscribers.col.scope": "Alcance",
  "admin.subscribers.col.active": "Activo",
  "admin.subscribers.col.lastSeen": "Último contacto",
  "admin.subscribers.col.actions": "Acciones",
  "admin.subscribers.scopeAll": "Todos los reportes y proyectos",
  "admin.subscribers.active": "Activo",
  "admin.subscribers.inactive": "Inactivo",
  "admin.subscribers.revokeBtn": "Revocar",
  "admin.subscribers.revokeConfirm": "¿Revocar \"{name}\"? Su token deja de funcionar inmediatamente.",
  "admin.subscribers.create.title": "Nuevo suscriptor del portal",
  "admin.subscribers.create.nameLabel": "Nombre",
  "admin.subscribers.create.descriptionLabel": "Descripción (opcional)",
  "admin.subscribers.create.reportKeysLabel": "Reportes permitidos",
  "admin.subscribers.create.reportKeysHint": "Dejar vacío para permitir TODOS los reportes.",
  "admin.subscribers.create.projectsLabel": "Proyectos permitidos",
  "admin.subscribers.create.projectsHint": "Dejar vacío para permitir TODOS los proyectos.",
  "admin.subscribers.create.submit": "Crear",
  "admin.subscribers.token.title": "Token para {name}",
  "admin.subscribers.token.warning": "Copia este token ahora — se muestra una sola vez y el servidor solo guarda su hash bcrypt.",
  "admin.subscribers.token.tokenLabel": "Token de API",
  "admin.subscribers.token.copyBtn": "Copiar",
  "admin.subscribers.token.copied": "Copiado",
  "admin.subscribers.token.usageHint": "Usar como: X-Builder-Token: <token> en pedidos a /api/v1/* desde el portal.",
  "admin.subscribers.token.acknowledge": "He guardado este token de forma segura.",
  "admin.subscribers.token.close": "Cerrar",

  // ── Synonyms editor (iter 20) ──────────────────────────────
  "editor.synonyms.title": "Sinónimos (opcional)",
  "editor.synonyms.hint":
    "Términos alternativos que el chat reconocerá como este KPI / chart / columna / filtro. Escribe un término + Enter (o coma) para agregarlo; Backspace elimina el último.",
  "editor.synonyms.inputPlaceholder": "+ agregar sinónimo",
  "editor.synonyms.removeChip": "Quitar",

  // ── Filter label (iter 19) ─────────────────────────────────
  "editor.filter.labelLabel": "Etiqueta visible (opcional)",
  "editor.filter.labelHint":
    "Texto bilingüe que se muestra arriba del filtro. Si lo dejas vacío se usa el key.",
  "editor.filter.labelPlaceholder": "País",

  // ── Dashboard tab canvas + inspector (iter 21) ─────────────
  "canvas.readOnly.label": "Render de solo lectura.",
  "canvas.readOnly.hint":
    "Los cambios estructurales (agregar/quitar items, cambiar tipos, etc.) se hacen con Claude vía el skill report-builder. Haz click en un item para inspeccionar sus detalles.",
  "canvas.expand": "Expandir a pantalla completa",
  "canvas.collapse": "Restaurar layout 50/50",

  "inspector.itemTitle": "{element}",
  "inspector.itemHint":
    "La edición inline está limitada a labels bilingüe + sinónimos. El resto es de solo lectura.",
  "inspector.sectionFallback": "Sección",
  "inspector.sectionHint":
    "El título + subtítulo bilingüe siguen siendo editables. Los campos estructurales son solo lectura.",
  "inspector.dashboardTitle": "Dashboard",
  "inspector.dashboardHint":
    "Identidad del reporte + labels de filtros editables. El resto es solo lectura.",

  "inspector.kpi.editable": "Editables",
  "inspector.kpi.structural": "Configuración (solo lectura)",
  "inspector.kpi.sql": "SQL",
  "inspector.kpi.format": "Formato",
  "inspector.kpi.variant": "Variante",
  "inspector.kpi.unit": "Unidad",
  "inspector.kpi.ignoreFilters": "Filtros ignorados",
  "inspector.kpi.drillTarget": "Drill destino",

  "inspector.chart.editable": "Editables",
  "inspector.chart.structural": "Configuración (solo lectura)",
  "inspector.chart.type": "Tipo",
  "inspector.chart.dimension": "Dimensión",
  "inspector.chart.metric": "Métrica",
  "inspector.chart.metrics": "Métricas",
  "inspector.chart.sort": "Orden",
  "inspector.chart.limit": "Límite",
  "inspector.chart.axes": "Ejes",
  "inspector.chart.ignoreFilters": "Filtros ignorados",

  "inspector.table.editable": "Editables por columna",
  "inspector.table.editableHint":
    "Encabezado bilingüe + sinónimos por columna. Formato, render y estructurales son solo lectura.",
  "inspector.table.structural": "Configuración (solo lectura)",
  "inspector.table.pageSize": "Tamaño de página",
  "inspector.table.sort": "Orden por defecto",
  "inspector.table.ignoreFilters": "Filtros ignorados",
  "inspector.table.rowDrill": "Drill por fila",
  "inspector.table.rowDrillMode": "Cómo se muestra el detalle",
  "inspector.table.rowDrillMode.swap": "Reemplazar en la celda (con deslizamiento) — por defecto",
  "inspector.table.rowDrillMode.accordion": "Acordeón bajo la fila",
  "inspector.table.rowDrillModeHint":
    "Reemplazar: el detalle ocupa el lugar de la tabla con un deslizamiento; Volver la restaura. Acordeón: la fila queda visible y el detalle scrollea bajo ella dentro de la tabla. La tarjeta nunca crece en ninguno de los dos.",
  "inspector.table.crossFilter": "Filtro cruzado →",
  "inspector.table.crossFilterNone": "(ninguno)",
  "inspector.table.noColumns": "Sin columnas configuradas.",

  "inspector.text.editable": "Contenido",
  "inspector.text.contentLabel": "Markdown / texto plano",
  "inspector.text.contentHint":
    "Contenido autoría que se muestra en el dashboard. Se renderea tal cual — sin fallback estructural.",
  "inspector.text.contentPlaceholder": "Escribe Markdown o texto plano…",

  "inspector.section.editable": "Etiquetas de la sección (editables)",
  "inspector.section.structural": "Configuración de la sección (solo lectura)",
  "inspector.section.id": "ID",
  "inspector.section.kind": "Tipo",
  "inspector.section.drillId": "Drill ID",
  "inspector.section.items": "Ítems ({count})",
  "inspector.section.filters": "Filtros de sección ({count})",

  "inspector.dashboard.identityEditable": "Identidad del reporte (editable)",
  "inspector.dashboard.identityStructural": "Identidad del reporte (solo lectura)",
  "inspector.dashboard.key": "Clave",
  "inspector.dashboard.duckdbTable": "Tabla DuckDB",
  "inspector.dashboard.promptLanguage": "Idioma del prompt",
  "inspector.dashboard.globalFilters": "Filtros globales ({count})",
  "inspector.dashboard.noFilters": "Sin filtros globales configurados.",

  "inspector.filter.column": "Columna",
  "inspector.filter.default": "Valor por defecto",
  "inspector.filter.locked": "Bloqueado",
  "inspector.filter.sliderStep": "Paso del slider",
  "inspector.filter.sliderStep.day": "Día",
  "inspector.filter.sliderStep.week": "Semana (lunes)",
  "inspector.filter.sliderStep.month": "Mes (día 1)",
  "inspector.filter.sliderStepHint":
    "Granularidad de las manijas del slider de fechas. El valor emitido siempre es un rango de fechas inicio/fin plano.",
  "inspector.filter.rangeFormat": "Formato del valor",
  "inspector.filter.rangeFormat.number": "Número",
  "inspector.filter.rangeFormat.percent": "Porcentaje (la columna es una fracción 0-1)",
  "inspector.filter.rangeFormat.currency": "Moneda (USD)",
  "inspector.filter.rangeDefaultMin": "Mínimo por defecto",
  "inspector.filter.rangeDefaultMax": "Máximo por defecto",
  "inspector.filter.rangeScale": "Escala del slider",
  "inspector.filter.rangeScale.linear": "Lineal",
  "inspector.filter.rangeScale.log": "Logarítmica (valores sesgados, como revenue)",
  "inspector.filter.rangeHistogramTable": "Tabla del histograma (id)",
  "inspector.filter.rangeHint":
    "Escribe los límites como los ve el usuario (30 para 30 %). Deja un lado vacío para no acotarlo. El slider cubre todo el rango de la columna.",

  "editor.report.displayNameLabel": "Nombre visible (bilingüe)",
  "editor.report.descriptionLabel": "Descripción (bilingüe)",

  // ── Accesos: ámbitos, grupos, personas, auditoría (Plan J, J3) ──
  "access.tabs.label": "Secciones de accesos",
  "access.tabs.scopes": "Ámbitos",
  "access.tabs.groups": "Grupos",
  "access.tabs.people": "Personas",
  "access.tabs.audit": "Auditoría",
  "access.common.errorTitle": "Algo falló",
  "access.common.missingLabel": "Este valor todavía no tiene etiqueta: se muestra tal cual.",
  "access.common.notComputed": "sin calcular",
  "access.common.notComputedHint": "Nadie lo calculó todavía. No es cero.",
  "access.common.badDate": "fecha ilegible",
  "access.common.refresh": "Actualizar",
  "access.common.prev": "Anterior",
  "access.common.next": "Siguiente",
  "access.scopes.title": "Ámbitos",
  "access.scopes.subtitle": "Un ámbito es el recorte de datos que lee una persona. Las reglas se combinan con Y, y se materializan en un archivo por ámbito y proyecto.",
  "access.scopes.add": "Nuevo ámbito",
  "access.scopes.all": "Todo",
  "access.scopes.col.scope": "Ámbito",
  "access.scopes.col.rules": "Reglas",
  "access.scopes.col.projects": "Proyectos",
  "access.scopes.col.usedBy": "Lo usan",
  "access.scopes.col.state": "Estado",
  "access.scopes.col.updated": "Actualizado",
  "access.scopes.appliesTo": "{n} con acceso · {m} sin acceso",
  "access.scopes.appliesToNone": "ningún proyecto",
  "access.scopes.appliesToNoneHint": "No aplica a ningún proyecto, así que no otorga acceso en ninguna parte.",
  "access.scopes.usedBy": "{n} grupo(s) · {people} persona(s)",
  "access.scopes.usedByNone": "nadie",
  "access.scopes.usedByNoneHint": "Ningún grupo lo lleva y nadie resuelve por él.",
  "access.scopes.empty.title": "Todavía no hay ámbitos",
  "access.scopes.empty.desc": "Crea uno, asígnalo a un grupo, y quienes estén en ese grupo leen solo su recorte.",
  "access.scopes.actions.detail": "Abrir",
  "access.scopes.actions.rebuild": "Reconstruir",
  "access.scopes.actions.delete": "Eliminar",
  "access.scopes.state.built": "construido",
  "access.scopes.state.noMapping": "vacío (sin coincidencias)",
  "access.scopes.state.building": "construyendo",
  "access.scopes.state.failed": "falló",
  "access.scopes.state.notApplicable": "no aplica",
  "access.scopes.state.neverBuilt": "nunca construido",
  "access.scopes.state.skippedConcurrent": "otro build lo tenía",
  "access.scopes.rules.chip": "{key}: {n} valor(es)",
  "access.scopes.rules.all": "sin reglas: todo",
  "access.scopes.rules.allHint": "El ámbito reservado. No tiene reglas, ni archivo, ni reconstrucción.",
  "access.scopes.rules.none": "sin reglas",
  "access.scopes.rules.noneHint": "Un ámbito sin reglas no recorta nada. Agrega al menos una.",
  "access.scopes.rules.andHint": "Se tienen que cumplir todas las reglas: se combinan con Y.",
  "access.scopes.rules.emptyTitle": "Todavía sin reglas",
  "access.scopes.rules.emptyDesc": "Un ámbito sin reglas no recorta nada y no se puede guardar.",
  "access.scopes.rules.declaredIn": "declarada en {n} proyecto(s)",
  "access.scopes.rules.removeRule": "Quitar",
  "access.scopes.rules.reference": "Leer los valores de",
  "access.scopes.rules.referenceHelp": "Un ámbito vale para todo el cliente; los valores salen del archivo vivo de un proyecto. Dos proyectos pueden tener valores distintos para la misma clave.",
  "access.scopes.rules.addLabel": "Agregar una regla",
  "access.scopes.rules.addHelp": "Solo se ofrecen las claves declaradas como utilizables para ámbitos.",
  "access.scopes.rules.addPlaceholder": "Elige una dimensión…",
  "access.scopes.rules.keyOption": "{key} — {n} proyecto(s)",
  "access.scopes.rules.allKeysUsed": "Todas las dimensiones disponibles ya tienen una regla.",
  "access.scopes.rules.noKeysTitle": "Todavía no hay dimensiones para ámbitos",
  "access.scopes.rules.noKeysDesc": "Primero declara una dimensión como utilizable para ámbitos en la pestaña Datos de un proyecto.",
  "access.scopes.rules.orphanTitle": "Esta dimensión ya no está disponible",
  "access.scopes.rules.orphanDesc": "Ningún proyecto declara «{key}» como utilizable para ámbitos. La regla se conserva tal cual: elimínala o restaura la dimensión.",
  "access.scopes.values.noneSelected": "todavía sin valores elegidos",
  "access.scopes.values.remove": "Quitar este valor",
  "access.scopes.values.searchLabel": "Buscar valores",
  "access.scopes.values.searchPlaceholder": "Escribe para filtrar…",
  "access.scopes.values.errorTitle": "No se pudieron leer los valores",
  "access.scopes.values.summary": "Se muestran {shown} de {total} valores distintos de {column} ({type}).",
  "access.scopes.values.nulls": "{n} fila(s) no tienen valor. No pertenecen a ningún ámbito restringido.",
  "access.scopes.values.notScopableTitle": "Esta columna no sirve para ámbitos",
  "access.scopes.values.notScopable": "Su tipo ({type}) se rechaza al construir el ámbito. Los valores sirven igual para un filtro.",
  "access.scopes.values.emptyTitle": "Esta columna no tiene valores",
  "access.scopes.values.noMatchTitle": "Nada coincide con «{search}»",
  "access.scopes.values.noMatchDesc": "Solo se pueden elegir valores que existan en los datos: acá no hay texto libre, a propósito.",
  "access.scopes.values.rowCount": "{n} fila(s)",
  "access.scopes.values.range": "{from}–{to} de {total}",
  "access.scopes.form.createTitle": "Nuevo ámbito",
  "access.scopes.form.grantsNothingTitle": "Crearlo no le da acceso a nadie",
  "access.scopes.form.grantsNothing": "Un ámbito nuevo no tiene grupo, ni integrantes, ni archivo hasta que algo lo construya.",
  "access.scopes.form.id": "Identificador",
  "access.scopes.form.idHelp": "Minúsculas, dígitos, guion y guion bajo. ES el nombre del archivo, así que «us-north» y «us_north» son dos ámbitos distintos.",
  "access.scopes.form.idRequired": "Escribe un identificador.",
  "access.scopes.form.idSpaces": "No puede empezar ni terminar con un espacio.",
  "access.scopes.form.idShape": "Empieza con una letra minúscula; después letras minúsculas, dígitos, «-» o «_». Entre 2 y 41 caracteres.",
  "access.scopes.form.name": "Nombre",
  "access.scopes.form.description": "Descripción",
  "access.scopes.form.descriptionHelp": "Qué es este recorte, en las palabras que usa el equipo.",
  "access.scopes.form.rulesTitle": "Reglas",
  "access.scopes.delete.title": "Eliminar «{name}»",
  "access.scopes.delete.confirm": "Eliminar el ámbito y sus archivos",
  "access.scopes.delete.blockedTitle": "No se puede eliminar",
  "access.scopes.delete.blocked": "{groups} grupo(s) lo llevan y {people} persona(s) resuelven por él. Muévelos primero: no hay opción de forzar.",
  "access.scopes.delete.filesTitle": "Los archivos de datos también se van",
  "access.scopes.delete.files": "Sus archivos también se van, borrados bajo el bloqueo de escritura. No hay vuelta atrás: reconstruirlo después empieza de cero.",
  "access.scopes.detail.identity": "Identidad",
  "access.scopes.detail.identityDesc": "El identificador no cambia: es el nombre del archivo donde se escribe el recorte de cada proyecto.",
  "access.scopes.detail.kind": "Tipo",
  "access.scopes.detail.usedBy": "Lo usan",
  "access.scopes.detail.rules": "Reglas",
  "access.scopes.detail.rulesDesc": "Guardar RETIRA los archivos y punteros de este ámbito en todos los proyectos y borra sus snapshots de IA, en la misma solicitud. Hasta que lo reconstruyas, todas las personas que leen por este ámbito reciben un error (503) en cada reporte, nunca el recorte anterior. Si una recarga tiene el bloqueo de escritura, el guardado se rechaza y nada cambia.",
  "access.scopes.detail.notEditableTitle": "Este ámbito no se edita",
  "access.scopes.detail.preview": "Ver el impacto",
  "access.scopes.detail.mustPreviewTitle": "Revisa primero el impacto",
  "access.scopes.detail.mustPreview": "El guardado tiene que nombrar los grupos que afecta, así que primero se consulta el impacto.",
  "access.scopes.detail.impactTitle": "Qué cambiaría esta edición",
  "access.scopes.detail.rulesInvalid": "Estas reglas se rechazan",
  "access.scopes.detail.impactWho": "{groups} grupo(s) y {people} persona(s) resuelven por este ámbito.",
  "access.scopes.detail.impactRows": "{before} filas ahora, {after} después",
  "access.scopes.detail.impactUnknown": "filas desconocidas ({reason})",
  "access.scopes.detail.noticeTitle": "Qué pasó",
  "access.scopes.detail.retiredTitle": "Guardado: lectores bloqueados hasta reconstruir",
  "access.scopes.detail.retired": "Se retiraron los archivos y se borraron los snapshots de IA. Todas las personas ligadas a este ámbito reciben un 503 en cada reporte hasta que lo reconstruyas, proyecto por proyecto, acá abajo.",
  "access.scopes.detail.projects": "Proyectos",
  "access.scopes.detail.projectsDesc": "Si las reglas pueden recortar cada proyecto, y qué dejó allí la última construcción.",
  "access.scopes.detail.col.project": "Proyecto",
  "access.scopes.detail.col.state": "Estado",
  "access.scopes.detail.col.rows": "Filas",
  "access.scopes.detail.col.version": "Versión de datos",
  "access.scopes.detail.col.built": "Última construcción",
  "access.scopes.detail.col.started": "Empezó",
  "access.scopes.detail.col.rebuild": "Reconstruir",
  "access.scopes.detail.rebuild": "Reconstruir",
  "access.scopes.detail.rebuilding": "Construyendo…",
  "access.scopes.detail.rowsOf": "{rows} de {total}",
  "access.scopes.detail.noRowsHint": "Todavía no hay una construcción para este proyecto. No son cero filas.",
  "access.scopes.detail.noVersionHint": "Ninguna construcción registró qué archivo de datos leyó.",
  "access.scopes.detail.stuck": "TRABADO",
  "access.scopes.detail.stuckTitle": "Esta construcción quedó trabada",
  "access.scopes.detail.builds": "Historial de construcciones",
  "access.scopes.detail.buildsDesc": "{total} construcción(es), de la más nueva a la más vieja.",
  "access.scopes.detail.buildsCapped": "Se muestran {shown} de {total}. El historial sigue.",
  "access.groups.title": "Grupos",
  "access.groups.subtitle": "Un grupo lleva un ámbito y una matriz de contenido. Lo que una persona puede ver es la unión de sus grupos.",
  "access.groups.add": "Nuevo grupo",
  "access.groups.col.group": "Grupo",
  "access.groups.col.scope": "Ámbito",
  "access.groups.col.members": "Integrantes",
  "access.groups.col.content": "Contenido",
  "access.groups.col.health": "Estado",
  "access.groups.col.updated": "Actualizado",
  "access.groups.contentCount": "{reports} reporte(s) · {projects} celda(s) de proyecto",
  "access.groups.scopeAllHint": "Quienes estén en este grupo leen todos los datos.",
  "access.groups.health.grantsNothing": "no abre nada",
  "access.groups.health.noMembers": "sin integrantes",
  "access.groups.health.ok": "en uso",
  "access.groups.empty.title": "Todavía no hay grupos",
  "access.groups.empty.desc": "Una persona sin grupo no ve ningún reporte. Crea el primero y dale un ámbito.",
  "access.groups.actions.detail": "Abrir",
  "access.groups.actions.delete": "Eliminar",
  "access.groups.base.chip": "Base",
  "access.groups.base.chipTitle": "El grupo base: abre todos los reportes publicados y todos los datos. No se borra y su ámbito no cambia.",
  "access.groups.base.contentTitle": "Todos los reportes publicados",
  "access.groups.base.contentBody": "Este grupo abre todos los reportes publicados, también los que se publiquen después. Sus acciones son fijas: ver el reporte, sus datos, el chat, el análisis IA, y ver y descargar maestros y archivos. No incluye enviar por correo ni programar envíos. Para dar a alguien otro conjunto, ponlo en un subgrupo.",
  "access.groups.tab.groups": "Grupos",
  "access.groups.tab.defaults": "Predeterminados por rol",
  "access.groups.tab.members": "Integrantes",
  "access.groups.tab.scope": "Ámbito",
  "access.groups.tab.content": "Contenido",
  "access.groups.form.createTitle": "Nuevo grupo",
  "access.groups.form.emptyTitle": "Empieza vacío y no otorga nada",
  "access.groups.form.empty": "Después agregas personas y abres reportes. Los dos estados se reportan en la lista con una palabra.",
  "access.groups.form.name": "Nombre",
  "access.groups.form.nameHelp": "Tiene que ser único, y es lo que muestra la auditoría.",
  "access.groups.form.description": "Descripción",
  "access.groups.form.scope": "Ámbito",
  "access.groups.form.scopeHelp": "No tiene valor por omisión, a propósito: un grupo existe para llevar una restricción.",
  "access.groups.form.scopePlaceholder": "Elige un ámbito…",
  "access.groups.form.scopeAllOption": "Todo (sin restricción)",
  "access.groups.form.scopeAllTitle": "Este grupo no restringe nada",
  "access.groups.form.scopeAllWarn": "Sus integrantes leen todos los datos de cada proyecto que puedan abrir — y también quien entre después, sin importar lo que restrinjan sus otros grupos: la unión con «Todo» es «Todo».",
  "access.groups.delete.title": "Eliminar «{name}»",
  "access.groups.delete.confirm": "Eliminar el grupo",
  "access.groups.delete.body": "{members} persona(s) pierden los {reports} reporte(s) que abre este grupo.",
  "access.groups.delete.scopeNote": "El ámbito no se elimina: solo este grupo y sus celdas.",
  "access.groups.members.add": "Agregar personas",
  "access.groups.members.addTitle": "Agregar personas al grupo",
  "access.groups.members.addCount": "Agregar {count}",
  "access.groups.members.addNote": "Agregar a alguien puede cambiar el ámbito que resuelve, y el archivo nuevo puede no existir todavía. A una persona con rol de administrador no le cambia nada: lee todo sin importar sus grupos, así que una restricción escrita aquí no se le aplicaría.",
  "access.groups.members.searchLabel": "Buscar por nombre o correo",
  "access.groups.members.noneOfferable": "No queda nadie para agregar",
  "access.groups.members.noneOfferableDesc": "Todos los de la lista ya son integrantes.",
  "access.groups.members.col.person": "Persona",
  "access.groups.members.col.role": "Rol",
  "access.groups.members.col.status": "Estado",
  "access.groups.members.col.effective": "Resuelve a",
  "access.groups.members.col.added": "Se agregó",
  "access.groups.members.union": "unión de {n} ámbitos",
  "access.groups.members.scope.unrestricted": "lee todo",
  "access.groups.members.scope.unrestrictedHint": "Su unión resuelve a «Todo», así que el recorte de este grupo no decide nada para esta persona. Con un solo grupo sin restricción alcanza — la unión con «Todo» es «Todo». Para que esta restricción muerda, sácala del grupo que lleva el ámbito sin restricción.",
  "access.groups.members.scope.elsewhere": "lo decide otro grupo",
  "access.groups.members.scope.elsewhereHint": "Su unión resuelve a un ámbito que no es el de este grupo, así que lo que lee lo decide otro de sus grupos. Abre a la persona para ver de qué grupos salió.",
  "access.groups.members.scope.holds": "el recorte de este grupo",
  "access.groups.members.scope.holdsHint": "Su unión resuelve exactamente al ámbito de este grupo, así que el recorte de esta página es el que la persona lee.",
  "access.groups.members.remove": "Quitar del grupo",
  "access.groups.members.footnote": "Se listan todos los integrantes, nunca una página: decidir un acceso mirando una lista recortada es lo peor que podría hacer esta pantalla.",
  "access.groups.members.noticeTitle": "Qué hace este cambio",
  "access.groups.scope.title": "Ámbito de este grupo",
  "access.groups.scope.desc": "Cambiarlo cambia lo que lee cada integrante, desde la próxima consulta — hasta donde permita su grupo más amplio. El ámbito de una persona es la unión de los de sus grupos, y la unión con «Todo» es «Todo».",
  "access.groups.scope.openScope": "Abrir el ámbito",
  "access.groups.scope.projectsDesc": "Dónde este ámbito tiene archivo, y dónde no.",
  "access.groups.scope.warningTitle": "Esto le cuesta el acceso a alguien",
  "access.groups.content.desc": "Las celdas sin fila caen a los otros grupos de la persona, y después a los predeterminados por rol — salvo «Verlo», que no tiene predeterminado: un reporte que ningún grupo suyo abre no se abre.",
  "access.groups.content.hint": "Haz clic en una celda para rotar: por omisión → otorgado → denegado → por omisión.",
  "access.groups.content.save": "Guardar ({count})",
  "access.groups.content.entitled": "Este grupo abre {n} reporte(s). Un reporte sin la celda «verlo» no se abre.",
  "access.groups.content.projectLevel": "Nivel de proyecto",
  "access.groups.content.projectLevelBadge": "maestros y archivos",
  "access.groups.content.projectSectionsHint": "Estas dos secciones se otorgan para todo el proyecto, no por reporte.",
  "access.groups.content.noProject": "Sin proyecto",
  "access.groups.content.granted": "otorgado",
  "access.groups.content.denied": "denegado",
  "access.groups.content.defaultTitle": "Acá no hay fila. Decide el predeterminado del rol, y dice: {value}. La excepción es «Verlo»: no tiene predeterminado, así que sin fila el reporte no se abre.",
  "access.groups.content.grantedTitle": "Otorgado acá. Además le gana a una denegación en otro grupo.",
  "access.groups.content.entitlementTitle": "Esta es la celda que ABRE el reporte. Sin ella, las otras celdas no otorgan nada.",
  "access.groups.content.denyIsWeak": "Denegado acá, pero si OTRO grupo de la misma persona lo otorga, gana el otorgado. Para revocar, quita el otorgado en todos lados.",
  "access.groups.content.unknownDefault": "No existe fila de predeterminado por rol para {section}.{action}, así que nadie sabe qué significa una celda vacía acá.",
  "access.defaults.subtitle": "Qué significa una celda vacía en la matriz de contenido de un grupo. Solo se edita el rol «user».",
  "access.defaults.save": "Guardar ({count})",
  "access.defaults.readonly": "solo lectura",
  "access.defaults.adminNote": "El rol admin se saltea todos los chequeos, así que estas celdas no deciden nada.",
  "access.defaults.missingTitle": "Faltan filas de predeterminados",
  "access.defaults.missing": "Nadie sabe qué significa una celda vacía para: {cells}. Defínelos para que la matriz del grupo pueda decirlo.",
  "access.defaults.grantedTitle": "Otorgado por omisión",
  "access.defaults.deniedTitle": "Denegado por omisión",
  "access.defaults.unknownCell": "No hay fila para {section}.{action}. Eso es desconocido, no denegado.",
  "access.people.title": "Personas",
  "access.people.subtitle": "Quién entra, a qué grupos pertenece, y a qué ámbito resuelven esos grupos.",
  "access.people.add": "Nueva persona",
  "access.people.count": "{count} persona(s)",
  "access.people.allRoles": "Todos los roles",
  "access.people.col.person": "Persona",
  "access.people.col.role": "Rol",
  "access.people.col.groups": "Grupos",
  "access.people.col.effective": "Ámbito",
  "access.people.col.builder": "Builder",
  "access.people.col.lastLogin": "Último ingreso",
  "access.people.lastLogin.window": "{n} en los últimos 30 días",
  "access.people.lastLogin.noneSince": "Sin ingresos desde el {date}",
  "access.people.lastLogin.noneYet": "Aún no hay ingresos registrados",
  "access.people.col.status": "Estado",
  "access.people.active": "activa",
  "access.people.inactive": "inactiva",
  "access.people.builderNone": "sin acceso",
  "access.people.builderFull": "completo",
  "access.people.builderPartial": "parcial",
  "access.people.groupCount": "{n} grupo(s)",
  "access.people.noGroups": "sin grupo",
  "access.people.noGroupsHint": "Una persona sin grupo no recibe ningún reporte de ningún grupo.",
  "access.people.partialTitle": "Esta lista está incompleta",
  "access.people.partial": "No se pudieron leer los integrantes de estos grupos: {groups}. Los conteos de grupos de abajo son un mínimo.",
  "access.people.empty.title": "Acá no hay nadie",
  "access.people.empty.desc": "Crea la primera persona y ponla en un grupo.",
  "access.people.actions.detail": "Abrir",
  "access.people.actions.reset": "Restablecer la contraseña",
  "access.people.actions.delete": "Eliminar",
  "access.people.legacyCounts": "Filas antiguas que todavía tiene esta persona: {subs} suscripción(es) y {overrides} excepción(es). Las lee el sistema de permisos viejo y se están retirando.",
  "access.people.identity": "Identidad",
  "access.people.identityDesc": "El correo es la identidad y acá no cambia.",
  "access.people.form.name": "Nombre",
  "access.people.form.email": "Correo",
  "access.people.form.emailFixed": "No se puede cambiar desde esta consola.",
  "access.people.form.role": "Rol",
  "access.people.form.roleHelp": "Un admin se saltea todos los chequeos de permisos, así que los grupos no deciden nada para él.",
  "access.people.form.status": "Estado",
  "access.people.form.password": "Contraseña",
  "access.people.form.passwordHelp": "Al menos 8 caracteres. Se muestra una sola vez y no hay correo para enviarla.",
  "access.people.form.passwordWarning": "Cópiala antes de cerrar: no se puede volver a mostrar.",
  "access.people.form.generate": "Generar",
  "access.people.form.createTitle": "Nueva persona — paso {step} de {total}",
  "access.people.form.next": "Siguiente",
  "access.people.form.back": "Volver",
  "access.people.form.fillRequired": "Completa el nombre, el correo y una contraseña de al menos 8 caracteres.",
  "access.people.form.groupsTitle": "Los grupos son lo único que puedes dar",
  "access.people.form.groupsHint": "Ya no hay permisos por persona: lo que alguien ve sale de sus grupos.",
  "access.people.form.noGroupsTitle": "Todavía no hay grupos",
  "access.people.form.noGroupsHint": "Crea uno primero, o esta persona va a entrar y no va a ver nada.",
  "access.people.form.noPickTitle": "Sin grupo elegido",
  "access.people.form.noPick": "Va a poder entrar y no va a ver ningún reporte.",
  "access.people.groups.title": "Grupos",
  "access.people.groups.desc": "Todo lo que ve esta persona sale de acá.",
  "access.people.groups.col.group": "Grupo",
  "access.people.groups.col.scope": "Ámbito",
  "access.people.groups.join": "Agregar a grupos",
  "access.people.groups.joinTitle": "Agregar esta persona a grupos",
  "access.people.groups.joinCount": "Agregar a {count}",
  "access.people.groups.joinNote": "Así se expresa ahora «el mismo acceso que otra persona»: compartiendo grupo, algo que sobrevive a que esa persona se vaya.",
  "access.people.groups.leave": "Quitar del grupo",
  "access.people.groups.noneOfferable": "Ya está en todos los grupos",
  "access.people.groups.noneOfferableDesc": "No hay otro grupo al que agregarla.",
  "access.people.effective.title": "Ámbito resultante",
  "access.people.effective.eyebrow": "Calculado desde los grupos",
  "access.people.effective.name": "unión de {n} grupo(s)",
  "access.people.effective.derived": "Este identificador se deriva de las reglas de los grupos de arriba. Se recalcula con cada cambio de integrantes.",
  "access.people.effective.allHint": "Sin restricción: esta persona lee todos los datos. Hay DOS maneras de llegar aquí y este chip no las distingue: un grupo sin restricción (la unión con «Todo» es «Todo»), o el rol de administrador, que lee todo sin importar sus grupos. En cualquiera de los dos casos, un grupo restringido que también tenga no decide nada.",
  "access.people.effective.unknown": "desconocido",
  "access.people.effective.unknownHint": "Ningún grupo reporta un ámbito para esta persona, así que esta consola no puede decir a cuál resuelve.",
  "access.people.effective.noGroups": "No está en ningún grupo, así que ningún grupo le otorga reportes.",
  "access.people.effective.orphanHint": "Su vínculo nombra un ámbito que ninguno de sus grupos lleva. Algo lo escribió a mano, o el recálculo todavía no llegó.",
  "access.people.effective.computed": "Esto se calcula desde los grupos de la persona. Para cambiarlo, cambia los grupos.",
  "access.people.effective.editGroups": "Editar los grupos",
  "access.people.builder.title": "Acceso al builder",
  "access.people.builder.desc": "Acceso del equipo de Omnitok a esta consola. No es un ajuste de grupo, ni lo va a ser.",
  "access.people.reset.title": "Restablecer la contraseña",
  "access.people.reset.prompt": "Se genera una contraseña nueva para {name} y se muestra una sola vez.",
  "access.people.reset.generate": "Generar",
  "access.people.reset.newPassword": "Contraseña nueva",
  "access.people.reset.warning": "Cópiala antes de cerrar: no se puede volver a mostrar, y no hay correo para enviarla.",
  "access.people.reset.done": "Listo",
  "access.people.delete.title": "Eliminar «{name}»",
  "access.people.delete.confirm": "Eliminar la persona",
  "access.people.delete.body": "Está en {groups} grupo(s) y no va a poder entrar.",
  "access.people.delete.soft": "Es un borrado suave: la fila y sus grupos se conservan, así que reactivarla restaura lo que tenía.",
  "access.audit.title": "Auditoría",
  "access.audit.subtitle": "Cada cambio de acceso hecho desde Accesos, con quién lo hizo y cómo estaba antes.",
  "access.audit.startsHereTitle": "El registro empieza acá",
  "access.audit.startsHere": "Los cambios anteriores a esta función no se registraron. Un resultado vacío para una fecha vieja significa «no se registró», nunca «no pasó nada».",
  "access.audit.filter.action": "Acción",
  "access.audit.filter.actor": "Quién",
  "access.audit.filter.target": "Objeto",
  "access.audit.filter.limit": "Filas",
  "access.audit.apply": "Aplicar",
  "access.audit.limitRange": "Filas: un número entero entre 1 y {max}.",
  "access.audit.col.when": "Cuándo",
  "access.audit.col.who": "Quién",
  "access.audit.col.actorKind": "Consola",
  "access.audit.col.action": "Acción",
  "access.audit.col.target": "Objeto",
  "access.audit.col.detail": "Detalle",
  "access.audit.view": "Verlo",
  "access.audit.atCap": "Se muestran {limit} de {total}. El registro sigue: pide más filas.",
  "access.audit.empty.title": "Nada registrado para este filtro",
  "access.audit.empty.desc": "Amplía el filtro, o pide más filas.",
  "access.audit.detailTitle": "Qué cambió",
  "access.audit.actorId": "Identificador de quién actuó",
  "access.audit.actorGone": "la persona ya no existe; el correo se conserva",
  "access.audit.before": "Antes",
  "access.audit.after": "Después",
  "access.audit.noBefore": "Antes no existía.",
  "access.audit.noAfter": "Ya no existe.",
  "access.audit.actor.providerAdmin": "builder",
  "access.audit.actor.tenantAdmin": "consola del cliente",
  "access.audit.actor.platform": "plataforma",
  "access.audit.actor.system": "automático",
  "access.audit.action.dimensionCreate": "dimensión creada",
  "access.audit.action.dimensionUpdate": "dimensión editada",
  "access.audit.action.dimensionDelete": "dimensión eliminada",
  "access.audit.action.scopeCreate": "ámbito creado",
  "access.audit.action.scopeUpdate": "ámbito editado",
  "access.audit.action.scopeDelete": "ámbito eliminado",
  "access.audit.action.scopeRebuild": "ámbito reconstruido",
  "access.audit.action.groupCreate": "grupo creado",
  "access.audit.action.groupUpdate": "grupo editado",
  "access.audit.action.groupDelete": "grupo eliminado",
  "access.audit.action.groupPermissionSet": "contenido cambiado",
  "access.audit.action.groupMemberAdd": "agregada a un grupo",
  "access.audit.action.groupMemberRemove": "quitada de un grupo",
  "access.audit.action.userEffectiveScopeChange": "ámbito recalculado",
  "access.audit.action.roleDefaultUpdate": "valores por defecto del rol",
  "access.audit.action.userCreate": "persona creada",
  "access.audit.action.userRoleChange": "rol cambiado",
  "access.audit.action.userDeactivate": "desactivada",
  "access.audit.action.userReactivate": "reactivada",
  "access.audit.action.userLoginAllowedChange": "ingreso modificado",
  "access.audit.action.userDelete": "persona eliminada",
  "access.audit.action.userPasswordReset": "contraseña restablecida",
  "access.audit.action.userBuilderAccess": "acceso al builder cambiado",
  "access.audit.action.viewAsScopePreview": "ámbito previsualizado",

  // ── Accesos → Grupos → AI y Experiencia (Plan J, J4) ──────────────
  //
  // Español latinoamericano neutro, con tuteo, igual que el resto del
  // corpus. Cada CONTROL que nombran estas cadenas se guarda y alguien
  // lo lee; las cadenas que no describen un control dicen qué decide la
  // cosa, en vez de ofrecer una perilla para ella. Por eso
  // `access.experience.mail.*` son cuatro cadenas y no treinta: nada lee
  // la plantilla de correo de un grupo, así que la pestaña responde la
  // pregunta en lugar de dibujar un formulario.
  "access.groups.tab.ai": "AI",
  "access.groups.tab.experience": "Experiencia",
  "access.ai.save": "Guardar la configuración de AI",
  "access.ai.saveEffect": "Las dos opciones toman efecto en la siguiente request que haga la gente de este grupo: la nota les llega al chat y a los correos, y el análisis se genera en la próxima recarga.",
  "access.ai.insights.title": "Análisis con AI para este grupo",
  "access.ai.insights.desc": "El análisis se genera por ÁMBITO, no por grupo: dos grupos con el mismo ámbito leen los mismos párrafos. Encender esto pide uno bajo el ámbito de este grupo. Es la palanca de costo, y el costo está al lado del interruptor.",
  "access.ai.insights.switch": "Generar Análisis con AI bajo el ámbito de este grupo",
  "access.ai.insights.switchHelp": "Apagado hasta que alguien lo encienda: cobrarle al cliente N veces las generaciones porque se creó una fila de grupo no es un valor por defecto que haya elegido nadie.",
  "access.ai.note.title": "Nota de contexto del grupo",
  "access.ai.note.desc": "Contexto que escribes para quienes leen a través de este grupo. Llega al chat, al Análisis con AI y a los correos de reporte, y orienta al modelo — no otorga nada.",
  "access.ai.note.label": "Nota",
  "access.ai.note.help": "Prosa simple: «este equipo solo mira Amazon US», «un pedido cuenta cuando se despacha». Lo que una pregunta puede leer lo decide el archivo de datos, así que una nota que pida algo fuera del ámbito no devuelve nada.",
  "access.ai.note.tooLong": "{n} caracteres, y el límite es {max}. No se corta nada por ti: esta nota se agrega entera a cada prompt que alcanza la gente de este grupo, así que se iría con media frase adentro. Acórtala y guarda.",
  "access.ai.note.sharedTitle": "Dónde más se lee esta nota",
  "access.ai.note.shared": "El Análisis con AI se guarda por ÁMBITO, no por grupo, así que dos grupos con el mismo ámbito leen un párrafo escrito con sus dos notas unidas. En el chat y en el correo de una persona, cada quien recibe las notas de sus propios grupos.",
  "access.ai.reach.title": "Hasta dónde llega el chat",
  "access.ai.reach.desc": "No es una opción. El chat lee el mismo archivo que abre el ámbito de quien pregunta, así que su alcance es el ámbito y nada de esta página puede ampliarlo.",
  "access.ai.reach.projectWide": "Preguntas sobre todo un proyecto",
  "access.ai.reach.projectWideBody": "Se ofrecen automáticamente en los proyectos con dos o más reportes publicados, para todo el cliente. Hoy no existe un interruptor por grupo, así que esta pestaña no muestra uno.",
  "access.ai.examples.title": "Ejemplos de entrenamiento que se le ocultan a este ámbito",
  "access.ai.examples.desc": "Un ejemplo se le oculta a un ámbito restringido salvo que su autor lo haya marcado como seguro. El corpus que ya existe empieza oculto, a propósito: un ejemplo que cita cifras reales de México se las enseñaría a un grupo que solo ve Estados Unidos.",
  "access.ai.examples.unknown": "cantidad desconocida",
  "access.ai.examples.unknownHint": "Ningún endpoint de lectura devuelve la marca de seguro-para-ámbitos-restringidos, así que desde acá nadie puede contarlos. Un cero diría que no se está ocultando nada, que es lo contrario del comportamiento por defecto.",
  "access.ai.examples.body": "La cantidad queda escrita en el log del chat en cada respuesta restringida. La marca que deja pasar un ejemplo es report_training.scope_safe, y el builder es el lado que va a escribirla — todavía no hay editor que la muestre.",
  "access.ai.verbosity.title": "Qué tan largas son las respuestas",
  "access.ai.verbosity.desc": "No hay una opción de largo por grupo.",
  "access.ai.verbosity.body": "El único control de largo que tiene el producto — corto, medio o largo — es el del envío automático que cada persona se arma en el portal, y es esa persona la que lo elige. Un control por grupo acá escribiría una opción que nadie lee.",
  "access.ai.cost.title": "Costo del Análisis con AI por ámbito",
  "access.ai.cost.tenantDesc": "Todos los proyectos de este cliente, con la frecuencia de recarga que indiques. Las cifras por mes son aritmética sobre un mes que no ocurrió; la columna medida es un conteo simple de los análisis que se escribieron de verdad en los últimos 30 días.",
  "access.ai.cost.beforeTitle": "Cuánto cuesta encenderlo",
  "access.ai.cost.beforeBody": "Un ámbito más sobre los proyectos donde el ámbito de este grupo tiene archivo. La cifra es la misma en los dos sentidos: apagado, es lo que sumarías; encendido, es lo que ahorrarías.",
  "access.ai.cost.unrestricted": "Este grupo lleva el ámbito sin restricción, así que el interruptor no suma nada: el análisis sin restricción ya se genera para cada proyecto, y es el que leen sus miembros.",
  "access.ai.cost.runs": "Recargas por mes",
  "access.ai.cost.runsHelp": "Cuántas veces al mes estos proyectos recargan su data — 4 si la carga es semanal, unas 30 si es diaria. No tiene valor por defecto porque el análisis se regenera cuando cambia la data, no por calendario, y esa frecuencia solo la sabes tú.",
  "access.ai.cost.runsRange": "Un número entero entre {min} y {max}. Acá no se asume nada por ti.",
  "access.ai.cost.compute": "Estimar",
  "access.ai.cost.notYet": "Completa las recargas por mes para ver la cifra. Hasta entonces no hay ninguna: una frecuencia inventada convertiría una suposición en un número en pantalla.",
  "access.ai.cost.adds": "Encenderlo suma unas {n} generaciones de Análisis con AI al mes.",
  "access.ai.cost.saves": "Apagarlo ahorra unas {n} generaciones de Análisis con AI al mes.",
  "access.ai.cost.tenantTotal": "Unas {n} generaciones de Análisis con AI al mes en todos los proyectos.",
  "access.ai.cost.partial": "Parcial: {n} proyecto(s) no se pudieron estimar y no están en el total.",
  "access.ai.cost.alreadyGenerating": "Este ámbito ya recibe análisis en: {projects}. Ahí su parte del costo ya se está gastando — por este grupo o por otro que lleve el mismo ámbito.",
  "access.ai.cost.basis.estimate": "estimación",
  "access.ai.cost.col.project": "Proyecto",
  "access.ai.cost.col.reports": "Reportes con análisis",
  "access.ai.cost.col.scopes": "Ámbitos que reciben uno",
  "access.ai.cost.col.perMonth": "Por mes",
  "access.ai.cost.col.measured": "Escritos en 30 días",
  "access.ai.cost.unavailable": "sin estimar",
  "access.ai.cost.unavailableHint": "La estimación de este proyecto no volvió. El motivo está debajo de la tabla, completo. Un cero acá sería una respuesta real y distinta.",
  "access.ai.cost.failedTitle": "No se pudo estimar «{project}»",
  "access.ai.cost.projectsFailedTitle": "No se pudo leer la lista de proyectos",
  "access.ai.cost.noProjects": "El ámbito de este grupo no tiene archivo en ningún proyecto, así que no hay nada que narrar bajo él.",
  "access.ai.cost.noProjectsTenant": "Este cliente no tiene proyectos.",
  "access.experience.grantsNothingTitle": "Nada de esta pestaña otorga acceso",
  "access.experience.grantsNothing": "Un widget que se le entrega a un miembro se sigue resolviendo contra sus propios permisos y su propio ámbito en cada lectura, y vuelve denegado cuando no puede abrirlo. Esta pestaña decide con qué EMPIEZA una persona, nunca qué puede ver.",
  "access.experience.save": "Guardar el perfil",
  "access.experience.clear": "Borrar el perfil",
  "access.experience.board.title": "Plantilla del tablero de Inicio",
  "access.experience.board.desc": "Cómo se ve el Inicio de un miembro la primera vez que lo abre. Cuando la plantilla cambia, su tablero se vuelve a sincronizar — y un widget que agregó esa persona no se toca nunca.",
  "access.experience.board.resync": "Volver a sincronizar los tableros de los miembros",
  "access.experience.board.resyncHelp": "Encendido: los widgets del grupo se reemplazan en cada lectura del tablero y todo lo que agregó la persona se queda. Apagado: la plantilla se aplica una sola vez, cuando la persona todavía no tiene tablero, y nunca más.",
  "access.experience.board.document": "Widgets, en JSON",
  "access.experience.board.documentHelp": "Un arreglo de objetos widget, con la misma forma que tiene un tablero de Inicio guardado. Como máximo {max}, cada uno con su propio id, y sin widgets de imagen — una imagen pertenece a los archivos que subió una sola persona y quedaría rota para el resto. El builder no tiene lienzo de tablero, así que la plantilla se edita como el documento que es; la vista previa de abajo es la ubicación real.",
  "access.experience.board.count": "{n} de {max} widgets.",
  "access.experience.board.overCapTitle": "Demasiados widgets",
  "access.experience.board.overCap": "El portal rechaza un tablero de más de {max} widgets, así que acá no se recorta nada para que entre: quita algunos y guarda de nuevo.",
  "access.experience.board.badJson": "Esto no es JSON válido: {detail}",
  "access.experience.board.notAnArray": "El documento tiene que ser un arreglo de widgets, aunque tenga uno solo.",
  "access.experience.board.notAnObject": "El elemento {position} no es un objeto. Cada widget es un objeto.",
  "access.experience.board.layoutError": "Esta disposición no se puede ubicar",
  "access.experience.board.preview": "Vista previa",
  "access.experience.board.previewHelp": "Doce columnas, en el orden del arreglo — la misma ubicación que corre el tablero del miembro, con el estirado vertical incluido.",
  "access.experience.board.previewEmpty": "Todavía no hay widgets.",
  "access.experience.board.noTiles": "Sin widgets en la plantilla. Un miembro sin plantilla recibe un Inicio vacío y arma el suyo.",
  "access.experience.board.col.position": "#",
  "access.experience.board.positionHint": "El índice que nombran los rechazos del servidor — «tile 2 of the board template».",
  "access.experience.board.col.kind": "Tipo",
  "access.experience.board.col.title": "Título",
  "access.experience.board.col.source": "Reporte o proyecto",
  "access.experience.board.col.size": "Tamaño",
  "access.experience.board.col.id": "Id",
  "access.experience.board.noTitle": "sin título",
  "access.experience.board.noKind": "sin tipo",
  "access.experience.board.noKindHint": "Un widget sin tipo no lo puede leer el esquema de widgets del portal, y la plantilla entera del grupo va a ser rechazada.",
  "access.experience.board.noId": "sin id",
  "access.experience.board.noIdHint": "El id es lo que le dice al tablero de un miembro qué widgets vinieron del grupo y cuáles agregó esa persona. Sin él, el widget no se puede resincronizar ni distinguir de los suyos.",
  "access.experience.board.badSize": "tamaño inválido",
  "access.experience.board.badSizeHint": "El ancho y el alto tienen que ser números: de 1 a 12 columnas, de 1 a 40 filas.",
  "access.experience.mail.title": "Correo por defecto",
  "access.experience.mail.desc": "El plan le da a un grupo un reporte programado que recibe cada integrante. Acá no se configura, porque todavía nada lo envía.",
  "access.experience.mail.body": "La columna puede guardar una plantilla de correo y el servidor la sigue validando, pero ningún código escribe la fila de programación de un miembro a partir de ella — eso necesita una columna de origen en la tabla de programaciones, junto a la API de programaciones del portal. Por eso acá no hay formulario. Un formulario dejaría que alguien fije una frecuencia, lea un guardado exitoso y crea que programó algo, y un aviso arriba no deshace eso. Hasta que exista quien la escriba, lo que la gente recibe es la programación que se arma en el portal. Algo queda resuelto igual por el esquema: una plantilla de grupo no lleva lista de destinatarios y no puede llevarla — un predeterminado de grupo se genera una vez por integrante, bajo el ámbito de esa persona y dirigido a ella, así que no hay forma de decir «manda el reporte de este grupo a otra persona».",
  "access.experience.mail.storedTitle": "Este grupo tiene una plantilla de correo guardada",
  "access.experience.mail.stored": "Alguien la escribió a mano en la columna. Se conserva tal cual cuando guardas esta pestaña, y sigue sin entregarse a nadie. «Borrar el perfil» la elimina junto con la plantilla del tablero.",
  "access.experience.threshold.title": "Alertas por umbral",
  "access.experience.threshold.desc": "No son una opción de grupo, y no pueden serlo.",
  "access.experience.threshold.body": "Una alerta por umbral lleva estado por fila — la última versión que vio, si ya disparó — y eso solo tiene sentido en una programación que una persona escribió y puede editar, así que vive en la programación de esa persona y no acá. Lo que sí agregó el Plan J es que cuando una de esas dispara, lee el archivo del ámbito del dueño, así que el número que compara es uno que el dueño tiene derecho a ver.",
  // Tareas: la bandera de la tabla compartida (solo en el portal).
  "tasks.mark.flag": "Reportar",
  "tasks.mark.reported": "Reportado",
  "tasks.mark.resolved": "Resuelto",
  "tasks.mark.column": "Reporte",
  "tasks.mark.wholeChart": "El gráfico completo",
  "tasks.mark.pickPoint": "Elegir un punto",
  "tasks.mark.pickHint": "Haz clic en el punto que quieres reportar",
  "tasks.mark.pickCancel": "Cancelar (Esc)",
};

// ── react-i18next init ──────────────────────────────────────
//
// Single namespace ("translation") with flat dotted keys
// ("editor.chart.titleLabel" etc). We disable keySeparator so
// dots are treated as literal — `nsSeparator: false` is left at
// its default `":"` because we saw in the hp-portal that disabling
// it breaks fallback lookup against EN.
//
// `prefix`/`suffix` override i18next's default `{{var}}` syntax to
// `{var}` so the existing dictionaries don't need a regex pass.
const _initPromise: Promise<unknown> = i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: EN },
      es: { translation: ES },
      pt: { translation: PT },
    },
    fallbackLng: "en",
    supportedLngs: ["en", "es", "pt"],
    keySeparator: false,
    interpolation: {
      escapeValue: false,
      prefix: "{",
      suffix: "}",
    },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "i18nextLng",
      caches: ["localStorage"],
    },
  });

// Surface init failures so they don't get swallowed silently
// in production. Keep as a side-effect — callers must not await.
_initPromise.catch((err: unknown) => {
  // eslint-disable-next-line no-console
  console.error("[i18n] init failed", err);
});

export default i18n;

// ── Helpers + backwards-compatible exports ──────────────────

export type TParams = Record<string, string | number>;

// Same rule as dashboard-render's ``toLang`` (an unknown tag reads as
// English). Not imported from there: this module is loaded by node-env
// tests that must not pull the render package.
function _normalizeLocale(raw: string | undefined): Locale {
  const prefix = (raw ?? "").slice(0, 2).toLowerCase();
  return prefix === "es" || prefix === "pt" ? prefix : "en";
}

/** Imperative `t()` for non-React callers (logs, mutations, etc.). */
export function t(key: string, params?: TParams): string {
  return i18n.t(key, (params ?? {}) as Record<string, unknown>);
}

/** Current locale ("en", "es" or "pt") for non-React callers. */
export function getLocale(): Locale {
  return _normalizeLocale(i18n.language);
}

/** Imperative locale switch — wraps `i18n.changeLanguage`. */
export function setLocale(locale: Locale): void {
  void i18n.changeLanguage(locale);
}

/**
 * Shim hook with the SAME shape as the old custom `useT()` so we
 * don't have to touch every consumer site:
 *
 *   const { t, locale, setLocale } = useT();
 *
 * Backed by `react-i18next::useTranslation()`. Triggers a re-render
 * automatically when the language changes.
 */
export function useT(): {
  t: (key: string, params?: TParams) => string;
  locale: Locale;
  setLocale: (locale: Locale) => void;
} {
  const { t: i18nT, i18n: i18nInst } = useTranslation();
  const localizedT = useCallback(
    (key: string, params?: TParams): string =>
      i18nT(key, (params ?? {}) as Record<string, unknown>),
    [i18nT],
  );
  const setLocaleCb = useCallback(
    (loc: Locale) => {
      void i18nInst.changeLanguage(loc);
    },
    [i18nInst],
  );
  return {
    t: localizedT,
    locale: _normalizeLocale(i18nInst.language),
    setLocale: setLocaleCb,
  };
}
